import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

// Lock Overlay Component - Moved outside to prevent recreation on every render
const LockOverlay: React.FC<{ compact?: boolean }> = React.memo(({ compact = false }) => (
  <div className={`absolute inset-0 bg-black/95 backdrop-blur-md z-[100] flex items-center justify-center ${compact ? 'min-h-[80px] rounded' : 'min-h-[400px] rounded-lg'} overflow-hidden touch-none`} style={{ touchAction: 'none', overscrollBehavior: 'none' }} onTouchStart={(e) => e.preventDefault()} onTouchMove={(e) => e.preventDefault()}>
    {compact ? (
      // Compact version for JSON containers - pill-shaped button only
      <Link
        to="/ask-access"
        className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap"
      >
        <span className="text-sm text-white font-semibold">Ask for access</span>
      </Link>
    ) : (
      // Full version for tool sections
      <div className="flex items-center gap-3">
        <svg className="w-8 h-8 text-[#26ab4d]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
        <Link
          to="/ask-access"
          className="text-base text-[#26ab4d] hover:text-[#1e8a3f] font-semibold transition-all duration-300"
        >
          Ask for access
        </Link>
      </div>
    )}
  </div>
));
LockOverlay.displayName = 'LockOverlay';

const ProductPage: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  // Use a function to prevent type narrowing
  const getProductId = () => productId;
  const currentProductId = getProductId();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<any>(null);
  const [riskyPhoneResult, setRiskyPhoneResult] = useState<any>(null);
  const [simCardResult, setSimCardResult] = useState<any>(null);
  const [demographicsResult, setDemographicsResult] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'quality' | 'risky' | 'simcard' | 'demographics'>('risky');
  
  // Email Intelligence states
  const [emailAddress, setEmailAddress] = useState('');
  const [isAnalyzingEmail, setIsAnalyzingEmail] = useState(false);
  const [emailReputationResult, setEmailReputationResult] = useState<any>(null);
  const [emailQualityResult, setEmailQualityResult] = useState<any>(null);
  const [emailRiskyResult, setEmailRiskyResult] = useState<any>(null);
  const [emailESRSResult, setEmailESRSResult] = useState<any>(null);
  const [emailStringResult, setEmailStringResult] = useState<any>(null);
  const [activeEmailTab, setActiveEmailTab] = useState<'reputation' | 'risky' | 'esrs' | 'string' | 'quality'>('reputation');
  
  // IP Intelligence states
  const [ipAddress, setIpAddress] = useState('');
  const [isAnalyzingIP, setIsAnalyzingIP] = useState(false);
  const [ipRiskyResult, setIpRiskyResult] = useState<any>(null);
  const [ipNonHumanResult, setIpNonHumanResult] = useState<any>(null);
  const [ipQualityResult, setIpQualityResult] = useState<any>(null);
  const [ipDemographicsResult, setIpDemographicsResult] = useState<any>(null);
  const [activeIPTab, setActiveIPTab] = useState<'risky' | 'nonhuman' | 'demographics' | 'quality'>('risky');
  
  // Data Leaks Intelligence states
  const [dataLeaksInput, setDataLeaksInput] = useState('');
  const [isAnalyzingDataLeaks, setIsAnalyzingDataLeaks] = useState(false);
  const [emailLeakResult, setEmailLeakResult] = useState<any>(null);
  const [phoneLeakResult, setPhoneLeakResult] = useState<any>(null);
  const [activeDataLeaksTab, setActiveDataLeaksTab] = useState<'email' | 'phone'>('email');
  
  // Location Intelligence states
  const [locationInput, setLocationInput] = useState('');
  const [isAnalyzingLocation, setIsAnalyzingLocation] = useState(false);
  const [locationResult, setLocationResult] = useState<any>(null);
  
  // Mobile to Address states
  const [mobileToAddressInput, setMobileToAddressInput] = useState('');
  const [isAnalyzingMobileToAddress, setIsAnalyzingMobileToAddress] = useState(false);
  const [mobileToAddressResult, setMobileToAddressResult] = useState<any>(null);
  
  // Mobile to UPI states
  const [mobileToUPIInput, setMobileToUPIInput] = useState('');
  const [isAnalyzingMobileToUPI, setIsAnalyzingMobileToUPI] = useState(false);
  const [mobileToUPIResult, setMobileToUPIResult] = useState<any>(null);
  
  // Mobile to LPG states
  const [mobileToLPGInput, setMobileToLPGInput] = useState('');
  const [isAnalyzingMobileToLPG, setIsAnalyzingMobileToLPG] = useState(false);
  const [mobileToLPGResult, setMobileToLPGResult] = useState<any>(null);
  
  const [showContactPopup, setShowContactPopup] = useState(false);
  const [heroImageLoaded, setHeroImageLoaded] = useState(true);
  const { accessGranted } = useAuth();
  const [hoveredAccordion, setHoveredAccordion] = useState<string | null>(null);
  const [openAccordions, setOpenAccordions] = useState<{ [key: string]: boolean }>({
    quality: false,
    risky: false,
    simcard: false,
    demographics: false,
    // Individual features
    'quality-reachability': false,
    'quality-recently-abused': false,
    'quality-linked-email': false,
    'quality-linked-phones': false,
    'quality-internet-based': false,
    'quality-frequent-abuse': false,
    'risky-valid': false,
    'risky-active': false,
    'risky-risky-activity': false,
    'risky-spam': false,
    'simcard-carrier': false,
    'simcard-line-type': false,
    'simcard-prepaid': false,
    'simcard-user-name': false,
    'demographics-country': false,
    'demographics-city': false,
    'demographics-zip': false,
    'demographics-timezone': false,
    // Email Intelligence accordions
    'email-reputation': false,
    'email-quality': false,
    'email-risky': false,
    'email-esrs': false,
    'email-string': false,
    // Email Reputation features
    'email-reputation-domain-trust': false,
    'email-reputation-domain-activity': false,
    'email-reputation-risky-extension': false,
    'email-reputation-sender-validation': false,
    'email-reputation-phishing': false,
    // Email Quality Score features
    'email-quality-spam-trap': false,
    'email-quality-recent-abuse': false,
    'email-quality-activity': false,
    'email-quality-complaint-history': false,
    'email-quality-linked-emails': false,
    'email-quality-linked-phones': false,
    // Email Risky Score features
    'email-risky-delivered': false,
    'email-risky-sender-reputation': false,
    'email-risky-suspicious': false,
    'email-risky-temporary': false,
    // Email ESRS features
    'email-esrs-format': false,
    'email-esrs-name-risk': false,
    'email-esrs-stop-words': false,
    // Email String Score features
    'email-string-account-length': false,
    'email-string-digit-count': false,
    'email-string-string-length': false,
    'email-string-vowels-count': false,
    'email-string-consonants-count': false,
    // IP Intelligence accordions
    'ip-risky': false,
    'ip-nonhuman': false,
    'ip-quality': false,
    'ip-demographics': false,
    // Risky IP Activity features
    'ip-risky-proxy': false,
    'ip-risky-vpn': false,
    'ip-risky-tor': false,
    // Non Human Activity features
    'ip-nonhuman-bot': false,
    'ip-nonhuman-crawler': false,
    // Network (IP) Quality features
    'ip-quality-trusted': false,
    'ip-quality-recent-fraud': false,
    'ip-quality-frequent-fraud': false,
    'ip-quality-high-risk': false,
    'ip-quality-connection-type': false,
    // Network Demographics features
    'ip-demographics-region': false,
    'ip-demographics-city': false,
    'ip-demographics-latitude': false,
    'ip-demographics-longitude': false,
    // Data Leaks Intelligence accordions
    'email-leak-footprint': false,
    'phone-leak-footprint': false,
    // Email Leak Footprint features
    'data-leaks-count': false,
    'email-leak-list': false,
    'email-leak-first-seen': false,
    'email-leak-last-seen': false,
    // Phone Leak Footprint features
    'phone-leak-count': false,
    'phone-leak-list': false,
    'phone-leak-first-seen': false,
    'phone-leak-last-seen': false,
    // Hardware Fingerprinting accordions
    'hardware-device-identification': false,
    // Hardware Device Identification features
    'hardware-device-brand-name': false,
    'hardware-device-model': false,
    'hardware-device-product-name': false,
    'hardware-app-version': false,
    'hardware-imei': false,
    'hardware-mac-address': false,
    'hardware-device-security': false,
    'hardware-rooted': false,
    'hardware-mock-location': false,
    'hardware-remote-access': false,
    'hardware-rat-detection': false,
    'hardware-emulator': false,
    'hardware-bot-detection': false,
    'hardware-fingerprint': false,
    'hardware-network-info': false,
    'hardware-wifi': false,
    'hardware-ip-address': false,
    // Behavioral Analysis accordions
    'behavioral-device-identification': false,
    'behavioral-risk-profile': false,
    'behavioral-network-geolocation': false,
    // Device Identification features
    'behavioral-device-brand': false,
    'behavioral-device-model': false,
    'behavioral-device-product': false,
    'behavioral-app-version': false,
    'behavioral-android-id': false,
    'behavioral-imei': false,
    'behavioral-mac-address': false,
    // Device Risk Profile features
    'behavioral-rooted': false,
    'behavioral-mock-location': false,
    'behavioral-remote-access': false,
    'behavioral-rat-detection': false,
    'behavioral-emulator': false,
    'behavioral-bot-detection': false,
    'behavioral-fingerprint': false,
    // Network & Geolocation features
    'behavioral-wifi': false,
    'behavioral-ip-address': false,
    'behavioral-timezone': false,
    'behavioral-sim-card': false,
    'behavioral-location': false,
    // Cognitive Behavioral Patterns
    'cognitive-behavioral-patterns': false,
    'form-timing-insights': false,
    'touch-patterns': false,
    'device-sensor-signals': false,
    'keyboard-behaviour': false,
    // Location Intelligence accordions
    'location-address-components': false,
    // Location Address Components features
    'location-city': false,
    'location-district': false,
    'location-state': false,
    'location-pin': false,
    'location-street': false,
    'location-sub-street': false,
    'location-locality': false,
    'location-street-dist': false,
    'location-formatted-address': false,
    // Mobile to Address accordions
    'mobile-to-address-components': false,
    // Mobile to Address Components features
    'mobile-to-address-first-name': false,
    'mobile-to-address-last-name': false,
    'mobile-to-address-email': false,
    'mobile-to-address-isd-code': false,
    'mobile-to-address-line-1': false,
    'mobile-to-address-line-2': false,
    'mobile-to-address-city': false,
    'mobile-to-address-state': false,
    'mobile-to-address-country': false,
    'mobile-to-address-country-code': false,
    'mobile-to-address-pincode': false,
    'mobile-to-address-last-delivery-date': false,
    // Mobile to UPI accordions
    'mobile-to-upi-bank-details': false,
    'mobile-to-upi-upi-id': false,
    // Fetch Bank Details features
    'mobile-to-upi-name': false,
    'mobile-to-upi-utr': false,
    'mobile-to-upi-account-number': false,
    'mobile-to-upi-ifsc': false,
    // Fetch UPI Id features
    'mobile-to-upi-upi-id-field': false,
    'mobile-to-upi-name-field': false,
    'mobile-to-upi-status': false,
    // Mobile to LPG accordions
    'mobile-to-lpg-components': false,
    // Mobile to LPG Components features
    'mobile-to-lpg-gas-provider': false,
    'mobile-to-lpg-name': false,
    'mobile-to-lpg-mobile': false,
    'mobile-to-lpg-consumer-id': false,
    'mobile-to-lpg-status': false,
    'mobile-to-lpg-consumer-type': false,
    'mobile-to-lpg-address': false,
    'mobile-to-lpg-distributor-code': false,
    'mobile-to-lpg-distributor-name': false,
    'mobile-to-lpg-distributor-contact': false,
    'mobile-to-lpg-distributor-address': false
  });


  // Close accordions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      
      // Don't close if clicking on scrollbar
      // Scrollbar clicks often target body/html, or the click position is near viewport edges
      if (target === document.body || target === document.documentElement) {
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const clickX = event.clientX;
        const clickY = event.clientY;
        const scrollbarWidth = 17; // Typical scrollbar width
        
        // Check if click is near right edge (vertical scrollbar) or bottom edge (horizontal scrollbar)
        if (clickX > viewportWidth - scrollbarWidth || clickY > viewportHeight - scrollbarWidth) {
          return;
        }
      }
      
      // Don't close if clicking on interactive elements (buttons, links, inputs)
      if (target.tagName === 'BUTTON' || target.tagName === 'A' || target.tagName === 'INPUT' || target.closest('button') || target.closest('a')) {
        return;
      }
      
      // Check if click is inside an accordion container
      // Accordion containers have: border, rounded-lg, overflow-hidden classes
      const accordionContainer = target.closest('div.border.rounded-lg.overflow-hidden');
      
      // If click is outside accordion containers, close all accordions and clear hover state
      // (Button clicks inside accordions will be handled by toggleAccordion)
      if (!accordionContainer) {
        setOpenAccordions({});
        setHoveredAccordion(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Memoize toggleAccordion to prevent unnecessary re-renders
  const toggleAccordion = useCallback((key: string) => {
    setOpenAccordions(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  }, []);

  const products = useMemo(() => [
    { 
      id: "digital-footprints", 
      title: "Digital Footprint", 
      subtitle: "Identify risk in every digital trace", 
      image: "/product-hero-icons/digital footprint.png",
      description: "Our Smart Intelligence analyzes digital footprints across email, phone, social profiles, and data leaks to detect fake signups, spot mule networks, and verify real users for fraud-free onboarding"
    },
    { 
      id: "phone-intelligence", 
      title: "Phone Intelligence", 
      subtitle: "Spot fake calls instantly", 
      image: "/web vectors/web vectors/phone.png",
      description: "Our Smart Intelligence checks phone behaviour, carrier details and VOIP risks to detect fake identities and mule numbers, helping institutions flag suspicious users early and onboard genuine customers"
    },
    { 
      id: "email-intelligence", 
      title: "Email Intelligence", 
      subtitle: "Catch fake email senders early", 
      image: "/web vectors/web vectors/email validation.png",
      description: "Our Smart Intelligence checks email behaviour and domain signals to detect valid, spoofed, disposable or high-risk addresses, helping businesses verify identities instantly and block fraud early"
    },
    { 
      id: "ip-intelligence", 
      title: "Network Quality", 
      subtitle: "Verify every connection from the Journey Begins.", 
      image: "/sub-product-tiles/phone.png",
      description: "Our Smart Risk Intelligence detects VPNs, proxies, TOR, and abnormal IP behaviour in real time, flagging hidden identities and risky networks early to block fraud before onboarding begins."
    },
    { 
      id: "social-media-intelligence", 
      title: "Social Media Intelligence", 
      subtitle: "Spot risky accounts early", 
      image: "/sub-product-tiles/social Media.png",
      description: "Our Smart Intelligence checks social profiles and flags fake, bot, or risky identities by analysing activity and behaviour patterns, helping institutions onboard real users and prevent social-driven fraud"
    },
    { 
      id: "data-leaks-intelligence", 
      title: "Data Leaks Intelligence", 
      subtitle: "Spot data leaks before fraud", 
      image: "/web vectors/web vectors/data leaks.png",
      description: "Our Smart Intelligence detects exposed emails, numbers, and personal data from verified breaches, scoring leak severity to flag stolen credentials, prevent account takeovers, and secure onboarding"
    },
    { 
      id: "cognitive-behaviour", 
      title: "Cognitive Behaviour", 
      subtitle: "Detect real intent behind actions", 
      image: "/product-hero-icons/cognitive behaviour.png",
      description: "Our Smart Intelligence tracks how users type, scroll, and interact to uncover real intent, spot bots and account takeovers, and keep genuine journeys safe and smooth"
    },
    { 
      id: "device-forensic", 
      title: "Device Forensic", 
      subtitle: "Identify device risks instantly", 
      image: "/product-hero-icons/device forensics.png",
      description: "Our Smart Intelligence detects risky devices through fingerprinting and forensics, spotting cloned, rooted, or emulated setups in real time to block fraud before onboarding."
    },
    { 
      id: "geolocation", 
      title: "Geolocation", 
      subtitle: "Detect fraud using geolocation data", 
      image: "/product-hero-icons/geolocation.png",
      description: "Our Smart Intelligence verifies user locations from latitude & longitude data, giving accurate region details to confirm authenticity, spot mismatches, and improve fraud checks during onboarding."
    },
    { 
      id: "network-quality", 
      title: "Network Quality", 
      subtitle: "Verify every network connection", 
      image: "/product-hero-icons/network quality.png",
      description: "Our Smart Intelligence detects VPNs, proxies, TOR, and abnormal IP activity in real time, identifying hidden identities and risky networks early to stop fraud before onboarding."
    },
    { 
      id: "mobile-to-sources", 
      title: "Mobile to Sources", 
      subtitle: "Verify identity behind every number", 
      image: "/product-hero-icons/mobile to sources vector.png",
      description: "Our Smart Intelligence connects mobile numbers to verified sources like UPI, address, and LPG data to spot mismatches, detect synthetic identities, and confirm authenticity in real time."
    },
    { 
      id: "mobile-to-address", 
      title: "Mobile to address", 
      subtitle: "Map mobile numbers to addresses", 
      image: "/mobile-to-sources-icons/address-m2s.png",
      description: "Our Smart Intelligence maps mobile numbers to verified addresses, detects mismatches, and flags high-risk identities, helping institutions validate users fast and prevent address-based fraud"
    },
    { 
      id: "mobile-to-upi", 
      title: "Mobile to UPI", 
      subtitle: "Verify UPI links with confidence.", 
      image: "/mobile-to-sources-icons/upi-m2s.png",
      description: "Our Smart Intelligence maps mobile numbers to verified UPI IDs, confirms real ownership, detects fake or inactive links, and flags suspicious patterns to help institutions prevent payment fraud."
    },
    { 
      id: "mobile-to-lpg", 
      title: "Mobile to LPG", 
      subtitle: "Verify identities through LPG data.", 
      image: "/mobile-to-sources-icons/lpg.png",
      description: "Our Smart Intelligence maps mobile numbers to verified LPG connections, confirming consumer and distributor details, checking address accuracy to validate users and prevent synthetic identity fraud."
    },
    { 
      id: "litigation-lookup", 
      title: "Litigation Lookup", 
      subtitle: "Spot legal risks early", 
      image: "/product-hero-icons/litigation lookup.png",
      description: "Our Smart Intelligence scans court and regulatory records to find past fraud or disputes, helping businesses verify credibility, flag high-risk users, and make safer onboarding decisions."
    },
    { 
      id: "document-forged", 
      title: "Document Forged", 
      subtitle: "Detect document fraud instantly", 
      image: "/product-hero-icons/forged doc.png",
      description: "Our Smart Intelligence detects forged or tampered documents in real time, validating authenticity, spotting deepfakes, and stopping fake IDs early to ensure only genuine users complete onboarding."
    },
    { 
      id: "transaction-patterns", 
      title: "Transaction pattern", 
      subtitle: "Track intent in every patterns", 
      image: "/product-hero-icons/transaction patterns, graph db.png",
      description: "Our Smart Intelligence checks transaction flows and behaviour patterns to detect mule networks, abnormal activity, and coordinated fraud in real time to confirm genuine intent."
    },
  ], []);

  const product = useMemo(() => {
    return products.find(p => p.id === productId);
  }, [productId, products]);

  // Separate mapping for below-hero section content (new punch lines and descriptions)
  const belowHeroContent = useMemo(() => {
    const contentMap: { [key: string]: { subtitle: string; description: string } } = {
      'digital-footprints': {
        subtitle: 'Track digital behaviour clearly to identify risk early.',
        description: 'Our Smart Intelligence analyzes digital footprints across email, phone, social profiles, and data leaks to uncover fake signups, spot mule networks, and verify real users to keep onboarding journey trusted and fraud-free.'
      },
      'cognitive-behaviour': {
        subtitle: 'Identify user intent before it turns into fraud.',
        description: 'Our Smart Risk Intelligence analyzes how user\'s type, scroll, and interact to reveal true intent, detect bots and account takeovers, that keep genuine user journeys safe, smooth, and trusted.'
      },
      'device-forensic': {
        subtitle: 'Know every device\'s risk before it enters your system.',
        description: 'Our Smart Risk Intelligence identifies risky devices using advanced fingerprinting and forensics, detecting cloned, rooted, or emulated setups in real time to block fraudulent environments before onboarding begins.'
      },
      'geolocation': {
        subtitle: 'Verify real locations with accurate, trusted data.',
        description: 'Our Smart Risk Intelligence validates user locations from latitude–longitude input, providing precise regional details to confirm authenticity, detect inconsistencies, and strengthen fraud prevention during onboarding.'
      },
      'network-quality': {
        subtitle: 'Identify risky connections with real-time network insights.',
        description: 'Our Smart Risk Intelligence detects VPNs, proxies, TOR, and abnormal IP behaviour in real time, flagging hidden identities and risky networks early to block fraud before onboarding begins.'
      },
      'mobile-to-sources': {
        subtitle: 'Uncover verified identities linked to any mobile number.',
        description: 'Our Smart Risk Intelligence links mobile numbers to verified sources like UPI, address, and LPG records. Bargad.ai flags mismatches, detects synthetic identities, and confirms user authenticity in real time.'
      },
      'litigation-lookup': {
        subtitle: 'Ensure trust by verifying legal history instantly.',
        description: 'Our Smart Risk Intelligence scans court and regulatory records to identify past fraud and litigation patterns, helping businesses verify credibility, flag high-risk users, and make safer onboarding decisions.'
      },
      'document-forged': {
        subtitle: 'Catch forged documents at the first glance.',
        description: 'Our Smart Risk Intelligence detects forged and tampered documents in real time. Bargad.ai validates authenticity, flags deepfakes, and stops fake IDs early to ensure only genuine users complete onboarding.'
      },
      'transaction-patterns': {
        subtitle: 'Spot unusual financial behaviour before it becomes a threat.',
        description: 'Our Smart Risk Intelligence analyzes transaction flow and behaviour patterns to detect mule networks, abnormal activity, and coordinated fraud in real time ensuring every transaction reflects genuine user intent.'
      }
    };
    return productId ? contentMap[productId] : null;
  }, [productId]);

  // Aggressive preload critical hero image with multiple strategies
  useEffect(() => {
    if (product?.image) {
      const imageUrl = product.image;
      // Encode URL properly for preload links (handles spaces and special characters)
      const encodedImageUrl = imageUrl.split('/').map(segment => 
        segment.includes(' ') ? encodeURIComponent(segment) : segment
      ).join('/');
      const links: HTMLLinkElement[] = [];
      
      // Strategy 1: Link preload with high priority (browser hint)
      const preloadLink = document.createElement('link');
      preloadLink.rel = 'preload';
      preloadLink.as = 'image';
      preloadLink.href = encodedImageUrl;
      preloadLink.setAttribute('fetchpriority', 'high');
      document.head.appendChild(preloadLink);
      links.push(preloadLink);
      
      // Strategy 2: Force immediate fetch using Image constructor (parallel download)
      const img = new Image();
      img.src = imageUrl; // Use original URL for Image constructor
      // Set high priority if supported
      if ('fetchPriority' in img) {
        (img as any).fetchPriority = 'high';
      }
      
      // Strategy 3: Also add prefetch hint for browsers that support it
      const prefetchLink = document.createElement('link');
      prefetchLink.rel = 'prefetch';
      prefetchLink.href = encodedImageUrl;
      document.head.appendChild(prefetchLink);
      links.push(prefetchLink);
      
      return () => {
        // Cleanup: Remove all preload links
        links.forEach(link => {
          if (link && link.parentNode) {
            document.head.removeChild(link);
          }
        });
      };
    }
  }, [product?.image, product?.title]);

  // Reset image loaded state when product changes and force immediate load
  useEffect(() => {
    setHeroImageLoaded(true);
    
    // If product image exists, pre-warm the image immediately
    if (product?.image) {
      const img = new Image();
      img.onload = () => {
        // Small delay for smooth transition
        setTimeout(() => {
        setHeroImageLoaded(false);
        }, 50);
      };
      img.onerror = () => {
        setHeroImageLoaded(false);
      };
      img.src = product.image;
    } else {
      // Fade out after a short delay if no product
      const timer = setTimeout(() => {
        setHeroImageLoaded(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [productId, product?.image]);

  // Reset Data Leaks results when navigating to/away from Data Leaks page
  useEffect(() => {
    if (productId !== 'data-leaks-intelligence') {
      // Reset results when navigating away
      setEmailLeakResult(null);
      setPhoneLeakResult(null);
      setDataLeaksInput('');
      setIsAnalyzingDataLeaks(false);
    } else {
      // Reset results when navigating to Data Leaks page
      setEmailLeakResult(null);
      setPhoneLeakResult(null);
      setIsAnalyzingDataLeaks(false);
    }
  }, [productId]);

  // Helper function to format JSON with color-coded booleans - Memoized with useCallback
  const formatJSONWithColors = useCallback((obj: any): React.ReactElement[] => {
    const jsonString = JSON.stringify(obj, null, 2);
    const lines = jsonString.split('\n');
    
    return lines.map((line: string, index: number) => {
      // Match boolean values with proper indentation
      const trueMatch = line.match(/^(\s*)(".*?"):\s*(true)(,?)$/);
      const falseMatch = line.match(/^(\s*)(".*?"):\s*(false)(,?)$/);
      
      if (trueMatch) {
        const [, indent, key, , comma] = trueMatch;
        return (
          <React.Fragment key={index}>
            {indent}<span className="text-[#9a9a9a] font-bold">{key}</span> : <span className="text-[#9a9a9a] font-bold">true</span>{comma}
            {'\n'}
          </React.Fragment>
        );
      } else if (falseMatch) {
        const [, indent, key, , comma] = falseMatch;
        return (
          <React.Fragment key={index}>
            {indent}<span className="text-[#9a9a9a] font-bold">{key}</span> : <span className="text-[#9a9a9a] font-bold">false</span>{comma}
            {'\n'}
          </React.Fragment>
        );
      }
      // For other lines, also make property names green if they contain a colon
      const propertyMatch = line.match(/^(\s*)(".*?")(\s*:\s*)(.+?)(,?)$/);
      if (propertyMatch) {
        const [, indent, key, , value, comma] = propertyMatch;
        // Convert string values to lowercase (but preserve quotes and structure)
        let lowerValue = value;
        if (value.startsWith('"') && value.endsWith('"')) {
          // It's a string value, convert to lowercase
          const innerValue = value.slice(1, -1);
          lowerValue = `"${innerValue.toLowerCase()}"`;
        } else if (value.toLowerCase() === 'true' || value.toLowerCase() === 'false') {
          // It's a boolean, ensure lowercase
          lowerValue = value.toLowerCase();
        }
        return (
          <React.Fragment key={index}>
            {indent}<span className="text-[#9a9a9a] font-bold">{key}</span>{` : `}<span className="text-[#9a9a9a] font-bold">{lowerValue}</span>{comma}
            {'\n'}
          </React.Fragment>
        );
      }
      return <React.Fragment key={index}>{line}{'\n'}</React.Fragment>;
    });
  }, []);

  // Helper function to flatten nested objects into a single level - Memoized
  const flattenObject = useCallback((obj: any, prefix: string = '', result: any = {}): any => {
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const newKey = prefix ? `${prefix}.${key}` : key;
        if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
          flattenObject(obj[key], newKey, result);
        } else {
          result[newKey] = obj[key];
        }
      }
    }
    return result;
  }, []);

  // Helper function to format flattened JSON with color-coded values - Memoized with useCallback
  const formatFlattenedJSON = useCallback((obj: any): React.ReactElement[] => {
    const flattened = flattenObject(obj);
    const keys = Object.keys(flattened);
    const elements: React.ReactElement[] = [];
    
    elements.push(<React.Fragment key="start">{`{\n`}</React.Fragment>);
    
    keys.forEach((key, index) => {
      const value = flattened[key];
      const isLast = index === keys.length - 1;
      const comma = isLast ? '' : ',';
      
      if (typeof value === 'boolean') {
        elements.push(
          <React.Fragment key={key}>
            {` `}<span className="text-[#9a9a9a] font-bold">{`"${key}"`}</span>{` : `}
                                    <span className="text-[#9a9a9a] font-bold">{value ? 'true' : 'false'}</span>
            {comma}
            {'\n'}
          </React.Fragment>
        );
      } else if (Array.isArray(value)) {
        // Convert array values to lowercase if they are strings
        const lowerArray = value.map(item => typeof item === 'string' ? item.toLowerCase() : item);
        const arrayStr = JSON.stringify(lowerArray);
        elements.push(
          <React.Fragment key={key}>
            {` `}<span className="text-[#9a9a9a] font-bold">{`"${key}"`}</span>{` : `}<span className="text-[#9a9a9a] font-bold">{arrayStr}</span>{comma}
            {'\n'}
          </React.Fragment>
        );
      } else if (typeof value === 'string') {
        elements.push(
          <React.Fragment key={key}>
            {` `}<span className="text-[#9a9a9a] font-bold">{`"${key}"`}</span>{` : `}<span className="text-[#9a9a9a] font-bold">{`"${value.toLowerCase()}"`}</span>{comma}
            {'\n'}
          </React.Fragment>
        );
      } else if (typeof value === 'number') {
        elements.push(
          <React.Fragment key={key}>
            {` `}<span className="text-[#9a9a9a] font-bold">{`"${key}"`}</span>{` : `}<span className="text-[#9a9a9a] font-bold">{value}</span>{comma}
            {'\n'}
          </React.Fragment>
        );
      } else {
        const valueStr = JSON.stringify(value);
        elements.push(
          <React.Fragment key={key}>
            {` `}<span className="text-[#9a9a9a] font-bold">{`"${key}"`}</span>{` : `}<span className="text-[#9a9a9a] font-bold">{valueStr}</span>{comma}
            {'\n'}
          </React.Fragment>
        );
      }
    });
    
    elements.push(<React.Fragment key="end">{`}`}</React.Fragment>);
    
    return elements;
  }, [flattenObject]);

  // Validation functions
  const isValidPhone = (phone: string): boolean => {
    if (!phone.trim()) return true; // Empty is valid (no error shown until user types)
    // Remove spaces, dashes, and plus signs for validation
    const cleaned = phone.replace(/[\s\-+]/g, '');
    // Check if it's 10 digits (Indian format) or 10-15 digits (international)
    return /^\d{10,15}$/.test(cleaned);
  };

  const isValidEmail = (email: string): boolean => {
    if (!email.trim()) return true; // Empty is valid
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isValidIP = (ip: string): boolean => {
    if (!ip.trim()) return true; // Empty is valid
    const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    return ipRegex.test(ip);
  };

  const isValidDataLeaksInput = (input: string): boolean => {
    if (!input.trim()) return true; // Empty is valid
    // Can be either email or phone
    return isValidEmail(input) || isValidPhone(input);
  };

  const isValidLocation = (location: string): boolean => {
    if (!location.trim()) return true; // Empty is valid
    // Should be numeric (pincode) - 4 to 10 digits
    return /^\d{4,10}$/.test(location.trim());
  };

  // Unified handler for all phone intelligence analysis
  const handleAnalyzeAll = () => {
    if (!phoneNumber.trim()) return;
    
    setIsAnalyzing(true);
    
    // Reset all results
    setAnalysisResult(null);
    setRiskyPhoneResult(null);
    setSimCardResult(null);
    setDemographicsResult(null);
    
    // Simulate API calls for all 4 categories - Replace with actual API integration
    setTimeout(() => {
      // 1. Phone Number Quality Score
      const qualityScoreResult = {
        overallScore: Math.floor(Math.random() * 40) + 60, // Score between 60-100
        reachability: Math.random() > 0.3,
        recentlyAbused: Math.random() > 0.7,
        internetBased: Math.random() > 0.6,
        frequentAbuse: Math.random() > 0.8,
        linkedEmails: Math.random() > 0.5 
          ? ['anandj123@gmail.com', 'anandjain@gmail.com'] 
          : [],
        linkedPhones: Math.random() > 0.5 
          ? ['998855XXXX', '982144XXXX'] 
          : [],
        jsonResponse: {
          Phone_No_Reachability: Math.random() > 0.3,
          Phone_No_Recently_abused_activity: Math.random() > 0.7,
          Phone_No_Linked_to_Email: Math.random() > 0.5 
            ? ['anandj123@gmail.com', 'anandjain@gmail.com'] 
            : [],
          Phone_No_List_of_linked_phone_numbers: Math.random() > 0.5 
            ? ['9988558732', '9821442345'] 
            : [],
          Phone_No_Internet_based: Math.random() > 0.6,
          Phone_No_Frequent_abuse_History: Math.random() > 0.8
        }
      };
      
      // 2. Risky Phone
      const riskyPhoneData = {
        jsonResponse: {
          Phone_No_Valid: Math.random() > 0.2,
          Phone_No_Active: Math.random() > 0.3,
          Phone_No_Risky_activity: Math.random() > 0.7,
          Phone_No_Spam: Math.random() > 0.7
        }
      };
      
      // 3. Sim Card Info
      const simCardData = {
        jsonResponse: {
          Phone_No_Network_carrier: ['JIO', 'Airtel', 'VI', 'BSNL'][Math.floor(Math.random() * 4)],
          Phone_No_Line_Type: ['Wireless', 'Landline', 'VOIP', 'Satellite'][Math.floor(Math.random() * 4)],
          Phone_No_Prepaid: Math.random() > 0.5,
          Phone_No_User_name: 'ANAND JAIN'
        }
      };
      
      // 4. Phone Number Demographics
      const demographicsData = {
        jsonResponse: {
          Phone_No_Country: 'IN',
          Phone_No_City: ['Mumbai', 'Delhi', 'Bangalore', 'Chennai'][Math.floor(Math.random() * 4)],
          Phone_No_Region: ['Maharashtra', 'Delhi', 'Karnataka', 'Tamil Nadu'][Math.floor(Math.random() * 4)],
          Phone_No_Zip_Code: ['400079', '110001', '560001', '600001'][Math.floor(Math.random() * 4)],
          Phone_No_Timezone: 'Asia/Kolkata'
        }
      };
      
      setAnalysisResult(qualityScoreResult);
      setRiskyPhoneResult(riskyPhoneData);
      setSimCardResult(simCardData);
      setDemographicsResult(demographicsData);
      setIsAnalyzing(false);
      setActiveTab('risky'); // Set to first tab after analysis completes
    }, 2000);
  };

  // Unified handler for all email intelligence analysis
  const handleAnalyzeEmailAll = () => {
    if (!emailAddress.trim()) return;
    
    setIsAnalyzingEmail(true);
    
    // Reset all results
    setEmailReputationResult(null);
    setEmailQualityResult(null);
    setEmailRiskyResult(null);
    setEmailESRSResult(null);
    setEmailStringResult(null);
    
    // Simulate API calls for all 5 categories - Replace with actual API integration
    setTimeout(() => {
      // 1. Email Reputation
      const reputationData = {
        jsonResponse: {
          "email_domain_trust": ["trusted", "positive", "neutral", "suspicious", "malicious", "not rated"][Math.floor(Math.random() * 6)],
          "email_domain_activity": ["high", "medium", "low", "none"][Math.floor(Math.random() * 4)],
          "email_risky_extension": Math.random() > 0.7,
          "email_sender_validation": Math.random() > 0.5,
          "email_phishing": Math.random() > 0.8
        }
      };
      
      // 2. Email Quality Score
      const qualityData = {
        jsonResponse: {
          "email_spam_trap_score": ["high", "medium", "low", "none"][Math.floor(Math.random() * 4)],
          "email_recent_abuse_activity": Math.random() > 0.7,
          "email_activity": Math.random() > 0.3,
          "email_frequent_complaint_history": Math.random() > 0.7,
          "emails_linked_to_this_id": Math.floor(Math.random() * 5) + 1,
          "emails_linked_to_phone_nos": Math.random() > 0.5 
            ? ["887766XXXX", "776699XXXX"] 
            : []
        }
      };
      
      // 3. Email Risky Score
      const riskyData = {
        jsonResponse: {
          "email_be_delivered": ["high", "medium", "low"][Math.floor(Math.random() * 3)],
          "email_sender_reputation_score": Math.floor(Math.random() * 5) - 1, // -1 to 3
          "email_suspicious": Math.random() > 0.7,
          "email_temporary": Math.random() > 0.8
        }
      };
      
      // 4. Email Syntax Risk Score (ESRS)
      const esrsData = {
        jsonResponse: {
          "email_format": Math.random() > 0.8,
          "email_name_risk_type": Math.random() > 0.7 ? "N/A" : ["random_typing", "placeholder", "fictional", "invalid_characters"][Math.floor(Math.random() * 4)],
          "email_contains_stop_words": Math.random() > 0.7
        }
      };
      
      // 5. Email String Score
      const stringData = {
        jsonResponse: {
          "email_account_length": Math.floor(Math.random() * 20) + 5,
          "email_digit_count": Math.floor(Math.random() * 10),
          "email_string_length": Math.floor(Math.random() * 15) + 3,
          "email_vowels_counts": Math.floor(Math.random() * 8) + 1,
          "email_consonants_counts": Math.floor(Math.random() * 12) + 2
        }
      };
      
      setEmailReputationResult(reputationData);
      setEmailQualityResult(qualityData);
      setEmailRiskyResult(riskyData);
      setEmailESRSResult(esrsData);
      setEmailStringResult(stringData);
      setIsAnalyzingEmail(false);
      setActiveEmailTab('reputation'); // Set to first tab after analysis completes
    }, 2000);
  };

  // Unified handler for all IP intelligence analysis
  const handleAnalyzeIPAll = () => {
    if (!ipAddress.trim()) return;
    
    setIsAnalyzingIP(true);
    
    // Reset all results
    setIpRiskyResult(null);
    setIpNonHumanResult(null);
    setIpQualityResult(null);
    setIpDemographicsResult(null);
    
    // Simulate API calls for all 4 categories - Replace with actual API integration
    setTimeout(() => {
      // 1. Risky IP Activity
      const riskyData = {
        jsonResponse: {
          "IP - Proxy": Math.random() > 0.5,
          "IP - Virtual Private Network": Math.random() > 0.6,
          "IP - TOR Network": Math.random() > 0.8
        }
      };
      
      // 2. Non Human Activity
      const nonHumanData = {
        jsonResponse: {
          "IP - Recent Bot Activity": Math.random() > 0.7,
          "IP - Crawler Activity": Math.random() > 0.8
        }
      };
      
      // 3. Network (IP) Quality
      const qualityData = {
        jsonResponse: {
          "IP - Trusted": Math.random() > 0.5,
          "IP - Recent Fraud": Math.random() > 0.7,
          "IP - Frequent Fraud History": ["high", "medium", "low", "none"][Math.floor(Math.random() * 4)],
          "IP - High Risk Attack Record": Math.random() > 0.8,
          "IP - Connection Type": ["residential", "corporate", "education", "mobile", "data center"][Math.floor(Math.random() * 5)]
        }
      };
      
      // 4. Network Demographics
      const demographicsData = {
        jsonResponse: {
          "IP - Region": "Maharashtra",
          "IP - City": "Dombivali",
          "IP - Latitide": 19.22,
          "IP - Longitude": 73.08
        }
      };
      
      setIpRiskyResult(riskyData);
      setIpNonHumanResult(nonHumanData);
      setIpQualityResult(qualityData);
      setIpDemographicsResult(demographicsData);
      setIsAnalyzingIP(false);
      setActiveIPTab('risky'); // Set to first tab after analysis completes
    }, 2000);
  };

  // Unified handler for Data Leaks analysis
  const handleAnalyzeDataLeaks = () => {
    if (!dataLeaksInput.trim()) return;
    
    setIsAnalyzingDataLeaks(true);
    
    // Reset all results
    setEmailLeakResult(null);
    setPhoneLeakResult(null);
    
    // Auto-detect if input is email or phone
    const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(dataLeaksInput.trim());
    const detectedType = isEmail ? 'email' : 'phone';
    
    // Simulate API calls - Replace with actual API integration
    setTimeout(() => {
      if (detectedType === 'email') {
        // Email Leak Footprint
        const emailLeakData = {
          jsonResponse: {
            "Email - Data Leaks Count": 2,
            "Email - Data Leaks List": [
              {
                "name": "Zomato",
                "title": "Zomato",
                "breach_date": "2016-07-01"
              },
              {
                "name": "FreshMenu",
                "breach_date": "2017-03-07"
              }
            ],
            "Email - Leaks First Seen": "2016-07-01",
            "Email - Leaks Last Seen": "2017-03-07"
          }
        };
        setEmailLeakResult(emailLeakData);
        setActiveDataLeaksTab('email'); // Set to first tab after analysis completes
      } else {
        // Phone Leak Footprint
        const phoneLeakData = {
          jsonResponse: {
            "Phone No - Data Leaks Count": 1,
            "Phone No - List of Data Leaks": [
              {
                "Name": "indiamart",
                "Breach_date": "2021-05-23"
              }
            ],
            "Phone No - First Seen Data Leaks": "2021-05-23",
            "Phone No - Last Seen Data Leaks": "2021-05-23"
          }
        };
        setPhoneLeakResult(phoneLeakData);
        setActiveDataLeaksTab('phone'); // Set to phone tab when phone results are available
      }
      
      setIsAnalyzingDataLeaks(false);
    }, 2000);
  };

  // Handler for Location Intelligence
  const handleAnalyzeLocation = () => {
    if (!locationInput.trim()) return;
    
    setIsAnalyzingLocation(true);
    setLocationResult(null);
    
    // Simulate API call - Replace with actual API integration
    setTimeout(() => {
      const locationData = {
        jsonResponse: {
          "City": "Mumbai",
          "District": "Mumbai Suburban District",
          "State": "Maharashtra",
          "Pin": "400079",
          "Street": "Hiranandani Link Road",
          "sub_street": "Wing B, Kailash Complex",
          "Locality": "Vikhroli West",
          "Street_dist": 61,
          "Formatted_Address": "Hiranandani Link Road, Kailash Industrial Complex, Vikhroli West, Mumbai, Maharashtra 400079 (India)"
        }
      };
      
      setLocationResult(locationData);
      setIsAnalyzingLocation(false);
    }, 2000);
  };

  // Handler for Mobile to Address
  const handleAnalyzeMobileToAddress = () => {
    if (!mobileToAddressInput.trim()) return;
    
    setIsAnalyzingMobileToAddress(true);
    setMobileToAddressResult(null);
    
    // Simulate API call - Replace with actual API integration
    setTimeout(() => {
      const mobileToAddressData = {
        jsonResponse: {
          "first_name": "Karan",
          "last_name": "Deshmukh",
          "email": "karan.deshmukh@example.com",
          "isd_code": "+91",
          "line_1": "Flat 402, Shantivan Residency",
          "line_2": "Near River Park",
          "city": "Pune",
          "state": "Maharashtra",
          "state_code": "MH",
          "country": "India",
          "country_code": "IN",
          "pin_code": 411038,
          "last_delivery_date": "2025-01-14"
        }
      };
      
      setMobileToAddressResult(mobileToAddressData);
      setIsAnalyzingMobileToAddress(false);
    }, 2000);
  };

  // Handler for Mobile to UPI
  const handleAnalyzeMobileToUPI = () => {
    if (!mobileToUPIInput.trim()) return;
    
    setIsAnalyzingMobileToUPI(true);
    setMobileToUPIResult(null);
    
    // Simulate API call - Replace with actual API integration
    setTimeout(() => {
      const mobileToUPIData = {
        jsonResponse: {
          "name": "HERO INDUSTRY TEXT PVT LTD",
          "utr": "P2509251722124359741384986",
          "account_number": 657104000201315,
          "ifsc": "ABCD0000000",
          "upi": "patternsheet097-10@okicici",
          "upi_id": "patternsheet097-10@okicici",
          "status": "VALID"
        }
      };
      
      setMobileToUPIResult(mobileToUPIData);
      setIsAnalyzingMobileToUPI(false);
    }, 2000);
  };

  // Handler for Mobile to LPG
  const handleAnalyzeMobileToLPG = () => {
    if (!mobileToLPGInput.trim()) return;
    
    setIsAnalyzingMobileToLPG(true);
    setMobileToLPGResult(null);
    
    // Simulate API call - Replace with actual API integration
    setTimeout(() => {
      const mobileToLPGData = {
        jsonResponse: {
          "gas_provider": "IndiGas Utilities",
          "name": "Ramesh S. Kulkarni",
          "mobile": "9000000001",
          "consumer_id": "LPG-2025-45871234",
          "status": "Active",
          "consumer_type": "Domestic",
          "address": "Flat 402, Sunrise Apartments, MG Road, Pune, Maharashtra, 411001",
          "distributor_code": "DSTR-0458",
          "distributor_name": "Asha LPG Distributors",
          "distributor_contact": "7022233445",
          "distributor_address": "No. 12, Industrial Estate Road, Pune, Maharashtra, 411018"
        }
      };
      
      setMobileToLPGResult(mobileToLPGData);
      setIsAnalyzingMobileToLPG(false);
    }, 2000);
  };

  // Define sub-products for each product
  const getSubProducts = (productId: string | undefined) => {
    if (!productId) return [];
    
    const subProductsMap: { [key: string]: any[] } = {
      'digital-footprints': [
    {
      id: 'email-intelligence',
      title: 'Email Intelligence',
      subtitle: 'Email validation and verification',
      description: 'Email validation, verification, domain reputation, and cross-platform matching',
      icon: '✉️',
      color: 'from-[#26ab4d] to-[#1e8a3f]',
      image: '/sub-product-tiles/email.png'
    },
    {
      id: 'phone-intelligence',
      title: 'Phone Intelligence',
      subtitle: 'Phone validation and fraud detection',
      description: 'Comprehensive phone number validation, fraud detection, and risk analysis',
      icon: '📱',
      color: 'from-[#26ab4d] to-[#1e8a3f]',
      image: '/sub-product-tiles/phone.png'
    },
    {
      id: 'social-media-intelligence',
      title: 'Social Media Intelligence',
      subtitle: 'Social media profiling and discovery',
      description: 'Social media profile discovery, cross-platform identity linking, and reputation analysis',
      icon: '🌐',
      color: 'from-[#26ab4d] to-[#1e8a3f]',
      image: '/sub-product-tiles/social Media.png'
    },
    {
      id: 'data-leaks-intelligence',
      title: 'Data Leaks Intelligence',
      subtitle: 'Breach detection and credential monitoring',
      description: 'Breach detection, compromised credential monitoring, and dark web scanning',
      icon: '🔒',
      color: 'from-[#26ab4d] to-[#1e8a3f]',
      image: '/sub-product-tiles/Data Leaks.png'
    }
      ],
      'cognitive-behaviour': [
        {
          id: 'behavioral-analysis',
          title: 'Behavioral Analysis',
          subtitle: 'Pattern recognition',
          description: 'Advanced behavioral pattern recognition and cognitive analytics',
          icon: '🧠',
          color: 'from-[#26ab4d] to-[#1e8a3f]',
          image: '/nine-box-model/cognitive behaviour.png'
        }
      ],
      'device-forensic': [
        {
          id: 'hardware-fingerprinting',
          title: 'Hardware Fingerprinting',
          subtitle: 'Device identification',
          description: 'Deep device intelligence and hardware fingerprinting technology',
          icon: '💻',
          color: 'from-[#26ab4d] to-[#1e8a3f]',
          image: '/nine-box-model/device forensic.png'
        }
      ],
      'geolocation': [
        {
          id: 'location-intelligence',
          title: 'Location Intelligence',
          subtitle: 'Geospatial analysis',
          description: 'Real-time location intelligence and geospatial analysis',
          icon: '📍',
          color: 'from-[#26ab4d] to-[#1e8a3f]',
          image: '/nine-box-model/geo location.png'
        }
      ],
      'network-quality': [
        {
          id: 'ip',
          title: 'IP',
          subtitle: 'IP address intelligence',
          description: 'Analyze IP addresses for risk and geolocation',
          icon: '🌐',
          color: 'from-[#26ab4d] to-[#1e8a3f]',
          image: '/nine-box-model/network connectivity.png'
        }
      ],
      'mobile-to-sources': [
        {
          id: 'mobile-to-address',
          title: 'Mobile to address',
          subtitle: 'See where every number truly belongs.',
          description: 'Our Smart Risk Intelligence maps mobile numbers to verified addresses, uncovering real-world identity links. Bargad.ai validates authenticity, detects mismatches, and flags high-risk profiles that helps institutions verify users faster and stop address-based fraud.',
          icon: '🏠',
          color: 'from-[#26ab4d] to-[#1e8a3f]',
          image: '/mobile-to-sources-icons/address.png'
        },
        {
          id: 'mobile-to-upi',
          title: 'Mobile to UPI',
          subtitle: 'Verify the real accounts behind every UPI.',
          description: 'Our Smart Risk Intelligence maps mobile numbers to verified UPI IDs, revealing genuine ownership and transaction credibility. Bargad.ai validates UPI details, detects fake or inactive links, and flags suspicious patterns that helps institutions verify users instantly and prevent payment fraud.',
          icon: '💳',
          color: 'from-[#26ab4d] to-[#1e8a3f]',
          image: '/mobile-to-sources-icons/upi.png'
        },
        {
          id: 'mobile-to-lpg',
          title: 'Mobile to LPG',
          subtitle: 'Reveal real identities through LPG.',
          description: 'Our Smart Risk Intelligence maps mobile numbers to verified LPG connections, revealing authentic household identities. Bargad.ai validates consumer details, distributor info, and address accuracy that helps institutions confirm real users, detect inconsistencies, and prevent synthetic identity fraud.',
          icon: '⛽',
          color: 'from-[#26ab4d] to-[#1e8a3f]',
          image: '/mobile-to-sources-icons/lpg.png'
        }
      ],
      'litigation-lookup': [
        {
          id: 'criminal-check',
          title: 'Criminal Check',
          subtitle: 'Criminal records',
          description: 'Comprehensive litigation and background check services',
          icon: '⚖️',
          color: 'from-[#26ab4d] to-[#1e8a3f]',
          image: '/nine-box-model/litigation lookup.png'
        }
      ],
      'document-forged': [
        {
          id: 'all-docs',
          title: 'All docs',
          subtitle: 'Document verification',
          description: 'Verify all types of documents including identity, income, and bank statements',
          icon: '📄',
          color: 'from-[#26ab4d] to-[#1e8a3f]',
          image: '/nine-box-model/doc forged.png'
        }
      ],
      'transaction-patterns': [
        {
          id: 'graph-db-connection',
          title: 'Neural networks',
          subtitle: 'Graph database',
          description: 'Transaction pattern analysis using graph database connections',
          icon: '📊',
          color: 'from-[#26ab4d] to-[#1e8a3f]',
          image: '/nine-box-model/transaction patters.png'
        }
      ]
    };
    
    return subProductsMap[productId] || [];
  };
  
  const subProducts = getSubProducts(productId);

  // List of sub-products that have their own dedicated pages (not in main products array)
  const specialSubProducts = ['ip', 'phone-intelligence', 'email-intelligence', 'data-leaks-intelligence', 'hardware-fingerprinting', 'behavioral-analysis', 'social-media-intelligence', 'location-intelligence', 'mobile-to-address', 'mobile-to-upi', 'mobile-to-lpg'];

  // Function to find parent product for a given sub-product ID
  const getParentProduct = (subProductId: string | undefined) => {
    if (!subProductId) return null;
    
    const subProductsMap: { [key: string]: any[] } = {
      'digital-footprints': [
        { id: 'email-intelligence' },
        { id: 'phone-intelligence' },
        { id: 'social-media-intelligence' },
        { id: 'data-leaks-intelligence' }
      ],
      'cognitive-behaviour': [
        { id: 'behavioral-analysis' }
      ],
      'device-forensic': [
        { id: 'hardware-fingerprinting' }
      ],
      'geolocation': [
        { id: 'location-intelligence' }
      ],
      'network-quality': [
        { id: 'ip' }
      ],
      'mobile-to-sources': [
        { id: 'mobile-to-address' },
        { id: 'mobile-to-upi' },
        { id: 'mobile-to-lpg' }
      ],
      'litigation-lookup': [
        { id: 'criminal-check' }
      ],
      'document-forged': [
        { id: 'all-docs' }
      ],
      'transaction-patterns': [
        { id: 'graph-db-connection' }
      ]
    };
    
    for (const [parentId, subs] of Object.entries(subProductsMap)) {
      if (subs.some(sub => sub.id === subProductId)) {
        return products.find(p => p.id === parentId);
      }
    }
    
    return null;
  };

  const parentProduct = getParentProduct(productId);
  const isSubProduct = !!parentProduct || (productId && !product && specialSubProducts.includes(productId));
  
  // Function to get subproduct title
  const getSubProductTitle = (subProductId: string | undefined): string => {
    if (!subProductId) return '';
    
    const subProductTitles: { [key: string]: string } = {
      'email-intelligence': 'Email Intelligence',
      'phone-intelligence': 'Phone Intelligence',
      'social-media-intelligence': 'Social Media Intelligence',
      'data-leaks-intelligence': 'Data Leaks Intelligence',
      'behavioral-analysis': 'Behavioral Analysis',
      'hardware-fingerprinting': 'Hardware Fingerprinting',
      'location-intelligence': 'Location Intelligence',
      'mobile-to-address': 'Mobile to address',
      'mobile-to-upi': 'Mobile to UPI',
      'mobile-to-lpg': 'Mobile to LPG',
      'ip': 'IP Intelligence'
    };
    
    return subProductTitles[subProductId] || subProductId;
  };
  
  // List of industry/use case IDs that should redirect to use-cases page
  const industryIds = ['matrimony', 'banking', 'nbfc', 'insurance', 'gaming', 'ecommerce'];

  // Redirect industry pages to use-cases route
  if (productId && industryIds.includes(productId)) {
    return <Navigate to={`/use-cases/${productId}`} replace />;
  }

  if (!product && !specialSubProducts.includes(productId || '')) {
    return (
      <div className="min-h-screen bg-black relative overflow-hidden">
        {/* Background Gradient */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#26ab4d]/25 via-black to-[#1e8a3f]/25"></div>
          <div className="absolute inset-0 bg-gradient-radial from-[#26ab4d]/10 via-transparent to-transparent" style={{
            background: 'radial-gradient(circle at 20% 30%, rgba(38, 171, 77, 0.15) 0%, transparent 50%), radial-gradient(circle at 80% 70%, rgba(30, 138, 63, 0.15) 0%, transparent 50%)'
          }}></div>
        </div>

        {/* Content */}
        <div className="relative z-10 flex items-center justify-center min-h-screen">
          <div className="container mx-auto px-[1.5rem] py-[7rem] sm:py-[8rem]">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-[1.5rem] lg:text-[1.75rem] xl:text-[3rem] 2xl:text-[4.5rem] font-black text-[#ffffff] mb-4 xl:mb-6 leading-tight">
                We are still working on this <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#26ab4d] to-[#1e8a3f] whitespace-nowrap">page</span>.. stay tuned!!
              </h1>
              <p className="text-[0.8125rem] lg:text-[0.875rem] xl:text-[1rem] text-[#ffffff] leading-relaxed">
                Our knowledge base is coming soon with comprehensive resources, documentation, and insights about Bargad.ai's fraud prevention solutions.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black relative overflow-hidden">
      {/* Background Gradient - Applied to entire page (matching IndustryPage) */}
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-[#26ab4d]/25 via-black to-[#26ab4d]/25"></div>
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(circle at 20% 30%, rgba(38, 171, 77, 0.15) 0%, transparent 50%), radial-gradient(circle at 80% 70%, rgba(38, 171, 77, 0.15) 0%, transparent 50%)'
        }}></div>
      </div>
      {/* Hero Section */}
      {productId !== 'hardware-fingerprinting' && productId !== 'behavioral-analysis' && productId !== 'location-intelligence' && productId !== 'ip' && (
      <section id="hero" className="h-[75vh] xl:h-[85vh] relative overflow-hidden bg-transparent">
                
        <div className="relative z-30 h-full flex items-center pt-[7rem] lg:pt-[10rem] xl:pt-[14rem] pb-[2rem] lg:pb-[2.5rem] xl:pb-[4rem]">
          <div className="container mx-auto px-[1.5rem] w-full">
            <div className={`grid ${currentProductId === 'ip' ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1 lg:grid-cols-2'} gap-[1.25rem] lg:gap-[1.75rem] xl:gap-[2.5rem] items-center`}>
              {/* Left Side - Text Content (shown second on mobile, centered) */}
              {product && (
                <div className="space-y-[0.875rem] lg:space-y-[1.25rem] xl:space-y-[1.5rem] lg:max-w-[90%] order-2 lg:order-1 text-center lg:text-left">
                  <div className="inline-block lg:inline-block">
                    <div>
                      <h1 className="text-[1.5rem] lg:text-[1.75rem] xl:text-[2.5rem] 2xl:text-[3rem] font-black text-[#ffffff] leading-tight mb-2 lg:mb-4 xl:mb-6 whitespace-nowrap">
                        {product.title}
                      </h1>
                      <h2 className="text-[1.25rem] lg:text-[1.5rem] xl:text-[2rem] 2xl:text-[2.25rem] font-bold text-[#26ab4d] mb-2 lg:mb-4 xl:mb-6 whitespace-nowrap">
                        {product.subtitle}
                      </h2>
                    </div>
                    
                    <p className="block text-[0.8125rem] lg:text-[0.875rem] xl:text-[1rem] text-[#ffffff] leading-relaxed tracking-wider mx-0 text-left">
                      {product.description}
                    </p>
                  </div>
                </div>
              )}

              {/* Right Side - Product Image (shown first on mobile) */}
              {product && (
              <div className={`flex justify-center lg:justify-end lg:items-start order-1 lg:order-2 lg:ml-8 xl:ml-12`}>
                <div className="relative w-[200px] h-[200px] lg:w-[380px] lg:h-[320px] xl:w-[500px] xl:h-[400px] flex items-center justify-center overflow-hidden mt-10 lg:self-start lg:mt-[1rem] xl:mt-[1.5rem]">
                  <img 
                    src={currentProductId === 'social-media-intelligence' ? '/web vectors/web vectors/social media.png' : currentProductId === 'mobile-to-lpg' ? '/mobile-to-sources-icons/lpg-m2s.png' : product.image} 
                    alt={product.title}
                    className={`w-full h-full transition-opacity duration-1000 ease-in-out transform ${['network-quality', 'cognitive-behaviour', 'document-forged', 'digital-footprints', 'transaction-patterns'].includes(currentProductId || '') ? 'scale-200 object-cover' : 'object-contain scale-100'}`}
                    style={{
                      backgroundColor: 'transparent',
                      mixBlendMode: 'normal',
                      objectFit: ['network-quality', 'cognitive-behaviour', 'document-forged', 'digital-footprints', 'transaction-patterns'].includes(currentProductId || '') ? 'cover' : 'contain',
                      objectPosition: 'center center',
                      opacity: heroImageLoaded ? 0 : 1
                    }}
                    loading="eager"
                    decoding="async"
                    fetchPriority="high"
                    width={500}
                    height={500}
                    onLoad={() => {
                      if (heroImageLoaded) {
                        setTimeout(() => {
                      setHeroImageLoaded(false);
                        }, 50);
                      }
                    }}
                    onError={() => {
                      setHeroImageLoaded(false);
                    }}
                  />
                </div>
              </div>
              )}
              
              {/* Social Media Intelligence Hero Image (shown first on mobile) */}
              {currentProductId === 'social-media-intelligence' && !product && (
                <div className="flex justify-center lg:justify-end lg:items-start order-1 lg:order-2 lg:ml-8 xl:ml-12">
                  <div className="relative w-[200px] h-[200px] lg:w-[380px] lg:h-[320px] xl:w-[500px] xl:h-[400px] flex items-center justify-center overflow-hidden mt-10 lg:self-start lg:-mt-[1.25rem] xl:-mt-[2rem]">
                    <img 
                      src="/web vectors/web vectors/social media.png" 
                      alt="Social Media Intelligence"
                      className="w-full h-full object-contain transition-opacity duration-1000 ease-in-out transform scale-100"
                      style={{
                        backgroundColor: 'transparent',
                        mixBlendMode: 'normal',
                        objectFit: 'contain',
                        objectPosition: 'center center',
                        opacity: heroImageLoaded ? 0 : 1
                      }}
                      loading="eager"
                      decoding="async"
                      fetchPriority="high"
                      width={500}
                      height={500}
                      onLoad={() => {
                        if (heroImageLoaded) {
                          setTimeout(() => {
                            setHeroImageLoaded(false);
                          }, 50);
                        }
                      }}
                      onError={() => {
                        setHeroImageLoaded(false);
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Breadcrumb Navigation - For main products */}
      {product && !isSubProduct && (
        <div className="relative z-20 bg-transparent py-3 sm:py-4">
          <div className="container mx-auto px-[1.5rem]">
            <nav className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-[0.875rem] lg:text-[0.9375rem] xl:text-[1rem] font-bold w-fit flex-wrap">
              <Link 
                to="/" 
                className="text-[#ffffff] hover:text-[#26ab4d] transition-colors"
              >
                Home
              </Link>
              <span className="text-[#26ab4d]">/</span>
              <Link 
                to="/products" 
                className="text-[#ffffff] hover:text-[#26ab4d] transition-colors"
              >
                Products
              </Link>
              <span className="text-[#26ab4d]">/</span>
              <span className="text-[#ffffff]">
                {product.title}
              </span>
            </nav>
          </div>
        </div>
      )}

      {/* Breadcrumb Navigation - For sub-products with parent */}
      {isSubProduct && parentProduct && !['hardware-fingerprinting', 'behavioral-analysis', 'location-intelligence', 'ip'].includes(productId || '') && (
        <div className="relative z-20 bg-transparent py-3 sm:py-4">
          <div className="container mx-auto px-[1.5rem]">
            <nav className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-[0.875rem] lg:text-[0.9375rem] xl:text-[1rem] font-bold w-fit flex-wrap">
              <Link 
                to="/" 
                className="text-[#ffffff] hover:text-[#26ab4d] transition-colors"
              >
                Home
              </Link>
              <span className="text-[#26ab4d]">/</span>
              <Link 
                to="/products" 
                className="text-[#ffffff] hover:text-[#26ab4d] transition-colors"
              >
                Products
              </Link>
              <span className="text-[#26ab4d]">/</span>
              <Link 
                to={`/products/${parentProduct.id}`}
                className="text-[#ffffff] hover:text-[#26ab4d] transition-colors"
              >
                {parentProduct.title}
              </Link>
              <span className="text-[#26ab4d]">/</span>
              <span className="text-[#ffffff]">
                {getSubProductTitle(productId)}
              </span>
            </nav>
          </div>
        </div>
      )}

      {/* Breadcrumb Navigation - For sub-products without parent (special cases) */}
      {isSubProduct && !parentProduct && productId && specialSubProducts.includes(productId) && !['ip', 'hardware-fingerprinting', 'behavioral-analysis', 'location-intelligence'].includes(productId) && (
        <div className="relative z-20 bg-transparent py-3 sm:py-4">
          <div className="container mx-auto px-[1.5rem]">
            <nav className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-[0.875rem] lg:text-[0.9375rem] xl:text-[1rem] font-bold w-fit flex-wrap">
              <Link 
                to="/" 
                className="text-[#ffffff] hover:text-[#26ab4d] transition-colors"
              >
                Home
              </Link>
              <span className="text-[#26ab4d]">/</span>
              <Link 
                to="/products" 
                className="text-[#ffffff] hover:text-[#26ab4d] transition-colors"
              >
                Products
              </Link>
              <span className="text-[#26ab4d]">/</span>
              <span className="text-[#ffffff]">
                {getSubProductTitle(productId)}
              </span>
            </nav>
          </div>
        </div>
      )}

      {/* Social Media Intelligence - Ask for Access Subheading Section */}
      {currentProductId === 'social-media-intelligence' && (
        <section className="pt-[1.5rem] pb-0 relative z-20 bg-transparent overflow-hidden">
          <div className="container mx-auto px-[1.5rem] relative z-10">
            <div className="w-full">
              <div className="text-center mb-4">
                <h2 className="text-[1.25rem] xl:text-[1.5rem] font-bold text-[#26ab4d] mb-3">
                  For deeper insights, <Link to="/ask-access" className="text-[#26ab4d] hover:text-[#1e8a3f] underline transition-colors">ask for access</Link> or connect with our team.
                </h2>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Product Punch Line Section - Below breadcrumb */}
      {subProducts.length > 0 && belowHeroContent && (
        <section className="pt-[3rem] sm:pt-[3.5rem] lg:pt-[4rem] pb-[2rem] sm:pb-[2.5rem] lg:pb-[3rem] relative z-20 bg-transparent overflow-hidden">
          <div className="container mx-auto px-[1.5rem] relative z-10">
            <div className="w-full">
              <div className="text-center mb-3 sm:mb-4">
                {belowHeroContent.subtitle && (
                  <h2 className="text-[1.25rem] lg:text-[1.5rem] xl:text-[2rem] 2xl:text-[2.25rem] font-bold text-[#26ab4d] mb-2 sm:mb-3">
                    {belowHeroContent.subtitle}
                  </h2>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Explore Our Solutions Section - For all products */}
      {subProducts.length > 0 && (
        <section className="pt-4 sm:pt-6 pb-[3rem] sm:pb-[4rem] lg:pb-[5rem] relative z-20 bg-transparent overflow-hidden">
            <div className="container mx-auto px-[1.5rem] relative z-10">
            <div className="w-full">
              {/* Tiles Grid - Responsive grid layout */}
              <div className={`flex flex-col items-center lg:grid ${subProducts.length === 1 ? 'lg:grid-cols-1' : subProducts.length === 2 ? 'lg:grid-cols-2' : subProducts.length === 3 ? 'lg:grid-cols-3' : 'lg:grid-cols-2 xl:grid-cols-4'} gap-4 lg:gap-3 xl:gap-4 justify-items-center max-w-[800px] mx-auto`}>
                {subProducts.map((subProduct) => (
                  <Link
                    key={subProduct.id}
                    to={`/products/${subProduct.id}`}
                    className="group bg-gradient-to-br from-[#26ab4d]/20 to-[#1e8a3f]/20 rounded-xl p-2.5 sm:p-3 lg:p-[1rem] border border-[#26ab4d]/30 text-center aspect-square flex flex-col justify-center items-center w-[9rem] h-[9rem] sm:w-[10rem] sm:h-[10rem] lg:w-[9rem] lg:h-[9rem] xl:w-[12rem] xl:h-[12rem] hover:scale-105 transition-transform duration-300 mb-4 lg:mb-0 last:mb-0 mx-auto lg:mx-0"
                  >
                    <div className="flex items-center justify-center mb-1.5 sm:mb-2 w-full">
                      <img 
                        src={subProduct.image} 
                        alt={subProduct.title} 
                        className="w-[2.5rem] h-[2.5rem] sm:w-[3rem] sm:h-[3rem] lg:w-[3.5rem] lg:h-[3.5rem] object-contain max-w-[3.5rem] max-h-[3.5rem] mx-auto" 
                        loading="lazy" 
                        decoding="async" 
                        width="56" 
                        height="56"
                        style={{ maxWidth: '56px', maxHeight: '56px', display: 'block' }}
                        onError={(e) => {
                          const el = e.currentTarget as HTMLImageElement;
                          if (!el.dataset.fallbackTried) {
                            el.dataset.fallbackTried = '1';
                            el.src = subProduct.image;
                          }
                        }}
                      />
                    </div>
                    <h5 className="text-xs sm:text-sm font-semibold text-[#ffffff] text-center w-full line-clamp-2 leading-tight">
                      {subProduct.title.includes(' Intelligence') 
                        ? (
                          <>
                            {subProduct.title.replace(' Intelligence', '')}
                            <br />
                            Intelligence
                          </>
                        )
                        : subProduct.title
                      }
                    </h5>
                  </Link>
                ))}
              </div>
            </div>
          </div>
          </section>
      )}

      {/* Phone Intelligence Section - Standalone Product */}
      {currentProductId === 'phone-intelligence' && (
        <>
          {/* Phone Intelligence Section - Consolidated */}
          <section className="pt-[4rem] pb-[0.5rem] relative z-20 bg-transparent overflow-hidden">
            <div className="container mx-auto px-3 sm:px-4 lg:px-[1.5rem] relative z-10">
              {/* Features & JSON Types Section */}
              <div className="bg-black/20 rounded-lg p-3 sm:p-4 lg:p-6 mb-6 sm:mb-8 border border-[#26ab4d]/30">
                <h4 className="text-lg sm:text-xl lg:text-2xl font-bold text-[#ffffff] mb-3 sm:mb-4 text-center">
                  Features & JSON Types
                </h4>
                
                <div className="space-y-3">
                    {/* Risky Phone Accordion */}
                    <div className="border border-[#26ab4d]/30 rounded-lg overflow-hidden">
                      <button
                        onClick={() => toggleAccordion('risky')}
                        className="w-full relative p-3 sm:p-4 text-left hover:bg-[#26ab4d]/10 transition-colors"
                      >
                  <div className="pr-8 sm:pr-10">
                          <h5 className="text-sm sm:text-base lg:text-lg font-bold text-[#26ab4d]">Risky Phone</h5>
                          <p className="text-[#ffffff] text-[0.75rem] sm:text-xs lg:text-sm mt-1 leading-relaxed">
                            Assesses phone authenticity and behavior to detect misuse or fraud.
                          </p>
                      </div>
                        <span className={`absolute top-3 right-3 sm:top-4 sm:right-4 text-[#26ab4d] text-base sm:text-lg lg:text-xl xl:text-2xl font-bold transition-all duration-300 ${openAccordions.risky ? '' : ''}`}>
                          {openAccordions.risky ? '−' : '+'}
                        </span>
                      </button>
                      <div className={`overflow-hidden transition-all duration-300 ${openAccordions.risky ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                        <div className="p-4 pt-0 space-y-2">
                        {/* Phone No - Valid */}
                        <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                          {accessGranted ? (
                            <>
                          <button
                            onClick={() => toggleAccordion('risky-valid')}
                            className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                          >
                            {/* Mobile: Top row with icon and plus-minus */}
                            <div className="flex justify-between items-start mb-2 lg:hidden">
                              <img src="/phone-intelligence-icons/valid.png" alt="Valid" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                              <span className={`text-[#26ab4d] text-lg sm:text-xl font-bold transition-all duration-300 flex-shrink-0 ${openAccordions['risky-valid'] ? '' : ''}`}>
                                {openAccordions['risky-valid'] ? '−' : '+'}
                              </span>
                            </div>
                            {/* Mobile: Header and Description stacked vertically */}
                            <div className="flex flex-col mb-2 lg:hidden">
                              <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Phone No - Valid</h6>
                              <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                  Checks if the phone number looks correct and follows the proper format for that country.
                                </p>
                              </div>
                            {/* Desktop: Original layout */}
                            <div className="hidden lg:flex items-center gap-2 flex-1">
                              <img src="/phone-intelligence-icons/valid.png" alt="Valid" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                              <div className="flex-1">
                                <h6 className="text-base font-semibold text-[#ffffff]">Phone No - Valid</h6>
                                <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                  Checks if the phone number looks correct and follows the proper format for that country.
                                </p>
                            </div>
                            </div>
                            <span className={`hidden lg:block text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['risky-valid'] ? 'mr-1' : ''}`}>
                              {openAccordions['risky-valid'] ? '−' : '+'}
                            </span>
                          </button>
                            </>
                          ) : (
                            <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                              {/* Mobile: Top row with icon and lock */}
                              <div className="flex justify-between items-start mb-2 lg:hidden">
                                <img src="/phone-intelligence-icons/valid.png" alt="Valid" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                <div 
                                  className="flex-shrink-0 flex items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('risky-valid')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'risky-valid' 
                                      ? 'opacity-100 translate-x-0 scale-100' 
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg 
                                    className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'risky-valid' 
                                        ? 'scale-110 rotate-[-5deg]' 
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                  >
                                    <path 
                                      strokeLinecap="round" 
                                      strokeLinejoin="round" 
                                      strokeWidth={2} 
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                    />
                                  </svg>
                                </div>
                              </div>
                              {/* Mobile: Header and Description stacked vertically */}
                              <div className="flex flex-col mb-2 lg:hidden">
                                <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Phone No - Valid</h6>
                                <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                  Checks if the phone number looks correct and follows the proper format for that country.
                                </p>
                              </div>
                              {/* Desktop: Original layout */}
                              <div className="hidden lg:flex items-center gap-2 flex-1">
                                <img src="/phone-intelligence-icons/valid.png" alt="Valid" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Phone No - Valid</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Checks if the phone number looks correct and follows the proper format for that country.
                                  </p>
                                </div>
                              </div>
                              <div 
                                className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                onMouseEnter={() => setHoveredAccordion('risky-valid')}
                                onMouseLeave={() => setHoveredAccordion(null)}
                              >
                                <div className={`transition-all duration-300 ease-out ${
                                  hoveredAccordion === 'risky-valid' 
                                    ? 'opacity-100 translate-x-0 scale-100' 
                                    : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                }`}>
                                  <Link
                                    to="/ask-access"
                                    className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                  >
                                    <span className="text-sm text-white font-semibold">Ask for access</span>
                                  </Link>
                                </div>
                                <svg 
                                  className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                    hoveredAccordion === 'risky-valid' 
                                      ? 'scale-110 rotate-[-5deg]' 
                                      : 'hover:scale-105'
                                  }`}
                                  fill="none" 
                                  stroke="currentColor" 
                                  viewBox="0 0 24 24"
                                >
                                  <path 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round" 
                                    strokeWidth={2} 
                                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                  />
                                </svg>
                              </div>
                            </div>
                          )}
                          <div className={`overflow-hidden transition-all duration-300 ${openAccordions['risky-valid'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                            <div className="px-3 pb-3">
                              <div className="mt-2">
                                <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"phone_no_valid": true}, null, 2)}
                                  </pre>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Phone No - Active */}
                        <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                          {accessGranted ? (
                            <>
                          <button
                            onClick={() => toggleAccordion('risky-active')}
                            className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                          >
                            {/* Mobile: Top row with icon and plus-minus */}
                            <div className="flex justify-between items-start mb-2 lg:hidden">
                              <img src="/phone-intelligence-icons/active.png" alt="Active" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                              <span className={`text-[#26ab4d] text-lg sm:text-xl font-bold transition-all duration-300 flex-shrink-0 ${openAccordions['risky-active'] ? '' : ''}`}>
                                {openAccordions['risky-active'] ? '−' : '+'}
                              </span>
                            </div>
                            {/* Mobile: Header and Description stacked vertically */}
                            <div className="flex flex-col mb-2 lg:hidden">
                              <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Phone No - Active</h6>
                              <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                  Tells you whether the phone number is currently active or not.
                                </p>
                              </div>
                            {/* Desktop: Original layout */}
                            <div className="hidden lg:flex items-center gap-2 flex-1">
                              <img src="/phone-intelligence-icons/active.png" alt="Active" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                              <div className="flex-1">
                                <h6 className="text-base font-semibold text-[#ffffff]">Phone No - Active</h6>
                                <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                  Tells you whether the phone number is currently active or not.
                                </p>
                            </div>
                            </div>
                            <span className={`hidden lg:block text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['risky-active'] ? 'mr-1' : ''}`}>
                              {openAccordions['risky-active'] ? '−' : '+'}
                            </span>
                          </button>
                            </>
                          ) : (
                            <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                              {/* Mobile: Top row with icon and lock */}
                              <div className="flex justify-between items-start mb-2 lg:hidden">
                                <img src="/phone-intelligence-icons/active.png" alt="Active" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                <div 
                                  className="flex-shrink-0 flex items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('risky-active')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'risky-active' 
                                      ? 'opacity-100 translate-x-0 scale-100' 
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg 
                                    className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'risky-active' 
                                        ? 'scale-110 rotate-[-5deg]' 
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                  >
                                    <path 
                                      strokeLinecap="round" 
                                      strokeLinejoin="round" 
                                      strokeWidth={2} 
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                    />
                                  </svg>
                                </div>
                              </div>
                              {/* Mobile: Header and Description stacked vertically */}
                              <div className="flex flex-col mb-2 lg:hidden">
                                <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Phone No - Active</h6>
                                <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                  Tells you whether the phone number is currently active or not.
                                </p>
                              </div>
                              {/* Desktop: Original layout */}
                              <div className="hidden lg:flex items-center gap-2 flex-1">
                                <img src="/phone-intelligence-icons/active.png" alt="Active" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Phone No - Active</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Tells you whether the phone number is currently active or not.
                                  </p>
                                </div>
                              </div>
                              <div 
                                className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                onMouseEnter={() => setHoveredAccordion('risky-active')}
                                onMouseLeave={() => setHoveredAccordion(null)}
                              >
                                <div className={`transition-all duration-300 ease-out ${
                                  hoveredAccordion === 'risky-active' 
                                    ? 'opacity-100 translate-x-0 scale-100' 
                                    : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                }`}>
                                  <Link
                                    to="/ask-access"
                                    className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                  >
                                    <span className="text-sm text-white font-semibold">Ask for access</span>
                                  </Link>
                                </div>
                                <svg 
                                  className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                    hoveredAccordion === 'risky-active' 
                                      ? 'scale-110 rotate-[-5deg]' 
                                      : 'hover:scale-105'
                                  }`}
                                  fill="none" 
                                  stroke="currentColor" 
                                  viewBox="0 0 24 24"
                                >
                                  <path 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round" 
                                    strokeWidth={2} 
                                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                  />
                                </svg>
                              </div>
                            </div>
                          )}
                          <div className={`overflow-hidden transition-all duration-300 ${openAccordions['risky-active'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                            <div className="px-3 pb-3">
                              <div className="mt-2">
                                <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"phone_no_active": true}, null, 2)}
                                  </pre>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Phone No - Risky Activity */}
                        <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                          {accessGranted ? (
                            <>
                          <button
                            onClick={() => toggleAccordion('risky-risky-activity')}
                            className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                          >
                            {/* Mobile: Top row with icon and plus-minus */}
                            <div className="flex justify-between items-start mb-2 lg:hidden">
                              <img src="/phone-intelligence-icons/risk.png" alt="Risky Activity" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                              <span className={`text-[#26ab4d] text-lg sm:text-xl font-bold transition-all duration-300 flex-shrink-0 ${openAccordions['risky-risky-activity'] ? '' : ''}`}>
                                {openAccordions['risky-risky-activity'] ? '−' : '+'}
                              </span>
                            </div>
                            {/* Mobile: Header and Description stacked vertically */}
                            <div className="flex flex-col mb-2 lg:hidden">
                              <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Phone No - Risky Activity</h6>
                              <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                  Checks whether a phone number has a history of being used in fraud, scams, robocalls, fake accounts, or suspicious behavior.
                                </p>
                              </div>
                            {/* Desktop: Original layout */}
                            <div className="hidden lg:flex items-center gap-2 flex-1">
                              <img src="/phone-intelligence-icons/risk.png" alt="Risky Activity" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                              <div className="flex-1">
                                <h6 className="text-base font-semibold text-[#ffffff]">Phone No - Risky Activity</h6>
                                <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                  Checks whether a phone number has a history of being used in fraud, scams, robocalls, fake accounts, or suspicious behavior.
                                </p>
                            </div>
                            </div>
                            <span className={`hidden lg:block text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['risky-risky-activity'] ? 'mr-1' : ''}`}>
                              {openAccordions['risky-risky-activity'] ? '−' : '+'}
                            </span>
                          </button>
                            </>
                          ) : (
                            <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                              {/* Mobile: Top row with icon and lock */}
                              <div className="flex justify-between items-start mb-2 lg:hidden">
                                <img src="/phone-intelligence-icons/risk.png" alt="Risky Activity" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                <div 
                                  className="flex-shrink-0 flex items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('risky-risky-activity')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'risky-risky-activity' 
                                      ? 'opacity-100 translate-x-0 scale-100' 
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg 
                                    className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'risky-risky-activity' 
                                        ? 'scale-110 rotate-[-5deg]' 
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                  >
                                    <path 
                                      strokeLinecap="round" 
                                      strokeLinejoin="round" 
                                      strokeWidth={2} 
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                    />
                                  </svg>
                                </div>
                              </div>
                              {/* Mobile: Header and Description stacked vertically */}
                              <div className="flex flex-col mb-2 lg:hidden">
                                <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Phone No - Risky Activity</h6>
                                <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                  Checks whether a phone number has a history of being used in fraud, scams, robocalls, fake accounts, or suspicious behavior.
                                </p>
                              </div>
                              {/* Desktop: Original layout */}
                              <div className="hidden lg:flex items-center gap-2 flex-1">
                                <img src="/phone-intelligence-icons/risk.png" alt="Risky Activity" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Phone No - Risky Activity</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Checks whether a phone number has a history of being used in fraud, scams, robocalls, fake accounts, or suspicious behavior.
                                  </p>
                                </div>
                              </div>
                              <div 
                                className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                onMouseEnter={() => setHoveredAccordion('risky-risky-activity')}
                                onMouseLeave={() => setHoveredAccordion(null)}
                              >
                                <div className={`transition-all duration-300 ease-out ${
                                  hoveredAccordion === 'risky-risky-activity' 
                                    ? 'opacity-100 translate-x-0 scale-100' 
                                    : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                }`}>
                                  <Link
                                    to="/ask-access"
                                    className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                  >
                                    <span className="text-sm text-white font-semibold">Ask for access</span>
                                  </Link>
                                </div>
                                <svg 
                                  className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                    hoveredAccordion === 'risky-risky-activity' 
                                      ? 'scale-110 rotate-[-5deg]' 
                                      : 'hover:scale-105'
                                  }`}
                                  fill="none" 
                                  stroke="currentColor" 
                                  viewBox="0 0 24 24"
                                >
                                  <path 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round" 
                                    strokeWidth={2} 
                                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                  />
                                </svg>
                              </div>
                            </div>
                          )}
                          <div className={`overflow-hidden transition-all duration-300 ${openAccordions['risky-risky-activity'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                            <div className="px-3 pb-3">
                              <div className="mt-2">
                                <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"phone_no_risky_activity": false}, null, 2)}
                                  </pre>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Phone No - Spam */}
                        <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                          {accessGranted ? (
                            <>
                          <button
                            onClick={() => toggleAccordion('risky-spam')}
                            className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                          >
                            {/* Mobile: Top row with icon and plus-minus */}
                            <div className="flex justify-between items-start mb-2 lg:hidden">
                              <img src="/phone-intelligence-icons/spam.png" alt="Spam" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                              <span className={`text-[#26ab4d] text-lg sm:text-xl font-bold transition-all duration-300 flex-shrink-0 ${openAccordions['risky-spam'] ? '' : ''}`}>
                                {openAccordions['risky-spam'] ? '−' : '+'}
                              </span>
                            </div>
                            {/* Mobile: Header and Description stacked vertically */}
                            <div className="flex flex-col mb-2 lg:hidden">
                              <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Phone No - Spam</h6>
                              <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                Checks whether a phone number is known for sending spam messages or making spam calls.
                              </p>
                            </div>
                            {/* Desktop: Original layout */}
                            <div className="hidden lg:flex items-center gap-2 flex-1">
                              <img src="/phone-intelligence-icons/spam.png" alt="Spam" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                              <div className="flex-1">
                                <h6 className="text-base font-semibold text-[#ffffff]">Phone No - Spam</h6>
                                <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                  Checks whether a phone number is known for sending spam messages or making spam calls.
                                </p>
                              </div>
                            </div>
                            <span className={`hidden lg:block text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['risky-spam'] ? 'mr-1' : ''}`}>
                              {openAccordions['risky-spam'] ? '−' : '+'}
                            </span>
                          </button>
                            </>
                          ) : (
                            <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                              {/* Mobile: Top row with icon and lock */}
                              <div className="flex justify-between items-start mb-2 lg:hidden">
                                <img src="/phone-intelligence-icons/spam.png" alt="Spam" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                <div 
                                  className="flex-shrink-0 flex items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('risky-spam')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'risky-spam' 
                                      ? 'opacity-100 translate-x-0 scale-100' 
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg 
                                    className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'risky-spam' 
                                        ? 'scale-110 rotate-[-5deg]' 
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                  >
                                    <path 
                                      strokeLinecap="round" 
                                      strokeLinejoin="round" 
                                      strokeWidth={2} 
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                    />
                                  </svg>
                                </div>
                              </div>
                              {/* Mobile: Header and Description stacked vertically */}
                              <div className="flex flex-col mb-2 lg:hidden">
                                <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Phone No - Spam</h6>
                                <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                  Checks whether a phone number is known for sending spam messages or making spam calls.
                                </p>
                              </div>
                              {/* Desktop: Original layout */}
                              <div className="hidden lg:flex items-center gap-2 flex-1">
                                <img src="/phone-intelligence-icons/spam.png" alt="Spam" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Phone No - Spam</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Checks whether a phone number is known for sending spam messages or making spam calls.
                                  </p>
                                </div>
                              </div>
                              <div 
                                className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                onMouseEnter={() => setHoveredAccordion('risky-spam')}
                                onMouseLeave={() => setHoveredAccordion(null)}
                              >
                                <div className={`transition-all duration-300 ease-out ${
                                  hoveredAccordion === 'risky-spam' 
                                    ? 'opacity-100 translate-x-0 scale-100' 
                                    : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                }`}>
                                  <Link
                                    to="/ask-access"
                                    className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                  >
                                    <span className="text-sm text-white font-semibold">Ask for access</span>
                                  </Link>
                                </div>
                                <svg 
                                  className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                    hoveredAccordion === 'risky-spam' 
                                      ? 'scale-110 rotate-[-5deg]' 
                                      : 'hover:scale-105'
                                  }`}
                                  fill="none" 
                                  stroke="currentColor" 
                                  viewBox="0 0 24 24"
                                >
                                  <path 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round" 
                                    strokeWidth={2} 
                                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                  />
                                </svg>
                              </div>
                            </div>
                          )}
                          <div className={`overflow-hidden transition-all duration-300 ${openAccordions['risky-spam'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                            <div className="px-3 pb-3">
                              <div className="mt-2">
                                <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"phone_no_spam": false}, null, 2)}
                                  </pre>
                                </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Sim Card Info Accordion */}
                    <div className="border border-[#26ab4d]/30 rounded-lg overflow-hidden mt-3">
                    <button
                        onClick={() => toggleAccordion('simcard')}
                        className="w-full relative p-3 sm:p-4 text-left hover:bg-[#26ab4d]/10 transition-colors"
                      >
                        <div className="pr-8 sm:pr-10">
                          <h5 className="text-sm sm:text-base lg:text-lg font-bold text-[#26ab4d]">Sim Card Info</h5>
                          <p className="text-[#ffffff] text-[0.75rem] sm:text-xs lg:text-sm mt-1 leading-relaxed">
                            Provides telecom intelligence by analyzing carrier type, line type, plan, and ownership details.
                          </p>
                        </div>
                        <span className={`absolute top-3 right-3 sm:top-4 sm:right-4 text-[#26ab4d] text-base sm:text-lg lg:text-xl xl:text-2xl font-bold transition-all duration-300 ${openAccordions.simcard ? '' : ''}`}>
                          {openAccordions.simcard ? '−' : '+'}
                        </span>
                    </button>
                      <div className={`overflow-hidden transition-all duration-300 ${openAccordions.simcard ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                        <div className="p-4 pt-0 space-y-2">
                        {/* Phone No - Network Carrier */}
                        <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                          {accessGranted ? (
                            <>
                          <button
                            onClick={() => toggleAccordion('simcard-carrier')}
                            className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                          >
                            {/* Mobile: Top row with icon and plus-minus */}
                            <div className="flex justify-between items-start mb-2 lg:hidden">
                              <img src="/phone-intelligence-icons/carrirer.png" alt="Network Carrier" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                              <span className={`text-[#26ab4d] text-lg sm:text-xl font-bold transition-all duration-300 flex-shrink-0 ${openAccordions['simcard-carrier'] ? '' : ''}`}>
                                {openAccordions['simcard-carrier'] ? '−' : '+'}
                              </span>
                            </div>
                            {/* Mobile: Header and Description stacked vertically */}
                            <div className="flex flex-col mb-2 lg:hidden">
                              <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Phone No - Network Carrier</h6>
                              <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                Tells you which mobile or telecom company is currently providing service to a phone number like Jio, Airtel, Vodafone, or BSNL.
                              </p>
                            </div>
                            {/* Desktop: Original layout */}
                            <div className="hidden lg:flex items-center gap-2 flex-1">
                              <img src="/phone-intelligence-icons/carrirer.png" alt="Network Carrier" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                              <div className="flex-1">
                                <h6 className="text-base font-semibold text-[#ffffff]">Phone No - Network Carrier</h6>
                                <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                  Tells you which mobile or telecom company is currently providing service to a phone number like Jio, Airtel, Vodafone, or BSNL.
                                </p>
                              </div>
                            </div>
                            <span className={`hidden lg:block text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['simcard-carrier'] ? 'mr-1' : ''}`}>
                              {openAccordions['simcard-carrier'] ? '−' : '+'}
                            </span>
                          </button>
                            </>
                          ) : (
                            <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                              {/* Mobile: Top row with icon and lock */}
                              <div className="flex justify-between items-start mb-2 lg:hidden">
                                <img src="/phone-intelligence-icons/carrirer.png" alt="Network Carrier" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                <div 
                                  className="flex-shrink-0 flex items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('simcard-carrier')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'simcard-carrier' 
                                      ? 'opacity-100 translate-x-0 scale-100' 
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg 
                                    className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'simcard-carrier' 
                                        ? 'scale-110 rotate-[-5deg]' 
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                  >
                                    <path 
                                      strokeLinecap="round" 
                                      strokeLinejoin="round" 
                                      strokeWidth={2} 
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                    />
                                  </svg>
                                </div>
                              </div>
                              {/* Mobile: Header and Description stacked vertically */}
                              <div className="flex flex-col mb-2 lg:hidden">
                                <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Phone No - Network Carrier</h6>
                                <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                  Tells you which mobile or telecom company is currently providing service to a phone number like Jio, Airtel, Vodafone, or BSNL.
                                </p>
                              </div>
                              {/* Desktop: Original layout */}
                              <div className="hidden lg:flex items-center gap-2 flex-1">
                                <img src="/phone-intelligence-icons/carrirer.png" alt="Network Carrier" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Phone No - Network Carrier</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Tells you which mobile or telecom company is currently providing service to a phone number like Jio, Airtel, Vodafone, or BSNL.
                                  </p>
                                </div>
                              </div>
                              <div 
                                className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                onMouseEnter={() => setHoveredAccordion('simcard-carrier')}
                                onMouseLeave={() => setHoveredAccordion(null)}
                              >
                                <div className={`transition-all duration-300 ease-out ${
                                  hoveredAccordion === 'simcard-carrier' 
                                    ? 'opacity-100 translate-x-0 scale-100' 
                                    : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                }`}>
                                  <Link
                                    to="/ask-access"
                                    className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                  >
                                    <span className="text-sm text-white font-semibold">Ask for access</span>
                                  </Link>
                                </div>
                                <svg 
                                  className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                    hoveredAccordion === 'simcard-carrier' 
                                      ? 'scale-110 rotate-[-5deg]' 
                                      : 'hover:scale-105'
                                  }`}
                                  fill="none" 
                                  stroke="currentColor" 
                                  viewBox="0 0 24 24"
                                >
                                  <path 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round" 
                                    strokeWidth={2} 
                                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                  />
                                </svg>
                              </div>
                            </div>
                          )}
                          <div className={`overflow-hidden transition-all duration-300 ${openAccordions['simcard-carrier'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                            <div className="px-3 pb-3">
                              <div className="mt-2">
                                <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"phone_no_network_carrier": "JIO"}, null, 2)}
                                  </pre>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Phone No - Line Type */}
                        <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                          {accessGranted ? (
                            <>
                          <button
                            onClick={() => toggleAccordion('simcard-line-type')}
                            className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                          >
                            {/* Mobile: Top row with icon and plus-minus */}
                            <div className="flex justify-between items-start mb-2 lg:hidden">
                              <img src="/phone-intelligence-icons/line type.png" alt="Line Type" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                              <span className={`text-[#26ab4d] text-lg sm:text-xl font-bold transition-all duration-300 flex-shrink-0 ${openAccordions['simcard-line-type'] ? '' : ''}`}>
                                {openAccordions['simcard-line-type'] ? '−' : '+'}
                              </span>
                            </div>
                            {/* Mobile: Header and Description stacked vertically */}
                            <div className="flex flex-col mb-2 lg:hidden">
                              <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Phone No - Line Type</h6>
                              <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                Tells what kind of phone number it is whether it's a mobile phone, landline, internet-based number like VoIP or something else.
                              </p>
                            </div>
                            {/* Desktop: Original layout */}
                            <div className="hidden lg:flex items-center gap-2 flex-1">
                              <img src="/phone-intelligence-icons/line type.png" alt="Line Type" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                              <div className="flex-1">
                                <h6 className="text-base font-semibold text-[#ffffff]">Phone No - Line Type</h6>
                                <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                  Tells what kind of phone number it is whether it's a mobile phone, landline, internet-based number like VoIP or something else.
                                </p>
                              </div>
                            </div>
                            <span className={`hidden lg:block text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['simcard-line-type'] ? 'mr-1' : ''}`}>
                              {openAccordions['simcard-line-type'] ? '−' : '+'}
                            </span>
                          </button>
                            </>
                          ) : (
                            <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                              {/* Mobile: Top row with icon and lock */}
                              <div className="flex justify-between items-start mb-2 lg:hidden">
                                <img src="/phone-intelligence-icons/line type.png" alt="Line Type" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                <div 
                                  className="flex-shrink-0 flex items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('simcard-line-type')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'simcard-line-type' 
                                      ? 'opacity-100 translate-x-0 scale-100' 
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg 
                                    className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'simcard-line-type' 
                                        ? 'scale-110 rotate-[-5deg]' 
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                  >
                                    <path 
                                      strokeLinecap="round" 
                                      strokeLinejoin="round" 
                                      strokeWidth={2} 
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                    />
                                  </svg>
                                </div>
                              </div>
                              {/* Mobile: Header and Description stacked vertically */}
                              <div className="flex flex-col mb-2 lg:hidden">
                                <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Phone No - Line Type</h6>
                                <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                  Tells what kind of phone number it is whether it's a mobile phone, landline, internet-based number like VoIP or something else.
                                </p>
                              </div>
                              {/* Desktop: Original layout */}
                              <div className="hidden lg:flex items-center gap-2 flex-1">
                                <img src="/phone-intelligence-icons/line type.png" alt="Line Type" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Phone No - Line Type</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Tells what kind of phone number it is whether it's a mobile phone, landline, internet-based number like VoIP or something else.
                                  </p>
                                </div>
                              </div>
                              <div 
                                className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                onMouseEnter={() => setHoveredAccordion('simcard-line-type')}
                                onMouseLeave={() => setHoveredAccordion(null)}
                              >
                                <div className={`transition-all duration-300 ease-out ${
                                  hoveredAccordion === 'simcard-line-type' 
                                    ? 'opacity-100 translate-x-0 scale-100' 
                                    : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                }`}>
                                  <Link
                                    to="/ask-access"
                                    className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                  >
                                    <span className="text-sm text-white font-semibold">Ask for access</span>
                                  </Link>
                                </div>
                                <svg 
                                  className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                    hoveredAccordion === 'simcard-line-type' 
                                      ? 'scale-110 rotate-[-5deg]' 
                                      : 'hover:scale-105'
                                  }`}
                                  fill="none" 
                                  stroke="currentColor" 
                                  viewBox="0 0 24 24"
                                >
                                  <path 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round" 
                                    strokeWidth={2} 
                                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                  />
                                </svg>
                              </div>
                            </div>
                          )}
                          <div className={`overflow-hidden transition-all duration-300 ${openAccordions['simcard-line-type'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                            <div className="px-3 pb-3">
                              <div className="mt-2">
                                <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"phone_no_line_type": "Wireless"}, null, 2)}
                                  </pre>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
            
                        {/* Phone No - Prepaid */}
                        <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                          {accessGranted ? (
                            <>
                          <button
                            onClick={() => toggleAccordion('simcard-prepaid')}
                            className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                          >
                            {/* Mobile: Top row with icon and plus-minus */}
                            <div className="flex justify-between items-start mb-2 lg:hidden">
                              <img src="/phone-intelligence-icons/sim type.png" alt="Prepaid" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                              <span className={`text-[#26ab4d] text-lg sm:text-xl font-bold transition-all duration-300 flex-shrink-0 ${openAccordions['simcard-prepaid'] ? '' : ''}`}>
                                {openAccordions['simcard-prepaid'] ? '−' : '+'}
                              </span>
                            </div>
                            {/* Mobile: Header and Description stacked vertically */}
                            <div className="flex flex-col mb-2 lg:hidden">
                              <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Phone No - Prepaid</h6>
                              <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                Shows whether the phone number is connected to a prepaid mobile plan or NOT.
                              </p>
                            </div>
                            {/* Desktop: Original layout */}
                            <div className="hidden lg:flex items-center gap-2 flex-1">
                              <img src="/phone-intelligence-icons/sim type.png" alt="Prepaid" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                              <div className="flex-1">
                                <h6 className="text-base font-semibold text-[#ffffff]">Phone No - Prepaid</h6>
                                <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                  Shows whether the phone number is connected to a prepaid mobile plan or NOT.
                                </p>
                              </div>
                            </div>
                            <span className={`hidden lg:block text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['simcard-prepaid'] ? 'mr-1' : ''}`}>
                              {openAccordions['simcard-prepaid'] ? '−' : '+'}
                            </span>
                          </button>
                            </>
                          ) : (
                            <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                              {/* Mobile: Top row with icon and lock */}
                              <div className="flex justify-between items-start mb-2 lg:hidden">
                                <img src="/phone-intelligence-icons/sim type.png" alt="Prepaid" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                <div 
                                  className="flex-shrink-0 flex items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('simcard-prepaid')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'simcard-prepaid' 
                                      ? 'opacity-100 translate-x-0 scale-100' 
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg 
                                    className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'simcard-prepaid' 
                                        ? 'scale-110 rotate-[-5deg]' 
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                  >
                                    <path 
                                      strokeLinecap="round" 
                                      strokeLinejoin="round" 
                                      strokeWidth={2} 
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                    />
                                  </svg>
                                </div>
                              </div>
                              {/* Mobile: Header and Description stacked vertically */}
                              <div className="flex flex-col mb-2 lg:hidden">
                                <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Phone No - Prepaid</h6>
                                <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                  Shows whether the phone number is connected to a prepaid mobile plan or NOT.
                                </p>
                              </div>
                              {/* Desktop: Original layout */}
                              <div className="hidden lg:flex items-center gap-2 flex-1">
                                <img src="/phone-intelligence-icons/sim type.png" alt="Prepaid" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Phone No - Prepaid</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Shows whether the phone number is connected to a prepaid mobile plan or NOT.
                                  </p>
                                </div>
                              </div>
                              <div 
                                className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                onMouseEnter={() => setHoveredAccordion('simcard-prepaid')}
                                onMouseLeave={() => setHoveredAccordion(null)}
                              >
                                <div className={`transition-all duration-300 ease-out ${
                                  hoveredAccordion === 'simcard-prepaid' 
                                    ? 'opacity-100 translate-x-0 scale-100' 
                                    : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                }`}>
                                  <Link
                                    to="/ask-access"
                                    className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                  >
                                    <span className="text-sm text-white font-semibold">Ask for access</span>
                                  </Link>
                                </div>
                                <svg 
                                  className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                    hoveredAccordion === 'simcard-prepaid' 
                                      ? 'scale-110 rotate-[-5deg]' 
                                      : 'hover:scale-105'
                                  }`}
                                  fill="none" 
                                  stroke="currentColor" 
                                  viewBox="0 0 24 24"
                                >
                                  <path 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round" 
                                    strokeWidth={2} 
                                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                  />
                                </svg>
                              </div>
                            </div>
                          )}
                          <div className={`overflow-hidden transition-all duration-300 ${openAccordions['simcard-prepaid'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                            <div className="px-3 pb-3">
                              <div className="mt-2">
                                <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"phone_no_prepaid": true}, null, 2)}
                                  </pre>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Phone No - User Name */}
                        <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                          {accessGranted ? (
                            <>
                          <button
                            onClick={() => toggleAccordion('simcard-user-name')}
                            className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                          >
                            {/* Mobile: Top row with icon and plus-minus */}
                            <div className="flex justify-between items-start mb-2 lg:hidden">
                              <img src="/phone-intelligence-icons/owner name.png" alt="User Name" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                              <span className={`text-[#26ab4d] text-lg sm:text-xl font-bold transition-all duration-300 flex-shrink-0 ${openAccordions['simcard-user-name'] ? '' : ''}`}>
                                {openAccordions['simcard-user-name'] ? '−' : '+'}
                              </span>
                            </div>
                            {/* Mobile: Header and Description stacked vertically */}
                            <div className="flex flex-col mb-2 lg:hidden">
                              <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Phone No - User Name</h6>
                              <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                Shows the name of the person that is linked to the phone number - like who owns or uses the number.
                              </p>
                            </div>
                            {/* Desktop: Original layout */}
                            <div className="hidden lg:flex items-center gap-2 flex-1">
                              <img src="/phone-intelligence-icons/owner name.png" alt="User Name" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                              <div className="flex-1">
                                <h6 className="text-base font-semibold text-[#ffffff]">Phone No - User Name</h6>
                                <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                  Shows the name of the person that is linked to the phone number - like who owns or uses the number.
                                </p>
                              </div>
                            </div>
                            <span className={`hidden lg:block text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['simcard-user-name'] ? 'mr-1' : ''}`}>
                              {openAccordions['simcard-user-name'] ? '−' : '+'}
                            </span>
                          </button>
                            </>
                          ) : (
                            <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                              {/* Mobile: Top row with icon and lock */}
                              <div className="flex justify-between items-start mb-2 lg:hidden">
                                <img src="/phone-intelligence-icons/owner name.png" alt="User Name" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                <div 
                                  className="flex-shrink-0 flex items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('simcard-user-name')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'simcard-user-name' 
                                      ? 'opacity-100 translate-x-0 scale-100' 
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg 
                                    className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'simcard-user-name' 
                                        ? 'scale-110 rotate-[-5deg]' 
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                  >
                                    <path 
                                      strokeLinecap="round" 
                                      strokeLinejoin="round" 
                                      strokeWidth={2} 
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                    />
                                  </svg>
                                </div>
                              </div>
                              {/* Mobile: Header and Description stacked vertically */}
                              <div className="flex flex-col mb-2 lg:hidden">
                                <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Phone No - User Name</h6>
                                <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                  Shows the name of the person that is linked to the phone number - like who owns or uses the number.
                                </p>
                              </div>
                              {/* Desktop: Original layout */}
                              <div className="hidden lg:flex items-center gap-2 flex-1">
                                <img src="/phone-intelligence-icons/owner name.png" alt="User Name" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Phone No - User Name</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Shows the name of the person that is linked to the phone number - like who owns or uses the number.
                                  </p>
                                </div>
                              </div>
                              <div 
                                className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                onMouseEnter={() => setHoveredAccordion('simcard-user-name')}
                                onMouseLeave={() => setHoveredAccordion(null)}
                              >
                                <div className={`transition-all duration-300 ease-out ${
                                  hoveredAccordion === 'simcard-user-name' 
                                    ? 'opacity-100 translate-x-0 scale-100' 
                                    : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                }`}>
                                  <Link
                                    to="/ask-access"
                                    className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                  >
                                    <span className="text-sm text-white font-semibold">Ask for access</span>
                                  </Link>
                                </div>
                                <svg 
                                  className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                    hoveredAccordion === 'simcard-user-name' 
                                      ? 'scale-110 rotate-[-5deg]' 
                                      : 'hover:scale-105'
                                  }`}
                                  fill="none" 
                                  stroke="currentColor" 
                                  viewBox="0 0 24 24"
                                >
                                  <path 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round" 
                                    strokeWidth={2} 
                                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                  />
                                </svg>
                              </div>
                            </div>
                          )}
                          <div className={`overflow-hidden transition-all duration-300 ${openAccordions['simcard-user-name'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                            <div className="px-3 pb-3">
                              <div className="mt-2">
                                <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"phone_no_user_name": "ANAND JAIN"}, null, 2)}
                                  </pre>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    </div>

                    {/* Phone Number Demographics Accordion */}
                    <div className="border border-[#26ab4d]/30 rounded-lg overflow-hidden mt-3">
                    <button
                        onClick={() => toggleAccordion('demographics')}
                        className="w-full relative p-3 sm:p-4 text-left hover:bg-[#26ab4d]/10 transition-colors"
                      >
                        <div className="pr-8 sm:pr-10">
                          <h5 className="text-sm sm:text-base lg:text-lg font-bold text-[#26ab4d]">Phone Number Demographics</h5>
                          <p className="text-[#ffffff] text-[0.75rem] sm:text-xs lg:text-sm mt-1 leading-relaxed">
                            Provides precise geographic insights by mapping a number's country, region, and timezone.
                          </p>
                        </div>
                        <span className={`absolute top-3 right-3 sm:top-4 sm:right-4 text-[#26ab4d] text-base sm:text-lg lg:text-xl xl:text-2xl font-bold transition-all duration-300 ${openAccordions.demographics ? '' : ''}`}>
                          {openAccordions.demographics ? '−' : '+'}
                        </span>
                    </button>
                      <div className={`overflow-hidden transition-all duration-300 ${openAccordions.demographics ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                        <div className="p-4 pt-0 space-y-2">
                        {/* Phone No - Country */}
                        <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                          {accessGranted ? (
                            <>
                          <button
                            onClick={() => toggleAccordion('demographics-country')}
                            className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                          >
                            {/* Mobile: Top row with icon and plus-minus */}
                            <div className="flex justify-between items-start mb-2 lg:hidden">
                              <img src="/phone-intelligence-icons/country.png" alt="Country" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                              <span className={`text-[#26ab4d] text-lg sm:text-xl font-bold transition-all duration-300 flex-shrink-0 ${openAccordions['demographics-country'] ? '' : ''}`}>
                                {openAccordions['demographics-country'] ? '−' : '+'}
                              </span>
                            </div>
                            {/* Mobile: Header and Description stacked vertically */}
                            <div className="flex flex-col mb-2 lg:hidden">
                              <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Phone No - Country</h6>
                              <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                Tells you which country the phone number belongs to, based on its international dialing code (like +91 for India).
                              </p>
                            </div>
                            {/* Desktop: Original layout */}
                            <div className="hidden lg:flex items-center gap-2 flex-1">
                              <img src="/phone-intelligence-icons/country.png" alt="Country" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                              <div className="flex-1">
                                <h6 className="text-base font-semibold text-[#ffffff]">Phone No - Country</h6>
                                <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                  Tells you which country the phone number belongs to, based on its international dialing code (like +91 for India).
                                </p>
                              </div>
                            </div>
                            <span className={`hidden lg:block text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['demographics-country'] ? 'mr-1' : ''}`}>
                              {openAccordions['demographics-country'] ? '−' : '+'}
                            </span>
                          </button>
                            </>
                          ) : (
                            <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                              {/* Mobile: Top row with icon and lock */}
                              <div className="flex justify-between items-start mb-2 lg:hidden">
                                <img src="/phone-intelligence-icons/country.png" alt="Country" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                <div 
                                  className="flex-shrink-0 flex items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('demographics-country')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'demographics-country' 
                                      ? 'opacity-100 translate-x-0 scale-100' 
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg 
                                    className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'demographics-country' 
                                        ? 'scale-110 rotate-[-5deg]' 
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                  >
                                    <path 
                                      strokeLinecap="round" 
                                      strokeLinejoin="round" 
                                      strokeWidth={2} 
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                    />
                                  </svg>
                                </div>
                              </div>
                              {/* Mobile: Header and Description stacked vertically */}
                              <div className="flex flex-col mb-2 lg:hidden">
                                <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Phone No - Country</h6>
                                <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                  Tells you which country the phone number belongs to, based on its international dialing code (like +91 for India).
                                </p>
                              </div>
                              {/* Desktop: Original layout */}
                              <div className="hidden lg:flex items-center gap-2 flex-1">
                                <img src="/phone-intelligence-icons/country.png" alt="Country" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Phone No - Country</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Tells you which country the phone number belongs to, based on its international dialing code (like +91 for India).
                                  </p>
                                </div>
                              </div>
                              <div 
                                className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                onMouseEnter={() => setHoveredAccordion('demographics-country')}
                                onMouseLeave={() => setHoveredAccordion(null)}
                              >
                                <div className={`transition-all duration-300 ease-out ${
                                  hoveredAccordion === 'demographics-country' 
                                    ? 'opacity-100 translate-x-0 scale-100' 
                                    : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                }`}>
                                  <Link
                                    to="/ask-access"
                                    className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                  >
                                    <span className="text-sm text-white font-semibold">Ask for access</span>
                                  </Link>
                                </div>
                                <svg 
                                  className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                    hoveredAccordion === 'demographics-country' 
                                      ? 'scale-110 rotate-[-5deg]' 
                                      : 'hover:scale-105'
                                  }`}
                                  fill="none" 
                                  stroke="currentColor" 
                                  viewBox="0 0 24 24"
                                >
                                  <path 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round" 
                                    strokeWidth={2} 
                                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                  />
                                </svg>
                              </div>
                            </div>
                          )}
                          <div className={`overflow-hidden transition-all duration-300 ${openAccordions['demographics-country'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                            <div className="px-3 pb-3">
                              <div className="mt-2">
                                <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"phone_no_country": "IN"}, null, 2)}
                                  </pre>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Phone No - City */}
                        <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                          {accessGranted ? (
                            <>
                          <button
                            onClick={() => toggleAccordion('demographics-city')}
                            className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                          >
                            {/* Mobile: Top row with icon and plus-minus */}
                            <div className="flex justify-between items-start mb-2 lg:hidden">
                              <img src="/phone-intelligence-icons/city.png" alt="City" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                              <span className={`text-[#26ab4d] text-lg sm:text-xl font-bold transition-all duration-300 flex-shrink-0 ${openAccordions['demographics-city'] ? '' : ''}`}>
                                {openAccordions['demographics-city'] ? '−' : '+'}
                              </span>
                            </div>
                            {/* Mobile: Header and Description stacked vertically */}
                            <div className="flex flex-col mb-2 lg:hidden">
                              <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Phone No - City</h6>
                              <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                Tells you the city where the phone number is likely registered or originated from, based on telecom data.
                              </p>
                            </div>
                            {/* Desktop: Original layout */}
                            <div className="hidden lg:flex items-center gap-2 flex-1">
                              <img src="/phone-intelligence-icons/city.png" alt="City" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                              <div className="flex-1">
                                <h6 className="text-base font-semibold text-[#ffffff]">Phone No - City</h6>
                                <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                  Tells you the city where the phone number is likely registered or originated from, based on telecom data.
                                </p>
                              </div>
                            </div>
                            <span className={`hidden lg:block text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['demographics-city'] ? 'mr-1' : ''}`}>
                              {openAccordions['demographics-city'] ? '−' : '+'}
                            </span>
                          </button>
                            </>
                          ) : (
                            <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                              {/* Mobile: Top row with icon and lock */}
                              <div className="flex justify-between items-start mb-2 lg:hidden">
                                <img src="/phone-intelligence-icons/city.png" alt="City" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                <div 
                                  className="flex-shrink-0 flex items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('demographics-city')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'demographics-city' 
                                      ? 'opacity-100 translate-x-0 scale-100' 
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg 
                                    className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'demographics-city' 
                                        ? 'scale-110 rotate-[-5deg]' 
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                  >
                                    <path 
                                      strokeLinecap="round" 
                                      strokeLinejoin="round" 
                                      strokeWidth={2} 
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                    />
                                  </svg>
                                </div>
                              </div>
                              {/* Mobile: Header and Description stacked vertically */}
                              <div className="flex flex-col mb-2 lg:hidden">
                                <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Phone No - City</h6>
                                <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                  Tells you the city where the phone number is likely registered or originated from, based on telecom data.
                                </p>
                              </div>
                              {/* Desktop: Original layout */}
                              <div className="hidden lg:flex items-center gap-2 flex-1">
                                <img src="/phone-intelligence-icons/city.png" alt="City" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Phone No - City</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Tells you the city where the phone number is likely registered or originated from, based on telecom data.
                                  </p>
                                </div>
                              </div>
                              <div 
                                className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                onMouseEnter={() => setHoveredAccordion('demographics-city')}
                                onMouseLeave={() => setHoveredAccordion(null)}
                              >
                                <div className={`transition-all duration-300 ease-out ${
                                  hoveredAccordion === 'demographics-city' 
                                    ? 'opacity-100 translate-x-0 scale-100' 
                                    : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                }`}>
                                  <Link
                                    to="/ask-access"
                                    className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                  >
                                    <span className="text-sm text-white font-semibold">Ask for access</span>
                                  </Link>
                                </div>
                                <svg 
                                  className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                    hoveredAccordion === 'demographics-city' 
                                      ? 'scale-110 rotate-[-5deg]' 
                                      : 'hover:scale-105'
                                  }`}
                                  fill="none" 
                                  stroke="currentColor" 
                                  viewBox="0 0 24 24"
                                >
                                  <path 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round" 
                                    strokeWidth={2} 
                                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                  />
                                </svg>
                              </div>
                            </div>
                          )}
                          <div className={`overflow-hidden transition-all duration-300 ${openAccordions['demographics-city'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                            <div className="px-3 pb-3">
                              <div className="mt-2">
                                <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"phone_no_city": "Mumbai"}, null, 2)}
                                  </pre>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Phone No - Zip Code */}
                        <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                          {accessGranted ? (
                            <>
                          <button
                            onClick={() => toggleAccordion('demographics-zip')}
                            className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                          >
                            {/* Mobile: Top row with icon and plus-minus */}
                            <div className="flex justify-between items-start mb-2 lg:hidden">
                              <img src="/phone-intelligence-icons/zip code.png" alt="Zip Code" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                              <span className={`text-[#26ab4d] text-lg sm:text-xl font-bold transition-all duration-300 flex-shrink-0 ${openAccordions['demographics-zip'] ? '' : ''}`}>
                                {openAccordions['demographics-zip'] ? '−' : '+'}
                              </span>
                            </div>
                            {/* Mobile: Header and Description stacked vertically */}
                            <div className="flex flex-col mb-2 lg:hidden">
                              <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Phone No - Zip Code</h6>
                              <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                Shows the postal code or PIN code of the area where the phone number was originally registered.
                              </p>
                            </div>
                            {/* Desktop: Original layout */}
                            <div className="hidden lg:flex items-center gap-2 flex-1">
                              <img src="/phone-intelligence-icons/zip code.png" alt="Zip Code" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                              <div className="flex-1">
                                <h6 className="text-base font-semibold text-[#ffffff]">Phone No - Zip Code</h6>
                                <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                  Shows the postal code or PIN code of the area where the phone number was originally registered.
                                </p>
                              </div>
                            </div>
                            <span className={`hidden lg:block text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['demographics-zip'] ? 'mr-1' : ''}`}>
                              {openAccordions['demographics-zip'] ? '−' : '+'}
                            </span>
                          </button>
                            </>
                          ) : (
                            <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                              {/* Mobile: Top row with icon and lock */}
                              <div className="flex justify-between items-start mb-2 lg:hidden">
                                <img src="/phone-intelligence-icons/zip code.png" alt="Zip Code" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                <div 
                                  className="flex-shrink-0 flex items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('demographics-zip')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'demographics-zip' 
                                      ? 'opacity-100 translate-x-0 scale-100' 
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg 
                                    className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'demographics-zip' 
                                        ? 'scale-110 rotate-[-5deg]' 
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                  >
                                    <path 
                                      strokeLinecap="round" 
                                      strokeLinejoin="round" 
                                      strokeWidth={2} 
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                    />
                                  </svg>
                                </div>
                              </div>
                              {/* Mobile: Header and Description stacked vertically */}
                              <div className="flex flex-col mb-2 lg:hidden">
                                <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Phone No - Zip Code</h6>
                                <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                  Shows the postal code or PIN code of the area where the phone number was originally registered.
                                </p>
                              </div>
                              {/* Desktop: Original layout */}
                              <div className="hidden lg:flex items-center gap-2 flex-1">
                                <img src="/phone-intelligence-icons/zip code.png" alt="Zip Code" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Phone No - Zip Code</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Shows the postal code or PIN code of the area where the phone number was originally registered.
                                  </p>
                                </div>
                              </div>
                              <div 
                                className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                onMouseEnter={() => setHoveredAccordion('demographics-zip')}
                                onMouseLeave={() => setHoveredAccordion(null)}
                              >
                                <div className={`transition-all duration-300 ease-out ${
                                  hoveredAccordion === 'demographics-zip' 
                                    ? 'opacity-100 translate-x-0 scale-100' 
                                    : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                }`}>
                                  <Link
                                    to="/ask-access"
                                    className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                  >
                                    <span className="text-sm text-white font-semibold">Ask for access</span>
                                  </Link>
                                </div>
                                <svg 
                                  className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                    hoveredAccordion === 'demographics-zip' 
                                      ? 'scale-110 rotate-[-5deg]' 
                                      : 'hover:scale-105'
                                  }`}
                                  fill="none" 
                                  stroke="currentColor" 
                                  viewBox="0 0 24 24"
                                >
                                  <path 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round" 
                                    strokeWidth={2} 
                                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                  />
                                </svg>
                              </div>
                            </div>
                          )}
                          <div className={`overflow-hidden transition-all duration-300 ${openAccordions['demographics-zip'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                            <div className="px-3 pb-3">
                              <div className="mt-2">
                                <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"phone_no_zip_code": "400079"}, null, 2)}
                                  </pre>
                              </div>
                            </div>
                          </div>
                        </div>
            </div>

                        {/* Phone No - Timezone */}
                        <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                          {accessGranted ? (
                            <>
                          <button
                            onClick={() => toggleAccordion('demographics-timezone')}
                            className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                          >
                            {/* Mobile: Top row with icon and plus-minus */}
                            <div className="flex justify-between items-start mb-2 lg:hidden">
                              <img src="/phone-intelligence-icons/time zone.png" alt="Timezone" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                              <span className={`text-[#26ab4d] text-lg sm:text-xl font-bold transition-all duration-300 flex-shrink-0 ${openAccordions['demographics-timezone'] ? '' : ''}`}>
                                {openAccordions['demographics-timezone'] ? '−' : '+'}
                              </span>
                            </div>
                            {/* Mobile: Header and Description stacked vertically */}
                            <div className="flex flex-col mb-2 lg:hidden">
                              <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Phone No - Timezone</h6>
                              <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                Shows the time zone where the phone number is registered, based on its country and region.
                              </p>
                            </div>
                            {/* Desktop: Original layout */}
                            <div className="hidden lg:flex items-center gap-2 flex-1">
                              <img src="/phone-intelligence-icons/time zone.png" alt="Timezone" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                              <div className="flex-1">
                                <h6 className="text-base font-semibold text-[#ffffff]">Phone No - Timezone</h6>
                                <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                  Shows the time zone where the phone number is registered, based on its country and region.
                                </p>
                              </div>
                            </div>
                            <span className={`hidden lg:block text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['demographics-timezone'] ? 'mr-1' : ''}`}>
                              {openAccordions['demographics-timezone'] ? '−' : '+'}
                            </span>
                          </button>
                            </>
                          ) : (
                            <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                              {/* Mobile: Top row with icon and lock */}
                              <div className="flex justify-between items-start mb-2 lg:hidden">
                                <img src="/phone-intelligence-icons/time zone.png" alt="Timezone" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                <div 
                                  className="flex-shrink-0 flex items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('demographics-timezone')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'demographics-timezone' 
                                      ? 'opacity-100 translate-x-0 scale-100' 
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg 
                                    className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'demographics-timezone' 
                                        ? 'scale-110 rotate-[-5deg]' 
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                  >
                                    <path 
                                      strokeLinecap="round" 
                                      strokeLinejoin="round" 
                                      strokeWidth={2} 
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                    />
                                  </svg>
                                </div>
                              </div>
                              {/* Mobile: Header and Description stacked vertically */}
                              <div className="flex flex-col mb-2 lg:hidden">
                                <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Phone No - Timezone</h6>
                                <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                  Shows the time zone where the phone number is registered, based on its country and region.
                                </p>
                              </div>
                              {/* Desktop: Original layout */}
                              <div className="hidden lg:flex items-center gap-2 flex-1">
                                <img src="/phone-intelligence-icons/time zone.png" alt="Timezone" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Phone No - Timezone</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Shows the time zone where the phone number is registered, based on its country and region.
                                  </p>
                                </div>
                              </div>
                              <div 
                                className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                onMouseEnter={() => setHoveredAccordion('demographics-timezone')}
                                onMouseLeave={() => setHoveredAccordion(null)}
                              >
                                <div className={`transition-all duration-300 ease-out ${
                                  hoveredAccordion === 'demographics-timezone' 
                                    ? 'opacity-100 translate-x-0 scale-100' 
                                    : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                }`}>
                                  <Link
                                    to="/ask-access"
                                    className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                  >
                                    <span className="text-sm text-white font-semibold">Ask for access</span>
                                  </Link>
                                </div>
                                <svg 
                                  className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                    hoveredAccordion === 'demographics-timezone' 
                                      ? 'scale-110 rotate-[-5deg]' 
                                      : 'hover:scale-105'
                                  }`}
                                  fill="none" 
                                  stroke="currentColor" 
                                  viewBox="0 0 24 24"
                                >
                                  <path 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round" 
                                    strokeWidth={2} 
                                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                  />
                                </svg>
                              </div>
                            </div>
                          )}
                          <div className={`overflow-hidden transition-all duration-300 ${openAccordions['demographics-timezone'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                            <div className="px-3 pb-3">
                              <div className="mt-2">
                                <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"phone_no_timezone": "Asia/Kolkata"}, null, 2)}
                                  </pre>
                                </div>
                </div>
              </div>
            </div>
                        </div>
                      </div>
                    </div>
                    </div>

                    {/* Phone Number Quality Score Accordion */}
                    <div className="border border-[#26ab4d]/30 rounded-lg overflow-hidden mt-3">
                      <button
                        onClick={() => toggleAccordion('quality')}
                        className="w-full relative p-3 sm:p-4 text-left hover:bg-[#26ab4d]/10 transition-colors"
                      >
                        <div className="pr-8 sm:pr-10">
                          <h5 className="text-sm sm:text-base lg:text-lg font-bold text-[#26ab4d]">Phone Number Quality Score</h5>
                          <p className="text-[#ffffff] text-[0.75rem] sm:text-xs lg:text-sm mt-1 leading-relaxed">
                            Evaluates a phone number's authenticity by analyzing its reachability, behavior, and digital associations.
                          </p>
                        </div>
                        <span className={`absolute top-3 right-3 sm:top-4 sm:right-4 text-[#26ab4d] text-base sm:text-lg lg:text-xl xl:text-2xl font-bold transition-all duration-300 ${openAccordions.quality ? '' : ''}`}>
                          {openAccordions.quality ? '−' : '+'}
                        </span>
                      </button>
                      <div className={`overflow-hidden transition-all duration-300 ${openAccordions.quality ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                        <div className="p-4 pt-0 space-y-2">
                          {/* Phone No. - Reachability */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('quality-reachability')}
                              className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              {/* Mobile: Top row with icon and plus-minus */}
                              <div className="flex justify-between items-start mb-2 lg:hidden">
                                <img src="/phone-intelligence-icons/reachable.png" alt="Reachability" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                <span className={`text-[#26ab4d] text-lg sm:text-xl font-bold transition-all duration-300 flex-shrink-0 ${openAccordions['quality-reachability'] ? '' : ''}`}>
                                  {openAccordions['quality-reachability'] ? '−' : '+'}
                                </span>
                              </div>
                              {/* Mobile: Header and Description stacked vertically */}
                              <div className="flex flex-col mb-2 lg:hidden">
                                <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Phone No. - Reachability</h6>
                                <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                  Checks whether a phone number is potentially reachable and able to receive calls or messages.
                                </p>
                              </div>
                              {/* Desktop: Original layout */}
                              <div className="hidden lg:flex items-center gap-2 flex-1">
                                <img src="/phone-intelligence-icons/reachable.png" alt="Reachability" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Phone No. - Reachability</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Checks whether a phone number is potentially reachable and able to receive calls or messages.
                                  </p>
                                </div>
                              </div>
                              <span className={`hidden lg:block text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['quality-reachability'] ? 'mr-1' : ''}`}>
                                {openAccordions['quality-reachability'] ? '−' : '+'}
                              </span>
                            </button>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/phone-intelligence-icons/reachable.png" alt="Reachability" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div 
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('quality-reachability')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'quality-reachability' 
                                        ? 'opacity-100 translate-x-0 scale-100' 
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg 
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'quality-reachability' 
                                          ? 'scale-110 rotate-[-5deg]' 
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none" 
                                      stroke="currentColor" 
                                      viewBox="0 0 24 24"
                                    >
                                      <path 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round" 
                                        strokeWidth={2} 
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Phone No. - Reachability</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Checks whether a phone number is potentially reachable and able to receive calls or messages.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/phone-intelligence-icons/reachable.png" alt="Reachability" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Phone No. - Reachability</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Checks whether a phone number is potentially reachable and able to receive calls or messages.
                                    </p>
                                  </div>
                                </div>
                                <div 
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('quality-reachability')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'quality-reachability' 
                                      ? 'opacity-100 translate-x-0 scale-100' 
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg 
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'quality-reachability' 
                                        ? 'scale-110 rotate-[-5deg]' 
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                  >
                                    <path 
                                      strokeLinecap="round" 
                                      strokeLinejoin="round" 
                                      strokeWidth={2} 
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['quality-reachability'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                              <div className="mt-2">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                    <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                      {JSON.stringify({"phone_no_reachability": true}, null, 2)}
                                    </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Phone No - Recently Abused Activity */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('quality-recently-abused')}
                              className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              {/* Mobile: Top row with icon and plus-minus */}
                              <div className="flex justify-between items-start mb-2 lg:hidden">
                                <img src="/phone-intelligence-icons/RAA.png" alt="Recently Abused Activity" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                <span className={`text-[#26ab4d] text-lg sm:text-xl font-bold transition-all duration-300 flex-shrink-0 ${openAccordions['quality-recently-abused'] ? '' : ''}`}>
                                  {openAccordions['quality-recently-abused'] ? '−' : '+'}
                                </span>
                              </div>
                              {/* Mobile: Header and Description stacked vertically */}
                              <div className="flex flex-col mb-2 lg:hidden">
                                <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Phone No - Recently Abused Activity</h6>
                                <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                  Checks whether the phone number has been recently reported for spam or abusive behavior.
                                </p>
                              </div>
                              {/* Desktop: Original layout */}
                              <div className="hidden lg:flex items-center gap-2 flex-1">
                                <img src="/phone-intelligence-icons/RAA.png" alt="Recently Abused Activity" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Phone No - Recently Abused Activity</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Checks whether the phone number has been recently reported for spam or abusive behavior.
                                  </p>
                                </div>
                              </div>
                              <span className={`hidden lg:block text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['quality-recently-abused'] ? 'mr-1' : ''}`}>
                                {openAccordions['quality-recently-abused'] ? '−' : '+'}
                              </span>
                            </button>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/phone-intelligence-icons/RAA.png" alt="Recently Abused Activity" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div 
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('quality-recently-abused')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'quality-recently-abused' 
                                        ? 'opacity-100 translate-x-0 scale-100' 
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg 
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'quality-recently-abused' 
                                          ? 'scale-110 rotate-[-5deg]' 
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none" 
                                      stroke="currentColor" 
                                      viewBox="0 0 24 24"
                                    >
                                      <path 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round" 
                                        strokeWidth={2} 
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Phone No - Recently Abused Activity</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Checks whether the phone number has been recently reported for spam or abusive behavior.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/phone-intelligence-icons/RAA.png" alt="Recently Abused Activity" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Phone No - Recently Abused Activity</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Checks whether the phone number has been recently reported for spam or abusive behavior.
                                    </p>
                                  </div>
                                </div>
                                <div 
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('quality-recently-abused')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'quality-recently-abused' 
                                      ? 'opacity-100 translate-x-0 scale-100' 
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg 
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'quality-recently-abused' 
                                        ? 'scale-110 rotate-[-5deg]' 
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                  >
                                    <path 
                                      strokeLinecap="round" 
                                      strokeLinejoin="round" 
                                      strokeWidth={2} 
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['quality-recently-abused'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                              <div className="mt-2">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                    <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                      {JSON.stringify({"phone_no_recently_abused_activity": false}, null, 2)}
                                    </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Phone No - Linked to Email */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('quality-linked-email')}
                              className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              {/* Mobile: Top row with icon and plus-minus */}
                              <div className="flex justify-between items-start mb-2 lg:hidden">
                                <img src="/phone-intelligence-icons/email link.png" alt="Linked to Email" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                <span className={`text-[#26ab4d] text-lg sm:text-xl font-bold transition-all duration-300 flex-shrink-0 ${openAccordions['quality-linked-email'] ? '' : ''}`}>
                                  {openAccordions['quality-linked-email'] ? '−' : '+'}
                                </span>
                              </div>
                              {/* Mobile: Header and Description stacked vertically */}
                              <div className="flex flex-col mb-2 lg:hidden">
                                <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Phone No - Linked to Email</h6>
                                <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                  Shows how many different emails are linked to this Phone No.
                                </p>
                              </div>
                              {/* Desktop: Original layout */}
                              <div className="hidden lg:flex items-center gap-2 flex-1">
                                <img src="/phone-intelligence-icons/email link.png" alt="Linked to Email" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Phone No - Linked to Email</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Shows how many different emails are linked to this Phone No.
                                  </p>
                                </div>
                              </div>
                              <span className={`hidden lg:block text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['quality-linked-email'] ? 'mr-1' : ''}`}>
                                {openAccordions['quality-linked-email'] ? '−' : '+'}
                              </span>
                            </button>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/phone-intelligence-icons/email link.png" alt="Linked to Email" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div 
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('quality-linked-email')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'quality-linked-email' 
                                        ? 'opacity-100 translate-x-0 scale-100' 
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg 
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'quality-linked-email' 
                                          ? 'scale-110 rotate-[-5deg]' 
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none" 
                                      stroke="currentColor" 
                                      viewBox="0 0 24 24"
                                    >
                                      <path 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round" 
                                        strokeWidth={2} 
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Phone No - Linked to Email</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Shows how many different emails are linked to this Phone No.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/phone-intelligence-icons/email link.png" alt="Linked to Email" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Phone No - Linked to Email</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Shows how many different emails are linked to this Phone No.
                                    </p>
                                  </div>
                                </div>
                                <div 
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('quality-linked-email')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'quality-linked-email' 
                                      ? 'opacity-100 translate-x-0 scale-100' 
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg 
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'quality-linked-email' 
                                        ? 'scale-110 rotate-[-5deg]' 
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                  >
                                    <path 
                                      strokeLinecap="round" 
                                      strokeLinejoin="round" 
                                      strokeWidth={2} 
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['quality-linked-email'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                              <div className="mt-2">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                    <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                      {JSON.stringify({"phone_no_linked_to_email": ["anandj123@gmail.com", "anandjain@gmail.com"]}, null, 2)}
                                    </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Phone No - List of Linked Phone Numbers */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('quality-linked-phones')}
                              className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              {/* Mobile: Top row with icon and plus-minus */}
                              <div className="flex justify-between items-start mb-2 lg:hidden">
                                <img src="/phone-intelligence-icons/phone link.png" alt="Linked Phone Numbers" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                <span className={`text-[#26ab4d] text-lg sm:text-xl font-bold transition-all duration-300 flex-shrink-0 ${openAccordions['quality-linked-phones'] ? '' : ''}`}>
                                  {openAccordions['quality-linked-phones'] ? '−' : '+'}
                                </span>
                              </div>
                              {/* Mobile: Header and Description stacked vertically */}
                              <div className="flex flex-col mb-2 lg:hidden">
                                <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Phone No - List of Linked Phone Numbers</h6>
                                <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                  Shows how many different phone numbers are linked to this Phone No.
                                </p>
                              </div>
                              {/* Desktop: Original layout */}
                              <div className="hidden lg:flex items-center gap-2 flex-1">
                                <img src="/phone-intelligence-icons/phone link.png" alt="Linked Phone Numbers" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Phone No - List of Linked Phone Numbers</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Shows how many different phone numbers are linked to this Phone No.
                                  </p>
                                </div>
                              </div>
                              <span className={`hidden lg:block text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['quality-linked-phones'] ? 'mr-1' : ''}`}>
                                {openAccordions['quality-linked-phones'] ? '−' : '+'}
                              </span>
                            </button>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/phone-intelligence-icons/phone link.png" alt="Linked Phone Numbers" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div 
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('quality-linked-phones')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'quality-linked-phones' 
                                        ? 'opacity-100 translate-x-0 scale-100' 
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg 
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'quality-linked-phones' 
                                          ? 'scale-110 rotate-[-5deg]' 
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none" 
                                      stroke="currentColor" 
                                      viewBox="0 0 24 24"
                                    >
                                      <path 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round" 
                                        strokeWidth={2} 
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Phone No - List of Linked Phone Numbers</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Shows how many different phone numbers are linked to this Phone No.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/phone-intelligence-icons/phone link.png" alt="Linked Phone Numbers" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Phone No - List of Linked Phone Numbers</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Shows how many different phone numbers are linked to this Phone No.
                                    </p>
                                  </div>
                                </div>
                                <div 
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('quality-linked-phones')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'quality-linked-phones' 
                                      ? 'opacity-100 translate-x-0 scale-100' 
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg 
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'quality-linked-phones' 
                                        ? 'scale-110 rotate-[-5deg]' 
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                  >
                                    <path 
                                      strokeLinecap="round" 
                                      strokeLinejoin="round" 
                                      strokeWidth={2} 
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['quality-linked-phones'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                              <div className="mt-2">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                    <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                      {JSON.stringify({"phone_no_list_of_linked_phone_numbers": ["9988558732", "9821442345"]}, null, 2)}
                                    </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Phone No - Internet Based */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('quality-internet-based')}
                              className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              {/* Mobile: Top row with icon and plus-minus */}
                              <div className="flex justify-between items-start mb-2 lg:hidden">
                                <img src="/phone-intelligence-icons/activity.png" alt="Internet Based" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                <span className={`text-[#26ab4d] text-lg sm:text-xl font-bold transition-all duration-300 flex-shrink-0 ${openAccordions['quality-internet-based'] ? '' : ''}`}>
                                  {openAccordions['quality-internet-based'] ? '−' : '+'}
                                </span>
                              </div>
                              {/* Mobile: Header and Description stacked vertically */}
                              <div className="flex flex-col mb-2 lg:hidden">
                                <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Phone No - Internet Based</h6>
                                <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                  Indicates whether the phone number operates through an internet-based service (VoIP).
                                </p>
                              </div>
                              {/* Desktop: Original layout */}
                              <div className="hidden lg:flex items-center gap-2 flex-1">
                                <img src="/phone-intelligence-icons/activity.png" alt="Internet Based" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Phone No - Internet Based</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Indicates whether the phone number operates through an internet-based service (VoIP).
                                  </p>
                                </div>
                              </div>
                              <span className={`hidden lg:block text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['quality-internet-based'] ? 'mr-1' : ''}`}>
                                {openAccordions['quality-internet-based'] ? '−' : '+'}
                              </span>
                            </button>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/phone-intelligence-icons/activity.png" alt="Internet Based" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div 
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('quality-internet-based')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'quality-internet-based' 
                                        ? 'opacity-100 translate-x-0 scale-100' 
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg 
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'quality-internet-based' 
                                          ? 'scale-110 rotate-[-5deg]' 
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none" 
                                      stroke="currentColor" 
                                      viewBox="0 0 24 24"
                                    >
                                      <path 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round" 
                                        strokeWidth={2} 
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Phone No - Internet Based</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Indicates whether the phone number operates through an internet-based service (VoIP).
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/phone-intelligence-icons/activity.png" alt="Internet Based" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Phone No - Internet Based</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Indicates whether the phone number operates through an internet-based service (VoIP).
                                    </p>
                                  </div>
                                </div>
                                <div 
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('quality-internet-based')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'quality-internet-based' 
                                      ? 'opacity-100 translate-x-0 scale-100' 
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg 
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'quality-internet-based' 
                                        ? 'scale-110 rotate-[-5deg]' 
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                  >
                                    <path 
                                      strokeLinecap="round" 
                                      strokeLinejoin="round" 
                                      strokeWidth={2} 
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['quality-internet-based'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                              <div className="mt-2">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                    <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                      {JSON.stringify({"phone_no_internet_based": false}, null, 2)}
                                    </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Phone - Frequent Abuse History */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('quality-frequent-abuse')}
                              className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              {/* Mobile: Top row with icon and plus-minus */}
                              <div className="flex justify-between items-start mb-2 lg:hidden">
                                <img src="/phone-intelligence-icons/FAA.png" alt="Frequent Abuse History" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                <span className={`text-[#26ab4d] text-lg sm:text-xl font-bold transition-all duration-300 flex-shrink-0 ${openAccordions['quality-frequent-abuse'] ? '' : ''}`}>
                                  {openAccordions['quality-frequent-abuse'] ? '−' : '+'}
                                </span>
                              </div>
                              {/* Mobile: Header and Description stacked vertically */}
                              <div className="flex flex-col mb-2 lg:hidden">
                                <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Phone - Frequent Abuse History</h6>
                                <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                  Checks whether the phone number has been previously involved in repeated malicious or abusive activities.
                                </p>
                              </div>
                              {/* Desktop: Original layout */}
                              <div className="hidden lg:flex items-center gap-2 flex-1">
                                <img src="/phone-intelligence-icons/FAA.png" alt="Frequent Abuse History" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Phone - Frequent Abuse History</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Checks whether the phone number has been previously involved in repeated malicious or abusive activities.
                                  </p>
                                </div>
                              </div>
                              <span className={`hidden lg:block text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['quality-frequent-abuse'] ? 'mr-1' : ''}`}>
                                {openAccordions['quality-frequent-abuse'] ? '−' : '+'}
                              </span>
                            </button>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/phone-intelligence-icons/FAA.png" alt="Frequent Abuse History" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div 
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('quality-frequent-abuse')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'quality-frequent-abuse' 
                                        ? 'opacity-100 translate-x-0 scale-100' 
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg 
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'quality-frequent-abuse' 
                                          ? 'scale-110 rotate-[-5deg]' 
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none" 
                                      stroke="currentColor" 
                                      viewBox="0 0 24 24"
                                    >
                                      <path 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round" 
                                        strokeWidth={2} 
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Phone - Frequent Abuse History</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Checks whether the phone number has been previously involved in repeated malicious or abusive activities.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/phone-intelligence-icons/FAA.png" alt="Frequent Abuse History" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Phone - Frequent Abuse History</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Checks whether the phone number has been previously involved in repeated malicious or abusive activities.
                                    </p>
                                  </div>
                                </div>
                                <div 
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('quality-frequent-abuse')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'quality-frequent-abuse' 
                                      ? 'opacity-100 translate-x-0 scale-100' 
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg 
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'quality-frequent-abuse' 
                                        ? 'scale-110 rotate-[-5deg]' 
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                  >
                                    <path 
                                      strokeLinecap="round" 
                                      strokeLinejoin="round" 
                                      strokeWidth={2} 
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['quality-frequent-abuse'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                              <div className="mt-2">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                    <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                      {JSON.stringify({"phone_no_frequent_abuse_history": true}, null, 2)}
                                    </pre>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Phone Intelligence Tool Section */}
          <section className="pt-[0.5rem] pb-[3rem] sm:pb-[4rem] relative z-20 bg-transparent overflow-hidden">
            <div className="container mx-auto px-3 sm:px-[1.5rem] relative z-10">
              <div className="w-full overflow-hidden">
                {/* Unified Phone Intelligence Tool */}
                <div className="bg-black/20 rounded-lg p-4 sm:p-6 lg:p-8 border border-[#26ab4d]/30 overflow-hidden">
                <div>
                    <h4 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold mb-4 sm:mb-6">
                      <span className="text-[#ffffff]">Phone Intelligence </span>
                      <span className="bg-gradient-to-r from-[#26ab4d] via-[#1e8a3f] to-[#26ab4d] bg-clip-text text-transparent">Tool</span>
                    </h4>
                    
                    {/* Input Section */}
                    <div className="mb-4 sm:mb-6">
                    <div className="flex flex-col sm:flex-row gap-2.5 sm:gap-3 lg:gap-4">
                      <div className="flex-1 min-w-0 overflow-hidden">
                        <input
                          type="tel"
                          value={phoneNumber}
                          onChange={(e) => setPhoneNumber(e.target.value)}
                          placeholder={'"9920042243"'}
                          className={`w-full px-3 sm:px-4 lg:px-6 py-2.5 sm:py-3 lg:py-4 bg-[#26ab4d]/10 border-2 rounded-full text-[#ffffff] placeholder-[#9a9a9a] placeholder:font-bold focus:outline-none focus:ring-2 text-sm sm:text-base lg:text-[1.125rem] ${
                            phoneNumber && !isValidPhone(phoneNumber)
                              ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                              : 'border-[#26ab4d]/50 focus:ring-[#26ab4d] focus:border-[#26ab4d]'
                          }`}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleAnalyzeAll();
                            }
                          }}
                        />
                </div>
                      <button
                        onClick={handleAnalyzeAll}
                        disabled={isAnalyzing || !phoneNumber.trim()}
                        className="px-4 sm:px-6 lg:px-8 py-2.5 sm:py-3 lg:py-4 bg-gradient-to-r from-[#26ab4d] to-[#1e8a3f] hover:from-[#1e8a3f] hover:to-[#26ab4d] text-[#ffffff] rounded-full font-semibold transition-all duration-300 shadow-lg shadow-[#26ab4d]/25 disabled:opacity-50 disabled:cursor-not-allowed min-w-[120px] sm:min-w-[140px] text-sm sm:text-base lg:text-[1.125rem] whitespace-nowrap flex-shrink-0"
                      >
                        {isAnalyzing ? (
                          <span className="flex items-center justify-center">
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-[#ffffff]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
                            Analyzing...
                    </span>
                        ) : (
                          'Submit'
                        )}
                      </button>
              </div>
            </div>
            
                  {/* Tabbed Results Section */}
                  {(analysisResult || riskyPhoneResult || simCardResult || demographicsResult) && (
                    <div 
                      className={`space-y-4 sm:space-y-6 relative min-h-[300px] sm:min-h-[400px] ${!accessGranted ? 'overflow-hidden touch-none' : ''}`} 
                      style={!accessGranted ? { touchAction: 'none' as const, overscrollBehavior: 'none' as const } : undefined}
                      onTouchStart={!accessGranted ? (e) => e.preventDefault() : undefined}
                      onTouchMove={!accessGranted ? (e) => e.preventDefault() : undefined}
                    >
                      {!accessGranted && <LockOverlay compact />}
                      {/* Tabs */}
                      <div className={`flex flex-wrap gap-1 sm:gap-1.5 lg:gap-2 border-b border-[#26ab4d]/30 ${!accessGranted ? 'pointer-events-none opacity-50' : ''}`}>
                    <button
                          onClick={() => setActiveTab('risky')}
                          className={`px-2 sm:px-3 lg:px-4 py-1.5 sm:py-2 rounded-t-lg text-[0.7rem] sm:text-xs lg:text-sm font-semibold transition-all ${
                            activeTab === 'risky'
                              ? 'bg-[#26ab4d] text-[#ffffff]'
                              : 'bg-black/40 text-[#ffffff] hover:text-[#ffffff]'
                          }`}
                        >
                          Risky Phone
                        </button>
                        <button
                          onClick={() => setActiveTab('simcard')}
                          className={`px-2 sm:px-3 lg:px-4 py-1.5 sm:py-2 rounded-t-lg text-[0.7rem] sm:text-xs lg:text-sm font-semibold transition-all ${
                            activeTab === 'simcard'
                              ? 'bg-[#26ab4d] text-[#ffffff]'
                              : 'bg-black/40 text-[#ffffff] hover:text-[#ffffff]'
                          }`}
                        >
                          Sim Card Info
                        </button>
                        <button
                          onClick={() => setActiveTab('demographics')}
                          className={`px-2 sm:px-3 lg:px-4 py-1.5 sm:py-2 rounded-t-lg text-[0.7rem] sm:text-xs lg:text-sm font-semibold transition-all ${
                            activeTab === 'demographics'
                              ? 'bg-[#26ab4d] text-[#ffffff]'
                              : 'bg-black/40 text-[#ffffff] hover:text-[#ffffff]'
                          }`}
                        >
                          Phone Number Demographics
                    </button>
                        <button
                          onClick={() => setActiveTab('quality')}
                          className={`px-2 sm:px-3 lg:px-4 py-1.5 sm:py-2 rounded-t-lg text-[0.7rem] sm:text-xs lg:text-sm font-semibold transition-all ${
                            activeTab === 'quality'
                              ? 'bg-[#26ab4d] text-[#ffffff]'
                              : 'bg-black/40 text-[#ffffff] hover:text-[#ffffff]'
                          }`}
                        >
                          Phone Number Quality Score
                        </button>
                  </div>
                
                      {/* Tab Content */}
                      <div className={`bg-black/20 rounded-lg p-3 sm:p-4 lg:p-6 border border-[#26ab4d]/20 ${!accessGranted ? 'pointer-events-none opacity-50' : ''}`}>
                        {activeTab === 'quality' && analysisResult && (
                          <div>
                            <h5 className="text-base sm:text-lg lg:text-xl font-bold text-[#ffffff] mb-3 sm:mb-4">Phone Number Quality Score</h5>
                            <div className={`bg-black/40 rounded-lg p-2.5 sm:p-3 lg:p-4 border border-[#26ab4d]/20 ${accessGranted ? 'overflow-x-auto' : 'overflow-hidden touch-none'}`} style={!accessGranted ? { touchAction: 'none' } : {}}>
                              <pre className="text-[0.7rem] sm:text-[0.75rem] lg:text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                {formatFlattenedJSON(analysisResult.jsonResponse)}
                              </pre>
                </div>
              </div>
                        )}

                        {activeTab === 'risky' && riskyPhoneResult && (
                          <div>
                            <h5 className="text-base sm:text-lg lg:text-xl font-bold text-[#ffffff] mb-3 sm:mb-4">Risky Phone</h5>
                            <div className={`bg-black/40 rounded-lg p-2.5 sm:p-3 lg:p-4 border border-[#26ab4d]/20 ${accessGranted ? 'overflow-x-auto' : 'overflow-hidden touch-none'}`} style={!accessGranted ? { touchAction: 'none' } : {}}>
                              <pre className="text-[0.7rem] sm:text-[0.75rem] lg:text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                {formatJSONWithColors(riskyPhoneResult.jsonResponse)}
                              </pre>
            </div>
                </div>
                        )}

                        {activeTab === 'simcard' && simCardResult && (
                          <div>
                            <h5 className="text-base sm:text-lg lg:text-xl font-bold text-[#ffffff] mb-3 sm:mb-4">Sim Card Info</h5>
                            <div className={`bg-black/40 rounded-lg p-2.5 sm:p-3 lg:p-4 border border-[#26ab4d]/20 ${accessGranted ? 'overflow-x-auto' : 'overflow-hidden touch-none'}`} style={!accessGranted ? { touchAction: 'none' } : {}}>
                              <pre className="text-[0.7rem] sm:text-[0.75rem] lg:text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                {formatJSONWithColors(simCardResult.jsonResponse)}
                              </pre>
            </div>
                </div>
                            )}

                        {activeTab === 'demographics' && demographicsResult && (
                          <div>
                            <h5 className="text-base sm:text-lg lg:text-xl font-bold text-[#ffffff] mb-3 sm:mb-4">Phone Number Demographics</h5>
                            <div className={`bg-black/40 rounded-lg p-2.5 sm:p-3 lg:p-4 border border-[#26ab4d]/20 ${accessGranted ? 'overflow-x-auto' : 'overflow-hidden touch-none'}`} style={!accessGranted ? { touchAction: 'none' } : {}}>
                              <pre className="text-[0.7rem] sm:text-[0.75rem] lg:text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                {formatJSONWithColors(demographicsResult.jsonResponse)}
                              </pre>
                </div>
              </div>
                            )}
            </div>

                      {/* Combined JSON Response - Flattened */}
                      <div className={`bg-black/20 rounded-lg p-3 sm:p-4 lg:p-6 border border-[#26ab4d]/20 ${!accessGranted ? 'pointer-events-none opacity-50' : ''}`}>
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 mb-3 sm:mb-4">
                          <h6 className="text-sm sm:text-base lg:text-lg font-bold text-[#ffffff] flex items-center">
                            <svg className="w-4 h-4 sm:w-5 sm:h-5 text-[#ffffff] mr-1.5 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
              </svg>
                            Combined JSON Response
                          </h6>
                          <button
                            onClick={() => {
                              // Flatten all JSONs into one object
                              const flattenedJson = {
                                ...(analysisResult?.jsonResponse || {}),
                                ...(riskyPhoneResult?.jsonResponse || {}),
                                ...(simCardResult?.jsonResponse || {}),
                                ...(demographicsResult?.jsonResponse || {})
                              };
                              const jsonStr = JSON.stringify(flattenedJson, null, 2);
                              navigator.clipboard.writeText(jsonStr);
                              alert('Combined JSON copied to clipboard!');
                            }}
                            className="text-[0.65rem] sm:text-xs text-[#ffffff] hover:text-[#ffffff] transition-colors flex items-center self-start sm:self-auto"
                          >
                            <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
                            Copy Combined JSON
                          </button>
            </div>
                        <div className={`bg-black/40 rounded-lg p-2.5 sm:p-3 lg:p-4 border border-[#26ab4d]/20 ${accessGranted ? 'overflow-x-auto' : 'overflow-hidden touch-none'}`} style={!accessGranted ? { touchAction: 'none' } : {}}>
                          <pre className="text-[0.65rem] sm:text-xs lg:text-sm text-[#656565] font-mono whitespace-pre">
                            {formatFlattenedJSON({
                              ...(analysisResult?.jsonResponse || {}),
                              ...(riskyPhoneResult?.jsonResponse || {}),
                              ...(simCardResult?.jsonResponse || {}),
                              ...(demographicsResult?.jsonResponse || {})
                            })}
                          </pre>
                </div>
                    </div>
                  </div>
                  )}

                    {/* Default Example - Show when no analysis */}
                  {!analysisResult && !riskyPhoneResult && !simCardResult && !demographicsResult && (
                      <div className="bg-black/20 rounded-lg p-3 sm:p-4 lg:p-6 border border-[#26ab4d]/20">
                      <p className="text-[#ffffff] text-center text-sm sm:text-base mb-3 sm:mb-4">Enter a phone number above to see comprehensive phone intelligence analysis</p>
                        <div className="bg-black/40 rounded-lg p-2.5 sm:p-3 lg:p-4 overflow-x-auto border border-[#26ab4d]/20 relative">
                          {!accessGranted && <LockOverlay compact />}
                          <pre className="text-[0.65rem] sm:text-xs lg:text-sm text-[#656565] font-mono whitespace-pre">
{`{
  `}<span className="text-[#656565]">"Phone_No_Reachability"</span>{` : `}<span className="text-[#656565]">true</span>{`,
  `}<span className="text-[#656565]">"Phone_No_Recently_abused_activity"</span>{` : `}<span className="text-[#656565]">false</span>{`,
  `}<span className="text-[#656565]">"Phone_No_Linked_to_Email"</span>{` : `}<span className="text-[#656565]">["anandj123@gmail.com"]</span>{`,
  `}<span className="text-[#656565]">"Phone_No_List_of_linked_phone_numbers"</span>{` : `}<span className="text-[#656565]">["9988558732", "9821442345"]</span>{`,
  `}<span className="text-[#656565]">"Phone_No_Internet_based"</span>{` : `}<span className="text-[#656565]">false</span>{`,
  `}<span className="text-[#656565]">"Phone_No_Frequent_abuse_History"</span>{` : `}<span className="text-[#656565]">true</span>{`,
  `}<span className="text-[#656565]">"Phone_No_Valid"</span>{` : `}<span className="text-[#656565]">true</span>{`,
  `}<span className="text-[#656565]">"Phone_No_Active"</span>{` : `}<span className="text-[#656565]">true</span>{`,
  `}<span className="text-[#656565]">"Phone_No_Risky_activity"</span>{` : `}<span className="text-[#656565]">false</span>{`,
  `}<span className="text-[#656565]">"Phone_No_Spam"</span>{` : `}<span className="text-[#656565]">false</span>{`,
  `}<span className="text-[#656565]">"Phone_No_Network_carrier"</span>{` : `}<span className="text-[#656565]">"JIO"</span>{`,
  `}<span className="text-[#656565]">"Phone_No_Line_Type"</span>{` : `}<span className="text-[#656565]">"Wireless"</span>{`,
  `}<span className="text-[#656565]">"Phone_No_Prepaid"</span>{` : `}<span className="text-[#656565]">true</span>{`,
  `}<span className="text-[#656565]">"Phone_No_User_name"</span>{` : `}<span className="text-[#656565]">"ANAND JAIN"</span>{`,
  `}<span className="text-[#656565]">"Phone_No_Country"</span>{` : `}<span className="text-[#656565]">"IN"</span>{`,
  `}<span className="text-[#656565]">"Phone_No_City"</span>{` : `}<span className="text-[#656565]">"Mumbai"</span>{`,
  `}<span className="text-[#656565]">"Phone_No_Region"</span>{` : `}<span className="text-[#656565]">"Maharashtra"</span>{`,
  `}<span className="text-[#656565]">"Phone_No_Zip_Code"</span>{` : `}<span className="text-[#656565]">"400079"</span>{`,
  `}<span className="text-[#656565]">"Phone_No_Timezone"</span>{` : `}<span className="text-[#656565]">"Asia/Kolkata"</span>{`
}`}
                          </pre>
                  </div>
                  </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-[2.5rem] sm:py-[3rem] lg:py-[4rem] relative z-20 bg-transparent overflow-hidden">
            {/* Multi-Layer Vector Background */}
            <div className="absolute inset-0 z-0">
              {/* Primary Neural Network Vector */}
              <svg className="absolute inset-0 w-full h-full opacity-20" viewBox="0 0 1400 900" fill="none">
                <defs>
                  <linearGradient id="neuralGradient1-cta" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#26ab4d" stopOpacity="0.9"/>
                    <stop offset="30%" stopColor="#1e8a3f" stopOpacity="0.7"/>
                    <stop offset="70%" stopColor="#26ab4d" stopOpacity="0.5"/>
                    <stop offset="100%" stopColor="#1e8a3f" stopOpacity="0.3"/>
                  </linearGradient>
                  <linearGradient id="neuralGradient2-cta" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#1e8a3f" stopOpacity="0.8"/>
                    <stop offset="50%" stopColor="#26ab4d" stopOpacity="0.6"/>
                    <stop offset="100%" stopColor="#1e8a3f" stopOpacity="0.4"/>
                  </linearGradient>
                  <filter id="neuralGlow-cta" x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                    <feMerge> 
                      <feMergeNode in="coloredBlur"/>
                      <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                  </filter>
                </defs>
              </svg>
              
              {/* Secondary Vector Layer - DNA Helix */}
              <svg className="absolute inset-0 w-full h-full opacity-10" viewBox="0 0 1400 900" fill="none">
                <defs>
                  <linearGradient id="dnaGradient-cta" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#26ab4d" stopOpacity="0.6"/>
                    <stop offset="50%" stopColor="#1e8a3f" stopOpacity="0.4"/>
                    <stop offset="100%" stopColor="#26ab4d" stopOpacity="0.2"/>
                  </linearGradient>
                </defs>
              </svg>
              
              {/* Tertiary Vector Layer - Quantum Patterns */}
              <svg className="absolute inset-0 w-full h-full opacity-8" viewBox="0 0 1400 900" fill="none">
                <defs>
                  <radialGradient id="quantumGradient-cta" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="#26ab4d" stopOpacity="0.8"/>
                    <stop offset="70%" stopColor="#1e8a3f" stopOpacity="0.4"/>
                    <stop offset="100%" stopColor="#26ab4d" stopOpacity="0.1"/>
                  </radialGradient>
                </defs>
              </svg>
            </div>
            
            <div className="container mx-auto px-3 sm:px-4 lg:px-[1.5rem] relative z-10">
              <div className="max-w-5xl lg:max-w-fit mx-auto text-center">
                <div className="bg-gradient-to-br from-[#26ab4d]/20 to-[#1e8a3f]/10 backdrop-blur-sm border border-[#26ab4d]/30 rounded-2xl sm:rounded-3xl lg:rounded-3xl p-5 sm:p-6 lg:p-8">
                  <h3 className="text-[1.125rem] sm:text-[1.375rem] lg:text-[2rem] xl:text-[2.25rem] font-bold mb-6 sm:mb-8 lg:mb-3 break-words lg:leading-tight lg:px-1">
                    <span className="text-[#ffffff]">Ready to Transform Your </span>
                    <span className="text-[#26ab4d]">Phone Intelligence</span>
                  </h3>
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 lg:gap-4 justify-center items-center">
                    <Link 
                      to="/ask-access" 
                      className="bg-[#26ab4d] hover:bg-[#1e8a3f] text-[#ffffff] px-5 sm:px-6 lg:px-8 py-2.5 sm:py-3 lg:py-4 rounded-full text-base sm:text-lg lg:text-lg font-bold transition-all duration-300 shadow-xl inline-flex items-center justify-center w-fit sm:w-auto sm:min-w-[160px] lg:min-w-[170px] whitespace-nowrap"
                      onClick={() => {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                    >
                      Ask for access
                    </Link>
                  </div>
                    </div>
              </div>
            </div>
          </section>
        </>
      )}

      {/* Email Intelligence Section - Standalone Product */}
      {currentProductId === 'email-intelligence' && (
        <>
          {/* Email Intelligence Section - Consolidated */}
          <section className="pt-[4rem] pb-[0.5rem] relative z-20 bg-transparent overflow-hidden">
            <div className="container mx-auto px-[1.5rem] relative z-10">
              <div className="w-full">
                {/* Features & JSON Types Section */}
                <div className="bg-black/20 rounded-lg p-4 sm:p-6 mb-8 border border-[#26ab4d]/30">
                  <h4 className="text-xl sm:text-2xl font-bold text-[#ffffff] mb-4 text-center">
                    Features & JSON Types
                  </h4>
                  
                  <div className="space-y-3">
                    {/* Email Reputation Accordion */}
                    <div className="border border-[#26ab4d]/30 rounded-lg overflow-hidden bg-black/40">
                      <button
                        onClick={() => toggleAccordion('email-reputation')}
                        className="w-full relative p-3 sm:p-4 text-left hover:bg-[#26ab4d]/10 transition-colors"
                      >
                        <div className="pr-8 sm:pr-10">
                          <h5 className="text-lg font-bold text-[#26ab4d]">Email Reputation</h5>
                          <p className="text-[#ffffff] text-sm mt-1 leading-relaxed">
                            Evaluates the trustworthiness and activity level of email domains based on past behavior and security policies.
                          </p>
                        </div>
                        <span className={`absolute top-3 right-3 sm:top-4 sm:right-4 text-[#26ab4d] text-2xl font-bold transition-all duration-300 ${openAccordions['email-reputation'] ? '' : ''}`}>
                          {openAccordions['email-reputation'] ? '−' : '+'}
                        </span>
                      </button>
                      <div className={`overflow-hidden transition-all duration-300 ${openAccordions['email-reputation'] ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                        <div className="p-4 pt-0 space-y-2">
                          {/* Email - Domain Trust */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                                <button
                                  onClick={() => toggleAccordion('email-reputation-domain-trust')}
                                  className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                                >
                                  <div className="flex items-center gap-2 flex-1">
                                    <img src="/email-intelligence-icons/domain%20trust.png" alt="Domain Trust" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                    <div className="flex-1">
                                      <h6 className="text-base font-semibold text-[#ffffff]">Email - Domain Trust</h6>
                                      <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                        Describes the trustworthiness of the email's domain based on past behavior.
                                      </p>
                                    </div>
                                  </div>
                                  <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['email-reputation-domain-trust'] ? 'mr-1' : ''}`}>
                                    {openAccordions['email-reputation-domain-trust'] ? '−' : '+'}
                                  </span>
                                </button>
                                <div className={`overflow-hidden transition-all duration-300 ${openAccordions['email-reputation-domain-trust'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                                  <div className="px-3 pb-3">
                                    <div className="mt-2">
                                      <div className="relative min-h-[80px]">
                                        <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20">
                                          <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                            {JSON.stringify({"email_domain_trust": "trusted"}, null, 2)}
                                          </pre>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/email-intelligence-icons/domain%20trust.png" alt="Domain Trust" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div 
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('email-reputation-domain-trust')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'email-reputation-domain-trust' 
                                        ? 'opacity-100 translate-x-0 scale-100' 
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg 
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'email-reputation-domain-trust' 
                                          ? 'scale-110 rotate-[-5deg]' 
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none" 
                                      stroke="currentColor" 
                                      viewBox="0 0 24 24"
                                    >
                                      <path 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round" 
                                        strokeWidth={2} 
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Email - Domain Trust</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Describes the trustworthiness of the email's domain based on past behavior.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/email-intelligence-icons/domain%20trust.png" alt="Domain Trust" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Email - Domain Trust</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Describes the trustworthiness of the email's domain based on past behavior.
                                    </p>
                                  </div>
                                </div>
                                <div 
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('email-reputation-domain-trust')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'email-reputation-domain-trust' 
                                      ? 'opacity-100 translate-x-0 scale-100' 
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg 
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'email-reputation-domain-trust' 
                                        ? 'scale-110 rotate-[-5deg]' 
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                  >
                                    <path 
                                      strokeLinecap="round" 
                                      strokeLinejoin="round" 
                                      strokeWidth={2} 
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Email - Domain Activity */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                                <button
                                  onClick={() => toggleAccordion('email-reputation-domain-activity')}
                                  className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                                >
                                  <div className="flex items-center gap-2 flex-1">
                                    <img src="/email-intelligence-icons/domain%20activity.png" alt="Domain Activity" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                    <div className="flex-1">
                                      <h6 className="text-base font-semibold text-[#ffffff]">Email - Domain Activity</h6>
                                      <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                        Indicates user interaction frequency with the domain.
                                      </p>
                                    </div>
                                  </div>
                                  <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['email-reputation-domain-activity'] ? 'mr-1' : ''}`}>
                                    {openAccordions['email-reputation-domain-activity'] ? '−' : '+'}
                                  </span>
                                </button>
                                <div className={`overflow-hidden transition-all duration-300 ${openAccordions['email-reputation-domain-activity'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                                  <div className="px-3 pb-3">
                                    <div className="mt-2">
                                      <div className="relative min-h-[80px]">
                                        <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20">
                                          <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                            {JSON.stringify({"email_domain_activity": "low"}, null, 2)}
                                          </pre>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/email-intelligence-icons/domain%20activity.png" alt="Domain Activity" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div 
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('email-reputation-domain-activity')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'email-reputation-domain-activity' 
                                        ? 'opacity-100 translate-x-0 scale-100' 
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg 
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'email-reputation-domain-activity' 
                                          ? 'scale-110 rotate-[-5deg]' 
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none" 
                                      stroke="currentColor" 
                                      viewBox="0 0 24 24"
                                    >
                                      <path 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round" 
                                        strokeWidth={2} 
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Email - Domain Activity</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Indicates user interaction frequency with the domain.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/email-intelligence-icons/domain%20activity.png" alt="Domain Activity" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Email - Domain Activity</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Indicates user interaction frequency with the domain.
                                    </p>
                                  </div>
                                </div>
                                <div 
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('email-reputation-domain-activity')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'email-reputation-domain-activity' 
                                      ? 'opacity-100 translate-x-0 scale-100' 
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg 
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'email-reputation-domain-activity' 
                                        ? 'scale-110 rotate-[-5deg]' 
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                  >
                                    <path 
                                      strokeLinecap="round" 
                                      strokeLinejoin="round" 
                                      strokeWidth={2} 
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Email - Risky Extension */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                                <button
                                  onClick={() => toggleAccordion('email-reputation-risky-extension')}
                                  className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                                >
                                  <div className="flex items-center gap-2 flex-1">
                                    <img src="/email-intelligence-icons/risky%20extention.png" alt="Risky Extension" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                    <div className="flex-1">
                                      <h6 className="text-base font-semibold text-[#ffffff]">Email - Risky Extension</h6>
                                      <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                        Checks if the email's Top-Level Domain (TLD) is frequently used in scams, spam, or phishing.
                                      </p>
                                    </div>
                                  </div>
                                  <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['email-reputation-risky-extension'] ? 'mr-1' : ''}`}>
                                    {openAccordions['email-reputation-risky-extension'] ? '−' : '+'}
                                  </span>
                                </button>
                                <div className={`overflow-hidden transition-all duration-300 ${openAccordions['email-reputation-risky-extension'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                                  <div className="px-3 pb-3">
                                    <div className="mt-2">
                                      <div className="relative min-h-[80px]">
                                        <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20">
                                          <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                            {JSON.stringify({"email_risky_extension": false}, null, 2)}
                                          </pre>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/email-intelligence-icons/risky%20extention.png" alt="Risky Extension" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div 
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('email-reputation-risky-extension')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'email-reputation-risky-extension' 
                                        ? 'opacity-100 translate-x-0 scale-100' 
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg 
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'email-reputation-risky-extension' 
                                          ? 'scale-110 rotate-[-5deg]' 
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none" 
                                      stroke="currentColor" 
                                      viewBox="0 0 24 24"
                                    >
                                      <path 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round" 
                                        strokeWidth={2} 
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Email - Risky Extension</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Checks if the email's Top-Level Domain (TLD) is frequently used in scams, spam, or phishing.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/email-intelligence-icons/risky%20extention.png" alt="Risky Extension" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Email - Risky Extension</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Checks if the email's Top-Level Domain (TLD) is frequently used in scams, spam, or phishing.
                                    </p>
                                  </div>
                                </div>
                                <div 
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('email-reputation-risky-extension')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'email-reputation-risky-extension' 
                                      ? 'opacity-100 translate-x-0 scale-100' 
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg 
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'email-reputation-risky-extension' 
                                        ? 'scale-110 rotate-[-5deg]' 
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                  >
                                    <path 
                                      strokeLinecap="round" 
                                      strokeLinejoin="round" 
                                      strokeWidth={2} 
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Email - Sender Validation */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                                <button
                                  onClick={() => toggleAccordion('email-reputation-sender-validation')}
                                  className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                                >
                                  <div className="flex items-center gap-2 flex-1">
                                    <img src="/email-intelligence-icons/sender%20validation.png" alt="Sender Validation" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                    <div className="flex-1">
                                      <h6 className="text-base font-semibold text-[#ffffff]">Email - Sender Validation</h6>
                                      <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                        Verifies if the domain has a valid SPF policy.
                                      </p>
                                    </div>
                                  </div>
                                  <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['email-reputation-sender-validation'] ? 'mr-1' : ''}`}>
                                    {openAccordions['email-reputation-sender-validation'] ? '−' : '+'}
                                  </span>
                                </button>
                                <div className={`overflow-hidden transition-all duration-300 ${openAccordions['email-reputation-sender-validation'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                                  <div className="px-3 pb-3">
                                    <div className="mt-2">
                                      <div className="relative min-h-[80px]">
                                        <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20">
                                          <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                            {JSON.stringify({"email_sender_validation": true}, null, 2)}
                                          </pre>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/email-intelligence-icons/sender%20validation.png" alt="Sender Validation" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div 
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('email-reputation-sender-validation')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'email-reputation-sender-validation' 
                                        ? 'opacity-100 translate-x-0 scale-100' 
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg 
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'email-reputation-sender-validation' 
                                          ? 'scale-110 rotate-[-5deg]' 
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none" 
                                      stroke="currentColor" 
                                      viewBox="0 0 24 24"
                                    >
                                      <path 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round" 
                                        strokeWidth={2} 
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Email - Sender Validation</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Verifies if the domain has a valid SPF policy.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/email-intelligence-icons/sender%20validation.png" alt="Sender Validation" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Email - Sender Validation</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Verifies if the domain has a valid SPF policy.
                                    </p>
                                  </div>
                                </div>
                                <div 
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('email-reputation-sender-validation')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'email-reputation-sender-validation' 
                                      ? 'opacity-100 translate-x-0 scale-100' 
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg 
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'email-reputation-sender-validation' 
                                        ? 'scale-110 rotate-[-5deg]' 
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                  >
                                    <path 
                                      strokeLinecap="round" 
                                      strokeLinejoin="round" 
                                      strokeWidth={2} 
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Email - Phishing */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                                <button
                                  onClick={() => toggleAccordion('email-reputation-phishing')}
                                  className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                                >
                                  <div className="flex items-center gap-2 flex-1">
                                    <img src="/email-intelligence-icons/email%20phishing.png" alt="Phishing" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                    <div className="flex-1">
                                      <h6 className="text-base font-semibold text-[#ffffff]">Email - Phishing</h6>
                                      <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                        Checks for a valid DMARC policy to prevent phishing and spoofing.
                                      </p>
                                    </div>
                                  </div>
                                  <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['email-reputation-phishing'] ? 'mr-1' : ''}`}>
                                    {openAccordions['email-reputation-phishing'] ? '−' : '+'}
                                  </span>
                                </button>
                                <div className={`overflow-hidden transition-all duration-300 ${openAccordions['email-reputation-phishing'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                                  <div className="px-3 pb-3">
                                    <div className="mt-2">
                                      <div className="relative min-h-[80px]">
                                        <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20">
                                          <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                            {JSON.stringify({"email_phishing": false}, null, 2)}
                                          </pre>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/email-intelligence-icons/email%20phishing.png" alt="Phishing" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div 
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('email-reputation-phishing')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'email-reputation-phishing' 
                                        ? 'opacity-100 translate-x-0 scale-100' 
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg 
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'email-reputation-phishing' 
                                          ? 'scale-110 rotate-[-5deg]' 
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none" 
                                      stroke="currentColor" 
                                      viewBox="0 0 24 24"
                                    >
                                      <path 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round" 
                                        strokeWidth={2} 
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Email - Phishing</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Checks for a valid DMARC policy to prevent phishing and spoofing.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/email-intelligence-icons/email%20phishing.png" alt="Phishing" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Email - Phishing</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Checks for a valid DMARC policy to prevent phishing and spoofing.
                                    </p>
                                  </div>
                                </div>
                                <div 
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('email-reputation-phishing')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'email-reputation-phishing' 
                                      ? 'opacity-100 translate-x-0 scale-100' 
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg 
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'email-reputation-phishing' 
                                        ? 'scale-110 rotate-[-5deg]' 
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                  >
                                    <path 
                                      strokeLinecap="round" 
                                      strokeLinejoin="round" 
                                      strokeWidth={2} 
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Email Risky Score Accordion */}
                    <div className="border border-[#26ab4d]/30 rounded-lg overflow-hidden bg-black/40 mt-3">
                      <button
                        onClick={() => toggleAccordion('email-risky')}
                        className="w-full relative p-3 sm:p-4 text-left hover:bg-[#26ab4d]/10 transition-colors"
                      >
                        <div className="pr-8 sm:pr-10">
                          <h5 className="text-lg font-bold text-[#26ab4d]">Email Risky Score</h5>
                          <p className="text-[#ffffff] text-sm mt-1 leading-relaxed">
                            Assesses email deliverability, sender reputation, and flags suspicious or temporary email addresses.
                          </p>
                        </div>
                        <span className={`absolute top-3 right-3 sm:top-4 sm:right-4 text-[#26ab4d] text-2xl font-bold transition-all duration-300 ${openAccordions['email-risky'] ? '' : ''}`}>
                          {openAccordions['email-risky'] ? '−' : '+'}
                        </span>
                      </button>
                      <div className={`overflow-hidden transition-all duration-300 ${openAccordions['email-risky'] ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                        <div className="p-4 pt-0 space-y-2">
                          {/* Email Be Delivered */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                                <button
                                  onClick={() => toggleAccordion('email-risky-delivered')}
                                  className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                                >
                                  <div className="flex items-center gap-2 flex-1">
                                    <img src="/email-intelligence-icons/delivered.png" alt="Delivered" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                    <div className="flex-1">
                                      <h6 className="text-base font-semibold text-[#ffffff]">Email Be Delivered</h6>
                                      <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                        Indicates the likelihood of the email being delivered.
                                      </p>
                                    </div>
                                  </div>
                                  <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['email-risky-delivered'] ? 'mr-1' : ''}`}>
                                    {openAccordions['email-risky-delivered'] ? '−' : '+'}
                                  </span>
                                </button>
                                <div className={`overflow-hidden transition-all duration-300 ${openAccordions['email-risky-delivered'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                                  <div className="px-3 pb-3">
                                    <div className="mt-2">
                                      <div className="relative min-h-[80px]">
                                        <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20">
                                          <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                            {JSON.stringify({"email_be_delivered": "high"}, null, 2)}
                                          </pre>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/email-intelligence-icons/delivered.png" alt="Delivered" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div 
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('email-risky-delivered')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'email-risky-delivered' 
                                        ? 'opacity-100 translate-x-0 scale-100' 
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg 
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'email-risky-delivered' 
                                          ? 'scale-110 rotate-[-5deg]' 
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none" 
                                      stroke="currentColor" 
                                      viewBox="0 0 24 24"
                                    >
                                      <path 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round" 
                                        strokeWidth={2} 
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Email Be Delivered</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Indicates the likelihood of the email being delivered.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/email-intelligence-icons/delivered.png" alt="Delivered" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Email Be Delivered</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Indicates the likelihood of the email being delivered.
                                    </p>
                                  </div>
                                </div>
                                <div 
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('email-risky-delivered')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'email-risky-delivered' 
                                      ? 'opacity-100 translate-x-0 scale-100' 
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg 
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'email-risky-delivered' 
                                        ? 'scale-110 rotate-[-5deg]' 
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                  >
                                    <path 
                                      strokeLinecap="round" 
                                      strokeLinejoin="round" 
                                      strokeWidth={2} 
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Email - Suspicious */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                                <button
                                  onClick={() => toggleAccordion('email-risky-suspicious')}
                                  className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                                >
                                  <div className="flex items-center gap-2 flex-1">
                                    <img src="/email-intelligence-icons/suspicious.png" alt="Suspicious" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                    <div className="flex-1">
                                      <h6 className="text-base font-semibold text-[#ffffff]">Email - Suspicious</h6>
                                      <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                        Flags unverified email addresses due to technical issues.
                                      </p>
                                    </div>
                                  </div>
                                  <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['email-risky-suspicious'] ? 'mr-1' : ''}`}>
                                    {openAccordions['email-risky-suspicious'] ? '−' : '+'}
                                  </span>
                                </button>
                                <div className={`overflow-hidden transition-all duration-300 ${openAccordions['email-risky-suspicious'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                                  <div className="px-3 pb-3">
                                    <div className="mt-2">
                                      <div className="relative min-h-[80px]">
                                        <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20">
                                          <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                            {JSON.stringify({"email_suspicious": false}, null, 2)}
                                          </pre>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/email-intelligence-icons/suspicious.png" alt="Suspicious" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div 
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('email-risky-suspicious')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'email-risky-suspicious' 
                                        ? 'opacity-100 translate-x-0 scale-100' 
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg 
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'email-risky-suspicious' 
                                          ? 'scale-110 rotate-[-5deg]' 
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none" 
                                      stroke="currentColor" 
                                      viewBox="0 0 24 24"
                                    >
                                      <path 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round" 
                                        strokeWidth={2} 
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Email - Suspicious</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Flags unverified email addresses due to technical issues.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/email-intelligence-icons/suspicious.png" alt="Suspicious" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Email - Suspicious</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Flags unverified email addresses due to technical issues.
                                    </p>
                                  </div>
                                </div>
                                <div 
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('email-risky-suspicious')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'email-risky-suspicious' 
                                      ? 'opacity-100 translate-x-0 scale-100' 
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg 
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'email-risky-suspicious' 
                                        ? 'scale-110 rotate-[-5deg]' 
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                  >
                                    <path 
                                      strokeLinecap="round" 
                                      strokeLinejoin="round" 
                                      strokeWidth={2} 
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Email - Temporary */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                                <button
                                  onClick={() => toggleAccordion('email-risky-temporary')}
                                  className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                                >
                                  <div className="flex items-center gap-2 flex-1">
                                    <img src="/email-intelligence-icons/temporary.png" alt="Temporary" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                    <div className="flex-1">
                                      <h6 className="text-base font-semibold text-[#ffffff]">Email - Temporary</h6>
                                      <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                        Detects disposable or temporary email addresses.
                                      </p>
                                    </div>
                                  </div>
                                  <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['email-risky-temporary'] ? 'mr-1' : ''}`}>
                                    {openAccordions['email-risky-temporary'] ? '−' : '+'}
                                  </span>
                                </button>
                                <div className={`overflow-hidden transition-all duration-300 ${openAccordions['email-risky-temporary'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                                  <div className="px-3 pb-3">
                                    <div className="mt-2">
                                      <div className="relative min-h-[80px]">
                                        <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20">
                                          <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                            {JSON.stringify({"email_temporary": false}, null, 2)}
                                          </pre>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/email-intelligence-icons/temporary.png" alt="Temporary" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div 
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('email-risky-temporary')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'email-risky-temporary' 
                                        ? 'opacity-100 translate-x-0 scale-100' 
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg 
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'email-risky-temporary' 
                                          ? 'scale-110 rotate-[-5deg]' 
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none" 
                                      stroke="currentColor" 
                                      viewBox="0 0 24 24"
                                    >
                                      <path 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round" 
                                        strokeWidth={2} 
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Email - Temporary</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Detects disposable or temporary email addresses.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/email-intelligence-icons/temporary.png" alt="Temporary" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Email - Temporary</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Detects disposable or temporary email addresses.
                                    </p>
                                  </div>
                                </div>
                                <div 
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('email-risky-temporary')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'email-risky-temporary' 
                                      ? 'opacity-100 translate-x-0 scale-100' 
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg 
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'email-risky-temporary' 
                                        ? 'scale-110 rotate-[-5deg]' 
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                  >
                                    <path 
                                      strokeLinecap="round" 
                                      strokeLinejoin="round" 
                                      strokeWidth={2} 
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Email Syntax Risk Score (ESRS) Accordion */}
                    <div className="border border-[#26ab4d]/30 rounded-lg overflow-hidden bg-black/40 mt-3">
                      <button
                        onClick={() => toggleAccordion('email-esrs')}
                        className="w-full relative p-3 sm:p-4 text-left hover:bg-[#26ab4d]/10 transition-colors"
                      >
                        <div className="pr-8 sm:pr-10">
                          <h5 className="text-lg font-bold text-[#26ab4d]">Email Syntax Risk Score (ESRS)</h5>
                          <p className="text-[#ffffff] text-sm mt-1 leading-relaxed">
                            Analyzes email format, username patterns, and detects suspicious naming conventions.
                          </p>
                        </div>
                        <span className={`absolute top-3 right-3 sm:top-4 sm:right-4 text-[#26ab4d] text-2xl font-bold transition-all duration-300 ${openAccordions['email-esrs'] ? '' : ''}`}>
                          {openAccordions['email-esrs'] ? '−' : '+'}
                        </span>
                      </button>
                      <div className={`overflow-hidden transition-all duration-300 ${openAccordions['email-esrs'] ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                        <div className="p-4 pt-0 space-y-2">
                          {/* Email - Format Is Bad */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                                <button
                                  onClick={() => toggleAccordion('email-esrs-format')}
                                  className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                                >
                                  <div className="flex items-center gap-2 flex-1">
                                    <img src="/email-intelligence-icons/format%20is%20bad.png" alt="Format Is Bad" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                    <div className="flex-1">
                                      <h6 className="text-base font-semibold text-[#ffffff]">Email - Format Is Bad</h6>
                                      <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                        Checks for confusing or misleading email address formats.
                                      </p>
                                    </div>
                                  </div>
                                  <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['email-esrs-format'] ? 'mr-1' : ''}`}>
                                    {openAccordions['email-esrs-format'] ? '−' : '+'}
                                  </span>
                                </button>
                                <div className={`overflow-hidden transition-all duration-300 ${openAccordions['email-esrs-format'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                                  <div className="px-3 pb-3">
                                    <div className="mt-2">
                                      <div className="relative min-h-[80px]">
                                        <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20">
                                          <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                            {JSON.stringify({"email_format": false}, null, 2)}
                                          </pre>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/email-intelligence-icons/format%20is%20bad.png" alt="Format Is Bad" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div 
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('email-esrs-format')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'email-esrs-format' 
                                        ? 'opacity-100 translate-x-0 scale-100' 
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg 
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'email-esrs-format' 
                                          ? 'scale-110 rotate-[-5deg]' 
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none" 
                                      stroke="currentColor" 
                                      viewBox="0 0 24 24"
                                    >
                                      <path 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round" 
                                        strokeWidth={2} 
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Email - Format Is Bad</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Checks for confusing or misleading email address formats.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/email-intelligence-icons/format%20is%20bad.png" alt="Format Is Bad" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Email - Format Is Bad</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Checks for confusing or misleading email address formats.
                                    </p>
                                  </div>
                                </div>
                                <div 
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('email-esrs-format')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'email-esrs-format' 
                                      ? 'opacity-100 translate-x-0 scale-100' 
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg 
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'email-esrs-format' 
                                        ? 'scale-110 rotate-[-5deg]' 
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                  >
                                    <path 
                                      strokeLinecap="round" 
                                      strokeLinejoin="round" 
                                      strokeWidth={2} 
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Email - Name Risk */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                                <button
                                  onClick={() => toggleAccordion('email-esrs-name-risk')}
                                  className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                                >
                                  <div className="flex items-center gap-2 flex-1">
                                    <img src="/email-intelligence-icons/name%20risk.png" alt="Name Risk" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                    <div className="flex-1">
                                      <h6 className="text-base font-semibold text-[#ffffff]">Email - Name Risk</h6>
                                      <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                        Detects suspicious patterns or terms in the email's username (before "@").
                                      </p>
                                    </div>
                                  </div>
                                  <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['email-esrs-name-risk'] ? 'mr-1' : ''}`}>
                                    {openAccordions['email-esrs-name-risk'] ? '−' : '+'}
                                  </span>
                                </button>
                                <div className={`overflow-hidden transition-all duration-300 ${openAccordions['email-esrs-name-risk'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                                  <div className="px-3 pb-3">
                                    <div className="mt-2">
                                      <div className="relative min-h-[80px]">
                                        <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20">
                                          <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                            {JSON.stringify({"email_name_risk_type": "N/A"}, null, 2)}
                                          </pre>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/email-intelligence-icons/name%20risk.png" alt="Name Risk" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div 
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('email-esrs-name-risk')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'email-esrs-name-risk' 
                                        ? 'opacity-100 translate-x-0 scale-100' 
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg 
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'email-esrs-name-risk' 
                                          ? 'scale-110 rotate-[-5deg]' 
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none" 
                                      stroke="currentColor" 
                                      viewBox="0 0 24 24"
                                    >
                                      <path 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round" 
                                        strokeWidth={2} 
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Email - Name Risk</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Detects suspicious patterns or terms in the email's username (before "@").
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/email-intelligence-icons/name%20risk.png" alt="Name Risk" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Email - Name Risk</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Detects suspicious patterns or terms in the email's username (before "@").
                                    </p>
                                  </div>
                                </div>
                                <div 
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('email-esrs-name-risk')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'email-esrs-name-risk' 
                                      ? 'opacity-100 translate-x-0 scale-100' 
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg 
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'email-esrs-name-risk' 
                                        ? 'scale-110 rotate-[-5deg]' 
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                  >
                                    <path 
                                      strokeLinecap="round" 
                                      strokeLinejoin="round" 
                                      strokeWidth={2} 
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Email - Contains Stop Words */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                                <button
                                  onClick={() => toggleAccordion('email-esrs-stop-words')}
                                  className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                                >
                                  <div className="flex items-center gap-2 flex-1">
                                    <img src="/email-intelligence-icons/contains%20stop%20words.png" alt="Contains Stop Words" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                    <div className="flex-1">
                                      <h6 className="text-base font-semibold text-[#ffffff]">Email - Contains Stop Words</h6>
                                      <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                        Checks for terms like "noreply" or "nomail" in the username.
                                      </p>
                                    </div>
                                  </div>
                                  <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['email-esrs-stop-words'] ? 'mr-1' : ''}`}>
                                    {openAccordions['email-esrs-stop-words'] ? '−' : '+'}
                                  </span>
                                </button>
                                <div className={`overflow-hidden transition-all duration-300 ${openAccordions['email-esrs-stop-words'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                                  <div className="px-3 pb-3">
                                    <div className="mt-2">
                                      <div className="relative min-h-[80px]">
                                        <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20">
                                          <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                            {JSON.stringify({"email_contains_stop_words": true}, null, 2)}
                                          </pre>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/email-intelligence-icons/contains%20stop%20words.png" alt="Contains Stop Words" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div 
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('email-esrs-stop-words')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'email-esrs-stop-words' 
                                        ? 'opacity-100 translate-x-0 scale-100' 
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg 
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'email-esrs-stop-words' 
                                          ? 'scale-110 rotate-[-5deg]' 
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none" 
                                      stroke="currentColor" 
                                      viewBox="0 0 24 24"
                                    >
                                      <path 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round" 
                                        strokeWidth={2} 
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Email - Contains Stop Words</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Checks for terms like "noreply" or "nomail" in the username.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/email-intelligence-icons/contains%20stop%20words.png" alt="Contains Stop Words" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Email - Contains Stop Words</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Checks for terms like "noreply" or "nomail" in the username.
                                    </p>
                                  </div>
                                </div>
                                <div 
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('email-esrs-stop-words')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'email-esrs-stop-words' 
                                      ? 'opacity-100 translate-x-0 scale-100' 
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg 
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'email-esrs-stop-words' 
                                        ? 'scale-110 rotate-[-5deg]' 
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                  >
                                    <path 
                                      strokeLinecap="round" 
                                      strokeLinejoin="round" 
                                      strokeWidth={2} 
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Email String Score Accordion */}
                    <div className="border border-[#26ab4d]/30 rounded-lg overflow-hidden bg-black/40 mt-3">
                      <button
                        onClick={() => toggleAccordion('email-string')}
                        className="w-full relative p-3 sm:p-4 text-left hover:bg-[#26ab4d]/10 transition-colors"
                      >
                        <div className="pr-8 sm:pr-10">
                          <h5 className="text-lg font-bold text-[#26ab4d]">Email String Score</h5>
                          <p className="text-[#ffffff] text-sm mt-1 leading-relaxed">
                            Analyzes the structural characteristics of the email username including length, digit count, and character composition.
                          </p>
                        </div>
                        <span className={`absolute top-3 right-3 sm:top-4 sm:right-4 text-[#26ab4d] text-2xl font-bold transition-all duration-300 ${openAccordions['email-string'] ? '' : ''}`}>
                          {openAccordions['email-string'] ? '−' : '+'}
                        </span>
                      </button>
                      <div className={`overflow-hidden transition-all duration-300 ${openAccordions['email-string'] ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                        <div className="p-4 pt-0 space-y-2">
                          {/* Email - Account Length */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                                <button
                                  onClick={() => toggleAccordion('email-string-account-length')}
                                  className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                                >
                                  <div className="flex items-center gap-2 flex-1">
                                    <img src="/email-intelligence-icons/account%20length.png" alt="Account Length" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                    <div className="flex-1">
                                      <h6 className="text-base font-semibold text-[#ffffff]">Email - Account Length</h6>
                                      <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                        Counts the username length.
                                      </p>
                                    </div>
                                  </div>
                                  <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['email-string-account-length'] ? 'mr-1' : ''}`}>
                                    {openAccordions['email-string-account-length'] ? '−' : '+'}
                                  </span>
                                </button>
                                <div className={`overflow-hidden transition-all duration-300 ${openAccordions['email-string-account-length'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                                  <div className="px-3 pb-3">
                                    <div className="mt-2">
                                      <div className="relative min-h-[80px]">
                                        <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20">
                                          <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                            {JSON.stringify({"email_account_length": 10}, null, 2)}
                                          </pre>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/email-intelligence-icons/account%20length.png" alt="Account Length" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div 
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('email-string-account-length')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'email-string-account-length' 
                                        ? 'opacity-100 translate-x-0 scale-100' 
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg 
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'email-string-account-length' 
                                          ? 'scale-110 rotate-[-5deg]' 
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none" 
                                      stroke="currentColor" 
                                      viewBox="0 0 24 24"
                                    >
                                      <path 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round" 
                                        strokeWidth={2} 
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Email - Account Length</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Counts the username length.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/email-intelligence-icons/account%20length.png" alt="Account Length" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Email - Account Length</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Counts the username length.
                                    </p>
                                  </div>
                                </div>
                                <div 
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('email-string-account-length')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'email-string-account-length' 
                                      ? 'opacity-100 translate-x-0 scale-100' 
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg 
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'email-string-account-length' 
                                        ? 'scale-110 rotate-[-5deg]' 
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                  >
                                    <path 
                                      strokeLinecap="round" 
                                      strokeLinejoin="round" 
                                      strokeWidth={2} 
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Email - Digit Count */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                                <button
                                  onClick={() => toggleAccordion('email-string-digit-count')}
                                  className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                                >
                                  <div className="flex items-center gap-2 flex-1">
                                    <img src="/email-intelligence-icons/digit%20count.png" alt="Digit Count" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                    <div className="flex-1">
                                      <h6 className="text-base font-semibold text-[#ffffff]">Email - Digit Count</h6>
                                      <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                        Counts digits in the email address.
                                      </p>
                                    </div>
                                  </div>
                                  <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['email-string-digit-count'] ? 'mr-1' : ''}`}>
                                    {openAccordions['email-string-digit-count'] ? '−' : '+'}
                                  </span>
                                </button>
                                <div className={`overflow-hidden transition-all duration-300 ${openAccordions['email-string-digit-count'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                                  <div className="px-3 pb-3">
                                    <div className="mt-2">
                                      <div className="relative min-h-[80px]">
                                        <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20">
                                          <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                            {JSON.stringify({"email_digit_count": 4}, null, 2)}
                                          </pre>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/email-intelligence-icons/digit%20count.png" alt="Digit Count" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div 
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('email-string-digit-count')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'email-string-digit-count' 
                                        ? 'opacity-100 translate-x-0 scale-100' 
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg 
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'email-string-digit-count' 
                                          ? 'scale-110 rotate-[-5deg]' 
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none" 
                                      stroke="currentColor" 
                                      viewBox="0 0 24 24"
                                    >
                                      <path 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round" 
                                        strokeWidth={2} 
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Email - Digit Count</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Counts digits in the email address.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/email-intelligence-icons/digit%20count.png" alt="Digit Count" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Email - Digit Count</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Counts digits in the email address.
                                    </p>
                                  </div>
                                </div>
                                <div 
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('email-string-digit-count')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'email-string-digit-count' 
                                      ? 'opacity-100 translate-x-0 scale-100' 
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg 
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'email-string-digit-count' 
                                        ? 'scale-110 rotate-[-5deg]' 
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                  >
                                    <path 
                                      strokeLinecap="round" 
                                      strokeLinejoin="round" 
                                      strokeWidth={2} 
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Email-String Length */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                                <button
                                  onClick={() => toggleAccordion('email-string-string-length')}
                                  className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                                >
                                  <div className="flex items-center gap-2 flex-1">
                                    <img src="/email-intelligence-icons/string%20length.png" alt="String Length" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                    <div className="flex-1">
                                      <h6 className="text-base font-semibold text-[#ffffff]">Email-String Length</h6>
                                      <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                        Difference between account length and digit count.
                                      </p>
                                    </div>
                                  </div>
                                  <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['email-string-string-length'] ? 'mr-1' : ''}`}>
                                    {openAccordions['email-string-string-length'] ? '−' : '+'}
                                  </span>
                                </button>
                                <div className={`overflow-hidden transition-all duration-300 ${openAccordions['email-string-string-length'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                                  <div className="px-3 pb-3">
                                    <div className="mt-2">
                                      <div className="relative min-h-[80px]">
                                        <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20">
                                          <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                            {JSON.stringify({"email_string_length": 6}, null, 2)}
                                          </pre>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/email-intelligence-icons/string%20length.png" alt="String Length" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div 
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('email-string-string-length')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'email-string-string-length' 
                                        ? 'opacity-100 translate-x-0 scale-100' 
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg 
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'email-string-string-length' 
                                          ? 'scale-110 rotate-[-5deg]' 
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none" 
                                      stroke="currentColor" 
                                      viewBox="0 0 24 24"
                                    >
                                      <path 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round" 
                                        strokeWidth={2} 
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Email-String Length</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Difference between account length and digit count.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/email-intelligence-icons/string%20length.png" alt="String Length" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Email-String Length</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Difference between account length and digit count.
                                    </p>
                                  </div>
                                </div>
                                <div 
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('email-string-string-length')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'email-string-string-length' 
                                      ? 'opacity-100 translate-x-0 scale-100' 
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg 
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'email-string-string-length' 
                                        ? 'scale-110 rotate-[-5deg]' 
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                  >
                                    <path 
                                      strokeLinecap="round" 
                                      strokeLinejoin="round" 
                                      strokeWidth={2} 
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Email - Vowels Count */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                                <button
                                  onClick={() => toggleAccordion('email-string-vowels-count')}
                                  className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                                >
                                  <div className="flex items-center gap-2 flex-1">
                                    <img src="/email-intelligence-icons/vowels%20count.png" alt="Vowels Count" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                    <div className="flex-1">
                                      <h6 className="text-base font-semibold text-[#ffffff]">Email - Vowels Count</h6>
                                      <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                        Counts vowels in the username.
                                      </p>
                                    </div>
                                  </div>
                                  <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['email-string-vowels-count'] ? 'mr-1' : ''}`}>
                                    {openAccordions['email-string-vowels-count'] ? '−' : '+'}
                                  </span>
                                </button>
                                <div className={`overflow-hidden transition-all duration-300 ${openAccordions['email-string-vowels-count'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                                  <div className="px-3 pb-3">
                                    <div className="mt-2">
                                      <div className="relative min-h-[80px]">
                                        <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20">
                                          <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                            {JSON.stringify({"email_vowels_counts": 2}, null, 2)}
                                          </pre>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/email-intelligence-icons/vowels%20count.png" alt="Vowels Count" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div 
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('email-string-vowels-count')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'email-string-vowels-count' 
                                        ? 'opacity-100 translate-x-0 scale-100' 
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg 
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'email-string-vowels-count' 
                                          ? 'scale-110 rotate-[-5deg]' 
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none" 
                                      stroke="currentColor" 
                                      viewBox="0 0 24 24"
                                    >
                                      <path 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round" 
                                        strokeWidth={2} 
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Email - Vowels Count</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Counts vowels in the username.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/email-intelligence-icons/vowels%20count.png" alt="Vowels Count" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Email - Vowels Count</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Counts vowels in the username.
                                    </p>
                                  </div>
                                </div>
                                <div 
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('email-string-vowels-count')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'email-string-vowels-count' 
                                      ? 'opacity-100 translate-x-0 scale-100' 
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg 
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'email-string-vowels-count' 
                                        ? 'scale-110 rotate-[-5deg]' 
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                  >
                                    <path 
                                      strokeLinecap="round" 
                                      strokeLinejoin="round" 
                                      strokeWidth={2} 
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Email - Consonants Count */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                                <button
                                  onClick={() => toggleAccordion('email-string-consonants-count')}
                                  className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                                >
                                  <div className="flex items-center gap-2 flex-1">
                                    <img src="/email-intelligence-icons/consonants%20count.png" alt="Consonants Count" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                    <div className="flex-1">
                                      <h6 className="text-base font-semibold text-[#ffffff]">Email - Consonants Count</h6>
                                      <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                        Counts consonants in the username.
                                      </p>
                                    </div>
                                  </div>
                                  <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['email-string-consonants-count'] ? 'mr-1' : ''}`}>
                                    {openAccordions['email-string-consonants-count'] ? '−' : '+'}
                                  </span>
                                </button>
                                <div className={`overflow-hidden transition-all duration-300 ${openAccordions['email-string-consonants-count'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                                  <div className="px-3 pb-3">
                                    <div className="mt-2">
                                      <div className="relative min-h-[80px]">
                                        <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20">
                                          <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                            {JSON.stringify({"email_consonants_counts": 4}, null, 2)}
                                          </pre>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/email-intelligence-icons/consonants%20count.png" alt="Consonants Count" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div 
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('email-string-consonants-count')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'email-string-consonants-count' 
                                        ? 'opacity-100 translate-x-0 scale-100' 
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg 
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'email-string-consonants-count' 
                                          ? 'scale-110 rotate-[-5deg]' 
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none" 
                                      stroke="currentColor" 
                                      viewBox="0 0 24 24"
                                    >
                                      <path 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round" 
                                        strokeWidth={2} 
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Email - Consonants Count</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Counts consonants in the username.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/email-intelligence-icons/consonants%20count.png" alt="Consonants Count" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Email - Consonants Count</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Counts consonants in the username.
                                    </p>
                                  </div>
                                </div>
                                <div 
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('email-string-consonants-count')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'email-string-consonants-count' 
                                      ? 'opacity-100 translate-x-0 scale-100' 
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg 
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'email-string-consonants-count' 
                                        ? 'scale-110 rotate-[-5deg]' 
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                  >
                                    <path 
                                      strokeLinecap="round" 
                                      strokeLinejoin="round" 
                                      strokeWidth={2} 
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Email Quality Score Accordion */}
                    <div className="border border-[#26ab4d]/30 rounded-lg overflow-hidden bg-black/40 mt-3">
                      <button
                        onClick={() => toggleAccordion('email-quality')}
                        className="w-full relative p-3 sm:p-4 text-left hover:bg-[#26ab4d]/10 transition-colors"
                      >
                        <div className="pr-8 sm:pr-10">
                          <h5 className="text-lg font-bold text-[#26ab4d]">Email Quality Score</h5>
                          <p className="text-[#ffffff] text-sm mt-1 leading-relaxed">
                            Assesses email authenticity, activity patterns, and association with spam traps or abuse reports.
                          </p>
                        </div>
                        <span className={`absolute top-3 right-3 sm:top-4 sm:right-4 text-[#26ab4d] text-2xl font-bold transition-all duration-300 ${openAccordions['email-quality'] ? '' : ''}`}>
                          {openAccordions['email-quality'] ? '−' : '+'}
                        </span>
                      </button>
                      <div className={`overflow-hidden transition-all duration-300 ${openAccordions['email-quality'] ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                        <div className="p-4 pt-0 space-y-2">
                          {/* Email - Spam Trap Score */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                                <button
                                  onClick={() => toggleAccordion('email-quality-spam-trap')}
                                  className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                                >
                                  <div className="flex items-center gap-2 flex-1">
                                    <img src="/email-intelligence-icons/spam%20trap%20score.png" alt="Spam Trap" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                    <div className="flex-1">
                                      <h6 className="text-base font-semibold text-[#ffffff]">Email - Spam Trap Score</h6>
                                      <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                        Indicates if the email is associated with or triggered a spam trap.
                                      </p>
                                    </div>
                                  </div>
                                  <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['email-quality-spam-trap'] ? 'mr-1' : ''}`}>
                                    {openAccordions['email-quality-spam-trap'] ? '−' : '+'}
                                  </span>
                                </button>
                                <div className={`overflow-hidden transition-all duration-300 ${openAccordions['email-quality-spam-trap'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                                  <div className="px-3 pb-3">
                                    <div className="mt-2">
                                      <div className="relative min-h-[80px]">
                                        <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20">
                                          <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                            {JSON.stringify({"email_spam_trap_score": "none"}, null, 2)}
                                          </pre>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/email-intelligence-icons/spam%20trap%20score.png" alt="Spam Trap" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div 
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('email-quality-spam-trap')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'email-quality-spam-trap' 
                                        ? 'opacity-100 translate-x-0 scale-100' 
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg 
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'email-quality-spam-trap' 
                                          ? 'scale-110 rotate-[-5deg]' 
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none" 
                                      stroke="currentColor" 
                                      viewBox="0 0 24 24"
                                    >
                                      <path 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round" 
                                        strokeWidth={2} 
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Email - Spam Trap Score</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Indicates if the email is associated with or triggered a spam trap.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/email-intelligence-icons/spam%20trap%20score.png" alt="Spam Trap" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Email - Spam Trap Score</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Indicates if the email is associated with or triggered a spam trap.
                                    </p>
                                  </div>
                                </div>
                                <div 
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('email-quality-spam-trap')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'email-quality-spam-trap' 
                                      ? 'opacity-100 translate-x-0 scale-100' 
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg 
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'email-quality-spam-trap' 
                                        ? 'scale-110 rotate-[-5deg]' 
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                  >
                                    <path 
                                      strokeLinecap="round" 
                                      strokeLinejoin="round" 
                                      strokeWidth={2} 
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Email - Recent Abuse Activity */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                                <button
                                  onClick={() => toggleAccordion('email-quality-recent-abuse')}
                                  className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                                >
                                  <div className="flex items-center gap-2 flex-1">
                                    <img src="/email-intelligence-icons/RAA.png" alt="Recent Abuse" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                    <div className="flex-1">
                                      <h6 className="text-base font-semibold text-[#ffffff]">Email - Recent Abuse Activity</h6>
                                      <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                        Flags if the email was recently used for fraud (e.g., phishing, fake accounts).
                                      </p>
                                    </div>
                                  </div>
                                  <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['email-quality-recent-abuse'] ? 'mr-1' : ''}`}>
                                    {openAccordions['email-quality-recent-abuse'] ? '−' : '+'}
                                  </span>
                                </button>
                                <div className={`overflow-hidden transition-all duration-300 ${openAccordions['email-quality-recent-abuse'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                                  <div className="px-3 pb-3">
                                    <div className="mt-2">
                                      <div className="relative min-h-[80px]">
                                        <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20">
                                          <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                            {JSON.stringify({"email_recent_abuse_activity": false}, null, 2)}
                                          </pre>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/email-intelligence-icons/RAA.png" alt="Recent Abuse" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div 
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('email-quality-recent-abuse')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'email-quality-recent-abuse' 
                                        ? 'opacity-100 translate-x-0 scale-100' 
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg 
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'email-quality-recent-abuse' 
                                          ? 'scale-110 rotate-[-5deg]' 
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none" 
                                      stroke="currentColor" 
                                      viewBox="0 0 24 24"
                                    >
                                      <path 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round" 
                                        strokeWidth={2} 
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Email - Recent Abuse Activity</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Flags if the email was recently used for fraud (e.g., phishing, fake accounts).
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/email-intelligence-icons/RAA.png" alt="Recent Abuse" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Email - Recent Abuse Activity</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Flags if the email was recently used for fraud (e.g., phishing, fake accounts).
                                    </p>
                                  </div>
                                </div>
                                <div 
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('email-quality-recent-abuse')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'email-quality-recent-abuse' 
                                      ? 'opacity-100 translate-x-0 scale-100' 
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg 
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'email-quality-recent-abuse' 
                                        ? 'scale-110 rotate-[-5deg]' 
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                  >
                                    <path 
                                      strokeLinecap="round" 
                                      strokeLinejoin="round" 
                                      strokeWidth={2} 
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Email - Activity */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                                <button
                                  onClick={() => toggleAccordion('email-quality-activity')}
                                  className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                                >
                                  <div className="flex items-center gap-2 flex-1">
                                    <img src="/email-intelligence-icons/activity.png" alt="Activity" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                    <div className="flex-1">
                                      <h6 className="text-base font-semibold text-[#ffffff]">Email - Activity</h6>
                                      <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                        Measures if the email is actively used online for legitimate behavior.
                                      </p>
                                    </div>
                                  </div>
                                  <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['email-quality-activity'] ? 'mr-1' : ''}`}>
                                    {openAccordions['email-quality-activity'] ? '−' : '+'}
                                  </span>
                                </button>
                                <div className={`overflow-hidden transition-all duration-300 ${openAccordions['email-quality-activity'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                                  <div className="px-3 pb-3">
                                    <div className="mt-2">
                                      <div className="relative min-h-[80px]">
                                        <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20">
                                          <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                            {JSON.stringify({"email_activity": true}, null, 2)}
                                          </pre>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/email-intelligence-icons/activity.png" alt="Activity" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div 
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('email-quality-activity')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'email-quality-activity' 
                                        ? 'opacity-100 translate-x-0 scale-100' 
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg 
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'email-quality-activity' 
                                          ? 'scale-110 rotate-[-5deg]' 
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none" 
                                      stroke="currentColor" 
                                      viewBox="0 0 24 24"
                                    >
                                      <path 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round" 
                                        strokeWidth={2} 
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Email - Activity</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Measures if the email is actively used online for legitimate behavior.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/email-intelligence-icons/activity.png" alt="Activity" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Email - Activity</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Measures if the email is actively used online for legitimate behavior.
                                    </p>
                                  </div>
                                </div>
                                <div 
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('email-quality-activity')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'email-quality-activity' 
                                      ? 'opacity-100 translate-x-0 scale-100' 
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg 
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'email-quality-activity' 
                                        ? 'scale-110 rotate-[-5deg]' 
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                  >
                                    <path 
                                      strokeLinecap="round" 
                                      strokeLinejoin="round" 
                                      strokeWidth={2} 
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Email - Frequent Complaint History */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                                <button
                                  onClick={() => toggleAccordion('email-quality-complaint-history')}
                                  className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                                >
                                  <div className="flex items-center gap-2 flex-1">
                                    <img src="/email-intelligence-icons/frequent%20complaint%20history.png" alt="Complaint History" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                    <div className="flex-1">
                                      <h6 className="text-base font-semibold text-[#ffffff]">Email - Frequent Complaint History</h6>
                                      <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                        Shows if the email has a history of marking marketing emails as spam or unsubscribing.
                                      </p>
                                    </div>
                                  </div>
                                  <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['email-quality-complaint-history'] ? 'mr-1' : ''}`}>
                                    {openAccordions['email-quality-complaint-history'] ? '−' : '+'}
                                  </span>
                                </button>
                                <div className={`overflow-hidden transition-all duration-300 ${openAccordions['email-quality-complaint-history'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                                  <div className="px-3 pb-3">
                                    <div className="mt-2">
                                      <div className="relative min-h-[80px]">
                                        <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20">
                                          <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                            {JSON.stringify({"email_frequent_complaint_history": false}, null, 2)}
                                          </pre>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/email-intelligence-icons/frequent%20complaint%20history.png" alt="Complaint History" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div 
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('email-quality-complaint-history')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'email-quality-complaint-history' 
                                        ? 'opacity-100 translate-x-0 scale-100' 
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg 
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'email-quality-complaint-history' 
                                          ? 'scale-110 rotate-[-5deg]' 
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none" 
                                      stroke="currentColor" 
                                      viewBox="0 0 24 24"
                                    >
                                      <path 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round" 
                                        strokeWidth={2} 
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Email - Frequent Complaint History</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Shows if the email has a history of marking marketing emails as spam or unsubscribing.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/email-intelligence-icons/frequent%20complaint%20history.png" alt="Complaint History" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Email - Frequent Complaint History</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Shows if the email has a history of marking marketing emails as spam or unsubscribing.
                                    </p>
                                  </div>
                                </div>
                                <div 
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('email-quality-complaint-history')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'email-quality-complaint-history' 
                                      ? 'opacity-100 translate-x-0 scale-100' 
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg 
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'email-quality-complaint-history' 
                                        ? 'scale-110 rotate-[-5deg]' 
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                  >
                                    <path 
                                      strokeLinecap="round" 
                                      strokeLinejoin="round" 
                                      strokeWidth={2} 
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Emails - Linked to this ID */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                                <button
                                  onClick={() => toggleAccordion('email-quality-linked-emails')}
                                  className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                                >
                                  <div className="flex items-center gap-2 flex-1">
                                    <img src="/email-intelligence-icons/email%20link.png" alt="Linked Emails" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                    <div className="flex-1">
                                      <h6 className="text-base font-semibold text-[#ffffff]">Emails - Linked to this ID</h6>
                                      <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                        Shows the count of different emails linked to the current ID.
                                      </p>
                                    </div>
                                  </div>
                                  <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['email-quality-linked-emails'] ? 'mr-1' : ''}`}>
                                    {openAccordions['email-quality-linked-emails'] ? '−' : '+'}
                                  </span>
                                </button>
                                <div className={`overflow-hidden transition-all duration-300 ${openAccordions['email-quality-linked-emails'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                                  <div className="px-3 pb-3">
                                    <div className="mt-2">
                                      <div className="relative min-h-[80px]">
                                        <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20">
                                          <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                            {JSON.stringify({"emails_linked_to_this_id": 1}, null, 2)}
                                          </pre>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/email-intelligence-icons/email%20link.png" alt="Linked Emails" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div 
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('email-quality-linked-emails')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'email-quality-linked-emails' 
                                        ? 'opacity-100 translate-x-0 scale-100' 
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg 
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'email-quality-linked-emails' 
                                          ? 'scale-110 rotate-[-5deg]' 
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none" 
                                      stroke="currentColor" 
                                      viewBox="0 0 24 24"
                                    >
                                      <path 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round" 
                                        strokeWidth={2} 
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Emails - Linked to this ID</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Shows the count of different emails linked to the current ID.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/email-intelligence-icons/email%20link.png" alt="Linked Emails" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Emails - Linked to this ID</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Shows the count of different emails linked to the current ID.
                                    </p>
                                  </div>
                                </div>
                                <div 
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('email-quality-linked-emails')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'email-quality-linked-emails' 
                                      ? 'opacity-100 translate-x-0 scale-100' 
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg 
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'email-quality-linked-emails' 
                                        ? 'scale-110 rotate-[-5deg]' 
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                  >
                                    <path 
                                      strokeLinecap="round" 
                                      strokeLinejoin="round" 
                                      strokeWidth={2} 
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Emails - Linked to Phone Numbers */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                                <button
                                  onClick={() => toggleAccordion('email-quality-linked-phones')}
                                  className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                                >
                                  <div className="flex items-center gap-2 flex-1">
                                    <img src="/email-intelligence-icons/phone%20link.png" alt="Linked Phones" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                    <div className="flex-1">
                                      <h6 className="text-base font-semibold text-[#ffffff]">Emails - Linked to Phone Numbers</h6>
                                      <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                        Identifies how many phone numbers are linked to this email.
                                      </p>
                                    </div>
                                  </div>
                                  <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['email-quality-linked-phones'] ? 'mr-1' : ''}`}>
                                    {openAccordions['email-quality-linked-phones'] ? '−' : '+'}
                                  </span>
                                </button>
                                <div className={`overflow-hidden transition-all duration-300 ${openAccordions['email-quality-linked-phones'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                                  <div className="px-3 pb-3">
                                    <div className="mt-2">
                                      <div className="relative min-h-[80px]">
                                        <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20">
                                          <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                            {JSON.stringify({"emails_linked_to_phone_nos": ["8877666677", "7766990000"]}, null, 2)}
                                          </pre>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/email-intelligence-icons/phone%20link.png" alt="Linked Phones" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div 
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('email-quality-linked-phones')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'email-quality-linked-phones' 
                                        ? 'opacity-100 translate-x-0 scale-100' 
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg 
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'email-quality-linked-phones' 
                                          ? 'scale-110 rotate-[-5deg]' 
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none" 
                                      stroke="currentColor" 
                                      viewBox="0 0 24 24"
                                    >
                                      <path 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round" 
                                        strokeWidth={2} 
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Emails - Linked to Phone Numbers</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Identifies how many phone numbers are linked to this email.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/email-intelligence-icons/phone%20link.png" alt="Linked Phones" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Emails - Linked to Phone Numbers</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Identifies how many phone numbers are linked to this email.
                                    </p>
                                  </div>
                                </div>
                                <div 
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('email-quality-linked-phones')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'email-quality-linked-phones' 
                                      ? 'opacity-100 translate-x-0 scale-100' 
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg 
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'email-quality-linked-phones' 
                                        ? 'scale-110 rotate-[-5deg]' 
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                  >
                                    <path 
                                      strokeLinecap="round" 
                                      strokeLinejoin="round" 
                                      strokeWidth={2} 
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            </div>
          </section>

          {/* Email Intelligence Tool Section */}
          <section className="pt-[0.5rem] pb-[4rem] relative z-20 bg-transparent overflow-hidden">
            <div className="container mx-auto px-[1.5rem] relative z-10">
              <div className="w-full">
                {/* Unified Email Intelligence Tool */}
                <div className="bg-black/20 rounded-lg p-6 sm:p-8 border border-[#26ab4d]/30">
                  <div>
                      <h4 className="text-3xl sm:text-4xl font-bold mb-6">
                      <span className="text-[#ffffff]">Email Intelligence </span>
                      <span className="bg-gradient-to-r from-[#26ab4d] via-[#1e8a3f] to-[#26ab4d] bg-clip-text text-transparent">Tool</span>
                    </h4>
                    
                    {/* Input Section */}
                    <div className="mb-6">
                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                      <div className="flex-1">
              <input
                type="email"
                value={emailAddress}
                onChange={(e) => setEmailAddress(e.target.value)}
                placeholder={`"example@email.com"`}
                className={`w-full px-4 sm:px-6 py-3 sm:py-4 bg-[#26ab4d]/10 border-2 rounded-full text-[#ffffff] placeholder-[#9a9a9a] placeholder:font-bold focus:outline-none focus:ring-2 text-[1.125rem] ${
                            emailAddress && !isValidEmail(emailAddress)
                              ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                              : 'border-[#26ab4d]/50 focus:ring-[#26ab4d] focus:border-[#26ab4d]'
                          }`}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleAnalyzeEmailAll();
                            }
                          }}
                        />
                </div>
                      <button
                        onClick={handleAnalyzeEmailAll}
                        disabled={isAnalyzingEmail || !emailAddress.trim()}
                        className="px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-[#26ab4d] to-[#1e8a3f] hover:from-[#1e8a3f] hover:to-[#26ab4d] text-[#ffffff] rounded-full font-semibold transition-all duration-300 shadow-lg shadow-[#26ab4d]/25 disabled:opacity-50 disabled:cursor-not-allowed min-w-[140px] text-[1.125rem] whitespace-nowrap"
                      >
                        {isAnalyzingEmail ? (
                          <span className="flex items-center justify-center">
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-[#ffffff]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
                            Analyzing...
                    </span>
                        ) : (
                          'Submit'
                        )}
                      </button>
              </div>
            </div>
            
                  {/* Tabbed Results Section */}
                  {(emailReputationResult || emailQualityResult || emailRiskyResult || emailESRSResult || emailStringResult) && (
                    <div className="space-y-6 relative min-h-[400px]">
                      {!accessGranted && <LockOverlay compact />}
                      {/* Tabs */}
                      <div className="flex flex-wrap gap-2 border-b border-[#26ab4d]/30">
                    <button
                          onClick={() => setActiveEmailTab('reputation')}
                          className={`px-4 py-2 rounded-t-lg font-semibold transition-all ${
                            activeEmailTab === 'reputation'
                              ? 'bg-[#26ab4d] text-[#ffffff]'
                              : 'bg-black/40 text-[#ffffff] hover:text-[#ffffff]'
                          }`}
                        >
                          Email Reputation
                        </button>
                    <button
                          onClick={() => setActiveEmailTab('risky')}
                          className={`px-4 py-2 rounded-t-lg font-semibold transition-all ${
                            activeEmailTab === 'risky'
                              ? 'bg-[#26ab4d] text-[#ffffff]'
                              : 'bg-black/40 text-[#ffffff] hover:text-[#ffffff]'
                          }`}
                        >
                          Email Risky Score
                        </button>
                        <button
                          onClick={() => setActiveEmailTab('esrs')}
                          className={`px-4 py-2 rounded-t-lg font-semibold transition-all ${
                            activeEmailTab === 'esrs'
                              ? 'bg-[#26ab4d] text-[#ffffff]'
                              : 'bg-black/40 text-[#ffffff] hover:text-[#ffffff]'
                          }`}
                        >
                          Email ESRS
                        </button>
                        <button
                          onClick={() => setActiveEmailTab('string')}
                          className={`px-4 py-2 rounded-t-lg font-semibold transition-all ${
                            activeEmailTab === 'string'
                              ? 'bg-[#26ab4d] text-[#ffffff]'
                              : 'bg-black/40 text-[#ffffff] hover:text-[#ffffff]'
                          }`}
                        >
                          Email String Score
                        </button>
                        <button
                          onClick={() => setActiveEmailTab('quality')}
                          className={`px-4 py-2 rounded-t-lg font-semibold transition-all ${
                            activeEmailTab === 'quality'
                              ? 'bg-[#26ab4d] text-[#ffffff]'
                              : 'bg-black/40 text-[#ffffff] hover:text-[#ffffff]'
                          }`}
                        >
                          Email Quality Score
                        </button>
                  </div>
                
                      {/* Tab Content */}
                      <div className="bg-black/20 rounded-lg p-6 border border-[#26ab4d]/20">
                        {activeEmailTab === 'reputation' && emailReputationResult && (
                          <div>
                            <h5 className="text-xl font-bold text-[#ffffff] mb-4">Email Reputation</h5>
                            <div className="bg-black/40 rounded-lg p-4 overflow-x-auto border border-[#26ab4d]/20">
                              <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                {formatJSONWithColors(emailReputationResult.jsonResponse)}
                              </pre>
                        </div>
              </div>
                        )}

                        {activeEmailTab === 'risky' && emailRiskyResult && (
                          <div>
                            <h5 className="text-xl font-bold text-[#ffffff] mb-4">Email Risky Score</h5>
                            <div className="bg-black/40 rounded-lg p-4 overflow-x-auto border border-[#26ab4d]/20">
                              <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                {formatJSONWithColors(emailRiskyResult.jsonResponse)}
                              </pre>
            </div>
                </div>
                        )}

                        {activeEmailTab === 'esrs' && emailESRSResult && (
                          <div>
                            <h5 className="text-xl font-bold text-[#ffffff] mb-4">Email Syntax Risk Score (ESRS)</h5>
                            <div className="bg-black/40 rounded-lg p-4 overflow-x-auto border border-[#26ab4d]/20">
                              <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                {formatJSONWithColors(emailESRSResult.jsonResponse)}
                              </pre>
            </div>
                </div>
                        )}

                        {activeEmailTab === 'string' && emailStringResult && (
                          <div>
                            <h5 className="text-xl font-bold text-[#ffffff] mb-4">Email String Score</h5>
                            <div className="bg-black/40 rounded-lg p-4 overflow-x-auto border border-[#26ab4d]/20">
                              <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                {formatJSONWithColors(emailStringResult.jsonResponse)}
                              </pre>
            </div>
                </div>
                        )}

                        {activeEmailTab === 'quality' && emailQualityResult && (
                          <div>
                            <h5 className="text-xl font-bold text-[#ffffff] mb-4">Email Quality Score</h5>
                            <div className="bg-black/40 rounded-lg p-4 overflow-x-auto border border-[#26ab4d]/20">
                              <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                {formatFlattenedJSON(emailQualityResult.jsonResponse)}
                              </pre>
                </div>
              </div>
                        )}
                        {activeEmailTab === 'reputation' && !emailReputationResult && (emailQualityResult || emailRiskyResult || emailESRSResult || emailStringResult) && (
                          <div className="text-center py-8">
                            <p className="text-[#ffffff] text-lg">Email Reputation results will appear here after analysis.</p>
                          </div>
                        )}
                        {activeEmailTab === 'risky' && !emailRiskyResult && (emailReputationResult || emailQualityResult || emailESRSResult || emailStringResult) && (
                          <div className="text-center py-8">
                            <p className="text-[#ffffff] text-lg">Email Risky Score results will appear here after analysis.</p>
                          </div>
                        )}
                        {activeEmailTab === 'esrs' && !emailESRSResult && (emailReputationResult || emailQualityResult || emailRiskyResult || emailStringResult) && (
                          <div className="text-center py-8">
                            <p className="text-[#ffffff] text-lg">Email ESRS results will appear here after analysis.</p>
                          </div>
                        )}
                        {activeEmailTab === 'string' && !emailStringResult && (emailReputationResult || emailQualityResult || emailRiskyResult || emailESRSResult) && (
                          <div className="text-center py-8">
                            <p className="text-[#ffffff] text-lg">Email String Score results will appear here after analysis.</p>
                          </div>
                        )}
                        {activeEmailTab === 'quality' && !emailQualityResult && (emailReputationResult || emailRiskyResult || emailESRSResult || emailStringResult) && (
                          <div className="text-center py-8">
                            <p className="text-[#ffffff] text-lg">Email Quality Score results will appear here after analysis.</p>
                          </div>
                        )}
                      </div>

                      {/* Combined JSON Response - Flattened */}
                      <div className="bg-black/20 rounded-lg p-6 border border-[#26ab4d]/20">
                        <div className="flex items-center justify-between mb-4">
                          <h6 className="text-lg font-bold text-[#ffffff] flex items-center">
                            <svg className="w-5 h-5 text-[#ffffff] mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
              </svg>
                            Combined JSON Response
                          </h6>
                          <button
                            onClick={() => {
                              // Flatten all JSONs into one object
                              const flattenedJson = {
                                ...(emailReputationResult?.jsonResponse || {}),
                                ...(emailQualityResult?.jsonResponse || {}),
                                ...(emailRiskyResult?.jsonResponse || {}),
                                ...(emailESRSResult?.jsonResponse || {}),
                                ...(emailStringResult?.jsonResponse || {})
                              };
                              const jsonStr = JSON.stringify(flattenedJson, null, 2);
                              navigator.clipboard.writeText(jsonStr);
                              alert('Combined JSON copied to clipboard!');
                            }}
                            className="text-xs text-[#ffffff] hover:text-[#ffffff] transition-colors flex items-center"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
                            Copy Combined JSON
                          </button>
            </div>
                        <div className="bg-black/40 rounded-lg p-4 overflow-x-auto border border-[#26ab4d]/20">
                          <pre className="text-xs sm:text-sm text-[#656565] font-mono whitespace-pre">
                            {formatFlattenedJSON({
                              ...(emailReputationResult?.jsonResponse || {}),
                              ...(emailQualityResult?.jsonResponse || {}),
                              ...(emailRiskyResult?.jsonResponse || {}),
                              ...(emailESRSResult?.jsonResponse || {}),
                              ...(emailStringResult?.jsonResponse || {})
                            })}
                          </pre>
                </div>
                    </div>
                    </div>
                  )}

                    {/* Default Example - Show when no analysis */}
                  {!emailReputationResult && !emailQualityResult && !emailRiskyResult && !emailESRSResult && !emailStringResult && (
                      <div className="bg-black/20 rounded-lg p-6 border border-[#26ab4d]/20">
                      <p className="text-[#ffffff] text-center mb-4">Enter an email address above to see comprehensive email intelligence analysis</p>
                        <div className="bg-black/40 rounded-lg p-4 overflow-x-auto border border-[#26ab4d]/20 relative">
                          {!accessGranted && <LockOverlay compact />}
                          <pre className="text-xs sm:text-sm text-[#656565] font-mono whitespace-pre">
{`{
  `}<span className="text-[#656565]">"email_domain_trust"</span>{` : `}<span className="text-[#656565]">"trusted"</span>{`,
  `}<span className="text-[#656565]">"email_domain_activity"</span>{` : `}<span className="text-[#656565]">"high"</span>{`,
  `}<span className="text-[#656565]">"email_risky_extension"</span>{` : `}<span className="text-[#656565]">false</span>{`,
  `}<span className="text-[#656565]">"email_sender_validation"</span>{` : `}<span className="text-[#656565]">true</span>{`,
  `}<span className="text-[#656565]">"email_phishing"</span>{` : `}<span className="text-[#656565]">false</span>{`,
  `}<span className="text-[#656565]">"email_spam_trap_score"</span>{` : `}<span className="text-[#656565]">"low"</span>{`,
  `}<span className="text-[#656565]">"email_recent_abuse_activity"</span>{` : `}<span className="text-[#656565]">false</span>{`,
  `}<span className="text-[#656565]">"email_activity"</span>{` : `}<span className="text-[#656565]">true</span>{`,
  `}<span className="text-[#656565]">"email_frequent_complaint_history"</span>{` : `}<span className="text-[#656565]">false</span>{`,
  `}<span className="text-[#656565]">"emails_linked_to_this_id"</span>{` : `}<span className="text-[#656565]">2</span>{`,
  `}<span className="text-[#656565]">"email_be_delivered"</span>{` : `}<span className="text-[#656565]">"high"</span>{`,
  `}<span className="text-[#656565]">"email_sender_reputation_score"</span>{` : `}<span className="text-[#656565]">3</span>{`,
  `}<span className="text-[#656565]">"email_suspicious"</span>{` : `}<span className="text-[#656565]">false</span>{`,
  `}<span className="text-[#656565]">"email_temporary"</span>{` : `}<span className="text-[#656565]">false</span>{`,
  `}<span className="text-[#656565]">"email_format"</span>{` : `}<span className="text-[#656565]">false</span>{`,
  `}<span className="text-[#656565]">"email_name_risk_type"</span>{` : `}<span className="text-[#656565]">"n/a"</span>{`,
  `}<span className="text-[#656565]">"email_contains_stop_words"</span>{` : `}<span className="text-[#656565]">false</span>{`,
  `}<span className="text-[#656565]">"email_account_length"</span>{` : `}<span className="text-[#656565]">8</span>{`,
  `}<span className="text-[#656565]">"email_digit_count"</span>{` : `}<span className="text-[#656565]">2</span>{`,
  `}<span className="text-[#656565]">"email_string_length"</span>{` : `}<span className="text-[#656565]">10</span>{`,
  `}<span className="text-[#656565]">"email_vowels_counts"</span>{` : `}<span className="text-[#656565]">4</span>{`,
  `}<span className="text-[#656565]">"email_consonants_counts"</span>{` : `}<span className="text-[#656565]">6</span>{`
}`}
                          </pre>
                  </div>
                  </div>
                    )}
                  </div>
                  </div>
              </div>
            </div>
          </section>
            
          {/* CTA Section */}
          <section className="py-[4rem] relative z-20 bg-transparent overflow-hidden">
              {/* Multi-Layer Vector Background */}
              <div className="absolute inset-0 z-0">
                {/* Primary Vector Layer - Neural Network */}
                <svg className="absolute inset-0 w-full h-full opacity-15" viewBox="0 0 1400 900" fill="none">
                  <defs>
                    <linearGradient id="neuralGradient1-email" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#26ab4d" stopOpacity="0.9"/>
                      <stop offset="30%" stopColor="#1e8a3f" stopOpacity="0.7"/>
                      <stop offset="70%" stopColor="#26ab4d" stopOpacity="0.5"/>
                      <stop offset="100%" stopColor="#1e8a3f" stopOpacity="0.3"/>
                    </linearGradient>
                    <linearGradient id="neuralGradient2-email" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#1e8a3f" stopOpacity="0.8"/>
                      <stop offset="50%" stopColor="#26ab4d" stopOpacity="0.6"/>
                      <stop offset="100%" stopColor="#1e8a3f" stopOpacity="0.4"/>
                    </linearGradient>
                    <filter id="neuralGlow-email" x="-50%" y="-50%" width="200%" height="200%">
                      <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                      <feMerge> 
                        <feMergeNode in="coloredBlur"/>
                        <feMergeNode in="SourceGraphic"/>
                      </feMerge>
                    </filter>
                  </defs>
                </svg>
                
                {/* Secondary Vector Layer - DNA Helix */}
                <svg className="absolute inset-0 w-full h-full opacity-10" viewBox="0 0 1400 900" fill="none">
                  <defs>
                    <linearGradient id="dnaGradient-email" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#26ab4d" stopOpacity="0.6"/>
                      <stop offset="50%" stopColor="#1e8a3f" stopOpacity="0.4"/>
                      <stop offset="100%" stopColor="#26ab4d" stopOpacity="0.2"/>
                    </linearGradient>
                  </defs>
                </svg>
                
                {/* Tertiary Vector Layer - Quantum Patterns */}
                <svg className="absolute inset-0 w-full h-full opacity-8" viewBox="0 0 1400 900" fill="none">
                  <defs>
                    <radialGradient id="quantumGradient-email" cx="50%" cy="50%" r="50%">
                      <stop offset="0%" stopColor="#26ab4d" stopOpacity="0.8"/>
                      <stop offset="70%" stopColor="#1e8a3f" stopOpacity="0.4"/>
                      <stop offset="100%" stopColor="#26ab4d" stopOpacity="0.1"/>
                    </radialGradient>
                  </defs>
                </svg>
              </div>
            
            <div className="container mx-auto px-[1.5rem] relative z-10">
              <div className="max-w-5xl lg:max-w-fit mx-auto text-center">
                <div className="bg-gradient-to-br from-[#26ab4d]/20 to-[#1e8a3f]/10 backdrop-blur-sm border border-[#26ab4d]/30 rounded-3xl p-8 sm:p-10 lg:p-8">
                  <h3 className="text-[1.125rem] sm:text-[1.375rem] lg:text-[2rem] xl:text-[2.25rem] font-bold mb-6 lg:mb-3 break-words lg:leading-tight lg:px-1">
                    <span className="text-[#ffffff]">Ready to Transform Your </span>
                    <span className="text-[#26ab4d]">Email Intelligence</span>
                  </h3>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                    <Link 
                      to="/ask-access" 
                      className="bg-[#26ab4d] hover:bg-[#1e8a3f] text-[#ffffff] px-5 sm:px-6 lg:px-8 py-2.5 sm:py-3 lg:py-4 rounded-full text-base sm:text-lg lg:text-lg font-bold transition-all duration-300 shadow-xl inline-flex items-center justify-center w-fit sm:w-auto sm:min-w-[160px] lg:min-w-[170px] whitespace-nowrap"
                      onClick={() => {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                    >
                      Ask for access
                    </Link>
                  </div>
                    </div>
              </div>
            </div>
          </section>
        </>
      )}

      {/* IP Intelligence Section - Network Quality Sub Product */}
      {currentProductId === 'ip' && (
        <>
          {/* Breadcrumb Navigation */}
          <div className="relative z-20 bg-transparent pt-[10rem] pb-4">
            <div className="container mx-auto px-[1.5rem]">
              <nav className="flex items-center gap-2 text-[0.875rem] lg:text-[0.9375rem] xl:text-[1rem] font-bold w-fit flex-wrap">
                <Link 
                  to="/" 
                  className="text-[#ffffff] hover:text-[#26ab4d] transition-colors"
                >
                  Home
                </Link>
                <span className="text-[#26ab4d]">/</span>
                <Link 
                  to="/products" 
                  className="text-[#ffffff] hover:text-[#26ab4d] transition-colors"
                >
                  Products
                </Link>
                <span className="text-[#26ab4d]">/</span>
                <Link 
                  to="/products/network-quality" 
                  className="text-[#ffffff] hover:text-[#26ab4d] transition-colors"
                >
                  Network Quality
                </Link>
                <span className="text-[#26ab4d]">/</span>
                <span className="text-[#ffffff]">
                  IP Intelligence
                </span>
              </nav>
                </div>
                </div>

          {/* IP Intelligence Section - Consolidated */}
          <section className="pt-[2rem] pb-0 relative z-20 bg-transparent overflow-hidden">
            <div className="container mx-auto px-[1.5rem] relative z-10">
              <div className="w-full">
                {/* Features & JSON Types Section */}
                <div className="bg-black/20 rounded-lg p-4 sm:p-6 mb-8 border border-[#26ab4d]/30">
                  <h4 className="text-xl sm:text-2xl font-bold text-[#ffffff] mb-4 text-center">
                    Features & JSON Types
                  </h4>
                  
                  <div className="space-y-3">
                    {/* Risky IP Activity Accordion */}
                    <div className="border border-[#26ab4d]/30 rounded-lg overflow-hidden bg-black/40">
                      <button
                        onClick={() => toggleAccordion('ip-risky')}
                        className="w-full relative p-3 sm:p-4 text-left hover:bg-[#26ab4d]/10 transition-colors"
                      >
                        <div className="pr-8 sm:pr-10">
                          <h5 className="text-lg font-bold text-[#26ab4d]">Risky IP Activity</h5>
                          <p className="text-[#ffffff] text-sm mt-1 leading-relaxed">
                            Detects proxy, VPN, and TOR network usage to identify potentially risky IP addresses.
                          </p>
                        </div>
                        <span className={`absolute top-3 right-3 sm:top-4 sm:right-4 text-[#26ab4d] text-2xl font-bold transition-all duration-300 ${openAccordions['ip-risky'] ? '' : ''}`}>
                          {openAccordions['ip-risky'] ? '−' : '+'}
                        </span>
                      </button>
                      <div className={`overflow-hidden transition-all duration-300 ${openAccordions['ip-risky'] ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                        <div className="p-4 pt-0 space-y-2">
                          {/* IP - Proxy */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('ip-risky-proxy')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/ip-intelligence-icons/proxy.png" alt="Proxy" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">IP - Proxy</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Indicates use of a proxy server to mask the user's real IP address
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['ip-risky-proxy'] ? 'mr-1' : ''}`}>
                                {openAccordions['ip-risky-proxy'] ? '−' : '+'}
                              </span>
                            </button>
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['ip-risky-proxy'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                              <div className="mt-2">
                                      <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                    <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                      {JSON.stringify({"ip_proxy": true}, null, 2)}
                                    </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/ip-intelligence-icons/proxy.png" alt="Proxy" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('ip-risky-proxy')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'ip-risky-proxy'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'ip-risky-proxy'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">IP - Proxy</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Indicates use of a proxy server to mask the user's real IP address
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/ip-intelligence-icons/proxy.png" alt="Proxy" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">IP - Proxy</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Indicates use of a proxy server to mask the user's real IP address
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('ip-risky-proxy')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'ip-risky-proxy'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'ip-risky-proxy'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* IP - Virtual Private Network */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('ip-risky-vpn')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/ip-intelligence-icons/vpn.png" alt="VPN" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">IP - Virtual Private Network</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Indicates if the IP is from a known VPN provider, often used to hide location or avoid detection.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['ip-risky-vpn'] ? 'mr-1' : ''}`}>
                                {openAccordions['ip-risky-vpn'] ? '−' : '+'}
                              </span>
                            </button>
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['ip-risky-vpn'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                              <div className="mt-2">
                                      <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                    <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                      {JSON.stringify({"ip_virtual_private_network": true}, null, 2)}
                                    </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/ip-intelligence-icons/vpn.png" alt="VPN" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('ip-risky-vpn')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'ip-risky-vpn'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'ip-risky-vpn'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">IP - Virtual Private Network</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Indicates if the IP is from a known VPN provider, often used to hide location or avoid detection.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/ip-intelligence-icons/vpn.png" alt="VPN" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">IP - Virtual Private Network</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Indicates if the IP is from a known VPN provider, often used to hide location or avoid detection.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('ip-risky-vpn')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'ip-risky-vpn'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'ip-risky-vpn'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* IP - TOR Network */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('ip-risky-tor')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/ip-intelligence-icons/tor%20network.png" alt="TOR" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">IP - TOR Network</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Indicates if the IP is from the TOR network, used to hide identity by routing traffic through multiple global servers.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['ip-risky-tor'] ? 'mr-1' : ''}`}>
                                {openAccordions['ip-risky-tor'] ? '−' : '+'}
                              </span>
                            </button>
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['ip-risky-tor'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                              <div className="mt-2">
                                      <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                    <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                      {JSON.stringify({"ip_tor_network": true}, null, 2)}
                                    </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/ip-intelligence-icons/tor%20network.png" alt="TOR" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('ip-risky-tor')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'ip-risky-tor'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'ip-risky-tor'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">IP - TOR Network</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Indicates if the IP is from the TOR network, used to hide identity by routing traffic through multiple global servers.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/ip-intelligence-icons/tor%20network.png" alt="TOR" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">IP - TOR Network</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Indicates if the IP is from the TOR network, used to hide identity by routing traffic through multiple global servers.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('ip-risky-tor')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'ip-risky-tor'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'ip-risky-tor'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Non Human Activity Accordion */}
                    <div className="border border-[#26ab4d]/30 rounded-lg overflow-hidden bg-black/40">
                      <button
                        onClick={() => toggleAccordion('ip-nonhuman')}
                        className="w-full relative p-3 sm:p-4 text-left hover:bg-[#26ab4d]/10 transition-colors"
                      >
                        <div className="pr-8 sm:pr-10">
                          <h5 className="text-lg font-bold text-[#26ab4d]">Non Human Activity</h5>
                          <p className="text-[#ffffff] text-sm mt-1 leading-relaxed">
                            Identifies bot activity and crawler behavior to detect automated or non-human traffic.
                          </p>
                        </div>
                        <span className={`absolute top-3 right-3 sm:top-4 sm:right-4 text-[#26ab4d] text-2xl font-bold transition-all duration-300 ${openAccordions['ip-nonhuman'] ? '' : ''}`}>
                          {openAccordions['ip-nonhuman'] ? '−' : '+'}
                        </span>
                      </button>
                      <div className={`overflow-hidden transition-all duration-300 ${openAccordions['ip-nonhuman'] ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                        <div className="p-4 pt-0 space-y-2">
                          {/* IP - Recent Bot Activity */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('ip-nonhuman-bot')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/ip-intelligence-icons/recent%20bot%20activity.png" alt="Bot Activity" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">IP - Recent Bot Activity</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Flags IPs showing bot-like behavior, rapid actions, scripted form fills, or fake human activity.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['ip-nonhuman-bot'] ? 'mr-1' : ''}`}>
                                {openAccordions['ip-nonhuman-bot'] ? '−' : '+'}
                              </span>
                            </button>
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['ip-nonhuman-bot'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                              <div className="mt-2">
                                      <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                    <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                      {JSON.stringify({"ip_recent_bot_activity": true}, null, 2)}
                                    </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/ip-intelligence-icons/recent%20bot%20activity.png" alt="Bot Activity" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('ip-nonhuman-bot')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'ip-nonhuman-bot'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'ip-nonhuman-bot'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">IP - Recent Bot Activity</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Flags IPs showing bot-like behavior, rapid actions, scripted form fills, or fake human activity.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/ip-intelligence-icons/recent%20bot%20activity.png" alt="Bot Activity" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">IP - Recent Bot Activity</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Flags IPs showing bot-like behavior, rapid actions, scripted form fills, or fake human activity.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('ip-nonhuman-bot')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'ip-nonhuman-bot'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'ip-nonhuman-bot'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* IP - Crawler Activity */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('ip-nonhuman-crawler')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/ip-intelligence-icons/crawler.png" alt="Crawler" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">IP - Crawler Activity</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Flags IPs linked to search engine bots like Googlebot or Bingbot used for content indexing.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['ip-nonhuman-crawler'] ? 'mr-1' : ''}`}>
                                {openAccordions['ip-nonhuman-crawler'] ? '−' : '+'}
                              </span>
                            </button>
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['ip-nonhuman-crawler'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                              <div className="mt-2">
                                      <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                    <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                      {JSON.stringify({"ip_crawler_activity": false}, null, 2)}
                                    </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/ip-intelligence-icons/crawler.png" alt="Crawler" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('ip-nonhuman-crawler')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'ip-nonhuman-crawler'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'ip-nonhuman-crawler'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">IP - Crawler Activity</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Flags IPs linked to search engine bots like Googlebot or Bingbot used for content indexing.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/ip-intelligence-icons/crawler.png" alt="Crawler" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">IP - Crawler Activity</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Flags IPs linked to search engine bots like Googlebot or Bingbot used for content indexing.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('ip-nonhuman-crawler')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'ip-nonhuman-crawler'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'ip-nonhuman-crawler'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Network Demographics Accordion */}
                    <div className="border border-[#26ab4d]/30 rounded-lg overflow-hidden bg-black/40">
                      <button
                        onClick={() => toggleAccordion('ip-demographics')}
                        className="w-full relative p-3 sm:p-4 text-left hover:bg-[#26ab4d]/10 transition-colors"
                      >
                        <div className="pr-8 sm:pr-10">
                          <h5 className="text-lg font-bold text-[#26ab4d]">Network Demographics</h5>
                          <p className="text-[#ffffff] text-sm mt-1 leading-relaxed">
                            Provides geographic location information including region, city, and coordinates based on IP address.
                          </p>
                        </div>
                        <span className={`absolute top-3 right-3 sm:top-4 sm:right-4 text-[#26ab4d] text-2xl font-bold transition-all duration-300 ${openAccordions['ip-demographics'] ? '' : ''}`}>
                          {openAccordions['ip-demographics'] ? '−' : '+'}
                        </span>
                      </button>
                      <div className={`overflow-hidden transition-all duration-300 ${openAccordions['ip-demographics'] ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                        <div className="p-4 pt-0 space-y-2">
                          {/* IP - Region */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('ip-demographics-region')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/ip-intelligence-icons/region.png" alt="Region" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">IP - Region</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    The administrative region (state, province, etc.) of the IP address.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['ip-demographics-region'] ? 'mr-1' : ''}`}>
                                {openAccordions['ip-demographics-region'] ? '−' : '+'}
                              </span>
                            </button>
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['ip-demographics-region'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                              <div className="mt-2">
                                      <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                    <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                      {JSON.stringify({"ip_region": "Maharashtra"}, null, 2)}
                                    </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/ip-intelligence-icons/region.png" alt="Region" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('ip-demographics-region')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'ip-demographics-region'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'ip-demographics-region'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">IP - Region</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    The administrative region (state, province, etc.) of the IP address.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/ip-intelligence-icons/region.png" alt="Region" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">IP - Region</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      The administrative region (state, province, etc.) of the IP address.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('ip-demographics-region')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'ip-demographics-region'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'ip-demographics-region'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* IP - City */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('ip-demographics-city')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/ip-intelligence-icons/city.png" alt="City" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">IP - City</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    The city or locality tied to the IP address.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['ip-demographics-city'] ? 'mr-1' : ''}`}>
                                {openAccordions['ip-demographics-city'] ? '−' : '+'}
                              </span>
                            </button>
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['ip-demographics-city'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                              <div className="mt-2">
                                      <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                    <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                      {JSON.stringify({"ip_city": "Thane"}, null, 2)}
                                    </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/ip-intelligence-icons/city.png" alt="City" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('ip-demographics-city')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'ip-demographics-city'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'ip-demographics-city'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">IP - City</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    The city or locality tied to the IP address.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/ip-intelligence-icons/city.png" alt="City" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">IP - City</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      The city or locality tied to the IP address.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('ip-demographics-city')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'ip-demographics-city'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'ip-demographics-city'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* IP - Latitude */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('ip-demographics-latitude')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/ip-intelligence-icons/lat%20long.png" alt="Latitude" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">IP - Latitude</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Latitude based on ip address.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['ip-demographics-latitude'] ? 'mr-1' : ''}`}>
                                {openAccordions['ip-demographics-latitude'] ? '−' : '+'}
                              </span>
                            </button>
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['ip-demographics-latitude'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                              <div className="mt-2">
                                      <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                    <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                      {JSON.stringify({"ip_latitude": 19.22}, null, 2)}
                                    </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/ip-intelligence-icons/lat%20long.png" alt="Latitude" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('ip-demographics-latitude')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'ip-demographics-latitude'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'ip-demographics-latitude'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">IP - Latitude</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Latitude based on ip address.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/ip-intelligence-icons/lat%20long.png" alt="Latitude" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">IP - Latitude</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Latitude based on ip address.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('ip-demographics-latitude')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'ip-demographics-latitude'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'ip-demographics-latitude'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* IP - Longitude */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('ip-demographics-longitude')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/ip-intelligence-icons/lat%20long.png" alt="Longitude" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">IP - Longitude</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Longitude based on ip address.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['ip-demographics-longitude'] ? 'mr-1' : ''}`}>
                                {openAccordions['ip-demographics-longitude'] ? '−' : '+'}
                              </span>
                            </button>
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['ip-demographics-longitude'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                              <div className="mt-2">
                                      <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                    <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                      {JSON.stringify({"ip_longitude": 73.08}, null, 2)}
                                    </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/ip-intelligence-icons/lat%20long.png" alt="Longitude" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('ip-demographics-longitude')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'ip-demographics-longitude'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'ip-demographics-longitude'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">IP - Longitude</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Longitude based on ip address.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/ip-intelligence-icons/lat%20long.png" alt="Longitude" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">IP - Longitude</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Longitude based on ip address.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('ip-demographics-longitude')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'ip-demographics-longitude'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'ip-demographics-longitude'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Network (IP) Quality Accordion */}
                    <div className="border border-[#26ab4d]/30 rounded-lg overflow-hidden bg-black/40">
                      <button
                        onClick={() => toggleAccordion('ip-quality')}
                        className="w-full relative p-3 sm:p-4 text-left hover:bg-[#26ab4d]/10 transition-colors"
                      >
                        <div className="pr-8 sm:pr-10">
                          <h5 className="text-lg font-bold text-[#26ab4d]">Network (IP) Quality</h5>
                          <p className="text-[#ffffff] text-sm mt-1 leading-relaxed">
                            Assesses IP trustworthiness, fraud history, and connection type to evaluate network quality and risk.
                          </p>
                        </div>
                        <span className={`absolute top-3 right-3 sm:top-4 sm:right-4 text-[#26ab4d] text-2xl font-bold transition-all duration-300 ${openAccordions['ip-quality'] ? '' : ''}`}>
                          {openAccordions['ip-quality'] ? '−' : '+'}
                        </span>
                      </button>
                      <div className={`overflow-hidden transition-all duration-300 ${openAccordions['ip-quality'] ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                        <div className="p-4 pt-0 space-y-2">
                          {/* IP - Trusted */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('ip-quality-trusted')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/ip-intelligence-icons/trusted.png" alt="Trusted" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">IP - Trusted</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Indicates if the IP is from a trusted network like a known ISP or clean corporate provider.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['ip-quality-trusted'] ? 'mr-1' : ''}`}>
                                {openAccordions['ip-quality-trusted'] ? '−' : '+'}
                              </span>
                            </button>
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['ip-quality-trusted'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                              <div className="mt-2">
                                      <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                    <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                      {JSON.stringify({"ip_trusted": false}, null, 2)}
                                    </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/ip-intelligence-icons/trusted.png" alt="Trusted" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('ip-quality-trusted')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'ip-quality-trusted'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'ip-quality-trusted'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">IP - Trusted</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Indicates if the IP is from a trusted network like a known ISP or clean corporate provider.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/ip-intelligence-icons/trusted.png" alt="Trusted" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">IP - Trusted</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Indicates if the IP is from a trusted network like a known ISP or clean corporate provider.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('ip-quality-trusted')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'ip-quality-trusted'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'ip-quality-trusted'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* IP - Recent Fraud */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('ip-quality-recent-fraud')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/ip-intelligence-icons/recent%20fraud.png" alt="Recent Fraud" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">IP - Recent Fraud</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    It checks whether the IP address has been recently reported for spam or abusive behavior.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['ip-quality-recent-fraud'] ? 'mr-1' : ''}`}>
                                {openAccordions['ip-quality-recent-fraud'] ? '−' : '+'}
                              </span>
                            </button>
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['ip-quality-recent-fraud'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                              <div className="mt-2">
                                      <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                    <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                      {JSON.stringify({"ip_recent_fraud": false}, null, 2)}
                                    </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/ip-intelligence-icons/recent%20fraud.png" alt="Recent Fraud" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('ip-quality-recent-fraud')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'ip-quality-recent-fraud'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'ip-quality-recent-fraud'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">IP - Recent Fraud</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    It checks whether the IP address has been recently reported for spam or abusive behavior.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/ip-intelligence-icons/recent%20fraud.png" alt="Recent Fraud" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">IP - Recent Fraud</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      It checks whether the IP address has been recently reported for spam or abusive behavior.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('ip-quality-recent-fraud')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'ip-quality-recent-fraud'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'ip-quality-recent-fraud'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* IP - Frequent Fraud History */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('ip-quality-frequent-fraud')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/ip-intelligence-icons/FFH.png" alt="Frequent Fraud" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">IP - Frequent Fraud History</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    It checks whether the IP address has been previously involved in Repeated malicious or abusive activities
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['ip-quality-frequent-fraud'] ? 'mr-1' : ''}`}>
                                {openAccordions['ip-quality-frequent-fraud'] ? '−' : '+'}
                              </span>
                            </button>
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['ip-quality-frequent-fraud'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                              <div className="mt-2">
                                      <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                    <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                      {JSON.stringify({"ip_frequent_fraud_history": true}, null, 2)}
                                    </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/ip-intelligence-icons/FFH.png" alt="Frequent Fraud" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('ip-quality-frequent-fraud')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'ip-quality-frequent-fraud'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'ip-quality-frequent-fraud'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">IP - Frequent Fraud History</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    It checks whether the IP address has been previously involved in Repeated malicious or abusive activities
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/ip-intelligence-icons/FFH.png" alt="Frequent Fraud" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">IP - Frequent Fraud History</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      It checks whether the IP address has been previously involved in Repeated malicious or abusive activities
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('ip-quality-frequent-fraud')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'ip-quality-frequent-fraud'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'ip-quality-frequent-fraud'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* IP - High Risk Attack Record */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('ip-quality-high-risk')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/ip-intelligence-icons/high%20risk%20attack%20record.png" alt="High Risk" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">IP - High Risk Attack Record</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    It checks whether the IP address has been involved in serious cyberattacks like credential stuffing, or hacking attempts etc.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['ip-quality-high-risk'] ? 'mr-1' : ''}`}>
                                {openAccordions['ip-quality-high-risk'] ? '−' : '+'}
                              </span>
                            </button>
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['ip-quality-high-risk'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                              <div className="mt-2">
                                      <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                    <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                      {JSON.stringify({"ip_high_risk_attack_record": false}, null, 2)}
                                    </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/ip-intelligence-icons/high%20risk%20attack%20record.png" alt="High Risk" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('ip-quality-high-risk')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'ip-quality-high-risk'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'ip-quality-high-risk'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">IP - High Risk Attack Record</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    It checks whether the IP address has been involved in serious cyberattacks like credential stuffing, or hacking attempts etc.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/ip-intelligence-icons/high%20risk%20attack%20record.png" alt="High Risk" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">IP - High Risk Attack Record</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      It checks whether the IP address has been involved in serious cyberattacks like credential stuffing, or hacking attempts etc.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('ip-quality-high-risk')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'ip-quality-high-risk'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'ip-quality-high-risk'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* IP - Connection Type */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('ip-quality-connection-type')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/ip-intelligence-icons/connection%20type.png" alt="Connection Type" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">IP - Connection Type</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Shows if the connection is residential, mobile, data center or data center links often signal bots or fraud.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['ip-quality-connection-type'] ? 'mr-1' : ''}`}>
                                {openAccordions['ip-quality-connection-type'] ? '−' : '+'}
                              </span>
                            </button>
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['ip-quality-connection-type'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                              <div className="mt-2">
                                      <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                    <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                      {JSON.stringify({"ip_connection_type": "Data Center"}, null, 2)}
                                    </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/ip-intelligence-icons/connection%20type.png" alt="Connection Type" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('ip-quality-connection-type')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'ip-quality-connection-type'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'ip-quality-connection-type'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">IP - Connection Type</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Shows if the connection is residential, mobile, data center or data center links often signal bots or fraud.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/ip-intelligence-icons/connection%20type.png" alt="Connection Type" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">IP - Connection Type</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Shows if the connection is residential, mobile, data center or data center links often signal bots or fraud.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('ip-quality-connection-type')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'ip-quality-connection-type'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'ip-quality-connection-type'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
              
              {/* Unified IP Intelligence Tool */}
          <section className="pt-0 pb-[4rem] relative z-20 bg-transparent overflow-hidden">
            <div className="container mx-auto px-[1.5rem] relative z-10">
              <div className="bg-black/20 rounded-lg p-6 sm:p-8 border border-[#26ab4d]/30">
                  <div>
                    <h4 className="text-3xl sm:text-4xl font-bold mb-6">
                      <span className="text-[#ffffff]">IP Intelligence </span>
                      <span className="bg-gradient-to-r from-[#26ab4d] via-[#1e8a3f] to-[#26ab4d] bg-clip-text text-transparent">Tool</span>
                    </h4>
                    
                    {/* Input Section */}
                    <div className="mb-6">
                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                      <div className="flex-1">
                        <input
                          type="text"
                          value={ipAddress}
                          onChange={(e) => setIpAddress(e.target.value)}
                          placeholder={`"192.168.1.1"`}
                          className={`w-full px-4 sm:px-6 py-3 sm:py-4 bg-[#26ab4d]/10 border-2 rounded-full text-[#ffffff] placeholder-[#9a9a9a] placeholder:font-bold focus:outline-none focus:ring-2 text-[1.125rem] ${
                            ipAddress && !isValidIP(ipAddress)
                              ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                              : 'border-[#26ab4d]/50 focus:ring-[#26ab4d] focus:border-[#26ab4d]'
                          }`}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleAnalyzeIPAll();
                            }
                          }}
                        />
                </div>
                      <button
                        onClick={handleAnalyzeIPAll}
                        disabled={isAnalyzingIP || !ipAddress.trim()}
                        className="px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-[#26ab4d] to-[#1e8a3f] hover:from-[#1e8a3f] hover:to-[#26ab4d] text-[#ffffff] rounded-full font-semibold transition-all duration-300 shadow-lg shadow-[#26ab4d]/25 disabled:opacity-50 disabled:cursor-not-allowed min-w-[140px] text-[1.125rem] whitespace-nowrap"
                      >
                        {isAnalyzingIP ? (
                          <span className="flex items-center justify-center">
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-[#ffffff]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
                            Analyzing...
                    </span>
                        ) : (
                          'Submit'
                        )}
                      </button>
              </div>
            </div>
            
                  {/* Tabbed Results Section */}
                  {(ipRiskyResult || ipNonHumanResult || ipQualityResult || ipDemographicsResult) && (
                    <div className="space-y-6 relative min-h-[400px]">
                      {!accessGranted && <LockOverlay compact />}
                      {/* Tabs */}
                      <div className="flex flex-wrap gap-2 border-b border-[#26ab4d]/30">
                    <button
                          onClick={() => setActiveIPTab('risky')}
                          className={`px-4 py-2 rounded-t-lg font-semibold transition-all ${
                            activeIPTab === 'risky'
                              ? 'bg-[#26ab4d] text-[#ffffff]'
                              : 'bg-black/40 text-[#ffffff] hover:text-[#ffffff]'
                          }`}
                        >
                          Risky IP Activity
                        </button>
                    <button
                          onClick={() => setActiveIPTab('nonhuman')}
                          className={`px-4 py-2 rounded-t-lg font-semibold transition-all ${
                            activeIPTab === 'nonhuman'
                              ? 'bg-[#26ab4d] text-[#ffffff]'
                              : 'bg-black/40 text-[#ffffff] hover:text-[#ffffff]'
                          }`}
                        >
                          Non Human Activity
                        </button>
                        <button
                          onClick={() => setActiveIPTab('demographics')}
                          className={`px-4 py-2 rounded-t-lg font-semibold transition-all ${
                            activeIPTab === 'demographics'
                              ? 'bg-[#26ab4d] text-[#ffffff]'
                              : 'bg-black/40 text-[#ffffff] hover:text-[#ffffff]'
                          }`}
                        >
                          Network Demographics
                        </button>
                        <button
                          onClick={() => setActiveIPTab('quality')}
                          className={`px-4 py-2 rounded-t-lg font-semibold transition-all ${
                            activeIPTab === 'quality'
                              ? 'bg-[#26ab4d] text-[#ffffff]'
                              : 'bg-black/40 text-[#ffffff] hover:text-[#ffffff]'
                          }`}
                        >
                          Network (IP) Quality
                        </button>
                  </div>
                
                      {/* Tab Content */}
                      <div className="bg-black/20 rounded-lg p-6 border border-[#26ab4d]/20">
                        {activeIPTab === 'risky' && ipRiskyResult && (
                          <div>
                            <h5 className="text-xl font-bold text-[#ffffff] mb-4">Risky IP Activity</h5>
                            <div className="bg-black/40 rounded-lg p-4 overflow-x-auto border border-[#26ab4d]/20 max-w-full mx-auto relative">
                              {!accessGranted && <LockOverlay compact />}
                              <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                {formatJSONWithColors(ipRiskyResult.jsonResponse)}
                              </pre>
                        </div>
              </div>
                        )}

                        {activeIPTab === 'nonhuman' && ipNonHumanResult && (
                          <div>
                            <h5 className="text-xl font-bold text-[#ffffff] mb-4">Non Human Activity</h5>
                            <div className="bg-black/40 rounded-lg p-4 overflow-x-auto border border-[#26ab4d]/20 max-w-full mx-auto relative">
                              {!accessGranted && <LockOverlay compact />}
                              <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                {formatJSONWithColors(ipNonHumanResult.jsonResponse)}
                              </pre>
            </div>
                </div>
                        )}

                        {activeIPTab === 'demographics' && ipDemographicsResult && (
                          <div>
                            <h5 className="text-xl font-bold text-[#ffffff] mb-4">Network Demographics</h5>
                            <div className="bg-black/40 rounded-lg p-4 overflow-x-auto border border-[#26ab4d]/20 max-w-full mx-auto relative">
                              {!accessGranted && <LockOverlay compact />}
                              <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                {formatJSONWithColors(ipDemographicsResult.jsonResponse)}
                              </pre>
            </div>
                </div>
                        )}

                        {activeIPTab === 'quality' && ipQualityResult && (
                          <div>
                            <h5 className="text-xl font-bold text-[#ffffff] mb-4">Network (IP) Quality</h5>
                            <div className="bg-black/40 rounded-lg p-4 overflow-x-auto border border-[#26ab4d]/20 max-w-full mx-auto relative">
                              {!accessGranted && <LockOverlay compact />}
                              <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                {formatFlattenedJSON(ipQualityResult.jsonResponse)}
                              </pre>
                </div>
              </div>
                        )}
                        {activeIPTab === 'risky' && !ipRiskyResult && (ipNonHumanResult || ipQualityResult || ipDemographicsResult) && (
                          <div className="text-center py-8">
                            <p className="text-[#ffffff] text-lg">Risky IP Activity results will appear here after analysis.</p>
                          </div>
                        )}
                        {activeIPTab === 'nonhuman' && !ipNonHumanResult && (ipRiskyResult || ipQualityResult || ipDemographicsResult) && (
                          <div className="text-center py-8">
                            <p className="text-[#ffffff] text-lg">Non Human Activity results will appear here after analysis.</p>
                          </div>
                        )}
                        {activeIPTab === 'demographics' && !ipDemographicsResult && (ipRiskyResult || ipNonHumanResult || ipQualityResult) && (
                          <div className="text-center py-8">
                            <p className="text-[#ffffff] text-lg">Network Demographics results will appear here after analysis.</p>
                          </div>
                        )}
                        {activeIPTab === 'quality' && !ipQualityResult && (ipRiskyResult || ipNonHumanResult || ipDemographicsResult) && (
                          <div className="text-center py-8">
                            <p className="text-[#ffffff] text-lg">Network (IP) Quality results will appear here after analysis.</p>
                          </div>
                        )}
                      </div>

                      {/* Combined JSON Response - Flattened */}
                      <div className="bg-black/20 rounded-lg p-6 border border-[#26ab4d]/20">
                        <div className="flex items-center justify-between mb-4">
                          <h6 className="text-lg font-bold text-[#ffffff] flex items-center">
                            <svg className="w-5 h-5 text-[#ffffff] mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
              </svg>
                            Combined JSON Response
                          </h6>
                          <button
                            onClick={() => {
                              // Flatten all JSONs into one object
                              const flattenedJson = {
                                ...(ipRiskyResult?.jsonResponse || {}),
                                ...(ipNonHumanResult?.jsonResponse || {}),
                                ...(ipQualityResult?.jsonResponse || {}),
                                ...(ipDemographicsResult?.jsonResponse || {})
                              };
                              const jsonStr = JSON.stringify(flattenedJson, null, 2);
                              navigator.clipboard.writeText(jsonStr);
                              alert('Combined JSON copied to clipboard!');
                            }}
                            className="text-xs text-[#ffffff] hover:text-[#ffffff] transition-colors flex items-center"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
                            Copy Combined JSON
                          </button>
            </div>
                        <div className="bg-black/40 rounded-lg p-4 overflow-x-auto border border-[#26ab4d]/20">
                          <pre className="text-xs sm:text-sm text-[#656565] font-mono whitespace-pre">
                            {formatFlattenedJSON({
                              ...(ipRiskyResult?.jsonResponse || {}),
                              ...(ipNonHumanResult?.jsonResponse || {}),
                              ...(ipQualityResult?.jsonResponse || {}),
                              ...(ipDemographicsResult?.jsonResponse || {})
                            })}
                          </pre>
                </div>
                    </div>
                    </div>
                  )}

                    {/* Default Example - Show when no analysis */}
                  {!ipRiskyResult && !ipNonHumanResult && !ipQualityResult && !ipDemographicsResult && (
                      <div className="bg-black/20 rounded-lg p-6 border border-[#26ab4d]/20">
                      <p className="text-[#ffffff] text-center mb-4">Enter an IP address above to see comprehensive IP intelligence analysis</p>
                        <div className="bg-black/40 rounded-lg p-4 overflow-x-auto border border-[#26ab4d]/20 max-w-full mx-auto relative">
                          {!accessGranted && <LockOverlay compact />}
                          <pre className="text-xs sm:text-sm text-[#656565] font-mono whitespace-pre">
{`{
  `}<span className="text-[#656565]">"IP_Proxy"</span>{` : `}<span className="text-[#656565]">true</span>{`,
  `}<span className="text-[#656565]">"IP_Virtual_Private_Network"</span>{` : `}<span className="text-[#656565]">true</span>{`,
  `}<span className="text-[#656565]">"IP_TOR_Network"</span>{` : `}<span className="text-[#656565]">false</span>{`,
  `}<span className="text-[#656565]">"IP_Recent_Bot_Activity"</span>{` : `}<span className="text-[#656565]">true</span>{`,
  `}<span className="text-[#656565]">"IP_Crawler_Activity"</span>{` : `}<span className="text-[#656565]">false</span>{`,
  `}<span className="text-[#656565]">"IP_Trusted"</span>{` : `}<span className="text-[#656565]">false</span>{`,
  `}<span className="text-[#656565]">"IP_Recent_Fraud"</span>{` : `}<span className="text-[#656565]">true</span>{`,
  `}<span className="text-[#656565]">"IP_Frequent_Fraud_History"</span>{` : `}<span className="text-[#656565]">false</span>{`,
  `}<span className="text-[#656565]">"IP_High_Risk_Attack_Record"</span>{` : `}<span className="text-[#656565]">false</span>{`,
  `}<span className="text-[#656565]">"IP_Connection_Type"</span>{` : `}<span className="text-[#656565]">"data center"</span>{`,
  `}<span className="text-[#656565]">"IP_Region"</span>{` : `}<span className="text-[#656565]">"maharashtra"</span>{`,
  `}<span className="text-[#656565]">"IP_City"</span>{` : `}<span className="text-[#656565]">"dombivali"</span>{`,
  `}<span className="text-[#656565]">"IP_Latitide"</span>{` : `}<span className="text-[#656565]">19.22</span>{`,
  `}<span className="text-[#656565]">"IP_Longitude"</span>{` : `}<span className="text-[#656565]">73.08</span>{`
}`}
                          </pre>
                  </div>
                  </div>
                    )}
              </div>
            </div>
          </div>
          </section>
            
          {/* CTA Section */}
          <section className="py-[4rem] relative z-20 bg-transparent overflow-hidden">
              {/* Multi-Layer Vector Background */}
              <div className="absolute inset-0 z-0">
                {/* Primary Vector Layer - Neural Network */}
                <svg className="absolute inset-0 w-full h-full opacity-15" viewBox="0 0 1400 900" fill="none">
                  <defs>
                    <linearGradient id="neuralGradient1-ip" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#26ab4d" stopOpacity="0.9"/>
                      <stop offset="30%" stopColor="#1e8a3f" stopOpacity="0.7"/>
                      <stop offset="70%" stopColor="#26ab4d" stopOpacity="0.5"/>
                      <stop offset="100%" stopColor="#1e8a3f" stopOpacity="0.3"/>
                    </linearGradient>
                    <linearGradient id="neuralGradient2-ip" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#1e8a3f" stopOpacity="0.8"/>
                      <stop offset="50%" stopColor="#26ab4d" stopOpacity="0.6"/>
                      <stop offset="100%" stopColor="#1e8a3f" stopOpacity="0.4"/>
                    </linearGradient>
                    <filter id="neuralGlow-ip" x="-50%" y="-50%" width="200%" height="200%">
                      <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                      <feMerge> 
                        <feMergeNode in="coloredBlur"/>
                        <feMergeNode in="SourceGraphic"/>
                      </feMerge>
                    </filter>
                  </defs>
                </svg>
                
                {/* Secondary Vector Layer - DNA Helix */}
                <svg className="absolute inset-0 w-full h-full opacity-10" viewBox="0 0 1400 900" fill="none">
                  <defs>
                    <linearGradient id="dnaGradient-ip" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#26ab4d" stopOpacity="0.6"/>
                      <stop offset="50%" stopColor="#1e8a3f" stopOpacity="0.4"/>
                      <stop offset="100%" stopColor="#26ab4d" stopOpacity="0.2"/>
                    </linearGradient>
                  </defs>
                </svg>
                
                {/* Tertiary Vector Layer - Quantum Patterns */}
                <svg className="absolute inset-0 w-full h-full opacity-8" viewBox="0 0 1400 900" fill="none">
                  <defs>
                    <radialGradient id="quantumGradient-ip" cx="50%" cy="50%" r="50%">
                      <stop offset="0%" stopColor="#26ab4d" stopOpacity="0.8"/>
                      <stop offset="70%" stopColor="#1e8a3f" stopOpacity="0.4"/>
                      <stop offset="100%" stopColor="#26ab4d" stopOpacity="0.1"/>
                    </radialGradient>
                  </defs>
                </svg>
              </div>
            
            <div className="container mx-auto px-[1.5rem] relative z-10">
              <div className="max-w-5xl lg:max-w-fit mx-auto text-center">
                <div className="bg-gradient-to-br from-[#26ab4d]/20 to-[#1e8a3f]/10 backdrop-blur-sm border border-[#26ab4d]/30 rounded-3xl p-8 sm:p-10 lg:p-8">
                  <h3 className="text-[1.125rem] sm:text-[1.375rem] lg:text-[2rem] xl:text-[2.25rem] font-bold mb-[2.5rem] lg:mb-3 break-words lg:leading-tight lg:px-1">
                    <span className="text-[#ffffff]">Ready to Transform Your </span>
                    <span className="text-[#26ab4d]">IP Intelligence</span>
                  </h3>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                    <Link 
                      to="/ask-access" 
                      className="bg-[#26ab4d] hover:bg-[#1e8a3f] text-[#ffffff] px-5 sm:px-6 lg:px-8 py-2.5 sm:py-3 lg:py-4 rounded-full text-base sm:text-lg lg:text-lg font-bold transition-all duration-300 shadow-xl inline-flex items-center justify-center w-fit sm:w-auto sm:min-w-[160px] lg:min-w-[170px] whitespace-nowrap"
                      onClick={() => {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                    >
                      Ask for access
                    </Link>
                  </div>
                    </div>
              </div>
            </div>
          </section>
        </>
      )}

      {/* Data Leaks Intelligence Section */}
      {currentProductId === 'data-leaks-intelligence' && (
        <>
          {/* Data Leaks Intelligence Section - Consolidated */}
          <section className="pt-[4rem] pb-[0.5rem] relative z-20 bg-transparent overflow-hidden">
            <div className="container mx-auto px-[1.5rem] relative z-10">
              <div className="w-full">
                {/* Features & JSON Types Section */}
                <div className="bg-black/20 rounded-lg p-4 sm:p-6 mb-8 border border-[#26ab4d]/30">
                  <h4 className="text-xl sm:text-2xl font-bold text-[#ffffff] mb-4 text-center">
                    Features & JSON Types
                  </h4>
                  
                  <div className="space-y-3">
                    {/* Email Leak Footprint Accordion */}
                    <div className="border border-[#26ab4d]/30 rounded-lg overflow-hidden bg-black/40">
                      <button
                        onClick={() => toggleAccordion('email-leak-footprint')}
                        className="w-full relative p-3 sm:p-4 text-left hover:bg-[#26ab4d]/10 transition-colors"
                      >
                        <div className="pr-8 sm:pr-10">
                          <h5 className="text-lg font-bold text-[#26ab4d]">Email Leak Footprint</h5>
                          <p className="text-[#ffffff] text-sm mt-1 leading-relaxed">
                            Detects data breaches and leaks associated with email addresses, including breach count, breach history, and timeline information.
                          </p>
                        </div>
                        <span className={`absolute top-3 right-3 sm:top-4 sm:right-4 text-[#26ab4d] text-2xl font-bold transition-all duration-300 ${openAccordions['email-leak-footprint'] ? '' : ''}`}>
                          {openAccordions['email-leak-footprint'] ? '−' : '+'}
                        </span>
                      </button>
                      <div className={`overflow-hidden transition-all duration-300 ${openAccordions['email-leak-footprint'] ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                        <div className="p-4 pt-0 space-y-2">
                          {/* Email - Data Leaks Count */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('data-leaks-count')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/data-leaks-intelligence-icons/data%20leaks%20count.png" alt="Data Leaks Count" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Email - Data Leaks Count</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Counts the total amount of data leaks derived from the given email address.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['data-leaks-count'] ? 'mr-1' : ''}`}>
                                {openAccordions['data-leaks-count'] ? '−' : '+'}
                              </span>
                            </button>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/data-leaks-intelligence-icons/data%20leaks%20count.png" alt="Data Leaks Count" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('data-leaks-count')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'data-leaks-count'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'data-leaks-count'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Email - Data Leaks Count</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Counts the total amount of data leaks derived from the given email address.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/data-leaks-intelligence-icons/data%20leaks%20count.png" alt="Data Leaks Count" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Email - Data Leaks Count</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Counts the total amount of data leaks derived from the given email address.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('data-leaks-count')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'data-leaks-count'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'data-leaks-count'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['data-leaks-count'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                              <div className="mt-2">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                    <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                      {JSON.stringify({"email_data_leaks_count": 2}, null, 2)}
                                    </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Email - Data Leaks List */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('email-leak-list')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/data-leaks-intelligence-icons/data%20leaks%20list.png" alt="Data Leaks List" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Email - Data Leaks List</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    It gives the list of all the data leaks associated with the Email ID.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['email-leak-list'] ? 'mr-1' : ''}`}>
                                {openAccordions['email-leak-list'] ? '−' : '+'}
                              </span>
                            </button>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/data-leaks-intelligence-icons/data%20leaks%20list.png" alt="Data Leaks List" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('email-leak-list')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'email-leak-list'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'email-leak-list'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Email - Data Leaks List</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    It gives the list of all the data leaks associated with the Email ID.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/data-leaks-intelligence-icons/data%20leaks%20list.png" alt="Data Leaks List" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Email - Data Leaks List</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      It gives the list of all the data leaks associated with the Email ID.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('email-leak-list')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'email-leak-list'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'email-leak-list'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['email-leak-list'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                              <div className="mt-2">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                    <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                      {JSON.stringify({"email_data_leaks_list": [{"name": "Zomato", "breach_date": "2016-07-01"}, {"name": "FreshMenu", "breach_date": "2017-03-07"}]}, null, 2)}
                                    </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Email - Leaks First Seen */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('email-leak-first-seen')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/data-leaks-intelligence-icons/first%20seen.png" alt="Leaks First Seen" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Email - Leaks First Seen</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    It gives the date of the first data leak detected with the Email ID.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['email-leak-first-seen'] ? 'mr-1' : ''}`}>
                                {openAccordions['email-leak-first-seen'] ? '−' : '+'}
                              </span>
                            </button>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/data-leaks-intelligence-icons/first%20seen.png" alt="Leaks First Seen" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('email-leak-first-seen')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'email-leak-first-seen'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'email-leak-first-seen'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Email - Leaks First Seen</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    It gives the date of the first data leak detected with the Email ID.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/data-leaks-intelligence-icons/first%20seen.png" alt="Leaks First Seen" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Email - Leaks First Seen</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      It gives the date of the first data leak detected with the Email ID.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('email-leak-first-seen')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'email-leak-first-seen'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'email-leak-first-seen'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['email-leak-first-seen'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"email_leaks_first_seen": "2016-07-01"}, null, 2)}
                                  </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Email - Leaks Last Seen */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('email-leak-last-seen')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/data-leaks-intelligence-icons/last%20seen.png" alt="Leaks Last Seen" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Email - Leaks Last Seen</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    It gives the date of the last data leak detected with the Email ID.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['email-leak-last-seen'] ? 'mr-1' : ''}`}>
                                {openAccordions['email-leak-last-seen'] ? '−' : '+'}
                              </span>
                            </button>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/data-leaks-intelligence-icons/last%20seen.png" alt="Leaks Last Seen" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('email-leak-last-seen')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'email-leak-last-seen'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'email-leak-last-seen'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Email - Leaks Last Seen</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    It gives the date of the last data leak detected with the Email ID.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/data-leaks-intelligence-icons/last%20seen.png" alt="Leaks Last Seen" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Email - Leaks Last Seen</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      It gives the date of the last data leak detected with the Email ID.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('email-leak-last-seen')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'email-leak-last-seen'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'email-leak-last-seen'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['email-leak-last-seen'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"email_leaks_last_seen": "2017-03-07"}, null, 2)}
                                  </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Phone No Leak Footprint Accordion */}
                    <div className="border border-[#26ab4d]/30 rounded-lg overflow-hidden bg-black/40">
                      <button
                        onClick={() => toggleAccordion('phone-leak-footprint')}
                        className="w-full relative p-3 sm:p-4 text-left hover:bg-[#26ab4d]/10 transition-colors"
                      >
                        <div className="pr-8 sm:pr-10">
                          <h5 className="text-lg font-bold text-[#26ab4d]">Phone No Leak Footprint</h5>
                          <p className="text-[#ffffff] text-sm mt-1 leading-relaxed">
                            Detects data breaches and leaks associated with phone numbers, including breach count, breach history with logos, and timeline information.
                          </p>
                        </div>
                        <span className={`absolute top-3 right-3 sm:top-4 sm:right-4 text-[#26ab4d] text-2xl font-bold transition-all duration-300 ${openAccordions['phone-leak-footprint'] ? '' : ''}`}>
                          {openAccordions['phone-leak-footprint'] ? '−' : '+'}
                        </span>
                      </button>
                      <div className={`overflow-hidden transition-all duration-300 ${openAccordions['phone-leak-footprint'] ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                        <div className="p-4 pt-0 space-y-2">
                          {/* Phone No - Data Leaks Count */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('phone-leak-count')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/data-leaks-intelligence-icons/data%20leaks%20count.png" alt="Data Leaks Count" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Phone No - Data Leaks Count</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Counts the total amount of data leaks derived from the given phone number.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['phone-leak-count'] ? 'mr-1' : ''}`}>
                                {openAccordions['phone-leak-count'] ? '−' : '+'}
                              </span>
                            </button>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/data-leaks-intelligence-icons/data%20leaks%20count.png" alt="Data Leaks Count" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('phone-leak-count')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'phone-leak-count'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'phone-leak-count'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Phone No - Data Leaks Count</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Counts the total amount of data leaks derived from the given phone number.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/data-leaks-intelligence-icons/data%20leaks%20count.png" alt="Data Leaks Count" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Phone No - Data Leaks Count</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Counts the total amount of data leaks derived from the given phone number.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('phone-leak-count')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'phone-leak-count'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'phone-leak-count'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['phone-leak-count'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"phone_no_data_leaks_count": 4}, null, 2)}
                                  </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Phone No - List of Data Leaks */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('phone-leak-list')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/data-leaks-intelligence-icons/data%20leaks%20list.png" alt="List of Data Leaks" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Phone No - List of Data Leaks</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    It gives the list of all the data leaks associated with the Phone No.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['phone-leak-list'] ? 'mr-1' : ''}`}>
                                {openAccordions['phone-leak-list'] ? '−' : '+'}
                              </span>
                            </button>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/data-leaks-intelligence-icons/data%20leaks%20list.png" alt="List of Data Leaks" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('phone-leak-list')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'phone-leak-list'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'phone-leak-list'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Phone No - List of Data Leaks</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    It gives the list of all the data leaks associated with the Phone No.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/data-leaks-intelligence-icons/data%20leaks%20list.png" alt="List of Data Leaks" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Phone No - List of Data Leaks</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      It gives the list of all the data leaks associated with the Phone No.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('phone-leak-list')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'phone-leak-list'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'phone-leak-list'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['phone-leak-list'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"phone_no_list_of_data_leaks": [{"name": "indiamart", "breach_date": "2021-05-23"}]}, null, 2)}
                                  </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Phone No - First Seen Data Leaks */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('phone-leak-first-seen')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/data-leaks-intelligence-icons/first%20seen.png" alt="First Seen Data Leaks" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Phone No - First Seen Data Leaks</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    It gives the date of the first data leak detected with the Phone No.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['phone-leak-first-seen'] ? 'mr-1' : ''}`}>
                                {openAccordions['phone-leak-first-seen'] ? '−' : '+'}
                              </span>
                            </button>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/data-leaks-intelligence-icons/first%20seen.png" alt="First Seen Data Leaks" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('phone-leak-first-seen')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'phone-leak-first-seen'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'phone-leak-first-seen'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Phone No - First Seen Data Leaks</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    It gives the date of the first data leak detected with the Phone No.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/data-leaks-intelligence-icons/first%20seen.png" alt="First Seen Data Leaks" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Phone No - First Seen Data Leaks</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      It gives the date of the first data leak detected with the Phone No.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('phone-leak-first-seen')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'phone-leak-first-seen'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'phone-leak-first-seen'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['phone-leak-first-seen'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"phone_no_first_seen_data_leaks": "2021-05-23"}, null, 2)}
                                  </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Phone No - Last Seen Data Leaks */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('phone-leak-last-seen')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/data-leaks-intelligence-icons/last%20seen.png" alt="Last Seen Data Leaks" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Phone No - Last Seen Data Leaks</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    It gives the date of the last data leak detected with the Phone No.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['phone-leak-last-seen'] ? 'mr-1' : ''}`}>
                                {openAccordions['phone-leak-last-seen'] ? '−' : '+'}
                              </span>
                            </button>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/data-leaks-intelligence-icons/last%20seen.png" alt="Last Seen Data Leaks" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('phone-leak-last-seen')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'phone-leak-last-seen'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'phone-leak-last-seen'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Phone No - Last Seen Data Leaks</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    It gives the date of the last data leak detected with the Phone No.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/data-leaks-intelligence-icons/last%20seen.png" alt="Last Seen Data Leaks" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Phone No - Last Seen Data Leaks</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      It gives the date of the last data leak detected with the Phone No.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('phone-leak-last-seen')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'phone-leak-last-seen'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'phone-leak-last-seen'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['phone-leak-last-seen'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"phone_no_last_seen_data_leaks": "2021-05-23"}, null, 2)}
                                  </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Unified Data Leaks Intelligence Tool */}
              <div className="bg-black/20 rounded-lg p-4 sm:p-6 lg:p-8 mt-8 sm:mt-10 lg:mt-12 border border-[#26ab4d]/30">
                  <div>
                    <h4 className="text-lg sm:text-xl lg:text-2xl xl:text-3xl font-bold mb-4 sm:mb-5 lg:mb-6">
                      <span className="text-[#ffffff]">Data Leaks Intelligence </span>
                      <span className="bg-gradient-to-r from-[#26ab4d] via-[#1e8a3f] to-[#26ab4d] bg-clip-text text-transparent">Tool</span>
                    </h4>
                    
                    {/* Input Section */}
                    <div className="mb-4 sm:mb-5 lg:mb-6">
                    <div className="flex flex-col sm:flex-row gap-2.5 sm:gap-3 lg:gap-4">
                      <div className="flex-1">
                        <input
                          type="text"
                          value={dataLeaksInput}
                          onChange={(e) => setDataLeaksInput(e.target.value)}
                          placeholder={`"example@email.com" or "9876012345"`}
                          className={`w-full px-3 sm:px-4 lg:px-6 py-2 sm:py-2.5 lg:py-3 xl:py-4 bg-[#26ab4d]/10 border-2 rounded-full text-[#ffffff] placeholder-[#9a9a9a] placeholder:font-bold focus:outline-none focus:ring-2 text-sm sm:text-base lg:text-[1.125rem] ${
                            dataLeaksInput && !isValidDataLeaksInput(dataLeaksInput)
                              ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                              : 'border-[#26ab4d]/50 focus:ring-[#26ab4d] focus:border-[#26ab4d]'
                          }`}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleAnalyzeDataLeaks();
                            }
                          }}
                        />
                </div>
                      <button
                        onClick={handleAnalyzeDataLeaks}
                        disabled={isAnalyzingDataLeaks || !dataLeaksInput.trim()}
                        className="px-4 sm:px-6 lg:px-8 py-2 sm:py-2.5 lg:py-3 xl:py-4 bg-gradient-to-r from-[#26ab4d] to-[#1e8a3f] hover:from-[#1e8a3f] hover:to-[#26ab4d] text-[#ffffff] rounded-full font-semibold transition-all duration-300 shadow-lg shadow-[#26ab4d]/25 disabled:opacity-50 disabled:cursor-not-allowed min-w-[100px] sm:min-w-[120px] lg:min-w-[140px] text-xs sm:text-sm lg:text-base xl:text-[1.125rem] whitespace-nowrap"
                      >
                        {isAnalyzingDataLeaks ? (
                          <span className="flex items-center justify-center">
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-[#ffffff]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
                            Analyzing...
                    </span>
                        ) : (
                          'Submit'
                        )}
                      </button>
              </div>
            </div>
            
                  {/* Tabbed Results Section */}
                  {(emailLeakResult || phoneLeakResult) && (
                    <div className="space-y-6 relative min-h-[400px]">
                      {!accessGranted && <LockOverlay compact />}
                      {/* Tabs */}
                      <div className="flex flex-wrap gap-1.5 sm:gap-2 border-b border-[#26ab4d]/30">
                        {emailLeakResult && (
                          <button
                            onClick={() => setActiveDataLeaksTab('email')}
                            className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-t-lg text-xs sm:text-sm font-semibold transition-all ${
                              activeDataLeaksTab === 'email'
                                ? 'bg-[#26ab4d] text-[#ffffff]'
                                : 'bg-black/40 text-[#ffffff] hover:text-[#ffffff]'
                            }`}
                          >
                            Email Leaks
                          </button>
                        )}
                        {phoneLeakResult && (
                          <button
                            onClick={() => setActiveDataLeaksTab('phone')}
                            className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-t-lg text-xs sm:text-sm font-semibold transition-all ${
                              activeDataLeaksTab === 'phone'
                                ? 'bg-[#26ab4d] text-[#ffffff]'
                                : 'bg-black/40 text-[#ffffff] hover:text-[#ffffff]'
                            }`}
                          >
                            Phone Leaks
                          </button>
                        )}
                      </div>
                  
                      {/* Tab Content */}
                      <div className="bg-black/20 rounded-lg p-6 border border-[#26ab4d]/20">
                        {activeDataLeaksTab === 'email' && emailLeakResult && (
                          <div>
                            <h5 className="text-xl font-bold text-[#ffffff] mb-4">Email Leak Footprint</h5>
                            <div className="bg-black/40 rounded-lg p-4 overflow-x-auto border border-[#26ab4d]/20 relative">
                              {!accessGranted && <LockOverlay compact />}
                              <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                {formatFlattenedJSON(emailLeakResult.jsonResponse)}
                              </pre>
                </div>
              </div>
                        )}

                        {activeDataLeaksTab === 'phone' && phoneLeakResult && (
                          <div>
                            <h5 className="text-xl font-bold text-[#ffffff] mb-4">Phone No Leak Footprint</h5>
                            <div className="bg-black/40 rounded-lg p-4 overflow-x-auto border border-[#26ab4d]/20 relative">
                              {!accessGranted && <LockOverlay compact />}
                              <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                {formatFlattenedJSON(phoneLeakResult.jsonResponse)}
                              </pre>
                </div>
              </div>
                        )}
            </div>
                  </div>
                  )}

                    {/* Default Example - Show when no analysis */}
                  {!emailLeakResult && !phoneLeakResult && (
                      <div className="bg-black/20 rounded-lg p-6 border border-[#26ab4d]/20">
                      <p className="text-[#ffffff] text-center mb-4">Enter an email address or phone number above to see comprehensive data leaks analysis</p>
                        <div className="bg-black/40 rounded-lg p-4 overflow-x-auto border border-[#26ab4d]/20 relative">
                          {!accessGranted && <LockOverlay compact />}
                          <pre className="text-xs sm:text-sm text-[#656565] font-mono whitespace-pre">
{`{
  `}<span className="text-[#656565]">"email_data_leaks_count"</span>{` : `}<span className="text-[#656565]">2</span>{`,
  `}<span className="text-[#656565]">"email_leaks_first_seen"</span>{` : `}<span className="text-[#656565]">"2016-07-01"</span>{`,
  `}<span className="text-[#656565]">"email_leaks_last_seen"</span>{` : `}<span className="text-[#656565]">"2017-03-07"</span>{`,
  `}<span className="text-[#656565]">"phone_no_data_leaks_count"</span>{` : `}<span className="text-[#656565]">1</span>{`,
  `}<span className="text-[#656565]">"phone_no_first_seen_data_leaks"</span>{` : `}<span className="text-[#656565]">"2021-05-23"</span>{`,
  `}<span className="text-[#656565]">"phone_no_last_seen_data_leaks"</span>{` : `}<span className="text-[#656565]">"2021-05-23"</span>{`
}`}
                          </pre>
                  </div>
                  </div>
                    )}
                </div>
              </div>
            </div>
          </section>

          {/* Ready to Transform Section */}
          <section className="py-[4rem] relative z-20 bg-transparent overflow-hidden">
            {/* Multi-Layer Vector Background */}
            <div className="absolute inset-0 z-0">
              {/* Primary Neural Network Vector */}
              <svg className="absolute inset-0 w-full h-full opacity-20" viewBox="0 0 1400 900" fill="none">
                <defs>
                  <linearGradient id="neuralGradient1-cta-data-leaks" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#26ab4d" stopOpacity="0.9"/>
                    <stop offset="30%" stopColor="#1e8a3f" stopOpacity="0.7"/>
                    <stop offset="70%" stopColor="#26ab4d" stopOpacity="0.5"/>
                    <stop offset="100%" stopColor="#1e8a3f" stopOpacity="0.3"/>
                  </linearGradient>
                  <linearGradient id="neuralGradient2-cta-data-leaks" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#1e8a3f" stopOpacity="0.8"/>
                    <stop offset="50%" stopColor="#26ab4d" stopOpacity="0.6"/>
                    <stop offset="100%" stopColor="#1e8a3f" stopOpacity="0.4"/>
                  </linearGradient>
                  <filter id="neuralGlow-cta-data-leaks" x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                    <feMerge> 
                      <feMergeNode in="coloredBlur"/>
                      <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                  </filter>
                </defs>
              </svg>
              
              {/* Secondary Vector Layer - DNA Helix */}
              <svg className="absolute inset-0 w-full h-full opacity-10" viewBox="0 0 1400 900" fill="none">
                <defs>
                  <linearGradient id="dnaGradient-cta-data-leaks" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#26ab4d" stopOpacity="0.6"/>
                    <stop offset="50%" stopColor="#1e8a3f" stopOpacity="0.4"/>
                    <stop offset="100%" stopColor="#26ab4d" stopOpacity="0.2"/>
                  </linearGradient>
                </defs>
              </svg>
              
              {/* Tertiary Vector Layer - Quantum Patterns */}
              <svg className="absolute inset-0 w-full h-full opacity-8" viewBox="0 0 1400 900" fill="none">
                <defs>
                  <radialGradient id="quantumGradient-cta-data-leaks" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="#26ab4d" stopOpacity="0.8"/>
                    <stop offset="70%" stopColor="#1e8a3f" stopOpacity="0.4"/>
                    <stop offset="100%" stopColor="#26ab4d" stopOpacity="0.1"/>
                  </radialGradient>
                </defs>
              </svg>
            </div>
            
            <div className="container mx-auto px-3 sm:px-4 lg:px-[1.5rem] relative z-10">
              <div className="max-w-5xl lg:max-w-fit mx-auto text-center">
                <div className="bg-gradient-to-br from-[#26ab4d]/20 to-[#1e8a3f]/10 backdrop-blur-sm border border-[#26ab4d]/30 rounded-2xl sm:rounded-3xl lg:rounded-3xl p-5 sm:p-6 lg:p-8">
                  <h3 className="text-[1.125rem] sm:text-[1.375rem] lg:text-[2rem] xl:text-[2.25rem] font-bold mb-6 sm:mb-8 lg:mb-3 break-words lg:leading-tight lg:px-1">
                    <span className="text-[#ffffff]">Ready to Transform Your </span>
                    <span className="text-[#26ab4d]">Data Leaks Intelligence</span>
                  </h3>
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 lg:gap-4 justify-center items-center">
                    <Link 
                      to="/ask-access" 
                      className="bg-[#26ab4d] hover:bg-[#1e8a3f] text-[#ffffff] px-5 sm:px-6 lg:px-8 py-2.5 sm:py-3 lg:py-4 rounded-full text-base sm:text-lg lg:text-lg font-bold transition-all duration-300 shadow-xl inline-flex items-center justify-center w-fit sm:w-auto sm:min-w-[160px] lg:min-w-[170px] whitespace-nowrap"
                      onClick={() => {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                    >
                      Ask for access
                    </Link>
                  </div>
                    </div>
              </div>
            </div>
          </section>
        </>
      )}

      {/* Hardware Fingerprinting Section - Standalone Product */}
      {currentProductId === 'hardware-fingerprinting' && (
        <>
          {/* Breadcrumb Navigation */}
          <div className="relative z-20 bg-transparent pt-[10rem] pb-4">
            <div className="container mx-auto px-[1.5rem]">
              <nav className="flex items-center gap-2 text-[0.875rem] lg:text-[0.9375rem] xl:text-[1rem] font-bold w-fit flex-wrap">
                <Link 
                  to="/" 
                  className="text-[#ffffff] hover:text-[#26ab4d] transition-colors"
                >
                  Home
                </Link>
                <span className="text-[#26ab4d]">/</span>
                <Link 
                  to="/products" 
                  className="text-[#ffffff] hover:text-[#26ab4d] transition-colors"
                >
                  Products
                </Link>
                <span className="text-[#26ab4d]">/</span>
                <Link 
                  to="/products/device-forensic" 
                  className="text-[#ffffff] hover:text-[#26ab4d] transition-colors"
                >
                  Device Forensic
                </Link>
                <span className="text-[#26ab4d]">/</span>
                <span className="text-[#ffffff]">
                  Hardware Fingerprinting
                </span>
              </nav>
            </div>
          </div>

          {/* Features & Return Types Section */}
          <section className="pt-[2rem] pb-0 relative z-20 bg-transparent overflow-hidden">
            <div className="container mx-auto px-[1.5rem] relative z-10">
              <div className="w-full">
                {/* Features & JSON Types Section */}
                <div className="bg-black/20 rounded-lg p-4 sm:p-6 mb-8 border border-[#26ab4d]/30">
                  <h4 className="text-xl sm:text-2xl font-bold text-[#ffffff] mb-4 text-center">
                    Features & JSON Types
                  </h4>
                  
                  <div className="space-y-3">
                    {/* Device Identification Accordion */}
                    <div className="border border-[#26ab4d]/30 rounded-lg overflow-hidden bg-black/40">
                      <button
                        onClick={() => toggleAccordion('behavioral-device-identification')}
                        className="w-full relative p-3 sm:p-4 text-left hover:bg-[#26ab4d]/10 transition-colors"
                      >
                        <div className="pr-8 sm:pr-10">
                          <h5 className="text-lg font-bold text-[#26ab4d]">Device Identification</h5>
                          <p className="text-[#ffffff] text-sm mt-1 leading-relaxed">
                            Provides details to identify the device including brand, model, product name, app version, and unique identifiers.
                          </p>
                        </div>
                        <span className={`absolute top-3 right-3 sm:top-4 sm:right-4 text-[#26ab4d] text-2xl font-bold transition-all duration-300 ${openAccordions['behavioral-device-identification'] ? '' : ''}`}>
                          {openAccordions['behavioral-device-identification'] ? '−' : '+'}
                        </span>
                      </button>
                      <div className={`overflow-hidden transition-all duration-300 ${openAccordions['behavioral-device-identification'] ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                        <div className="p-4 pt-0 space-y-2">
                          {/* SDK - Device Brand Name */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('hardware-device-brand-name')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/hardware-fingerprinting-icons/device brand name.png" alt="Device Brand Name" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">SDK - Device Brand Name</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    It shows the full name of the Device brand.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['hardware-device-brand-name'] ? 'mr-1' : ''}`}>
                                {openAccordions['hardware-device-brand-name'] ? '−' : '+'}
                              </span>
                            </button>
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['hardware-device-brand-name'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                      <div className="relative min-h-[80px]">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20">
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"sdk_device_brand_name": "NovaTech"}, null, 2)}
                                  </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                                </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/hardware-fingerprinting-icons/device brand name.png" alt="Device Brand Name" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('hardware-device-brand-name')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'hardware-device-brand-name'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'hardware-device-brand-name'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">SDK - Device Brand Name</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    It shows the full name of the Device brand.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/hardware-fingerprinting-icons/device brand name.png" alt="Device Brand Name" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">SDK - Device Brand Name</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      It shows the full name of the Device brand.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('hardware-device-brand-name')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'hardware-device-brand-name'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'hardware-device-brand-name'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* SDK - Device Model */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('hardware-device-model')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/hardware-fingerprinting-icons/device model.png" alt="Device Model" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">SDK - Device Model</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    It shows the exact model number of the device.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['hardware-device-model'] ? 'mr-1' : ''}`}>
                                {openAccordions['hardware-device-model'] ? '−' : '+'}
                              </span>
                            </button>
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['hardware-device-model'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                      <div className="relative min-h-[80px]">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20">
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"sdk_device_model": "Nova X5"}, null, 2)}
                                  </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                                </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/hardware-fingerprinting-icons/device model.png" alt="Device Model" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('hardware-device-model')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'hardware-device-model'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'hardware-device-model'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">SDK - Device Model</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    It shows the exact model number of the device.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/hardware-fingerprinting-icons/device model.png" alt="Device Model" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">SDK - Device Model</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      It shows the exact model number of the device.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('hardware-device-model')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'hardware-device-model'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'hardware-device-model'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* SDK - Device Product Name */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('hardware-device-product-name')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/hardware-fingerprinting-icons/device product name.png" alt="Device Product Name" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">SDK - Device Product Name</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    It shows the internal product name given to the phone by the brand.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['hardware-device-product-name'] ? 'mr-1' : ''}`}>
                                {openAccordions['hardware-device-product-name'] ? '−' : '+'}
                              </span>
                            </button>
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['hardware-device-product-name'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                      <div className="relative min-h-[80px]">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20">
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"sdk_device_product_name": "nova_x5_india"}, null, 2)}
                                  </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                                </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/hardware-fingerprinting-icons/device product name.png" alt="Device Product Name" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('hardware-device-product-name')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'hardware-device-product-name'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'hardware-device-product-name'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">SDK - Device Product Name</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    It shows the internal product name given to the phone by the brand.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/hardware-fingerprinting-icons/device product name.png" alt="Device Product Name" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">SDK - Device Product Name</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      It shows the internal product name given to the phone by the brand.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('hardware-device-product-name')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'hardware-device-product-name'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'hardware-device-product-name'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* SDK - App Version */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('hardware-app-version')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/hardware-fingerprinting-icons/app version.png" alt="App Version" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">SDK - App Version</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    It shows which version of the app is installed in your device and it's useful to check if the user is using the latest version.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['hardware-app-version'] ? 'mr-1' : ''}`}>
                                {openAccordions['hardware-app-version'] ? '−' : '+'}
                              </span>
                            </button>
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['hardware-app-version'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                      <div className="relative min-h-[80px]">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20">
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"sdk_app_version": "4.2.1"}, null, 2)}
                                  </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                                </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/hardware-fingerprinting-icons/app version.png" alt="App Version" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('hardware-app-version')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'hardware-app-version'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'hardware-app-version'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">SDK - App Version</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    It shows which version of the app is installed in your device and it's useful to check if the user is using the latest version.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/hardware-fingerprinting-icons/app version.png" alt="App Version" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">SDK - App Version</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      It shows which version of the app is installed in your device and it's useful to check if the user is using the latest version.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('hardware-app-version')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'hardware-app-version'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'hardware-app-version'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* SDK - Device IMEI Number */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('hardware-imei')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/hardware-fingerprinting-icons/device imei no.png" alt="Device IMEI Number" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">SDK - Device IMEI Number</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    It shows a unique ID number of the phone, used to identify the device across systems.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['hardware-imei'] ? 'mr-1' : ''}`}>
                                {openAccordions['hardware-imei'] ? '−' : '+'}
                              </span>
                            </button>
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['hardware-imei'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                      <div className="relative min-h-[80px]">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20">
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"sdk_device_imei_number": "359882107451236"}, null, 2)}
                                  </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                                </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/hardware-fingerprinting-icons/device imei no.png" alt="Device IMEI Number" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('hardware-imei')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'hardware-imei'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'hardware-imei'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">SDK - Device IMEI Number</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    It shows a unique ID number of the phone, used to identify the device across systems.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/hardware-fingerprinting-icons/device imei no.png" alt="Device IMEI Number" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">SDK - Device IMEI Number</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      It shows a unique ID number of the phone, used to identify the device across systems.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('hardware-imei')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'hardware-imei'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'hardware-imei'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* SDK - MAC Address */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('hardware-mac-address')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/hardware-fingerprinting-icons/mac address.png" alt="MAC Address" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">SDK - MAC Address</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    It shows a unique ID connected to the device Wi-Fi, used to help track or identify the device on a network.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['hardware-mac-address'] ? 'mr-1' : ''}`}>
                                {openAccordions['hardware-mac-address'] ? '−' : '+'}
                              </span>
                            </button>
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['hardware-mac-address'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                      <div className="relative min-h-[80px]">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20">
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"sdk_mac_address": "9A:3F:22:BC:88:10"}, null, 2)}
                                  </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/hardware-fingerprinting-icons/mac address.png" alt="MAC Address" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('hardware-mac-address')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'hardware-mac-address'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'hardware-mac-address'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">SDK - MAC Address</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    It shows a unique ID connected to the device Wi-Fi, used to help track or identify the device on a network.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/hardware-fingerprinting-icons/mac address.png" alt="MAC Address" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">SDK - MAC Address</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      It shows a unique ID connected to the device Wi-Fi, used to help track or identify the device on a network.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('hardware-mac-address')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'hardware-mac-address'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'hardware-mac-address'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Device Risk Profile Accordion */}
                    <div className="border border-[#26ab4d]/30 rounded-lg overflow-hidden bg-black/40">
                      <button
                        onClick={() => toggleAccordion('behavioral-risk-profile')}
                        className="w-full relative p-3 sm:p-4 text-left hover:bg-[#26ab4d]/10 transition-colors"
                      >
                        <div className="pr-8 sm:pr-10">
                          <h5 className="text-lg font-bold text-[#26ab4d]">Device Risk Profile</h5>
                          <p className="text-[#ffffff] text-sm mt-1 leading-relaxed">
                            Assesses potential risks associated with the device including rooting, emulation, bot detection, and security threats.
                          </p>
                        </div>
                        <span className={`absolute top-3 right-3 sm:top-4 sm:right-4 text-[#26ab4d] text-2xl font-bold transition-all duration-300 ${openAccordions['behavioral-risk-profile'] ? '' : ''}`}>
                          {openAccordions['behavioral-risk-profile'] ? '−' : '+'}
                        </span>
                      </button>
                      <div className={`overflow-hidden transition-all duration-300 ${openAccordions['behavioral-risk-profile'] ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                        <div className="p-4 pt-0 space-y-2">
                          {/* SDK - Rooted Device */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('hardware-rooted')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/hardware-fingerprinting-icons/rooted device.png" alt="Rooted Device" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">SDK - Rooted Device</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    It shows if the phone has been tampered with or rooted, which is a high-risk sign.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['hardware-rooted'] ? 'mr-1' : ''}`}>
                                {openAccordions['hardware-rooted'] ? '−' : '+'}
                              </span>
                            </button>
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['hardware-rooted'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                      <div className="relative min-h-[80px]">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20">
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"sdk_rooted_device": "Detected"}, null, 2)}
                                  </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                                </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/hardware-fingerprinting-icons/rooted device.png" alt="Rooted Device" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('hardware-rooted')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'hardware-rooted'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'hardware-rooted'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">SDK - Rooted Device</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    It shows if the phone has been tampered with or rooted, which is a high-risk sign.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/hardware-fingerprinting-icons/rooted device.png" alt="Rooted Device" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">SDK - Rooted Device</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      It shows if the phone has been tampered with or rooted, which is a high-risk sign.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('hardware-rooted')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'hardware-rooted'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'hardware-rooted'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* SDK - MLS, Mock Location Setting */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('hardware-mock-location')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/hardware-fingerprinting-icons/mls.png" alt="Mock Location Setting" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">SDK - MLS, Mock Location Setting</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    It checks if the user is using fake GPS or fake data settings on the device.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['hardware-mock-location'] ? 'mr-1' : ''}`}>
                                {openAccordions['hardware-mock-location'] ? '−' : '+'}
                              </span>
                            </button>
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['hardware-mock-location'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                      <div className="relative min-h-[80px]">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20">
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"sdk_mls_mock_location_setting": "Enabled"}, null, 2)}
                                  </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                                </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/hardware-fingerprinting-icons/mls.png" alt="Mock Location Setting" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('hardware-mock-location')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'hardware-mock-location'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'hardware-mock-location'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">SDK - MLS, Mock Location Setting</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    It checks if the user is using fake GPS or fake data settings on the device.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/hardware-fingerprinting-icons/mls.png" alt="Mock Location Setting" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">SDK - MLS, Mock Location Setting</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      It checks if the user is using fake GPS or fake data settings on the device.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('hardware-mock-location')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'hardware-mock-location'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'hardware-mock-location'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* SDK - Remote Access */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('hardware-remote-access')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/hardware-fingerprinting-icons/remote access.png" alt="Remote Access" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">SDK - Remote Access</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    It checks if someone is controlling the device remotely, which could indicate fraud.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['hardware-remote-access'] ? 'mr-1' : ''}`}>
                                {openAccordions['hardware-remote-access'] ? '−' : '+'}
                              </span>
                            </button>
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['hardware-remote-access'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                      <div className="relative min-h-[80px]">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20">
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"sdk_remote_access": "TeamViewer active on device"}, null, 2)}
                                  </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                                </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/hardware-fingerprinting-icons/remote access.png" alt="Remote Access" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('hardware-remote-access')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'hardware-remote-access'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'hardware-remote-access'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">SDK - Remote Access</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    It checks if someone is controlling the device remotely, which could indicate fraud.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/hardware-fingerprinting-icons/remote access.png" alt="Remote Access" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">SDK - Remote Access</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      It checks if someone is controlling the device remotely, which could indicate fraud.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('hardware-remote-access')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'hardware-remote-access'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'hardware-remote-access'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* SDK - RAT Detection, Remote Access Trojan Detection */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('hardware-rat-detection')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/hardware-fingerprinting-icons/RAT detection.png" alt="RAT Detection" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">SDK - RAT Detection, Remote Access Trojan Detection</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    It checks if the device has malware that allows someone to secretly take control of it.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['hardware-rat-detection'] ? 'mr-1' : ''}`}>
                                {openAccordions['hardware-rat-detection'] ? '−' : '+'}
                              </span>
                            </button>
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['hardware-rat-detection'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                      <div className="relative min-h-[80px]">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20">
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"sdk_rat_detection_remote_access_trojan_detection": "Andro RAT"}, null, 2)}
                                  </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                                </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/hardware-fingerprinting-icons/RAT detection.png" alt="RAT Detection" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('hardware-rat-detection')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'hardware-rat-detection'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'hardware-rat-detection'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">SDK - RAT Detection, Remote Access Trojan Detection</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    It checks if the device has malware that allows someone to secretly take control of it.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/hardware-fingerprinting-icons/RAT detection.png" alt="RAT Detection" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">SDK - RAT Detection, Remote Access Trojan Detection</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      It checks if the device has malware that allows someone to secretly take control of it.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('hardware-rat-detection')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'hardware-rat-detection'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'hardware-rat-detection'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* SDK - Emulator Detection */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('hardware-emulator')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/hardware-fingerprinting-icons/emulator detection.png" alt="Emulator Detection" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">SDK - Emulator Detection</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    It shows if the app is running in a fake environment (not on a real mobile device).
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['hardware-emulator'] ? 'mr-1' : ''}`}>
                                {openAccordions['hardware-emulator'] ? '−' : '+'}
                              </span>
                            </button>
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['hardware-emulator'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                      <div className="relative min-h-[80px]">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20">
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"sdk_emulator_detection": "Detected"}, null, 2)}
                                  </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                                </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/hardware-fingerprinting-icons/emulator detection.png" alt="Emulator Detection" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('hardware-emulator')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'hardware-emulator'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'hardware-emulator'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">SDK - Emulator Detection</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    It shows if the app is running in a fake environment (not on a real mobile device).
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/hardware-fingerprinting-icons/emulator detection.png" alt="Emulator Detection" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">SDK - Emulator Detection</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      It shows if the app is running in a fake environment (not on a real mobile device).
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('hardware-emulator')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'hardware-emulator'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'hardware-emulator'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* SDK - Bot Detection */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('hardware-bot-detection')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/hardware-fingerprinting-icons/bot detection.png" alt="Bot Detection" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">SDK - Bot Detection</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    It checks if a bot, not a real person, is using the app or website - useful for spotting automated fraud on both mobile and laptop.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['hardware-bot-detection'] ? 'mr-1' : ''}`}>
                                {openAccordions['hardware-bot-detection'] ? '−' : '+'}
                              </span>
                            </button>
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['hardware-bot-detection'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                      <div className="relative min-h-[80px]">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20">
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"sdk_bot_detection": false}, null, 2)}
                                  </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                                </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/hardware-fingerprinting-icons/bot detection.png" alt="Bot Detection" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('hardware-bot-detection')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'hardware-bot-detection'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'hardware-bot-detection'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">SDK - Bot Detection</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    It checks if a bot, not a real person, is using the app or website - useful for spotting automated fraud on both mobile and laptop.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/hardware-fingerprinting-icons/bot detection.png" alt="Bot Detection" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">SDK - Bot Detection</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      It checks if a bot, not a real person, is using the app or website - useful for spotting automated fraud on both mobile and laptop.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('hardware-bot-detection')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'hardware-bot-detection'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'hardware-bot-detection'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* SDK - Device Fingerprint */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('hardware-fingerprint')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/hardware-fingerprinting-icons/device fingerprint.png" alt="Device Fingerprint" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">SDK - Device Fingerprint</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    It shows a unique code made from device details to identify the same phone or laptop across sessions.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['hardware-fingerprint'] ? 'mr-1' : ''}`}>
                                {openAccordions['hardware-fingerprint'] ? '−' : '+'}
                              </span>
                            </button>
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['hardware-fingerprint'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                      <div className="relative min-h-[80px]">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20">
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"sdk_device_fingerprint": "DFP-7A92D1C4-AB23-4490-98F2-10E4CC77F822"}, null, 2)}
                                  </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/hardware-fingerprinting-icons/device fingerprint.png" alt="Device Fingerprint" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('hardware-fingerprint')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'hardware-fingerprint'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'hardware-fingerprint'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">SDK - Device Fingerprint</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    It shows a unique code made from device details to identify the same phone or laptop across sessions.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/hardware-fingerprinting-icons/device fingerprint.png" alt="Device Fingerprint" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">SDK - Device Fingerprint</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      It shows a unique code made from device details to identify the same phone or laptop across sessions.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('hardware-fingerprint')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'hardware-fingerprint'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'hardware-fingerprint'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Device Network & Geolocation Accordion */}
                    <div className="border border-[#26ab4d]/30 rounded-lg overflow-hidden bg-black/40">
                      <button
                        onClick={() => toggleAccordion('behavioral-network-geolocation')}
                        className="w-full relative p-3 sm:p-4 text-left hover:bg-[#26ab4d]/10 transition-colors"
                      >
                        <div className="pr-8 sm:pr-10">
                          <h5 className="text-lg font-bold text-[#26ab4d]">Device Network & Geolocation</h5>
                          <p className="text-[#ffffff] text-sm mt-1 leading-relaxed">
                            Covers network connectivity and geographical location information including Wi-Fi connections, IP addresses, timezone, and GPS location.
                          </p>
                        </div>
                        <span className={`absolute top-3 right-3 sm:top-4 sm:right-4 text-[#26ab4d] text-2xl font-bold transition-all duration-300 ${openAccordions['behavioral-network-geolocation'] ? '' : ''}`}>
                          {openAccordions['behavioral-network-geolocation'] ? '−' : '+'}
                        </span>
                      </button>
                      <div className={`overflow-hidden transition-all duration-300 ${openAccordions['behavioral-network-geolocation'] ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                        <div className="p-4 pt-0 space-y-2">
                          {/* SDK - Wi-Fi Connections */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('hardware-wifi')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/hardware-fingerprinting-icons/wifi connections.png" alt="Wi-Fi Connections" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">SDK - Wi-Fi Connections</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    It checks if the user is connected to a public, private, or unknown Wi-Fi network.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['hardware-wifi'] ? 'mr-1' : ''}`}>
                                {openAccordions['hardware-wifi'] ? '−' : '+'}
                              </span>
                            </button>
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['hardware-wifi'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                      <div className="relative min-h-[80px]">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20">
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"sdk_wifi_connections": "Connected to_Home_WiFi_52"}, null, 2)}
                                  </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                                </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/hardware-fingerprinting-icons/wifi connections.png" alt="Wi-Fi Connections" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('hardware-wifi')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'hardware-wifi'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'hardware-wifi'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">SDK - Wi-Fi Connections</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    It checks if the user is connected to a public, private, or unknown Wi-Fi network.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/hardware-fingerprinting-icons/wifi connections.png" alt="Wi-Fi Connections" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">SDK - Wi-Fi Connections</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      It checks if the user is connected to a public, private, or unknown Wi-Fi network.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('hardware-wifi')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'hardware-wifi'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'hardware-wifi'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* SDK - IP Addresses */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('hardware-ip-address')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/hardware-fingerprinting-icons/ip address.png" alt="IP Addresses" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">SDK - IP Addresses</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    It checks the user's IP address to detect the use of VPN, proxy, or if the location doesn't match their claim.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['hardware-ip-address'] ? 'mr-1' : ''}`}>
                                {openAccordions['hardware-ip-address'] ? '−' : '+'}
                              </span>
                            </button>
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['hardware-ip-address'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                      <div className="relative min-h-[80px]">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20">
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"sdk_ip_addresses": "203.91.114.22"}, null, 2)}
                                  </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                                </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/hardware-fingerprinting-icons/ip address.png" alt="IP Addresses" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('hardware-ip-address')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'hardware-ip-address'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'hardware-ip-address'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">SDK - IP Addresses</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    It checks the user's IP address to detect the use of VPN, proxy, or if the location doesn't match their claim.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/hardware-fingerprinting-icons/ip address.png" alt="IP Addresses" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">SDK - IP Addresses</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      It checks the user's IP address to detect the use of VPN, proxy, or if the location doesn't match their claim.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('hardware-ip-address')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'hardware-ip-address'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'hardware-ip-address'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* SDK - Device Time Zone */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('hardware-timezone')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/hardware-fingerprinting-icons/time zone.png" alt="Device Time Zone" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">SDK - Device Time Zone</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    It shows the time zone of the user's device to detect if they are pretending to be in a different region.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['hardware-timezone'] ? 'mr-1' : ''}`}>
                                {openAccordions['hardware-timezone'] ? '−' : '+'}
                              </span>
                            </button>
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['hardware-timezone'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                      <div className="relative min-h-[80px]">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20">
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"sdk_device_time_zone": "Asia/Kolkata"}, null, 2)}
                                  </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                                </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/hardware-fingerprinting-icons/time zone.png" alt="Device Time Zone" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('hardware-timezone')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'hardware-timezone'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'hardware-timezone'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">SDK - Device Time Zone</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    It shows the time zone of the user's device to detect if they are pretending to be in a different region.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/hardware-fingerprinting-icons/time zone.png" alt="Device Time Zone" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">SDK - Device Time Zone</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      It shows the time zone of the user's device to detect if they are pretending to be in a different region.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('hardware-timezone')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'hardware-timezone'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'hardware-timezone'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* SDK - SIM Card Country */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('hardware-sim-card')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/hardware-fingerprinting-icons/sim info.png" alt="SIM Card Country" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">SDK - SIM Card Country</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    It checks the SIM card's country and network details to verify if they match the user's claimed location.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['hardware-sim-card'] ? 'mr-1' : ''}`}>
                                {openAccordions['hardware-sim-card'] ? '−' : '+'}
                              </span>
                            </button>
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['hardware-sim-card'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                      <div className="relative min-h-[80px]">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20">
                                  {!accessGranted && <LockOverlay compact />}
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"sdk_sim_card_country": "IN"}, null, 2)}
                                  </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                                </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/hardware-fingerprinting-icons/sim info.png" alt="SIM Card Country" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('hardware-sim-card')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'hardware-sim-card'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'hardware-sim-card'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">SDK - SIM Card Country</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    It checks the SIM card's country and network details to verify if they match the user's claimed location.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/hardware-fingerprinting-icons/sim info.png" alt="SIM Card Country" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">SDK - SIM Card Country</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      It checks the SIM card's country and network details to verify if they match the user's claimed location.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('hardware-sim-card')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'hardware-sim-card'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'hardware-sim-card'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* SDK - Device Location */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('hardware-location')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/hardware-fingerprinting-icons/device location.png" alt="Device Location" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">SDK - Device Location</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    It shows the GPS location of the user to compare with the address submitted during KYC.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['hardware-location'] ? 'mr-1' : ''}`}>
                                {openAccordions['hardware-location'] ? '−' : '+'}
                              </span>
                            </button>
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['hardware-location'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                      <div className="relative min-h-[80px]">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20">
                                  {!accessGranted && <LockOverlay compact />}
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"sdk_device_location": ["latitude: 18.52", "longitude: 73.85"]}, null, 2)}
                                  </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                                </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/hardware-fingerprinting-icons/device location.png" alt="Device Location" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('hardware-location')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'hardware-location'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'hardware-location'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">SDK - Device Location</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    It shows the GPS location of the user to compare with the address submitted during KYC.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/hardware-fingerprinting-icons/device location.png" alt="Device Location" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">SDK - Device Location</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      It shows the GPS location of the user to compare with the address submitted during KYC.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('hardware-location')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'hardware-location'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'hardware-location'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
             
          {/* CTA Section */}
          <section className="py-[4rem] relative z-20 bg-transparent overflow-hidden">
            {/* Multi-Layer Vector Background */}
            <div className="absolute inset-0 z-0">
              {/* Primary Vector Layer - Neural Network */}
              <svg className="absolute inset-0 w-full h-full opacity-15" viewBox="0 0 1400 900" fill="none">
                <defs>
                  <linearGradient id="neuralGradient1-dl" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#26ab4d" stopOpacity="0.9"/>
                    <stop offset="30%" stopColor="#1e8a3f" stopOpacity="0.7"/>
                    <stop offset="70%" stopColor="#26ab4d" stopOpacity="0.5"/>
                    <stop offset="100%" stopColor="#1e8a3f" stopOpacity="0.3"/>
                  </linearGradient>
                </defs>
              </svg>
            </div>
          
            <div className="container mx-auto px-[1.5rem] relative z-10">
              <div className="max-w-5xl lg:max-w-fit mx-auto text-center">
                <div className="bg-gradient-to-br from-[#26ab4d]/20 to-[#1e8a3f]/10 backdrop-blur-sm border border-[#26ab4d]/30 rounded-3xl p-8 sm:p-10 lg:p-8">
                  <h3 className="text-[1.125rem] sm:text-[1.375rem] lg:text-[2rem] xl:text-[2.25rem] font-bold mb-[2.5rem] lg:mb-3 break-words lg:leading-tight lg:px-1">
                    <span className="text-[#ffffff]">Ready to Transform Your </span>
                    <span className="text-[#26ab4d]">Hardware Fingerprinting</span>
                  </h3>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                    <Link 
                      to="/ask-access" 
                      className="bg-[#26ab4d] hover:bg-[#1e8a3f] text-[#ffffff] px-5 sm:px-6 lg:px-8 py-2.5 sm:py-3 lg:py-4 rounded-full text-base sm:text-lg lg:text-lg font-bold transition-all duration-300 shadow-xl inline-flex items-center justify-center w-fit sm:w-auto sm:min-w-[160px] lg:min-w-[170px] whitespace-nowrap"
                      onClick={() => {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                    >
                      Ask for access
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </>
      )}

      {/* Behavioral Analysis Section - Cognitive Behavioral Patterns */}
      {currentProductId === 'behavioral-analysis' && (
        <>
          {/* Breadcrumb Navigation */}
          <div className="relative z-20 bg-transparent pt-[10rem] pb-4">
            <div className="container mx-auto px-[1.5rem]">
              <nav className="flex items-center gap-2 text-[0.875rem] lg:text-[0.9375rem] xl:text-[1rem] font-bold w-fit flex-wrap">
                <Link 
                  to="/" 
                  className="text-[#ffffff] hover:text-[#26ab4d] transition-colors"
                >
                  Home
                </Link>
                <span className="text-[#26ab4d]">/</span>
                <Link 
                  to="/products" 
                  className="text-[#ffffff] hover:text-[#26ab4d] transition-colors"
                >
                  Products
                </Link>
                <span className="text-[#26ab4d]">/</span>
                <Link 
                  to="/products/cognitive-behaviour" 
                  className="text-[#ffffff] hover:text-[#26ab4d] transition-colors"
                >
                  Cognitive Behaviour
                </Link>
                <span className="text-[#26ab4d]">/</span>
                <span className="text-[#ffffff]">
                  Behavioral Analysis
                </span>
              </nav>
            </div>
          </div>

          {/* Features & Return Types Section */}
          <section className="pt-[2rem] pb-0 relative z-20 bg-transparent overflow-hidden">
            <div className="container mx-auto px-[1.5rem] relative z-10">
              <div className="w-full">
                {/* Features & JSON Types Section */}
                <div className="bg-black/20 rounded-lg p-4 sm:p-6 mb-8 border border-[#26ab4d]/30">
                  <h4 className="text-xl sm:text-2xl font-bold text-[#ffffff] mb-4 text-center">
                    Features & JSON Types
                  </h4>
                  
                  <div className="space-y-3">
                    {/* Cognitive Behavioral Patterns Accordion */}
                    <div className="border border-[#26ab4d]/30 rounded-lg overflow-hidden bg-black/40">
                      <button
                        onClick={() => toggleAccordion('cognitive-behavioral-patterns')}
                        className="w-full relative p-3 sm:p-4 text-left hover:bg-[#26ab4d]/10 transition-colors"
                      >
                        <div className="pr-8 sm:pr-10">
                          <h5 className="text-lg font-bold text-[#26ab4d]">Cognitive Behavioral Patterns</h5>
                          <p className="text-[#ffffff] text-sm mt-1 leading-relaxed">
                            Analyzes user interaction patterns, timing, and behavioral signals to detect anomalies and fraud indicators.
                          </p>
                        </div>
                        <span className={`absolute top-3 right-3 sm:top-4 sm:right-4 text-[#26ab4d] text-2xl font-bold transition-all duration-300 ${openAccordions['cognitive-behavioral-patterns'] ? '' : ''}`}>
                          {openAccordions['cognitive-behavioral-patterns'] ? '−' : '+'}
                        </span>
                      </button>
                      <div className={`overflow-hidden transition-all duration-300 ${openAccordions['cognitive-behavioral-patterns'] ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                        <div className="p-4 pt-0 space-y-2">
                          {/* Form Timing Insights */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('form-timing-insights')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/hardware-fingerprinting-icons/form timing imsights.png" alt="Form Timing Insights" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Form Timing Insights</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Time Taken to Fill the Form, Time Spent on Each Page
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['form-timing-insights'] ? 'mr-1' : ''}`}>
                                {openAccordions['form-timing-insights'] ? '−' : '+'}
                              </span>
                            </button>
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['form-timing-insights'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                      <div className="relative min-h-[80px]">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20">
                                    <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre overflow-x-auto">
                                      {JSON.stringify({
                                        "form_timing_insights": {
                                          "time_taken_to_fill_form_ms": 84215,
                                          "time_spent_on_each_page_ms": {
                                            "page_1": 15400,
                                            "page_2": 28650,
                                            "page_3": 22340,
                                            "page_4": 17825
                                          }
                                        }
                                      }, null, 2)}
                                    </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                                </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/hardware-fingerprinting-icons/form timing imsights.png" alt="Form Timing Insights" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('form-timing-insights')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'form-timing-insights'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'form-timing-insights'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Form Timing Insights</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Time Taken to Fill the Form, Time Spent on Each Page
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/hardware-fingerprinting-icons/form timing imsights.png" alt="Form Timing Insights" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Form Timing Insights</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Time Taken to Fill the Form, Time Spent on Each Page
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('form-timing-insights')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'form-timing-insights'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'form-timing-insights'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Touch Patterns */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('touch-patterns')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/hardware-fingerprinting-icons/touch patterns.png" alt="Touch Patterns" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Touch Patterns</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Tap Events, Long Press Events, Fling Events, Pinch Events, Pan and Screen Coordinates
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['touch-patterns'] ? 'mr-1' : ''}`}>
                                {openAccordions['touch-patterns'] ? '−' : '+'}
                              </span>
                            </button>
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['touch-patterns'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                      <div className="relative min-h-[80px]">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20">
                                    <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre overflow-x-auto">
                                      {JSON.stringify({
                                        "touch_patterns": {
                                          "tap_events": 42,
                                          "long_press_events": 3,
                                          "fling_events": 5,
                                          "pinch_events": 1,
                                          "pan_events": 7,
                                          "screen_coordinates_sample": [
                                            {
                                              "x": 112,
                                              "y": 480
                                            }
                                          ]
                                        }
                                      }, null, 2)}
                                    </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                                </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/hardware-fingerprinting-icons/touch patterns.png" alt="Touch Patterns" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('touch-patterns')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'touch-patterns'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'touch-patterns'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Touch Patterns</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Tap Events, Long Press Events, Fling Events, Pinch Events, Pan and Screen Coordinates
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/hardware-fingerprinting-icons/touch patterns.png" alt="Touch Patterns" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Touch Patterns</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Tap Events, Long Press Events, Fling Events, Pinch Events, Pan and Screen Coordinates
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('touch-patterns')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'touch-patterns'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'touch-patterns'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Device Sensor Signals */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('device-sensor-signals')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/hardware-fingerprinting-icons/device sensor signals.png" alt="Device Sensor Signals" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Device Sensor Signals</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Accelerometer Events, Gyroscope, Orientation, Proximity, Light
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['device-sensor-signals'] ? 'mr-1' : ''}`}>
                                {openAccordions['device-sensor-signals'] ? '−' : '+'}
                              </span>
                            </button>
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['device-sensor-signals'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                      <div className="relative min-h-[80px]">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20">
                                    <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre overflow-x-auto">
                                      {JSON.stringify({
                                        "device_sensor_signals": {
                                          "accelerometer_events": {
                                            "x_axis_variation": 0.14,
                                            "y_axis_variation": 0.09,
                                            "z_axis_variation": 0.18
                                          },
                                          "gyroscope": {
                                            "rotation_x": 0.021,
                                            "rotation_y": 0.017,
                                            "rotation_z": 0.033
                                          },
                                          "orientation": "portrait",
                                          "proximity_sensor": "not_blocked",
                                          "light_sensor_lux": 184
                                        }
                                      }, null, 2)}
                                    </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                                </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/hardware-fingerprinting-icons/device sensor signals.png" alt="Device Sensor Signals" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('device-sensor-signals')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'device-sensor-signals'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'device-sensor-signals'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Device Sensor Signals</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Accelerometer Events, Gyroscope, Orientation, Proximity, Light
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/hardware-fingerprinting-icons/device sensor signals.png" alt="Device Sensor Signals" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Device Sensor Signals</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Accelerometer Events, Gyroscope, Orientation, Proximity, Light
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('device-sensor-signals')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'device-sensor-signals'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'device-sensor-signals'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Keyboard Behaviour */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('keyboard-behaviour')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/hardware-fingerprinting-icons/keyboard behaviour.png" alt="Keyboard Behaviour" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Keyboard Behaviour</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Clipboard Events, Key Events, Special Key Types, Numeric Key Type
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['keyboard-behaviour'] ? 'mr-1' : ''}`}>
                                {openAccordions['keyboard-behaviour'] ? '−' : '+'}
                              </span>
                            </button>
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['keyboard-behaviour'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                      <div className="relative min-h-[80px]">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20">
                                    <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre overflow-x-auto">
{JSON.stringify({
  "keyboard_behaviour": {
    "clipboard_events": 1,
    "key_events": 187,
    "special_key_types": {
      "backspace": 12,
      "shift": 8,
      "enter": 4,
      "delete": 0
    },
    "numeric_key_type_events": 39
  }
}, null, 2)}
                          </pre>
                  </div>
                  </div>
                              </div>
                            </div>
                          </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/hardware-fingerprinting-icons/keyboard behaviour.png" alt="Keyboard Behaviour" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('keyboard-behaviour')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'keyboard-behaviour'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'keyboard-behaviour'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Keyboard Behaviour</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Clipboard Events, Key Events, Special Key Types, Numeric Key Type
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/hardware-fingerprinting-icons/keyboard behaviour.png" alt="Keyboard Behaviour" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Keyboard Behaviour</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Clipboard Events, Key Events, Special Key Types, Numeric Key Type
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('keyboard-behaviour')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'keyboard-behaviour'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'keyboard-behaviour'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-[4rem] relative z-20 bg-transparent overflow-hidden">
            {/* Multi-Layer Vector Background */}
            <div className="absolute inset-0 z-0">
              {/* Primary Vector Layer - Neural Network */}
              <svg className="absolute inset-0 w-full h-full opacity-15" viewBox="0 0 1400 900" fill="none">
                <defs>
                  <linearGradient id="neuralGradient1-ba" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#26ab4d" stopOpacity="0.9"/>
                    <stop offset="30%" stopColor="#1e8a3f" stopOpacity="0.7"/>
                    <stop offset="70%" stopColor="#26ab4d" stopOpacity="0.5"/>
                    <stop offset="100%" stopColor="#1e8a3f" stopOpacity="0.3"/>
                  </linearGradient>
                </defs>
              </svg>
            </div>
          
            <div className="container mx-auto px-[1.5rem] relative z-10">
              <div className="max-w-5xl lg:max-w-fit mx-auto text-center">
                <div className="bg-gradient-to-br from-[#26ab4d]/20 to-[#1e8a3f]/10 backdrop-blur-sm border border-[#26ab4d]/30 rounded-3xl p-8 sm:p-10 lg:p-8">
                  <h3 className="text-[1.125rem] sm:text-[1.375rem] lg:text-[2rem] xl:text-[2.25rem] font-bold mb-[2.5rem] lg:mb-3 break-words lg:leading-tight lg:px-1">
                    <span className="text-[#ffffff]">Ready to Transform Your </span>
                    <span className="text-[#26ab4d]">Behavioral Analysis</span>
                  </h3>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                    <Link 
                      to="/ask-access" 
                      className="bg-[#26ab4d] hover:bg-[#1e8a3f] text-[#ffffff] px-5 sm:px-6 lg:px-8 py-2.5 sm:py-3 lg:py-4 rounded-full text-base sm:text-lg lg:text-lg font-bold transition-all duration-300 shadow-xl inline-flex items-center justify-center w-fit sm:w-auto sm:min-w-[160px] lg:min-w-[170px] whitespace-nowrap"
                      onClick={() => {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                    >
                      Ask for access
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </>
      )}

      {/* Location Intelligence Section */}
      {currentProductId === 'location-intelligence' && (
        <>
          {/* Breadcrumb Navigation */}
          <div className="relative z-20 bg-transparent pt-[10rem] pb-4">
            <div className="container mx-auto px-[1.5rem]">
              <nav className="flex items-center gap-2 text-[0.875rem] lg:text-[0.9375rem] xl:text-[1rem] w-fit flex-wrap">
                <Link 
                  to="/" 
                  className="text-[#ffffff] hover:text-[#26ab4d] transition-colors"
                >
                  Home
                </Link>
                <span className="text-[#26ab4d]">/</span>
                <Link 
                  to="/products" 
                  className="text-[#ffffff] hover:text-[#26ab4d] transition-colors"
                >
                  Products
                </Link>
                <span className="text-[#26ab4d]">/</span>
                <Link 
                  to="/products/geolocation" 
                  className="text-[#ffffff] hover:text-[#26ab4d] transition-colors"
                >
                  Geolocation
                </Link>
                <span className="text-[#26ab4d]">/</span>
                <span className="text-[#ffffff]">
                  Location Intelligence
                </span>
              </nav>
            </div>
          </div>

          {/* Features & Return Types Section */}
          <section className="pt-[2rem] pb-0 relative z-20 bg-transparent overflow-hidden">
            <div className="container mx-auto px-[1.5rem] relative z-10">
              <div className="w-full">
                {/* Features & JSON Types Section */}
                <div className="bg-black/20 rounded-lg p-4 sm:p-6 mb-8 border border-[#26ab4d]/30">
                  <h4 className="text-xl sm:text-2xl font-bold text-[#ffffff] mb-4 text-center">
                    Features & JSON Types
                  </h4>
                  
                  <div className="space-y-3">
                    {/* Address Components Accordion */}
                    <div className="border border-[#26ab4d]/30 rounded-lg overflow-hidden bg-black/40">
                      <button
                        onClick={() => toggleAccordion('location-address-components')}
                        className="w-full relative p-3 sm:p-4 text-left hover:bg-[#26ab4d]/10 transition-colors"
                      >
                        <div className="pr-8 sm:pr-10">
                          <h5 className="text-lg font-bold text-[#26ab4d]">Address Components</h5>
                          <p className="text-[#ffffff] text-sm mt-1 leading-relaxed">
                            Provides detailed address information derived from latitude and longitude coordinates including city, district, state, pin code, street, and formatted address.
                          </p>
                        </div>
                        <span className={`absolute top-3 right-3 sm:top-4 sm:right-4 text-[#26ab4d] text-2xl font-bold transition-all duration-300 ${openAccordions['location-address-components'] ? '' : ''}`}>
                          {openAccordions['location-address-components'] ? '−' : '+'}
                        </span>
                      </button>
                      <div className={`overflow-hidden transition-all duration-300 ${openAccordions['location-address-components'] ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                        <div className="p-4 pt-0 space-y-2">
                          {/* City */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('location-city')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/location-intelligence-icons/city.png" alt="City" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">City</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Detected city name from location.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['location-city'] ? 'mr-1' : ''}`}>
                                {openAccordions['location-city'] ? '−' : '+'}
                              </span>
                            </button>
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['location-city'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                      <div className="relative min-h-[80px]">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20">
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"city": "Mumbai"}, null, 2)}
                                  </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                                </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/location-intelligence-icons/city.png" alt="City" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('location-city')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'location-city'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'location-city'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">City</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Detected city name from location.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/location-intelligence-icons/city.png" alt="City" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">City</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Detected city name from location.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('location-city')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'location-city'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'location-city'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* District */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('location-district')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/location-intelligence-icons/district.png" alt="District" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">District</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Identified district of the location.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['location-district'] ? 'mr-1' : ''}`}>
                                {openAccordions['location-district'] ? '−' : '+'}
                              </span>
                            </button>
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['location-district'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                      <div className="relative min-h-[80px]">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20">
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"district": "Mumbai Suburban District"}, null, 2)}
                                  </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                                </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/location-intelligence-icons/district.png" alt="District" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('location-district')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'location-district'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'location-district'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">District</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Identified district of the location.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/location-intelligence-icons/district.png" alt="District" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">District</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Identified district of the location.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('location-district')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'location-district'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'location-district'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* State */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('location-state')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/location-intelligence-icons/state.png" alt="State" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">State</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    State derived from latitude and longitude.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['location-state'] ? 'mr-1' : ''}`}>
                                {openAccordions['location-state'] ? '−' : '+'}
                              </span>
                            </button>
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['location-state'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                      <div className="relative min-h-[80px]">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20">
                                    <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                      {JSON.stringify({"state": "Maharashtra"}, null, 2)}
                                    </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                                </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/location-intelligence-icons/state.png" alt="State" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('location-state')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'location-state'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'location-state'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">State</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    State derived from latitude and longitude.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/location-intelligence-icons/state.png" alt="State" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">State</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      State derived from latitude and longitude.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('location-state')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'location-state'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'location-state'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Pin */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('location-pin')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/location-intelligence-icons/pin code.png" alt="Pin Code" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Pin</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Area postal code from location.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['location-pin'] ? 'mr-1' : ''}`}>
                                {openAccordions['location-pin'] ? '−' : '+'}
                              </span>
                            </button>
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['location-pin'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                      <div className="relative min-h-[80px]">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20">
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"pin": "400079"}, null, 2)}
                                  </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                                </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/location-intelligence-icons/pin code.png" alt="Pin Code" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('location-pin')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'location-pin'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'location-pin'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Pin</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Area postal code from location.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/location-intelligence-icons/pin code.png" alt="Pin Code" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Pin</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Area postal code from location.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('location-pin')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'location-pin'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'location-pin'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Street */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('location-street')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/location-intelligence-icons/street.png" alt="Street" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Street</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Main street name detected.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['location-street'] ? 'mr-1' : ''}`}>
                                {openAccordions['location-street'] ? '−' : '+'}
                              </span>
                            </button>
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['location-street'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                      <div className="relative min-h-[80px]">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20">
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"street": "Hiranandani Link Road"}, null, 2)}
                                  </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                                </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/location-intelligence-icons/street.png" alt="Street" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('location-street')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'location-street'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'location-street'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Street</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Main street name detected.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/location-intelligence-icons/street.png" alt="Street" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Street</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Main street name detected.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('location-street')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'location-street'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'location-street'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* sub_street */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('location-sub-street')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/location-intelligence-icons/sub street.png" alt="Sub Street" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Sub Street</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Nearby building or complex name.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['location-sub-street'] ? 'mr-1' : ''}`}>
                                {openAccordions['location-sub-street'] ? '−' : '+'}
                              </span>
                            </button>
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['location-sub-street'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                      <div className="relative min-h-[80px]">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20">
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"sub_street": "Wing B, Kailash Complex"}, null, 2)}
                                  </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                                </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/location-intelligence-icons/sub street.png" alt="Sub Street" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('location-sub-street')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'location-sub-street'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'location-sub-street'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Sub Street</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Nearby building or complex name.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/location-intelligence-icons/sub street.png" alt="Sub Street" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Sub Street</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Nearby building or complex name.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('location-sub-street')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'location-sub-street'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'location-sub-street'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Locality */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('location-locality')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/location-intelligence-icons/locality.png" alt="Locality" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Locality</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Local area or neighborhood name.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['location-locality'] ? 'mr-1' : ''}`}>
                                {openAccordions['location-locality'] ? '−' : '+'}
                              </span>
                            </button>
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['location-locality'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                      <div className="relative min-h-[80px]">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20">
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"locality": "Vikhroli West"}, null, 2)}
                                  </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                                </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/location-intelligence-icons/locality.png" alt="Locality" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('location-locality')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'location-locality'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'location-locality'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Locality</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Local area or neighborhood name.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/location-intelligence-icons/locality.png" alt="Locality" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Locality</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Local area or neighborhood name.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('location-locality')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'location-locality'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'location-locality'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* street_dist */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('location-street-dist')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/location-intelligence-icons/street distance.png" alt="Street Distance" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Street Distance</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Distance from nearest known street (in meters).
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['location-street-dist'] ? 'mr-1' : ''}`}>
                                {openAccordions['location-street-dist'] ? '−' : '+'}
                              </span>
                            </button>
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['location-street-dist'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                      <div className="relative min-h-[80px]">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20">
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"street_dist": 61}, null, 2)}
                                  </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                                </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/location-intelligence-icons/street distance.png" alt="Street Distance" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('location-street-dist')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'location-street-dist'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'location-street-dist'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Street Distance</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Distance from nearest known street (in meters).
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/location-intelligence-icons/street distance.png" alt="Street Distance" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Street Distance</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Distance from nearest known street (in meters).
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('location-street-dist')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'location-street-dist'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'location-street-dist'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Formatted Address */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('location-formatted-address')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/location-intelligence-icons/formatted address.png" alt="Formatted Address" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Formatted Address</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Complete address generated from coordinates.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['location-formatted-address'] ? 'mr-1' : ''}`}>
                                {openAccordions['location-formatted-address'] ? '−' : '+'}
                              </span>
                            </button>
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['location-formatted-address'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                      <div className="relative min-h-[80px]">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20">
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"formatted_address": "Hiranandani Link Road, Kailash Industrial Complex, Vikhroli West, Mumbai, Maharashtra 400079 (India)"}, null, 2)}
                                  </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/location-intelligence-icons/formatted address.png" alt="Formatted Address" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('location-formatted-address')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'location-formatted-address'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'location-formatted-address'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Formatted Address</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Complete address generated from coordinates.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/location-intelligence-icons/formatted address.png" alt="Formatted Address" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Formatted Address</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Complete address generated from coordinates.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('location-formatted-address')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'location-formatted-address'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'location-formatted-address'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Unified Location Intelligence Tool */}
          <section className="pt-0 pb-[4rem] relative z-20 bg-transparent overflow-hidden">
            <div className="container mx-auto px-[1.5rem] relative z-10">
              <div className="bg-black/20 rounded-lg p-6 sm:p-8 border border-[#26ab4d]/30">
                  <div>
                    <h4 className="text-3xl sm:text-4xl font-bold mb-6">
                      <span className="text-[#ffffff]">Location Intelligence </span>
                      <span className="bg-gradient-to-r from-[#26ab4d] via-[#1e8a3f] to-[#26ab4d] bg-clip-text text-transparent">Tool</span>
                    </h4>
                    
                    {/* Input Section */}
                    <div className="mb-6">
                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                      <div className="flex-1">
                        <input
                          type="text"
                          value={locationInput}
                          onChange={(e) => setLocationInput(e.target.value)}
                          placeholder={`"110001"`}
                          className={`w-full px-4 sm:px-6 py-3 sm:py-4 bg-[#26ab4d]/10 border-2 rounded-full text-[#ffffff] placeholder-[#9a9a9a] placeholder:font-bold focus:outline-none focus:ring-2 text-[1.125rem] ${
                            locationInput && !isValidLocation(locationInput)
                              ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                              : 'border-[#26ab4d]/50 focus:ring-[#26ab4d] focus:border-[#26ab4d]'
                          }`}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleAnalyzeLocation();
                            }
                          }}
                        />
                </div>
                      <button
                        onClick={handleAnalyzeLocation}
                        disabled={isAnalyzingLocation || !locationInput.trim()}
                        className="px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-[#26ab4d] to-[#1e8a3f] hover:from-[#1e8a3f] hover:to-[#26ab4d] text-[#ffffff] rounded-full font-semibold transition-all duration-300 shadow-lg shadow-[#26ab4d]/25 disabled:opacity-50 disabled:cursor-not-allowed min-w-[140px] text-[1.125rem] whitespace-nowrap"
                      >
                        {isAnalyzingLocation ? (
                          <span className="flex items-center justify-center">
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-[#ffffff]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
                            Analyzing...
                    </span>
                        ) : (
                          'Submit'
                        )}
                      </button>
              </div>
            </div>
            
                  {/* Results Section */}
                  {locationResult && (
                    <div className="space-y-6 relative min-h-[400px]">
                      {!accessGranted && <LockOverlay compact />}
                      <div className="bg-black/20 rounded-lg p-6 border border-[#26ab4d]/20">
                        <h5 className="text-xl font-bold text-[#ffffff] mb-4">Location Intelligence Results</h5>
                        <div className="bg-black/40 rounded-lg p-4 overflow-x-auto border border-[#26ab4d]/20 max-w-full mx-auto relative">
                          <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                            {formatFlattenedJSON(locationResult.jsonResponse)}
                          </pre>
                </div>
              </div>
                    </div>
                  )}

                    {/* Default Example - Show when no analysis */}
                  {!locationResult && (
                      <div className="bg-black/20 rounded-lg p-6 border border-[#26ab4d]/20 relative">
                      {!accessGranted && <LockOverlay compact />}
                      <p className="text-[#ffffff] text-center mb-4">Enter latitude and longitude above to see comprehensive location intelligence analysis</p>
                        <div className="bg-black/40 rounded-lg p-4 overflow-x-auto border border-[#26ab4d]/20 relative">
                          <pre className="text-xs sm:text-sm text-[#656565] font-mono whitespace-pre">
{`{
  `}<span className="text-[#656565]">"City"</span>{` : `}<span className="text-[#656565]">"Mumbai"</span>{`,
  `}<span className="text-[#656565]">"District"</span>{` : `}<span className="text-[#656565]">"Mumbai Suburban District"</span>{`,
  `}<span className="text-[#656565]">"State"</span>{` : `}<span className="text-[#656565]">"Maharashtra"</span>{`,
  `}<span className="text-[#656565]">"Pin"</span>{` : `}<span className="text-[#656565]">"400079"</span>{`,
  `}<span className="text-[#656565]">"Street"</span>{` : `}<span className="text-[#656565]">"Hiranandani Link Road"</span>{`,
  `}<span className="text-[#656565]">"sub_street"</span>{` : `}<span className="text-[#656565]">"Wing B, Kailash Complex"</span>{`,
  `}<span className="text-[#656565]">"Locality"</span>{` : `}<span className="text-[#656565]">"Vikhroli West"</span>{`,
  `}<span className="text-[#656565]">"Street_dist"</span>{` : `}<span className="text-[#656565]">61</span>{`,
  `}<span className="text-[#656565]">"Formatted_Address"</span>{` : `}<span className="text-[#656565]">"Hiranandani Link Road, Kailash Industrial Complex, Vikhroli West, Mumbai, Maharashtra 400079 (India)"</span>{`
}`}
                          </pre>
                  </div>
                  </div>
                    )}
                </div>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-[4rem] relative z-20 bg-transparent overflow-hidden">
            {/* Multi-Layer Vector Background */}
            <div className="absolute inset-0 z-0">
              {/* Primary Vector Layer - Neural Network */}
              <svg className="absolute inset-0 w-full h-full opacity-15" viewBox="0 0 1400 900" fill="none">
                <defs>
                  <linearGradient id="neuralGradient1-li" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#26ab4d" stopOpacity="0.9"/>
                    <stop offset="30%" stopColor="#1e8a3f" stopOpacity="0.7"/>
                    <stop offset="70%" stopColor="#26ab4d" stopOpacity="0.5"/>
                    <stop offset="100%" stopColor="#1e8a3f" stopOpacity="0.3"/>
                  </linearGradient>
                </defs>
              </svg>
            </div>
          
            <div className="container mx-auto px-[1.5rem] relative z-10">
              <div className="max-w-5xl lg:max-w-fit mx-auto text-center">
                <div className="bg-gradient-to-br from-[#26ab4d]/20 to-[#1e8a3f]/10 backdrop-blur-sm border border-[#26ab4d]/30 rounded-3xl p-8 sm:p-10 lg:p-8">
                  <h3 className="text-[1.125rem] sm:text-[1.375rem] lg:text-[2rem] xl:text-[2.25rem] font-bold mb-[2.5rem] lg:mb-3 break-words lg:leading-tight lg:px-1">
                    <span className="text-[#ffffff]">Ready to Transform Your </span>
                    <span className="text-[#26ab4d]">Location Intelligence</span>
                  </h3>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                    <Link 
                      to="/ask-access" 
                      className="bg-[#26ab4d] hover:bg-[#1e8a3f] text-[#ffffff] px-5 sm:px-6 lg:px-8 py-2.5 sm:py-3 lg:py-4 rounded-full text-base sm:text-lg lg:text-lg font-bold transition-all duration-300 shadow-xl inline-flex items-center justify-center w-fit sm:w-auto sm:min-w-[160px] lg:min-w-[170px] whitespace-nowrap"
                      onClick={() => {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                    >
                      Ask for access
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </>
      )}

      {/* Mobile to Address Section */}
      {currentProductId === 'mobile-to-address' && (
        <>
          {/* Features & Return Types Section */}
          <section className="pt-[2rem] pb-0 relative z-20 bg-transparent overflow-hidden">
            <div className="container mx-auto px-[1.5rem] relative z-10">
              <div className="w-full">
                {/* Features & JSON Types Section */}
                <div className="bg-black/20 rounded-lg p-4 sm:p-6 mb-8 border border-[#26ab4d]/30">
                  <h4 className="text-xl sm:text-2xl font-bold text-[#ffffff] mb-4 text-center">
                    Features & JSON Types
                  </h4>
                  
                  <div className="space-y-3">
                    {/* Address Components Accordion */}
                    <div className="border border-[#26ab4d]/30 rounded-lg overflow-hidden bg-black/40">
                      <button
                        onClick={() => toggleAccordion('mobile-to-address-components')}
                        className="w-full relative p-3 sm:p-4 text-left hover:bg-[#26ab4d]/10 transition-colors"
                      >
                        <div className="pr-8 sm:pr-10">
                          <h5 className="text-lg font-bold text-[#26ab4d]">Address Components</h5>
                          <p className="text-[#ffffff] text-sm mt-1 leading-relaxed">
                            Provides detailed address information derived from mobile number including customer name, email, address lines, city, state, country, and delivery date.
                          </p>
                        </div>
                        <span className={`absolute top-3 right-3 sm:top-4 sm:right-4 text-[#26ab4d] text-2xl font-bold transition-all duration-300 ${openAccordions['mobile-to-address-components'] ? '' : ''}`}>
                          {openAccordions['mobile-to-address-components'] ? '−' : '+'}
                        </span>
                      </button>
                      <div className={`overflow-hidden transition-all duration-300 ${openAccordions['mobile-to-address-components'] ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                        <div className="p-4 pt-0 space-y-2">
                          {/* First Name */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('mobile-to-address-first-name')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/mobile-to-sources-address-icons/first name.png" alt="First Name" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">First Name</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Customer's first name linked to the mobile number.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['mobile-to-address-first-name'] ? 'mr-1' : ''}`}>
                                {openAccordions['mobile-to-address-first-name'] ? '−' : '+'}
                              </span>
                            </button>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/mobile-to-sources-address-icons/first name.png" alt="First Name" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('mobile-to-address-first-name')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'mobile-to-address-first-name'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'mobile-to-address-first-name'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">First Name</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Customer's first name linked to the mobile number.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/mobile-to-sources-address-icons/first name.png" alt="First Name" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">First Name</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Customer's first name linked to the mobile number.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('mobile-to-address-first-name')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'mobile-to-address-first-name'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'mobile-to-address-first-name'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['mobile-to-address-first-name'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"first_name": "Karan"}, null, 2)}
                                  </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Last Name */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('mobile-to-address-last-name')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/mobile-to-sources-address-icons/last name.png" alt="Last Name" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Last Name</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Customer's last name linked to the mobile number.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['mobile-to-address-last-name'] ? 'mr-1' : ''}`}>
                                {openAccordions['mobile-to-address-last-name'] ? '−' : '+'}
                              </span>
                            </button>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/mobile-to-sources-address-icons/last name.png" alt="Last Name" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('mobile-to-address-last-name')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'mobile-to-address-last-name'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'mobile-to-address-last-name'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Last Name</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Customer's last name linked to the mobile number.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/mobile-to-sources-address-icons/last name.png" alt="Last Name" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Last Name</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Customer's last name linked to the mobile number.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('mobile-to-address-last-name')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'mobile-to-address-last-name'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'mobile-to-address-last-name'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['mobile-to-address-last-name'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"last_name": "Deshmukh"}, null, 2)}
                                  </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Email */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('mobile-to-address-email')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/mobile-to-sources-address-icons/email.png" alt="Email" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Email</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Registered email ID of the customer.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['mobile-to-address-email'] ? 'mr-1' : ''}`}>
                                {openAccordions['mobile-to-address-email'] ? '−' : '+'}
                              </span>
                            </button>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/mobile-to-sources-address-icons/email.png" alt="Email" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('mobile-to-address-email')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'mobile-to-address-email'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'mobile-to-address-email'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Email</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Registered email ID of the customer.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/mobile-to-sources-address-icons/email.png" alt="Email" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Email</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Registered email ID of the customer.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('mobile-to-address-email')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'mobile-to-address-email'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'mobile-to-address-email'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['mobile-to-address-email'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"email": "karan.deshmukh@example.com"}, null, 2)}
                                  </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* ISD Code */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('mobile-to-address-isd-code')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/mobile-to-sources-address-icons/isd code.png" alt="ISD Code" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">ISD Code</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Country calling code of the mobile number.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['mobile-to-address-isd-code'] ? 'mr-1' : ''}`}>
                                {openAccordions['mobile-to-address-isd-code'] ? '−' : '+'}
                              </span>
                            </button>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/mobile-to-sources-address-icons/isd code.png" alt="ISD Code" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('mobile-to-address-isd-code')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'mobile-to-address-isd-code'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'mobile-to-address-isd-code'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">ISD Code</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Country calling code of the mobile number.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/mobile-to-sources-address-icons/isd code.png" alt="ISD Code" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">ISD Code</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Country calling code of the mobile number.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('mobile-to-address-isd-code')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'mobile-to-address-isd-code'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'mobile-to-address-isd-code'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['mobile-to-address-isd-code'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"isd_code": "+91"}, null, 2)}
                                  </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Line 1 */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('mobile-to-address-line-1')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/mobile-to-sources-address-icons/line 1.png" alt="Line 1" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Line 1</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    First line of the customer's address.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['mobile-to-address-line-1'] ? 'mr-1' : ''}`}>
                                {openAccordions['mobile-to-address-line-1'] ? '−' : '+'}
                              </span>
                            </button>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/mobile-to-sources-address-icons/line 1.png" alt="Line 1" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('mobile-to-address-line-1')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'mobile-to-address-line-1'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'mobile-to-address-line-1'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Line 1</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    First line of the customer's address.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/mobile-to-sources-address-icons/line 1.png" alt="Line 1" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Line 1</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      First line of the customer's address.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('mobile-to-address-line-1')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'mobile-to-address-line-1'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'mobile-to-address-line-1'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['mobile-to-address-line-1'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"line_1": "Flat 402, Shantivan Residency"}, null, 2)}
                                  </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Line 2 */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('mobile-to-address-line-2')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/mobile-to-sources-address-icons/line 2.png" alt="Line 2" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Line 2</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Second line or nearby landmark of the address.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['mobile-to-address-line-2'] ? 'mr-1' : ''}`}>
                                {openAccordions['mobile-to-address-line-2'] ? '−' : '+'}
                              </span>
                            </button>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/mobile-to-sources-address-icons/line 2.png" alt="Line 2" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('mobile-to-address-line-2')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'mobile-to-address-line-2'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'mobile-to-address-line-2'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Line 2</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Second line or nearby landmark of the address.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/mobile-to-sources-address-icons/line 2.png" alt="Line 2" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Line 2</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Second line or nearby landmark of the address.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('mobile-to-address-line-2')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'mobile-to-address-line-2'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'mobile-to-address-line-2'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['mobile-to-address-line-2'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"line_2": "Near River Park"}, null, 2)}
                                  </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* City */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('mobile-to-address-city')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/mobile-to-sources-address-icons/city.png" alt="City" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">City</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    City name from the registered address.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['mobile-to-address-city'] ? 'mr-1' : ''}`}>
                                {openAccordions['mobile-to-address-city'] ? '−' : '+'}
                              </span>
                            </button>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/mobile-to-sources-address-icons/city.png" alt="City" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('mobile-to-address-city')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'mobile-to-address-city'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'mobile-to-address-city'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">City</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    City name from the registered address.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/mobile-to-sources-address-icons/city.png" alt="City" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">City</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      City name from the registered address.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('mobile-to-address-city')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'mobile-to-address-city'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'mobile-to-address-city'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['mobile-to-address-city'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                              <div className="mt-2">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                    <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                      {JSON.stringify({"city": "Pune"}, null, 2)}
                                    </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* State */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('mobile-to-address-state')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/mobile-to-sources-address-icons/state.png" alt="State" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">State</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    State name from the registered address.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['mobile-to-address-state'] ? 'mr-1' : ''}`}>
                                {openAccordions['mobile-to-address-state'] ? '−' : '+'}
                              </span>
                            </button>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/mobile-to-sources-address-icons/state.png" alt="State" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('mobile-to-address-state')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'mobile-to-address-state'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'mobile-to-address-state'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">State</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    State name from the registered address.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/mobile-to-sources-address-icons/state.png" alt="State" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">State</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      State name from the registered address.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('mobile-to-address-state')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'mobile-to-address-state'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'mobile-to-address-state'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['mobile-to-address-state'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                              <div className="mt-2">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                    <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                      {JSON.stringify({"state": "Maharashtra"}, null, 2)}
                                    </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Country */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('mobile-to-address-country')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/mobile-to-sources-address-icons/country.png" alt="Country" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Country</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Country name linked to the mobile number.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['mobile-to-address-country'] ? 'mr-1' : ''}`}>
                                {openAccordions['mobile-to-address-country'] ? '−' : '+'}
                              </span>
                            </button>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/mobile-to-sources-address-icons/country.png" alt="Country" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('mobile-to-address-country')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'mobile-to-address-country'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'mobile-to-address-country'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Country</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Country name linked to the mobile number.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/mobile-to-sources-address-icons/country.png" alt="Country" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Country</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Country name linked to the mobile number.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('mobile-to-address-country')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'mobile-to-address-country'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'mobile-to-address-country'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['mobile-to-address-country'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"country": "India"}, null, 2)}
                                  </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Country Code */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('mobile-to-address-country-code')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/mobile-to-sources-address-icons/country code.png" alt="Country Code" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Country Code</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Short code representing the country.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['mobile-to-address-country-code'] ? 'mr-1' : ''}`}>
                                {openAccordions['mobile-to-address-country-code'] ? '−' : '+'}
                              </span>
                            </button>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/mobile-to-sources-address-icons/country code.png" alt="Country Code" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('mobile-to-address-country-code')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'mobile-to-address-country-code'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'mobile-to-address-country-code'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Country Code</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Short code representing the country.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/mobile-to-sources-address-icons/country code.png" alt="Country Code" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Country Code</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Short code representing the country.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('mobile-to-address-country-code')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'mobile-to-address-country-code'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'mobile-to-address-country-code'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['mobile-to-address-country-code'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"country_code": "IN"}, null, 2)}
                                  </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Pincode */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('mobile-to-address-pincode')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/mobile-to-sources-address-icons/pin code.png" alt="Pin Code" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Pincode</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Postal code of the customer's address.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['mobile-to-address-pincode'] ? 'mr-1' : ''}`}>
                                {openAccordions['mobile-to-address-pincode'] ? '−' : '+'}
                              </span>
                            </button>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/mobile-to-sources-address-icons/pin code.png" alt="Pin Code" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('mobile-to-address-pincode')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'mobile-to-address-pincode'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'mobile-to-address-pincode'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Pincode</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Postal code of the customer's address.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/mobile-to-sources-address-icons/pin code.png" alt="Pin Code" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Pincode</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Postal code of the customer's address.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('mobile-to-address-pincode')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'mobile-to-address-pincode'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'mobile-to-address-pincode'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['mobile-to-address-pincode'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"pin_code": 411038}, null, 2)}
                                  </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Last Ecommerce Delivery Date */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('mobile-to-address-last-delivery-date')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/mobile-to-sources-address-icons/ecom late delivery date.png" alt="Last Ecommerce Delivery Date" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Last Ecommerce Delivery Date</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Most recent delivery or activity date for this address.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['mobile-to-address-last-delivery-date'] ? 'mr-1' : ''}`}>
                                {openAccordions['mobile-to-address-last-delivery-date'] ? '−' : '+'}
                              </span>
                            </button>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/mobile-to-sources-address-icons/ecom late delivery date.png" alt="Last Ecommerce Delivery Date" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('mobile-to-address-last-delivery-date')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'mobile-to-address-last-delivery-date'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'mobile-to-address-last-delivery-date'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Last Ecommerce Delivery Date</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Most recent delivery or activity date for this address.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/mobile-to-sources-address-icons/ecom late delivery date.png" alt="Last Ecommerce Delivery Date" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Last Ecommerce Delivery Date</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Most recent delivery or activity date for this address.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('mobile-to-address-last-delivery-date')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'mobile-to-address-last-delivery-date'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'mobile-to-address-last-delivery-date'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['mobile-to-address-last-delivery-date'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"last_delivery_date": "2025-01-14"}, null, 2)}
                                  </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Unified Mobile to Address Tool */}
          <section className="pt-0 pb-[4rem] relative z-20 bg-transparent overflow-hidden">
            <div className="container mx-auto px-[1.5rem] relative z-10">
              <div className="bg-black/20 rounded-lg p-6 sm:p-8 border border-[#26ab4d]/30">
                  <div>
                    <h4 className="text-3xl sm:text-4xl font-bold mb-6">
                      <span className="text-[#ffffff]">Mobile to Address </span>
                      <span className="bg-gradient-to-r from-[#26ab4d] via-[#1e8a3f] to-[#26ab4d] bg-clip-text text-transparent">Tool</span>
                    </h4>
                    
                    {/* Input Section */}
                    <div className="mb-6">
                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                      <div className="flex-1">
                        <input
                          type="text"
                          value={mobileToAddressInput}
                          onChange={(e) => setMobileToAddressInput(e.target.value)}
                          placeholder={`"9876012345"`}
                          className={`w-full px-4 sm:px-6 py-3 sm:py-4 bg-[#26ab4d]/10 border-2 rounded-full text-[#ffffff] placeholder-[#9a9a9a] placeholder:font-bold focus:outline-none focus:ring-2 text-[1.125rem] ${
                            mobileToAddressInput && !isValidPhone(mobileToAddressInput)
                              ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                              : 'border-[#26ab4d]/50 focus:ring-[#26ab4d] focus:border-[#26ab4d]'
                          }`}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleAnalyzeMobileToAddress();
                            }
                          }}
                        />
                </div>
                      <button
                        onClick={handleAnalyzeMobileToAddress}
                        disabled={isAnalyzingMobileToAddress || !mobileToAddressInput.trim()}
                        className="px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-[#26ab4d] to-[#1e8a3f] hover:from-[#1e8a3f] hover:to-[#26ab4d] text-[#ffffff] rounded-full font-semibold transition-all duration-300 shadow-lg shadow-[#26ab4d]/25 disabled:opacity-50 disabled:cursor-not-allowed min-w-[140px] text-[1.125rem] whitespace-nowrap"
                      >
                        {isAnalyzingMobileToAddress ? (
                          <span className="flex items-center justify-center">
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-[#ffffff]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
                            Analyzing...
                    </span>
                        ) : (
                          'Submit'
                        )}
                      </button>
              </div>
            </div>
            
                  {/* Results Section */}
                  {mobileToAddressResult && (
                    <div className="space-y-6 relative">
                      {!accessGranted && (
                        <div className="absolute inset-0 bg-black/95 backdrop-blur-md z-[100] flex items-center justify-center rounded-lg">
                          <Link
                            to="/ask-access"
                            className="px-6 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center"
                          >
                            <span className="text-base text-white font-semibold">Ask for access</span>
                          </Link>
                        </div>
                      )}
                      <div className="bg-black/20 rounded-lg p-6 border border-[#26ab4d]/20">
                        <h5 className="text-xl font-bold text-[#ffffff] mb-4">Mobile to Address Results</h5>
                        <div className="bg-black/40 rounded-lg p-4 overflow-x-auto border border-[#26ab4d]/20">
                          <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                            {formatFlattenedJSON(mobileToAddressResult.jsonResponse)}
                          </pre>
                </div>
              </div>
                    </div>
                  )}

                    {/* Default Example - Show when no analysis */}
                  {!mobileToAddressResult && (
                      <div className="bg-black/20 rounded-lg p-6 border border-[#26ab4d]/20">
                      <p className="text-[#ffffff] text-center mb-4">Enter a mobile number above to see comprehensive address information</p>
                        <div className="bg-black/40 rounded-lg p-4 overflow-x-auto border border-[#26ab4d]/20 relative">
                          {!accessGranted && (
                            <div className="absolute inset-0 bg-black/95 backdrop-blur-md z-[100] flex items-center justify-center rounded">
                              <Link
                                to="/ask-access"
                                className="px-6 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center"
                              >
                                <span className="text-base text-white font-semibold">Ask for access</span>
                              </Link>
                            </div>
                          )}
                          <pre className="text-xs sm:text-sm text-[#656565] font-mono whitespace-pre">
{`{
  `}<span className="text-[#656565]">"first_name"</span>{` : `}<span className="text-[#656565]">"Karan"</span>{`,
  `}<span className="text-[#656565]">"last_name"</span>{` : `}<span className="text-[#656565]">"Deshmukh"</span>{`,
  `}<span className="text-[#656565]">"email"</span>{` : `}<span className="text-[#656565]">"karan.deshmukh@example.com"</span>{`,
  `}<span className="text-[#656565]">"isd_code"</span>{` : `}<span className="text-[#656565]">"+91"</span>{`,
  `}<span className="text-[#656565]">"line_1"</span>{` : `}<span className="text-[#656565]">"Flat 402, Shantivan Residency"</span>{`,
  `}<span className="text-[#656565]">"line_2"</span>{` : `}<span className="text-[#656565]">"Near River Park"</span>{`,
  `}<span className="text-[#656565]">"city"</span>{` : `}<span className="text-[#656565]">"Pune"</span>{`,
  `}<span className="text-[#656565]">"state"</span>{` : `}<span className="text-[#656565]">"Maharashtra"</span>{`,
  `}<span className="text-[#656565]">"state_code"</span>{` : `}<span className="text-[#656565]">"MH"</span>{`,
  `}<span className="text-[#656565]">"country"</span>{` : `}<span className="text-[#656565]">"India"</span>{`,
  `}<span className="text-[#656565]">"country_code"</span>{` : `}<span className="text-[#656565]">"IN"</span>{`,
  `}<span className="text-[#656565]">"pin_code"</span>{` : `}<span className="text-[#656565]">411038</span>{`,
  `}<span className="text-[#656565]">"last_delivery_date"</span>{` : `}<span className="text-[#656565]">"2025-01-14"</span>{`
}`}
                          </pre>
                  </div>
                  </div>
                    )}
                </div>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-[4rem] relative z-20 bg-transparent overflow-hidden">
            {/* Multi-Layer Vector Background */}
            <div className="absolute inset-0 z-0">
              {/* Primary Vector Layer - Neural Network */}
              <svg className="absolute inset-0 w-full h-full opacity-15" viewBox="0 0 1400 900" fill="none">
                <defs>
                  <linearGradient id="neuralGradient1-mta" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#26ab4d" stopOpacity="0.9"/>
                    <stop offset="30%" stopColor="#1e8a3f" stopOpacity="0.7"/>
                    <stop offset="70%" stopColor="#26ab4d" stopOpacity="0.5"/>
                    <stop offset="100%" stopColor="#1e8a3f" stopOpacity="0.3"/>
                  </linearGradient>
                </defs>
              </svg>
            </div>
          
            <div className="container mx-auto px-[1.5rem] relative z-10">
              <div className="max-w-5xl lg:max-w-fit mx-auto text-center">
                <div className="bg-gradient-to-br from-[#26ab4d]/20 to-[#1e8a3f]/10 backdrop-blur-sm border border-[#26ab4d]/30 rounded-3xl p-8 sm:p-10 lg:p-8">
                  <h3 className="text-[1.125rem] sm:text-[1.375rem] lg:text-[2rem] xl:text-[2.25rem] font-bold mb-[2.5rem] lg:mb-3 break-words lg:leading-tight lg:px-1">
                    <span className="text-[#ffffff]">Ready to Transform Your </span>
                    <span className="text-[#26ab4d]">Mobile to Address</span>
                  </h3>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                    <Link 
                      to="/ask-access" 
                      className="bg-[#26ab4d] hover:bg-[#1e8a3f] text-[#ffffff] px-5 sm:px-6 lg:px-8 py-2.5 sm:py-3 lg:py-4 rounded-full text-base sm:text-lg lg:text-lg font-bold transition-all duration-300 shadow-xl inline-flex items-center justify-center w-fit sm:w-auto sm:min-w-[160px] lg:min-w-[170px] whitespace-nowrap"
                      onClick={() => {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                    >
                      Ask for access
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </>
      )}

      {/* Mobile to UPI Section */}
      {currentProductId === 'mobile-to-upi' && (
        <>
          {/* Features & Return Types Section */}
          <section className="pt-[2rem] pb-0 relative z-20 bg-transparent overflow-hidden">
            <div className="container mx-auto px-[1.5rem] relative z-10">
              <div className="w-full">
                {/* Features & JSON Types Section */}
                <div className="bg-black/20 rounded-lg p-4 sm:p-6 mb-8 border border-[#26ab4d]/30">
                  <h4 className="text-xl sm:text-2xl font-bold text-[#ffffff] mb-4 text-center">
                    Features & JSON Types
                  </h4>
                  
                  <div className="space-y-3">
                    {/* Fetch Bank Details Accordion */}
                    <div className="border border-[#26ab4d]/30 rounded-lg overflow-hidden bg-black/40">
                      <button
                        onClick={() => toggleAccordion('mobile-to-upi-bank-details')}
                        className="w-full relative p-3 sm:p-4 text-left hover:bg-[#26ab4d]/10 transition-colors"
                      >
                        <div className="pr-8 sm:pr-10">
                          <h5 className="text-lg font-bold text-[#26ab4d]">Fetch Bank Details</h5>
                          <p className="text-[#ffffff] text-sm mt-1 leading-relaxed">
                            Provides bank account information linked to the mobile number including account holder name, UTR, account number, IFSC code, and UPI ID.
                          </p>
                        </div>
                        <span className={`absolute top-3 right-3 sm:top-4 sm:right-4 text-[#26ab4d] text-2xl font-bold transition-all duration-300 ${openAccordions['mobile-to-upi-bank-details'] ? '' : ''}`}>
                          {openAccordions['mobile-to-upi-bank-details'] ? '−' : '+'}
                        </span>
                      </button>
                      <div className={`overflow-hidden transition-all duration-300 ${openAccordions['mobile-to-upi-bank-details'] ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                        <div className="p-4 pt-0 space-y-2">
                          {/* Name */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('mobile-to-upi-name')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/mobile-to-sources-upi-icons/Bank/name.png" alt="Name" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Name</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Name linked to the mobile number.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['mobile-to-upi-name'] ? 'mr-1' : ''}`}>
                                {openAccordions['mobile-to-upi-name'] ? '−' : '+'}
                              </span>
                            </button>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/mobile-to-sources-upi-icons/Bank/name.png" alt="Name" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('mobile-to-upi-name')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'mobile-to-upi-name'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'mobile-to-upi-name'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Name</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Name linked to the mobile number.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/mobile-to-sources-upi-icons/Bank/name.png" alt="Name" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Name</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Name linked to the mobile number.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('mobile-to-upi-name')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'mobile-to-upi-name'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'mobile-to-upi-name'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['mobile-to-upi-name'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                              <div className="mt-2">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                    <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                      {JSON.stringify({"name": "HERO INDUSTRY TEXT PVT LTD"}, null, 2)}
                                    </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* UTR */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('mobile-to-upi-utr')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/mobile-to-sources-upi-icons/Bank/utr.png" alt="UTR" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">UTR</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Unique transaction reference id.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['mobile-to-upi-utr'] ? 'mr-1' : ''}`}>
                                {openAccordions['mobile-to-upi-utr'] ? '−' : '+'}
                              </span>
                            </button>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/mobile-to-sources-upi-icons/Bank/utr.png" alt="UTR" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('mobile-to-upi-utr')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'mobile-to-upi-utr'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'mobile-to-upi-utr'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">UTR</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Unique transaction reference id.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/mobile-to-sources-upi-icons/Bank/utr.png" alt="UTR" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">UTR</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Unique transaction reference id.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('mobile-to-upi-utr')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'mobile-to-upi-utr'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'mobile-to-upi-utr'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['mobile-to-upi-utr'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"utr": "P2509251722124359741384986"}, null, 2)}
                                  </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Account Number */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('mobile-to-upi-account-number')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/mobile-to-sources-upi-icons/Bank/ac no.png" alt="Account Number" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Account Number</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Bank account linked to the mobile.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['mobile-to-upi-account-number'] ? 'mr-1' : ''}`}>
                                {openAccordions['mobile-to-upi-account-number'] ? '−' : '+'}
                              </span>
                            </button>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/mobile-to-sources-upi-icons/Bank/ac no.png" alt="Account Number" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('mobile-to-upi-account-number')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'mobile-to-upi-account-number'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'mobile-to-upi-account-number'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Account Number</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Bank account linked to the mobile.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/mobile-to-sources-upi-icons/Bank/ac no.png" alt="Account Number" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Account Number</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Bank account linked to the mobile.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('mobile-to-upi-account-number')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'mobile-to-upi-account-number'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'mobile-to-upi-account-number'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['mobile-to-upi-account-number'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"account_number": 657104000201315}, null, 2)}
                                  </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* IFSC */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('mobile-to-upi-ifsc')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/mobile-to-sources-upi-icons/Bank/ifsc code.png" alt="IFSC" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">IFSC</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Bank branch code.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['mobile-to-upi-ifsc'] ? 'mr-1' : ''}`}>
                                {openAccordions['mobile-to-upi-ifsc'] ? '−' : '+'}
                              </span>
                            </button>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/mobile-to-sources-upi-icons/Bank/ifsc code.png" alt="IFSC" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('mobile-to-upi-ifsc')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'mobile-to-upi-ifsc'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'mobile-to-upi-ifsc'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">IFSC</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Bank branch code.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/mobile-to-sources-upi-icons/Bank/ifsc code.png" alt="IFSC" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">IFSC</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Bank branch code.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('mobile-to-upi-ifsc')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'mobile-to-upi-ifsc'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'mobile-to-upi-ifsc'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['mobile-to-upi-ifsc'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"ifsc": "ABCD0000000"}, null, 2)}
                                  </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Fetch UPI Id Accordion */}
                    <div className="border border-[#26ab4d]/30 rounded-lg overflow-hidden bg-black/40">
                      <button
                        onClick={() => toggleAccordion('mobile-to-upi-upi-id')}
                        className="w-full relative p-3 sm:p-4 text-left hover:bg-[#26ab4d]/10 transition-colors"
                      >
                        <div className="pr-8 sm:pr-10">
                          <h5 className="text-lg font-bold text-[#26ab4d]">Fetch UPI Id</h5>
                          <p className="text-[#ffffff] text-sm mt-1 leading-relaxed">
                            Provides UPI ID information linked to the mobile number including UPI ID, account holder name, and validity status.
                          </p>
                        </div>
                        <span className={`absolute top-3 right-3 sm:top-4 sm:right-4 text-[#26ab4d] text-2xl font-bold transition-all duration-300 ${openAccordions['mobile-to-upi-upi-id'] ? '' : ''}`}>
                          {openAccordions['mobile-to-upi-upi-id'] ? '−' : '+'}
                        </span>
                      </button>
                      <div className={`overflow-hidden transition-all duration-300 ${openAccordions['mobile-to-upi-upi-id'] ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                        <div className="p-4 pt-0 space-y-2">
                          {/* UPI ID */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('mobile-to-upi-upi-id-field')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/mobile-to-sources-upi-icons/UPI/upi.png" alt="UPI ID" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">UPI ID</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    UPI ID linked to the mobile number.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['mobile-to-upi-upi-id-field'] ? 'mr-1' : ''}`}>
                                {openAccordions['mobile-to-upi-upi-id-field'] ? '−' : '+'}
                              </span>
                            </button>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/mobile-to-sources-upi-icons/UPI/upi.png" alt="UPI ID" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('mobile-to-upi-upi-id-field')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'mobile-to-upi-upi-id-field'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'mobile-to-upi-upi-id-field'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">UPI ID</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    UPI ID linked to the mobile number.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/mobile-to-sources-upi-icons/UPI/upi.png" alt="UPI ID" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">UPI ID</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      UPI ID linked to the mobile number.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('mobile-to-upi-upi-id-field')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'mobile-to-upi-upi-id-field'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'mobile-to-upi-upi-id-field'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['mobile-to-upi-upi-id-field'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"upi_id": "patternsheet097-10@okicici"}, null, 2)}
                                  </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Name */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('mobile-to-upi-name-field')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/mobile-to-sources-upi-icons/UPI/name.png" alt="Name" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Name</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Account holder's name from the UPI.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['mobile-to-upi-name-field'] ? 'mr-1' : ''}`}>
                                {openAccordions['mobile-to-upi-name-field'] ? '−' : '+'}
                              </span>
                            </button>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/mobile-to-sources-upi-icons/UPI/name.png" alt="Name" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('mobile-to-upi-name-field')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'mobile-to-upi-name-field'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'mobile-to-upi-name-field'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Name</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Account holder's name from the UPI.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/mobile-to-sources-upi-icons/UPI/name.png" alt="Name" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Name</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Account holder's name from the UPI.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('mobile-to-upi-name-field')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'mobile-to-upi-name-field'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'mobile-to-upi-name-field'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['mobile-to-upi-name-field'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                              <div className="mt-2">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                    <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                      {JSON.stringify({"name": "HERO INDUSTRY TEXT PVT LTD"}, null, 2)}
                                    </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Status */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('mobile-to-upi-status')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/mobile-to-sources-upi-icons/UPI/status.png" alt="Status" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Status</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Validity status of the UPI ID.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['mobile-to-upi-status'] ? 'mr-1' : ''}`}>
                                {openAccordions['mobile-to-upi-status'] ? '−' : '+'}
                              </span>
                            </button>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/mobile-to-sources-upi-icons/UPI/status.png" alt="Status" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('mobile-to-upi-status')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'mobile-to-upi-status'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'mobile-to-upi-status'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Status</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Validity status of the UPI ID.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/mobile-to-sources-upi-icons/UPI/status.png" alt="Status" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Status</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Validity status of the UPI ID.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('mobile-to-upi-status')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'mobile-to-upi-status'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'mobile-to-upi-status'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['mobile-to-upi-status'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"status": "VALID"}, null, 2)}
                                  </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Unified Mobile to UPI Tool */}
          <section className="pt-0 pb-[4rem] relative z-20 bg-transparent overflow-hidden">
            <div className="container mx-auto px-[1.5rem] relative z-10">
              <div className="bg-black/20 rounded-lg p-6 sm:p-8 border border-[#26ab4d]/30">
                  <div>
                    <h4 className="text-3xl sm:text-4xl font-bold mb-6">
                      <span className="text-[#ffffff]">Mobile to UPI </span>
                      <span className="bg-gradient-to-r from-[#26ab4d] via-[#1e8a3f] to-[#26ab4d] bg-clip-text text-transparent">Tool</span>
                    </h4>
                    
                    {/* Input Section */}
                    <div className="mb-6">
                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                      <div className="flex-1">
                        <input
                          type="text"
                          value={mobileToUPIInput}
                          onChange={(e) => setMobileToUPIInput(e.target.value)}
                          placeholder={`"9876012345"`}
                          className={`w-full px-4 sm:px-6 py-3 sm:py-4 bg-[#26ab4d]/10 border-2 rounded-full text-[#ffffff] placeholder-[#9a9a9a] placeholder:font-bold focus:outline-none focus:ring-2 text-[1.125rem] ${
                            mobileToUPIInput && !isValidPhone(mobileToUPIInput)
                              ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                              : 'border-[#26ab4d]/50 focus:ring-[#26ab4d] focus:border-[#26ab4d]'
                          }`}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleAnalyzeMobileToUPI();
                            }
                          }}
                        />
                </div>
                      <button
                        onClick={handleAnalyzeMobileToUPI}
                        disabled={isAnalyzingMobileToUPI || !mobileToUPIInput.trim()}
                        className="px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-[#26ab4d] to-[#1e8a3f] hover:from-[#1e8a3f] hover:to-[#26ab4d] text-[#ffffff] rounded-full font-semibold transition-all duration-300 shadow-lg shadow-[#26ab4d]/25 disabled:opacity-50 disabled:cursor-not-allowed min-w-[140px] text-[1.125rem] whitespace-nowrap"
                      >
                        {isAnalyzingMobileToUPI ? (
                          <span className="flex items-center justify-center">
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-[#ffffff]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
                            Analyzing...
                    </span>
                        ) : (
                          'Submit'
                        )}
                      </button>
              </div>
            </div>
            
                  {/* Results Section */}
                  {mobileToUPIResult && (
                    <div className="space-y-6 relative">
                      {!accessGranted && (
                        <div className="absolute inset-0 bg-black/95 backdrop-blur-md z-[100] flex items-center justify-center rounded-lg">
                          <Link
                            to="/ask-access"
                            className="px-6 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center"
                          >
                            <span className="text-base text-white font-semibold">Ask for access</span>
                          </Link>
                        </div>
                      )}
                      <div className="bg-black/20 rounded-lg p-6 border border-[#26ab4d]/20">
                        <h5 className="text-xl font-bold text-[#ffffff] mb-4">Mobile to UPI Results</h5>
                        <div className="bg-black/40 rounded-lg p-4 overflow-x-auto border border-[#26ab4d]/20">
                          <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                            {formatFlattenedJSON(mobileToUPIResult.jsonResponse)}
                          </pre>
                </div>
              </div>
                    </div>
                  )}

                    {/* Default Example - Show when no analysis */}
                  {!mobileToUPIResult && (
                      <div className="bg-black/20 rounded-lg p-6 border border-[#26ab4d]/20">
                      <p className="text-[#ffffff] text-center mb-4">Enter a mobile number above to see comprehensive UPI and bank details</p>
                        <div className="bg-black/40 rounded-lg p-4 overflow-x-auto border border-[#26ab4d]/20 relative">
                          {!accessGranted && (
                            <div className="absolute inset-0 bg-black/95 backdrop-blur-md z-[100] flex items-center justify-center rounded">
                              <Link
                                to="/ask-access"
                                className="px-6 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center"
                              >
                                <span className="text-base text-white font-semibold">Ask for access</span>
                              </Link>
                            </div>
                          )}
                          <pre className="text-xs sm:text-sm text-[#656565] font-mono whitespace-pre">
{`{
  `}<span className="text-[#656565]">"name"</span>{` : `}<span className="text-[#656565]">"HERO INDUSTRY TEXT PVT LTD"</span>{`,
  `}<span className="text-[#656565]">"utr"</span>{` : `}<span className="text-[#656565]">"P2509251722124359741384986"</span>{`,
  `}<span className="text-[#656565]">"account_number"</span>{` : `}<span className="text-[#656565]">657104000201315</span>{`,
  `}<span className="text-[#656565]">"ifsc"</span>{` : `}<span className="text-[#656565]">"ABCD0000000"</span>{`,
  `}<span className="text-[#656565]">"upi"</span>{` : `}<span className="text-[#656565]">"patternsheet097-10@okicici"</span>{`,
  `}<span className="text-[#656565]">"upi_id"</span>{` : `}<span className="text-[#656565]">"patternsheet097-10@okicici"</span>{`,
  `}<span className="text-[#656565]">"status"</span>{` : `}<span className="text-[#656565]">"VALID"</span>{`
}`}
                          </pre>
                  </div>
                  </div>
                    )}
                  </div>
                </div>
              </div>
          </section>

          {/* CTA Section */}
          <section className="py-[4rem] relative z-20 bg-transparent overflow-hidden">
            {/* Multi-Layer Vector Background */}
            <div className="absolute inset-0 z-0">
              {/* Primary Vector Layer - Neural Network */}
              <svg className="absolute inset-0 w-full h-full opacity-15" viewBox="0 0 1400 900" fill="none">
                <defs>
                  <linearGradient id="neuralGradient1-mtu" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#26ab4d" stopOpacity="0.9"/>
                    <stop offset="30%" stopColor="#1e8a3f" stopOpacity="0.7"/>
                    <stop offset="70%" stopColor="#26ab4d" stopOpacity="0.5"/>
                    <stop offset="100%" stopColor="#1e8a3f" stopOpacity="0.3"/>
                  </linearGradient>
                </defs>
              </svg>
            </div>
          
            <div className="container mx-auto px-[1.5rem] relative z-10">
              <div className="max-w-5xl lg:max-w-fit mx-auto text-center">
                <div className="bg-gradient-to-br from-[#26ab4d]/20 to-[#1e8a3f]/10 backdrop-blur-sm border border-[#26ab4d]/30 rounded-3xl p-8 sm:p-10 lg:p-8">
                  <h3 className="text-[1.125rem] sm:text-[1.375rem] lg:text-[2rem] xl:text-[2.25rem] font-bold mb-[2.5rem] lg:mb-3 break-words lg:leading-tight lg:px-1">
                    <span className="text-[#ffffff]">Ready to Transform Your </span>
                    <span className="text-[#26ab4d]">Mobile to UPI</span>
                  </h3>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                    <Link 
                      to="/ask-access" 
                      className="bg-[#26ab4d] hover:bg-[#1e8a3f] text-[#ffffff] px-5 sm:px-6 lg:px-8 py-2.5 sm:py-3 lg:py-4 rounded-full text-base sm:text-lg lg:text-lg font-bold transition-all duration-300 shadow-xl inline-flex items-center justify-center w-fit sm:w-auto sm:min-w-[160px] lg:min-w-[170px] whitespace-nowrap"
                      onClick={() => {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                    >
                      Ask for access
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </>
      )}

      {/* Mobile to LPG Section */}
      {currentProductId === 'mobile-to-lpg' && (
        <>
          {/* Features & Return Types Section */}
          <section className="pt-[2rem] pb-0 relative z-20 bg-transparent overflow-hidden">
            <div className="container mx-auto px-[1.5rem] relative z-10">
              <div className="w-full">
                {/* Features & JSON Types Section */}
                <div className="bg-black/20 rounded-lg p-4 sm:p-6 mb-8 border border-[#26ab4d]/30">
                  <h4 className="text-xl sm:text-2xl font-bold text-[#ffffff] mb-4 text-center">
                    Features & JSON Types
                  </h4>
                  
                  <div className="space-y-3">
                    {/* LPG Components Accordion */}
                    <div className="border border-[#26ab4d]/30 rounded-lg overflow-hidden bg-black/40">
                      <button
                        onClick={() => toggleAccordion('mobile-to-lpg-components')}
                        className="w-full relative p-3 sm:p-4 text-left hover:bg-[#26ab4d]/10 transition-colors"
                      >
                        <div className="pr-8 sm:pr-10">
                          <h5 className="text-lg font-bold text-[#26ab4d]">LPG Components</h5>
                          <p className="text-[#ffffff] text-sm mt-1 leading-relaxed">
                            Provides LPG connection information linked to the mobile number including consumer details, connection status, address, and distributor information.
                          </p>
                        </div>
                        <span className={`absolute top-3 right-3 sm:top-4 sm:right-4 text-[#26ab4d] text-2xl font-bold transition-all duration-300 ${openAccordions['mobile-to-lpg-components'] ? '' : ''}`}>
                          {openAccordions['mobile-to-lpg-components'] ? '−' : '+'}
                        </span>
                      </button>
                      <div className={`overflow-hidden transition-all duration-300 ${openAccordions['mobile-to-lpg-components'] ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                        <div className="p-4 pt-0 space-y-2">
                          {/* Gas Provider */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('mobile-to-lpg-gas-provider')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/mobile-to-lpg-icons/gas provider.png" alt="Gas Provider" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Gas Provider</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Name of the LPG service provider.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['mobile-to-lpg-gas-provider'] ? 'mr-1' : ''}`}>
                                {openAccordions['mobile-to-lpg-gas-provider'] ? '−' : '+'}
                              </span>
                            </button>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/mobile-to-lpg-icons/gas provider.png" alt="Gas Provider" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('mobile-to-lpg-gas-provider')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'mobile-to-lpg-gas-provider'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'mobile-to-lpg-gas-provider'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Gas Provider</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Name of the LPG service provider.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/mobile-to-lpg-icons/gas provider.png" alt="Gas Provider" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Gas Provider</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Name of the LPG service provider.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('mobile-to-lpg-gas-provider')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'mobile-to-lpg-gas-provider'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'mobile-to-lpg-gas-provider'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['mobile-to-lpg-gas-provider'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"gas_provider": "IndiGas Utilities"}, null, 2)}
                                  </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Name */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('mobile-to-lpg-name')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/mobile-to-lpg-icons/name.png" alt="Name" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Name</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Consumer's registered name.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['mobile-to-lpg-name'] ? 'mr-1' : ''}`}>
                                {openAccordions['mobile-to-lpg-name'] ? '−' : '+'}
                              </span>
                            </button>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/mobile-to-lpg-icons/name.png" alt="Name" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('mobile-to-lpg-name')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'mobile-to-lpg-name'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'mobile-to-lpg-name'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Name</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Consumer's registered name.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/mobile-to-lpg-icons/name.png" alt="Name" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Name</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Consumer's registered name.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('mobile-to-lpg-name')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'mobile-to-lpg-name'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'mobile-to-lpg-name'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['mobile-to-lpg-name'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                              <div className="mt-2">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                    <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                      {JSON.stringify({"name": "Ramesh S. Kulkarni"}, null, 2)}
                                    </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Mobile */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('mobile-to-lpg-mobile')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/mobile-to-lpg-icons/mobile.png" alt="Mobile" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Mobile</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Registered mobile number.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['mobile-to-lpg-mobile'] ? 'mr-1' : ''}`}>
                                {openAccordions['mobile-to-lpg-mobile'] ? '−' : '+'}
                              </span>
                            </button>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/mobile-to-lpg-icons/mobile.png" alt="Mobile" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('mobile-to-lpg-mobile')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'mobile-to-lpg-mobile'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'mobile-to-lpg-mobile'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Mobile</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Registered mobile number.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/mobile-to-lpg-icons/mobile.png" alt="Mobile" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Mobile</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Registered mobile number.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('mobile-to-lpg-mobile')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'mobile-to-lpg-mobile'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'mobile-to-lpg-mobile'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['mobile-to-lpg-mobile'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"mobile": "9000000001"}, null, 2)}
                                  </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Consumer id */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('mobile-to-lpg-consumer-id')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/mobile-to-lpg-icons/consumer id.png" alt="Consumer ID" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Consumer id</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Unique LPG connection ID.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['mobile-to-lpg-consumer-id'] ? 'mr-1' : ''}`}>
                                {openAccordions['mobile-to-lpg-consumer-id'] ? '−' : '+'}
                              </span>
                            </button>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/mobile-to-lpg-icons/consumer id.png" alt="Consumer ID" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('mobile-to-lpg-consumer-id')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'mobile-to-lpg-consumer-id'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'mobile-to-lpg-consumer-id'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Consumer id</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Unique LPG connection ID.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/mobile-to-lpg-icons/consumer id.png" alt="Consumer ID" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Consumer id</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Unique LPG connection ID.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('mobile-to-lpg-consumer-id')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'mobile-to-lpg-consumer-id'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'mobile-to-lpg-consumer-id'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['mobile-to-lpg-consumer-id'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"consumer_id": "LPG-2025-45871234"}, null, 2)}
                                  </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Status */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('mobile-to-lpg-status')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/mobile-to-lpg-icons/status.png" alt="Status" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Status</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Current connection status.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['mobile-to-lpg-status'] ? 'mr-1' : ''}`}>
                                {openAccordions['mobile-to-lpg-status'] ? '−' : '+'}
                              </span>
                            </button>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/mobile-to-lpg-icons/status.png" alt="Status" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('mobile-to-lpg-status')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'mobile-to-lpg-status'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'mobile-to-lpg-status'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Status</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Current connection status.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/mobile-to-lpg-icons/status.png" alt="Status" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Status</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Current connection status.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('mobile-to-lpg-status')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'mobile-to-lpg-status'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'mobile-to-lpg-status'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['mobile-to-lpg-status'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"status": "Active"}, null, 2)}
                                  </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* consumer_type */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('mobile-to-lpg-consumer-type')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/mobile-to-lpg-icons/consumer type.png" alt="Consumer Type" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Consumer Type</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Type of LPG connection.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['mobile-to-lpg-consumer-type'] ? 'mr-1' : ''}`}>
                                {openAccordions['mobile-to-lpg-consumer-type'] ? '−' : '+'}
                              </span>
                            </button>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/mobile-to-lpg-icons/consumer type.png" alt="Consumer Type" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('mobile-to-lpg-consumer-type')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'mobile-to-lpg-consumer-type'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'mobile-to-lpg-consumer-type'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Consumer Type</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Type of LPG connection.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/mobile-to-lpg-icons/consumer type.png" alt="Consumer Type" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Consumer Type</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Type of LPG connection.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('mobile-to-lpg-consumer-type')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'mobile-to-lpg-consumer-type'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'mobile-to-lpg-consumer-type'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['mobile-to-lpg-consumer-type'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"consumer_type": "Domestic"}, null, 2)}
                                  </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Address */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('mobile-to-lpg-address')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/mobile-to-lpg-icons/address.png" alt="Address" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Address</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Consumer's registered address.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['mobile-to-lpg-address'] ? 'mr-1' : ''}`}>
                                {openAccordions['mobile-to-lpg-address'] ? '−' : '+'}
                              </span>
                            </button>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/mobile-to-lpg-icons/address.png" alt="Address" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('mobile-to-lpg-address')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'mobile-to-lpg-address'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'mobile-to-lpg-address'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Address</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Consumer's registered address.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/mobile-to-lpg-icons/address.png" alt="Address" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Address</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Consumer's registered address.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('mobile-to-lpg-address')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'mobile-to-lpg-address'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'mobile-to-lpg-address'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['mobile-to-lpg-address'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"address": "Flat 402, Sunrise Apartments, MG Road, Pune, Maharashtra, 411001"}, null, 2)}
                                  </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Distributor Code */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('mobile-to-lpg-distributor-code')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/mobile-to-lpg-icons/distributor code.png" alt="Distributor Code" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Distributor Code</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Code of the LPG distributor.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['mobile-to-lpg-distributor-code'] ? 'mr-1' : ''}`}>
                                {openAccordions['mobile-to-lpg-distributor-code'] ? '−' : '+'}
                              </span>
                            </button>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/mobile-to-lpg-icons/distributor code.png" alt="Distributor Code" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('mobile-to-lpg-distributor-code')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'mobile-to-lpg-distributor-code'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'mobile-to-lpg-distributor-code'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Distributor Code</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Code of the LPG distributor.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/mobile-to-lpg-icons/distributor code.png" alt="Distributor Code" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Distributor Code</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Code of the LPG distributor.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('mobile-to-lpg-distributor-code')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'mobile-to-lpg-distributor-code'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'mobile-to-lpg-distributor-code'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['mobile-to-lpg-distributor-code'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                  <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                                    {JSON.stringify({"distributor_code": "DSTR-0458"}, null, 2)}
                                  </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Distributor Name */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('mobile-to-lpg-distributor-name')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/mobile-to-lpg-icons/distributor name.png" alt="Distributor Name" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Distributor Name</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Name of the LPG distributor.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['mobile-to-lpg-distributor-name'] ? 'mr-1' : ''}`}>
                                {openAccordions['mobile-to-lpg-distributor-name'] ? '−' : '+'}
                              </span>
                            </button>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/mobile-to-lpg-icons/distributor name.png" alt="Distributor Name" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('mobile-to-lpg-distributor-name')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'mobile-to-lpg-distributor-name'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'mobile-to-lpg-distributor-name'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Distributor Name</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Name of the LPG distributor.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/mobile-to-lpg-icons/distributor name.png" alt="Distributor Name" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Distributor Name</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Name of the LPG distributor.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('mobile-to-lpg-distributor-name')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'mobile-to-lpg-distributor-name'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'mobile-to-lpg-distributor-name'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['mobile-to-lpg-distributor-name'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                  <code className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold">
                                    <span className="text-[#9a9a9a] font-bold">"distributor_name"</span> : <span className="text-[#9a9a9a] font-bold">"Asha LPG Distributors"</span>
                                  </code>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Distributor Contact */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('mobile-to-lpg-distributor-contact')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/mobile-to-lpg-icons/distributor contact.png" alt="Distributor Contact" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Distributor Contact</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Contact number of the distributor.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['mobile-to-lpg-distributor-contact'] ? 'mr-1' : ''}`}>
                                {openAccordions['mobile-to-lpg-distributor-contact'] ? '−' : '+'}
                              </span>
                            </button>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/mobile-to-lpg-icons/distributor contact.png" alt="Distributor Contact" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('mobile-to-lpg-distributor-contact')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'mobile-to-lpg-distributor-contact'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'mobile-to-lpg-distributor-contact'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Distributor Contact</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Contact number of the distributor.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/mobile-to-lpg-icons/distributor contact.png" alt="Distributor Contact" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Distributor Contact</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Contact number of the distributor.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('mobile-to-lpg-distributor-contact')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'mobile-to-lpg-distributor-contact'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'mobile-to-lpg-distributor-contact'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['mobile-to-lpg-distributor-contact'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                  <code className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold">
                                    <span className="text-[#9a9a9a] font-bold">"distributor_contact"</span> : <span className="text-[#9a9a9a] font-bold">"7022233445"</span>
                                  </code>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Distributor Address */}
                          <div className="border border-[#26ab4d]/20 rounded-lg overflow-hidden bg-black/30">
                            {accessGranted ? (
                              <>
                            <button
                              onClick={() => toggleAccordion('mobile-to-lpg-distributor-address')}
                              className="w-full flex items-center justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <img src="/mobile-to-lpg-icons/distributor address.png" alt="Distributor Address" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                <div className="flex-1">
                                  <h6 className="text-base font-semibold text-[#ffffff]">Distributor Address</h6>
                                  <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                    Address of the LPG distributor.
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[#26ab4d] text-xl font-bold transition-all duration-300 flex-shrink-0 ml-2 ${openAccordions['mobile-to-lpg-distributor-address'] ? 'mr-1' : ''}`}>
                                {openAccordions['mobile-to-lpg-distributor-address'] ? '−' : '+'}
                              </span>
                            </button>
                              </>
                            ) : (
                              <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 hover:bg-[#26ab4d]/10 transition-colors">
                                {/* Mobile: Top row with icon and lock */}
                                <div className="flex justify-between items-start mb-2 lg:hidden">
                                  <img src="/mobile-to-lpg-icons/distributor address.png" alt="Distributor Address" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                                  <div
                                    className="flex-shrink-0 flex items-center gap-2"
                                    onMouseEnter={() => setHoveredAccordion('mobile-to-lpg-distributor-address')}
                                    onMouseLeave={() => setHoveredAccordion(null)}
                                  >
                                    <div className={`transition-all duration-300 ease-out ${
                                      hoveredAccordion === 'mobile-to-lpg-distributor-address'
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                    }`}>
                                      <Link
                                        to="/ask-access"
                                        className="px-3 sm:px-4 py-2 sm:py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                      >
                                        <span className="text-xs sm:text-sm text-white font-semibold">Ask for access</span>
                                      </Link>
                                    </div>
                                    <svg
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                        hoveredAccordion === 'mobile-to-lpg-distributor-address'
                                          ? 'scale-110 rotate-[-5deg]'
                                          : 'hover:scale-105'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {/* Mobile: Header and Description stacked vertically */}
                                <div className="flex flex-col mb-2 lg:hidden">
                                  <h6 className="text-sm sm:text-base font-semibold text-[#ffffff]">Distributor Address</h6>
                                  <p className="text-[#ffffff] text-xs sm:text-sm leading-relaxed mt-0.5">
                                    Address of the LPG distributor.
                                  </p>
                                </div>
                                {/* Desktop: Original layout */}
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                  <img src="/mobile-to-lpg-icons/distributor address.png" alt="Distributor Address" className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0" />
                                  <div className="flex-1">
                                    <h6 className="text-base font-semibold text-[#ffffff]">Distributor Address</h6>
                                    <p className="text-[#ffffff] text-xs leading-relaxed mt-0.5">
                                      Address of the LPG distributor.
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="hidden lg:flex flex-shrink-0 ml-2 items-center gap-2"
                                  onMouseEnter={() => setHoveredAccordion('mobile-to-lpg-distributor-address')}
                                  onMouseLeave={() => setHoveredAccordion(null)}
                                >
                                  <div className={`transition-all duration-300 ease-out ${
                                    hoveredAccordion === 'mobile-to-lpg-distributor-address'
                                      ? 'opacity-100 translate-x-0 scale-100'
                                      : 'opacity-0 translate-x-2 scale-95 pointer-events-none'
                                  }`}>
                                    <Link
                                      to="/ask-access"
                                      className="px-4 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center hover:scale-105 active:scale-95"
                                    >
                                      <span className="text-sm text-white font-semibold">Ask for access</span>
                                    </Link>
                                  </div>
                                  <svg
                                    className={`w-6 h-6 text-[#26ab4d] transition-all duration-300 cursor-pointer ${
                                      hoveredAccordion === 'mobile-to-lpg-distributor-address'
                                        ? 'scale-110 rotate-[-5deg]'
                                        : 'hover:scale-105'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordions['mobile-to-lpg-distributor-address'] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="px-3 pb-3">
                                <div className="mt-2">
                                  <div className="bg-black/40 rounded p-1.5 border border-[#26ab4d]/20 relative">
                                  {!accessGranted && <LockOverlay compact />}
                                  <code className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold">
                                    <span className="text-[#9a9a9a] font-bold">"distributor_address"</span> : <span className="text-[#9a9a9a] font-bold">"No. 12, Industrial Estate Road, Pune, Maharashtra, 411018"</span>
                                  </code>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Unified Mobile to LPG Tool */}
          <section className="pt-0 pb-[4rem] relative z-20 bg-transparent overflow-hidden">
            <div className="container mx-auto px-[1.5rem] relative z-10">
              <div className="bg-black/20 rounded-lg p-4 sm:p-6 lg:p-8 border border-[#26ab4d]/30">
                  <div>
                    <h4 className="text-lg sm:text-xl lg:text-2xl xl:text-3xl font-bold mb-4 sm:mb-5 lg:mb-6">
                      <span className="text-[#ffffff]">Mobile to LPG </span>
                      <span className="bg-gradient-to-r from-[#26ab4d] via-[#1e8a3f] to-[#26ab4d] bg-clip-text text-transparent">Tool</span>
                    </h4>
                    
                    {/* Input Section */}
                    <div className="mb-4 sm:mb-5 lg:mb-6">
                    <div className="flex flex-col sm:flex-row gap-2.5 sm:gap-3 lg:gap-4">
                      <div className="flex-1">
                        <input
                          type="text"
                          value={mobileToLPGInput}
                          onChange={(e) => setMobileToLPGInput(e.target.value)}
                          placeholder={`"9876012345"`}
                          className={`w-full px-3 sm:px-4 lg:px-6 py-2 sm:py-2.5 lg:py-3 xl:py-4 bg-[#26ab4d]/10 border-2 rounded-full text-[#ffffff] placeholder-[#9a9a9a] placeholder:font-bold focus:outline-none focus:ring-2 text-sm sm:text-base lg:text-[1.125rem] ${
                            mobileToLPGInput && !isValidPhone(mobileToLPGInput)
                              ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                              : 'border-[#26ab4d]/50 focus:ring-[#26ab4d] focus:border-[#26ab4d]'
                          }`}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleAnalyzeMobileToLPG();
                            }
                          }}
                        />
                </div>
                      <button
                        onClick={handleAnalyzeMobileToLPG}
                        disabled={isAnalyzingMobileToLPG || !mobileToLPGInput.trim()}
                        className="px-4 sm:px-5 lg:px-6 xl:px-8 py-2 sm:py-2.5 lg:py-3 xl:py-4 bg-gradient-to-r from-[#26ab4d] to-[#1e8a3f] hover:from-[#1e8a3f] hover:to-[#26ab4d] text-[#ffffff] rounded-full font-semibold transition-all duration-300 shadow-lg shadow-[#26ab4d]/25 disabled:opacity-50 disabled:cursor-not-allowed min-w-[120px] sm:min-w-[140px] text-xs sm:text-sm lg:text-[1.125rem] whitespace-nowrap"
                      >
                        {isAnalyzingMobileToLPG ? (
                          <span className="flex items-center justify-center">
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-[#ffffff]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
                            Analyzing...
                    </span>
                        ) : (
                          'Submit'
                        )}
                      </button>
              </div>
            </div>
            
                  {/* Results Section */}
                  {mobileToLPGResult && (
                    <div className="space-y-6 relative">
                      {!accessGranted && (
                        <div className="absolute inset-0 bg-black/95 backdrop-blur-md z-[100] flex items-center justify-center rounded-lg">
                          <Link
                            to="/ask-access"
                            className="px-6 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center"
                          >
                            <span className="text-base text-white font-semibold">Ask for access</span>
                          </Link>
                        </div>
                      )}
                      <div className="bg-black/20 rounded-lg p-6 border border-[#26ab4d]/20">
                        <h5 className="text-xl font-bold text-[#ffffff] mb-4">Mobile to LPG Results</h5>
                        <div className="bg-black/40 rounded-lg p-4 overflow-x-auto border border-[#26ab4d]/20">
                          <pre className="text-[0.875rem] text-[#9a9a9a] font-mono font-bold whitespace-pre">
                            {formatFlattenedJSON(mobileToLPGResult.jsonResponse)}
                          </pre>
                </div>
              </div>
                    </div>
                  )}

                    {/* Default Example - Show when no analysis */}
                  {!mobileToLPGResult && (
                      <div className="bg-black/20 rounded-lg p-6 border border-[#26ab4d]/20">
                      <p className="text-[#ffffff] text-center mb-4">Enter a mobile number above to see comprehensive LPG connection information</p>
                        <div className="bg-black/40 rounded-lg p-4 overflow-x-auto border border-[#26ab4d]/20 relative">
                          {!accessGranted && (
                            <div className="absolute inset-0 bg-black/95 backdrop-blur-md z-[100] flex items-center justify-center rounded">
                              <Link
                                to="/ask-access"
                                className="px-6 py-3 rounded-full bg-[#26ab4d]/20 border border-[#26ab4d]/50 hover:bg-[#26ab4d]/30 hover:border-[#26ab4d] transition-all duration-300 whitespace-nowrap inline-flex items-center justify-center"
                              >
                                <span className="text-base text-white font-semibold">Ask for access</span>
                              </Link>
                            </div>
                          )}
                          <pre className="text-xs sm:text-sm text-[#656565] font-mono whitespace-pre">
{`{
  `}<span className="text-[#656565]">"gas_provider"</span>{` : `}<span className="text-[#656565]">"IndiGas Utilities"</span>{`,
  `}<span className="text-[#656565]">"name"</span>{` : `}<span className="text-[#656565]">"Ramesh S. Kulkarni"</span>{`,
  `}<span className="text-[#656565]">"mobile"</span>{` : `}<span className="text-[#656565]">"9000000001"</span>{`,
  `}<span className="text-[#656565]">"consumer_id"</span>{` : `}<span className="text-[#656565]">"LPG-2025-45871234"</span>{`,
  `}<span className="text-[#656565]">"status"</span>{` : `}<span className="text-[#656565]">"Active"</span>{`,
  `}<span className="text-[#656565]">"consumer_type"</span>{` : `}<span className="text-[#656565]">"Domestic"</span>{`,
  `}<span className="text-[#656565]">"address"</span>{` : `}<span className="text-[#656565]">"Flat 402, Sunrise Apartments, MG Road, Pune, Maharashtra, 411001"</span>{`,
  `}<span className="text-[#656565]">"distributor_code"</span>{` : `}<span className="text-[#656565]">"DSTR-0458"</span>{`,
  `}<span className="text-[#656565]">"distributor_name"</span>{` : `}<span className="text-[#656565]">"Asha LPG Distributors"</span>{`,
  `}<span className="text-[#656565]">"distributor_contact"</span>{` : `}<span className="text-[#656565]">"7022233445"</span>{`,
  `}<span className="text-[#656565]">"distributor_address"</span>{` : `}<span className="text-[#656565]">"No. 12, Industrial Estate Road, Pune, Maharashtra, 411018"</span>{`
}`}
                          </pre>
                  </div>
                  </div>
                    )}
                </div>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-[4rem] relative z-20 bg-transparent overflow-hidden">
            {/* Multi-Layer Vector Background */}
            <div className="absolute inset-0 z-0">
              {/* Primary Vector Layer - Neural Network */}
              <svg className="absolute inset-0 w-full h-full opacity-15" viewBox="0 0 1400 900" fill="none">
                <defs>
                  <linearGradient id="neuralGradient1-mtl" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#26ab4d" stopOpacity="0.9"/>
                    <stop offset="30%" stopColor="#1e8a3f" stopOpacity="0.7"/>
                    <stop offset="70%" stopColor="#26ab4d" stopOpacity="0.5"/>
                    <stop offset="100%" stopColor="#1e8a3f" stopOpacity="0.3"/>
                  </linearGradient>
                </defs>
              </svg>
            </div>
          
            <div className="container mx-auto px-[1.5rem] relative z-10">
              <div className="max-w-5xl lg:max-w-fit mx-auto text-center">
                <div className="bg-gradient-to-br from-[#26ab4d]/20 to-[#1e8a3f]/10 backdrop-blur-sm border border-[#26ab4d]/30 rounded-2xl sm:rounded-3xl lg:rounded-3xl p-4 sm:p-6 lg:p-8">
                  <h3 className="text-[1.125rem] sm:text-[1.375rem] lg:text-[2rem] xl:text-[2.25rem] font-bold mb-4 sm:mb-5 lg:mb-3 break-words lg:leading-tight lg:px-1">
                    <span className="text-[#ffffff]">Ready to Transform Your </span>
                    <span className="text-[#26ab4d]">Mobile to LPG</span>
                  </h3>
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 lg:gap-4 justify-center items-center">
                    <Link 
                      to="/ask-access" 
                      className="bg-[#26ab4d] hover:bg-[#1e8a3f] text-[#ffffff] px-5 sm:px-6 lg:px-8 py-2.5 sm:py-3 lg:py-4 rounded-full text-base sm:text-lg lg:text-lg font-bold transition-all duration-300 shadow-xl inline-flex items-center justify-center w-fit sm:w-auto sm:min-w-[160px] lg:min-w-[170px] whitespace-nowrap"
                      onClick={() => {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                    >
                      Ask for access
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </>
      )}

      {/* CTA Section - For all products */}
      {productId !== 'phone-intelligence' && productId !== 'email-intelligence' && productId !== 'ip' && productId !== 'data-leaks-intelligence' && productId !== 'hardware-fingerprinting' && productId !== 'behavioral-analysis' && productId !== 'location-intelligence' && productId !== 'mobile-to-address' && productId !== 'mobile-to-upi' && productId !== 'mobile-to-lpg' && (
        <section className="py-[4rem] relative z-20 bg-transparent overflow-hidden">
            {/* Multi-Layer Vector Background */}
            <div className="absolute inset-0 z-0">
            {/* Primary Neural Network Vector */}
              <svg className="absolute inset-0 w-full h-full opacity-20" viewBox="0 0 1400 900" fill="none">
                <defs>
                <linearGradient id="neuralGradient1-cta-all" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#26ab4d" stopOpacity="0.9"/>
                    <stop offset="30%" stopColor="#1e8a3f" stopOpacity="0.7"/>
                    <stop offset="70%" stopColor="#26ab4d" stopOpacity="0.5"/>
                    <stop offset="100%" stopColor="#1e8a3f" stopOpacity="0.3"/>
                  </linearGradient>
                <linearGradient id="neuralGradient2-cta-all" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#1e8a3f" stopOpacity="0.8"/>
                  <stop offset="50%" stopColor="#26ab4d" stopOpacity="0.6"/>
                  <stop offset="100%" stopColor="#1e8a3f" stopOpacity="0.4"/>
                </linearGradient>
                <filter id="neuralGlow-cta-all" x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                  <feMerge> 
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
                </defs>
              </svg>
              
            {/* Secondary Vector Layer - DNA Helix */}
              <svg className="absolute inset-0 w-full h-full opacity-10" viewBox="0 0 1400 900" fill="none">
                <defs>
                <linearGradient id="dnaGradient-cta-all" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#26ab4d" stopOpacity="0.6"/>
                    <stop offset="50%" stopColor="#1e8a3f" stopOpacity="0.4"/>
                    <stop offset="100%" stopColor="#26ab4d" stopOpacity="0.2"/>
                  </linearGradient>
                </defs>
              </svg>
              
            {/* Tertiary Vector Layer - Quantum Patterns */}
              <svg className="absolute inset-0 w-full h-full opacity-8" viewBox="0 0 1400 900" fill="none">
                <defs>
                <radialGradient id="quantumGradient-cta-all" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="#26ab4d" stopOpacity="0.8"/>
                    <stop offset="70%" stopColor="#1e8a3f" stopOpacity="0.4"/>
                    <stop offset="100%" stopColor="#26ab4d" stopOpacity="0.1"/>
                  </radialGradient>
                </defs>
              </svg>
            </div>
            
          <div className="container mx-auto px-3 sm:px-4 lg:px-[1.5rem] relative z-10">
              <div className="max-w-5xl lg:max-w-fit mx-auto text-center">
                <div className="bg-gradient-to-br from-[#26ab4d]/20 to-[#1e8a3f]/10 backdrop-blur-sm border border-[#26ab4d]/30 rounded-2xl sm:rounded-3xl lg:rounded-3xl p-5 sm:p-6 lg:p-8">
                <h3 className="text-[1.125rem] sm:text-[1.375rem] lg:text-[2rem] xl:text-[2.25rem] font-bold mb-6 sm:mb-8 lg:mb-3 break-words lg:leading-tight lg:px-1">
                  <span className="text-[#ffffff]">Ready to Transform Your </span>
                  <span className="text-[#26ab4d]">{product?.title}</span>
                  </h3>
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 lg:gap-4 justify-center items-center">
                    <Link 
                      to="/ask-access" 
                      className="bg-[#26ab4d] hover:bg-[#1e8a3f] text-[#ffffff] px-5 sm:px-6 lg:px-8 py-2.5 sm:py-3 lg:py-4 rounded-full text-base sm:text-lg lg:text-lg font-bold transition-all duration-300 shadow-xl inline-flex items-center justify-center w-fit sm:w-auto sm:min-w-[160px] lg:min-w-[170px] whitespace-nowrap"
                      onClick={() => {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                    >
                      Ask for access
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </section>
      )}


      {/* Contact Popup Modal */}
      {showContactPopup && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setShowContactPopup(false)}>
          <div className="bg-black border-2 border-[#26ab4d] rounded-2xl p-8 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-[#ffffff]">Contact Us</h3>
              <button
                onClick={() => setShowContactPopup(false)}
                className="text-[#ffffff] hover:text-[#ffffff] transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Phone Number */}
              <div className="flex items-center space-x-3 p-4 bg-[#26ab4d]/10 border border-[#26ab4d]/30 rounded-lg">
                <div className="flex-shrink-0">
                  <svg className="w-6 h-6 text-[#26ab4d]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-[#ffffff]">Phone Number</p>
                  <a href="tel:+919920042243" className="text-[#ffffff] text-lg font-semibold hover:text-[#26ab4d] transition-colors">
                    +919920042243
                  </a>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-center space-x-3 p-4 bg-[#26ab4d]/10 border border-[#26ab4d]/30 rounded-lg">
                <div className="flex-shrink-0">
                  <svg className="w-6 h-6 text-[#26ab4d]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-[#ffffff]">Email Address</p>
                  <a href="mailto:info@bargad.ai" className="text-[#ffffff] text-lg font-semibold hover:text-[#26ab4d] transition-colors">
                    info@bargad.ai
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Wrap component with React.memo to prevent unnecessary re-renders
export default React.memo(ProductPage);